import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FiUsers, FiUser, FiClock, FiShoppingCart, FiTag, FiDownload, FiUpload, FiPlus, FiSearch, FiTrash2, FiLogOut, FiX, FiCheck, FiDollarSign, FiGrid, FiBriefcase, FiDroplet, FiBox, FiTrendingUp, FiCreditCard, FiFileText, FiSettings, FiBell, FiChevronDown, FiCalendar, FiMapPin, FiChevronLeft, FiChevronRight, FiEdit, FiHash, FiEye } from "react-icons/fi";
import * as XLSX from "xlsx";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { updateActivity } from "../api/auth";
import { API_ENDPOINTS, constructEndpoint } from "../api/apiConfig";
import EmployeeAdvanceUpload from "./EmployeeAdvanceUpload";
import PurchaseUpload from "./PurchaseUpload";
import SellUpload from "./SellUpload";
import ExpenseUpload from "./ExpenseUpload";
import ManagementExpenseForm from "./ManagementExpenseForm";
import SalaryForm from "./SalaryForm";
import WorkOrderUpload from "./WorkOrderUpload";
import WorkOrderForm from "./WorkOrderForm";
import FuelPaymentForm from "./FuelPaymentForm";
import { buildWorkOrderQuery, filterWorkOrdersByType, getFuelManagementTabKey, getRefreshTabsAfterDelete, handleTabSelection, isKheplSite, shouldShowBulkActionTab } from "./dashboardTabUtils";
import { isAdminUser } from "./siteAccessUtils";
import { getListRowCount } from "./dashboardCountUtils";
import {
  buildExpenseEditState,
  buildPurchaseEditState,
  buildSellEditState,
  buildContractorWorkEditState,
  buildContractorPaymentEditState,
  buildFuelPumpEditState,
  normalizeContractorPaymentMethod,
} from "./editFormUtils";
import "../styles/dashboard.css";

const INIT = {
  employees: 0,
  attendance: [],
  employeeadvance: 0,
  salary: 0,
  purchases: 0,
  sells: 0,
  expenses: 0,
  plantFuelInAmount: 0,
  expenseTypeTotals: {
    Mess: 0,
    Machinery: 0,
    Site: 0,
    Refreshments: 0,
    "Office Expenses": 0,
    Hardware: 0,
    "Mobile Recharge": 0,
    Travel: 0,
  },
  diesel: 0,
  petrol: 0,
  fuelpump: 0,
  fueltransaction: 0,
  fuelpayment: 0,
  labour: 0,
  bills: 0,
  managementExpense: 0,
  contractor: [],
  contractorwork: [],
  contractorpayment: [],
  labouradvance: 0,
  labourattendance: 0,
  labourpayment: 0,
  concreteproduction: 0,
  contractorpayment: 0,
};

const BILL_TYPE_OPTIONS = ["SKT_INFRACON", "SKT_CONSTRUCTION", "SKT_RENTEL", "SKT_BUILDCON"];
const formatNumberIN = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("en-IN") : "0";
};

export const calculateTotalExpense = (data) => {
  const getAmount = (value) => {
    if (Array.isArray(value)) {
      return value.reduce((total, row) => {
        const amount = Number(row?.amount ?? row?.totalAmount ?? row?.paymentAmount ?? 0);
        return total + (Number.isFinite(amount) ? amount : 0);
      }, 0);
    }

    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  };

  return [
    data?.expenses,
    data?.fuelpump,
    data?.plantFuelInAmount,
    data?.employeeadvance,
    data?.contractorpayment,
  ].reduce((total, amount) => total + getAmount(amount), 0);
};

export const sortSalaryRowsLatestFirst = (rows) => [...rows].sort((a, b) => {
  const dateA = Date.parse(a?.salaryDate ?? a?.salary_date ?? "");
  const dateB = Date.parse(b?.salaryDate ?? b?.salary_date ?? "");
  const hasDateA = Number.isFinite(dateA);
  const hasDateB = Number.isFinite(dateB);

  if (!hasDateA && !hasDateB) return 0;
  if (!hasDateA) return 1;
  if (!hasDateB) return -1;
  return dateB - dateA;
});

export const formatAmountIN = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "-";
};

export const resolveRowId = (row, idKey = "id", index = 0) => {
  if (!row || typeof row !== "object") {
    return `row-${index}`;
  }

  const directValue = row[idKey];
  if (directValue !== undefined && directValue !== null && directValue !== "") {
    return directValue;
  }

  const fallbackValues = [
    row.id,
    row._id,
    row.expenseId,
    row.expense_id,
    row.expense?.id,
    row.expense?.expenseId,
    row.expense?.expense_id,
    row.managementExpenseId,
    row.management_expense_id,
    row.managementExpense?.id,
    row.managementExpense?.managementExpenseId,
    row.managementExpense?.management_expense_id,
    row.purchaseId,
    row.purchaseNo,
    row.purchaseNumber,
    row.purchase_no,
    row.sellId,
    row.sell_id,
    row.saleId,
    row.sale_id,
    row.attendanceId,
    row.fnfId,
    row.empId,
    row.employeeId,
    row.employee?.id,
    row.employeeId?.id,
    row.empCode,
    row.employeeCode,
    row.workOrderId,
    row.workorderId,
    row.woNumber,
    row.paymentId,
    row.contractorId,
    row.contractorID,
    row.contractor_id,
    row.concreteProductionId,
    row.concreteProductionID,
    row.concrete_production_id,
    row.productionId,
    row.productionID,
    row.production_id,
    row.concreteProduction?.id,
    row.production?.id,
    row.fuelTransactionPlantId,
    row.fuel_transaction_plant_id,
    row.fuelTransactionPumpId,
    row.fuel_transaction_pump_id,
    row.fuelPaymentId,
    row.fuel_payment_id,
    row.labourPaymentId,
    row.labour_payment_id,
    row.labourId,
    row.labour_id,
  ];

  const resolvedValue = fallbackValues.find(value => value !== undefined && value !== null && value !== "");
  return resolvedValue ?? `row-${index}`;
};

const getConcreteProductionId = (record) => [
  record?.id,
  record?._id,
  record?.concreteProductionId,
  record?.concreteProductionID,
  record?.concrete_production_id,
  record?.productionId,
  record?.productionID,
  record?.production_id,
  record?.concreteProduction?.id,
  record?.production?.id,
].find(id => id !== undefined && id !== null && id !== "") ?? null;

const getApproverEmployeeCode = employee => employee?.empCode
  ?? employee?.empcode
  ?? employee?.employeeCode
  ?? employee?.employee?.empCode
  ?? "";

const getApproverEmployeeId = employee => employee?.id
  ?? employee?.empId
  ?? employee?.employeeId?.id
  ?? employee?.employeeId
  ?? employee?.employee?.id
  ?? "";

const SIDEBAR_ITEMS = [
  { label: "Dashboard", icon: FiGrid, key: "dashboard" },
  { label: "Employees", icon: FiUsers, key: "employees" },
  { label: "Contractor Workforce", icon: FiBriefcase, key: "contractor" },
  { label: "Plant Labours", icon: FiUsers, key: "labour" },
  { label: "Purchase", icon: FiShoppingCart, key: "purchases" },
  { label: "Sell", icon: FiTrendingUp, key: "sells" },
  { label: "Expenses", icon: FiCreditCard, key: "expenses" },
    { label: "Work Orders", icon: FiFileText, key: "workorders" },
  { label: "Bills", icon: FiFileText, key: "bills" },
  { label: "Management Expense", icon: FiCreditCard, key: "managementExpense" },
  { label: "Fuel Management", icon: FiDroplet, key: "fueltransaction" },
  { label: "Concrete Production", icon: FiBox, key: "concreteproduction" },
  { label: "Partnership Management", icon: FiBriefcase, key: "partnership" },
];

function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  return (
    <div className="toast" data-type={type}>
      {type === "success" ? <FiCheck size={16} /> : <FiX size={16} />}
      {msg}
      <button onClick={onClose}>✕</button>
    </div>
  );
}

function Modal({ title, children, onClose, contentStyle }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={contentStyle} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

function EmployeeDetails({ employee, siteOptions }) {
  const record = employee?.employee && typeof employee.employee === "object"
    ? { ...employee, ...employee.employee }
    : employee?.employeeId && typeof employee.employeeId === "object"
      ? { ...employee, ...employee.employeeId }
      : employee || {};
  const address = record.address && typeof record.address === "object" ? record.address : {};
  const value = (...values) => values.find(item => item !== undefined && item !== null && String(item).trim() !== "");
  const formatAadhaar = number => {
    const digits = String(number).replace(/\D/g, "");
    return digits.length === 12 ? digits.replace(/(\d{4})(?=\d)/g, "$1 ") : String(number);
  };
  const siteId = value(record.site?.id, record.siteId, record.site_id, record.site?.siteId);
  const siteName = value(
    typeof record.site === "string" ? record.site : null,
    record.site?.siteName,
    record.site?.site_name,
    record.site?.name,
    record.siteName,
    record.site_name,
    siteOptions.find(site => String(site.id ?? site.siteId ?? site.site_id ?? "") === String(siteId))?.name,
    siteId
  );
  const activeStatus = value(record.isActive, record.active);
  const fields = [
    ["🆔", "Employee Code", value(record.empCode, record.empcode, record.emp_code, record.employeeCode)],
    ["👤", "Full Name", value(record.name, record.fullName, record.empName, record.emp_name)],
    ["✉️", "Email", value(record.email, record.emailAddress)],
    ["📞", "Phone", value(record.phone, record.phoneNumber)],
    ["💼", "Position", value(record.position, record.role, record.designation)],
    ["🧑‍💼", "Employee Type", value(record.empType, record.employeeType, record.emp_type)],
    ["📅", "Date of Joining", value(record.doj, record.joiningDate, record.joinDate)],
    ["💰", "Salary", value(record.salary, record.monthlySalary)],
    ["🪪", "Aadhaar Number", value(record.aadharNumber, record.aadhar_Number, record.aadhar_number, record.aadharNo)],
    ["🏢", "Site", siteName],
    ["✅", "Status", activeStatus !== undefined
      ? (activeStatus === true || String(activeStatus).toLowerCase() === "true" || String(activeStatus).toLowerCase() === "active" ? "Active" : "Inactive")
      : value(record.status)],
    ["🏡", "Village", value(record.village, address.village)],
    ["🏠", "Post", value(record.post, address.post)],
    ["🗺️", "District", value(record.district, address.district)],
    ["🌍", "State", value(record.state, address.state)],
    ["📮", "PIN Code", value(record.pin, record.pinCode, address.pin, address.pinCode)],
  ];

  return (
    <dl className="employee-details-grid">
      {fields.map(([icon, label, fieldValue]) => (
        <div className="employee-detail-item" key={label}>
          <dt><span aria-hidden="true">{icon}</span>{label}</dt>
          <dd>{fieldValue === undefined ? "-" : label === "Aadhaar Number" ? formatAadhaar(fieldValue) : String(fieldValue)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function DataTable({ cols, rows, onDelete, idKey = "id", isAdmin = false, onPreviewInvoice, onDownloadAttachment, onEditPaymentStatus, onEditEmployeeStatus, onEditEmployeeSite, employeeSiteOptions = [], invoiceLoadingId, onViewExpenseAttachment, onViewEmployeeAadhar, onViewContractorDocuments, actionColumnPosition = "end", onRowClick, selectedRowId, expandedRowId, renderExpandedRow, onEditRow, onViewRow, allowDelete = false }) {
  const visibleCols = !isAdmin && cols.includes("Bill No")
    ? cols.filter(column => column !== "Amount Received" && column !== "Remaining Amount")
    : cols;

  // Log for debugging
  useEffect(() => {
    console.log("DataTable rendered with isAdmin:", isAdmin);
  }, [isAdmin]);

  // Field mapping for API responses - context aware based on columns
  const isAttendanceTable = visibleCols.includes("Status") && visibleCols.includes("Attendance Date");
  const isEmployeeFNFTable = visibleCols.includes("Exit Date");
  const isPurchaseTable = visibleCols.includes("Purchase Number") && visibleCols.includes("Payment Status");
  const paymentStatusOptions = [
    { value: "PAID", label: "Paid" },
    { value: "UNPAID", label: "Unpaid" },
    { value: "PARTIAL_PAYMENT", label: "Partial Paid" },
  ];
  const normalizeStatusValue = (value) => {
    if (typeof value !== "string") return "";
    return value.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z_]/g, "");
  };
  
  const fieldMap = {
    "Empcode": ["empCode", "empcode", "emp_code"],
    "Name": ["name", "emp_name"],
    "Role": ["position", "role", "designation"],
    "Phone": ["phone", "phoneNumber"],
    "Joined": ["doj", "joined", "joinDate"],
    "ID": isAttendanceTable ? ["employeeId.empCode", "empCode", "empcode"] : ["empId", "id", "_id"],
    "Employee Code": ["employee.empCode", "employee.empcode", "employeeCode", "empCode"],
    "Employee": isAttendanceTable ? ["employeeId.name", "empName", "name"] : isEmployeeFNFTable ? ["employee.name", "employee.empName", "name"] : ["employee.empCode", "employee.name", "name", "employee_name"],
    "Exit Date": ["exitDate", "exit_date"],
    "Active Status": ["isActive", "active", "status"],
    "Date": ["attendanceDate", "date", "paymentDate", "fuelDate", "productionDate"],
    "Status": ["status", "attendanceStatus"],
    "Hours": ["hours", "hoursWorked"],
    "Purchase Number": ["purchaseNo", "purchaseNumber", "purchase_no"],
    "Material Name": ["materialName", "material", "item", "itemName"],
    "Party Name": ["partyName", "party", "vendor", "vendorName"],
    "Purchase Date": ["purchaseDate", "date"],
    "Quantity": ["quantity", "qty", "quantity"],
    "Payment Status": ["paymentStatus", "payment_status", "status"],
    "Receiver Name": ["receiverName", "receiver"],
    "Bill No": ["billNo", "billNumber", "bill_no"],
    "Bill Date": ["billDate", "date", "bill_date"],
    "Amount": ["amount", "totalAmount", "total_amount"],
    "Amount Received": ["amountReceived", "amount_received"],
    "Remaining Amount": ["remainingAmount", "remaining_amount"],
    "Bill Type": ["billType", "bill_type", "type"],
    "Sell No": ["sellNo", "sell_no", "saleNo", "billNo", "bill_number"],
    "Sell Date": ["sellDate", "date", "saleDate"],
    "Item": ["item", "itemName"],
    "Vendor": ["vendor", "vendorName"],
    "Amount": ["amount", "price", "paymentAmount"],
    "Category": ["category", "type"],
    "Buyer": ["buyer", "buyerName"],
    "Description": ["description", "desc"],
    "Expense Date": ["expenseDate", "date"],
    "Expense Type": ["category", "expenseType", "type"],
    "From Employee": ["fromEmployee.name", "fromEmployee.fullName", "fromEmployee.empCode", "fromEmployee.empcode"],
    "To Employee": ["toEmployee.name", "toEmployee.fullName", "toEmployee.empCode", "toEmployee.empcode"],
    "Vehicle Number": ["vehicleNumber", "vehicle_number"],
    "Received By": ["receivedBy", "received_by"],
    "Approved By": ["approvedBy", "approved_by"],
    "Diesel Date": ["dieselDate", "date"],
    "Petrol Date": ["petrolDate", "date"],
    "Labour Name": ["labourName", "labour_name"],
    "Labour Code": ["labour.labourCode", "labourCode", "labour_code"],
    "Labour Type": ["labourType", "labour_type"],
    "Daily Wage": ["labour.dailyWage", "dailyWage", "daily_wage"],
    "Contractor Code": ["contractorCode", "contractor_code", "code"],
    "Contractor Name": ["contractorName", "contractor_name", "name"],
    "Contractor Type": ["contractorType", "contractor_type"],
    "Mobile": ["mobile", "mobileNumber", "phone", "phoneNumber", "contactNumber"],
    "Mobile Number": ["mobileNumber", "mobile", "phone", "phoneNumber", "contactNumber"],
    "Village": ["village", "address.village"],
    "Post": ["post", "address.post"],
    "District": ["district", "address.district"],
    "State": ["state", "address.state"],
    "Pin": ["pin", "address.pin"],
    "Joining Date": ["joiningDate", "joinDate", "doj"],
    "Aadhar Number": ["aadharNumber", "aadhar_number", "aadhar_Number", "aadharNo"],
    "Number of Labours": ["numberOfLabours"],
    "Labour Rate": ["labourRate"],
    "Number of Masons": ["numberOfMasons"],
    "Mason Rate": ["masonRate", "masonAmount"],
    "Other Charges": ["otherCharges"],
    "Work Date": ["workDate", "date"],
    "Description": ["description"],
    "Payment Date": ["paymentDate", "date"],
    "Payment Method": ["paymentMethod", "payment_type"],
    "Remarks": ["remarks", "remark"],
    "Attendance Date": ["attendanceDate", "date"],
    "Advance Date": ["advanceDate", "advance_date"],
    "Salary Date": ["salaryDate", "salary_date"],
    "Paid By": ["paidBy.name", "paidBy.fullName", "paidBy.employeeName", "paidBy.empName", "paidBy.empCode", "paidByEmployee.name", "paidByEmployee.fullName", "paidByEmployee.employeeName", "paidByEmployee.empCode", "paid_by_employee.name"],
    "Paid To": ["paidTo", "paid_to"],
    "Fuel Date": ["fuelDate", "fuel_date"],
    "Salary Amount": ["salaryAmount", "salary_amount"],
    "Previous Pending Amount": ["previousPendingAmount", "previous_pending_amount"],
    "WO Number": ["woNumber", "workOrderNo", "work_order_no", "workOrderId", "workorderId"],
    "WO Type": ["woType", "wo_type"],
    "Issue Date": ["issueDate", "issue_date", "date"],
    "Project": ["projectName", "project"],
    "Site Name": ["site.siteName", "siteName", "site_name", "site.name"],
    "Grand Total": ["grandTotal", "grand_total", "amount", "total"],
    "Site": ["site.siteName", "siteName", "site_name"],
    "Site Name": ["site.siteName", "siteName", "site_name", "site.name", "siteName"],
    "Rate": ["rate", "hourlyRate"],
    "Spent By": ["spentBy", "spent_by"],
    "Uploaded By": ["authUser.username", "authUser.userName", "uploadedBy", "uploaded_by", "authUser.username"],
    "Remarks": ["remarks", "remark", "comment"],
    "Payment Type": ["paymentType", "payment_type"],
    "Production Date": ["productionDate", "date"],
    "Production Time": ["productionTime", "time"],
    "Transit Mixer Number": ["transitMixerNumber", "transit_mixer"],
    "Grade of Concrete": ["gradeOfConcrete", "grade"],
    "Quantity": ["quantity", "qty"],
    "Dispatched To": ["dispatchedTo", "dispatched_to"],
    "Chainage No": ["chainageNo", "chainage_no"],
    "Operator Sign": ["operatorSign", "operator_sign"],
    "Tested By": ["testedBy", "tested_by"],
    "Fuel Type": ["fuelType", "fuel_type"],
    "Transaction Type": ["transactionType", "transaction_type"],
    "Vehicle Name": ["vehicleName", "vehicle_name"],
    "Operator Name": ["operatorName", "operator_name"],
  };

  const getFieldValue = (row, columnName) => {
    const employeeObj = row.employee || row.employeeId;
    const empCode = employeeObj?.empCode || employeeObj?.empcode || row.empCode || row.empcode || "";
    const name = employeeObj?.name || employeeObj?.empName || row.empName || row.name || "";

    if (columnName === "Spent By") {
      const spentBy = row?.spentBy ?? row?.spent_by;
      if (spentBy && typeof spentBy === "object") {
        return spentBy.name || spentBy.fullName || spentBy.employeeName || "-";
      }
      return spentBy || "-";
    }

    if (columnName === "Paid By") {
      const paidBy = row?.paidBy ?? row?.paidByEmployee ?? row?.paid_by ?? row?.paid_by_employee;
      if (paidBy && typeof paidBy === "object") {
        return paidBy.name || paidBy.fullName || paidBy.employeeName || paidBy.empName || paidBy.empCode || paidBy.empcode || "-";
      }
      return paidBy || "-";
    }

    if (columnName === "Approved By") {
      const approvedBy = row?.approvedBy ?? row?.approved_by;
      if (approvedBy && typeof approvedBy === "object") {
        return approvedBy.name
          || approvedBy.fullName
          || approvedBy.employeeName
          || approvedBy.employee?.name
          || approvedBy.employee?.fullName
          || "-";
      }
      return approvedBy || "-";
    }

    if (columnName === "Employee Code") {
      return empCode || "-";
    }

    if (columnName === "Employee") {
      return name || empCode || "-";
    }

    if (columnName === "Address") {
      const address = row?.address || {};
      const village = address?.village || row?.village || "";
      const post = address?.post || row?.post || "";
      const district = address?.district || row?.district || "";
      const state = address?.state || row?.state || "";
      const pin = address?.pin || row?.pin || "";
      const parts = [village, post, district, state, pin].filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : "-";
    }

    const possibleKeys = fieldMap[columnName] || [columnName.toLowerCase().replace(/ /g, "_")];

    const nestedCandidateSources = [row, row.contractor, row.contractorDetails, row.contractorEntity, row.data];

    const contractorFallbacks = {
      "Contractor Code": [row?.contractorCode, row?.code, row?.contractor?.contractorCode, row?.contractor?.code],
      "Contractor Name": [row?.contractorName, row?.name, row?.contractor?.contractorName, row?.contractor?.name],
      "Mobile Number": [row?.mobileNumber, row?.mobile, row?.phone, row?.phoneNumber, row?.contactNumber, row?.contractor?.mobileNumber, row?.contractor?.mobile],
      "Joining Date": [row?.joiningDate, row?.joinDate, row?.contractor?.joiningDate, row?.contractor?.joinDate],
      "Aadhar Number": [row?.aadharNumber, row?.aadhar_Number, row?.aadharNo, row?.contractor?.aadharNumber, row?.contractor?.aadhar_Number],
      "Amount": [row?.amount, row?.paymentAmount, row?.payment?.amount, row?.contractorPayment?.amount],
      "Payment Date": [row?.paymentDate, row?.date, row?.contractorPayment?.paymentDate],
    };

    const fallbackValue = contractorFallbacks[columnName]?.find((value) => value !== null && value !== undefined && value !== "");
    if (fallbackValue !== undefined) {
      return fallbackValue;
    }

    for (let key of possibleKeys) {
      // Handle nested properties like "employeeId.empCode"
      if (key.includes(".")) {
        const [parent, child] = key.split(".");
        const parentValue = row[parent] || row.contractor?.[parent] || row.contractorDetails?.[parent] || row.contractorEntity?.[parent];
        if (parentValue && typeof parentValue === "object" && parentValue[child] !== null && parentValue[child] !== undefined) {
          return parentValue[child];
        }
      }

      for (const source of nestedCandidateSources) {
        if (!source || typeof source !== "object") continue;
        if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== null && source[key] !== undefined) {
          return source[key];
        }
      }
    }

    return "-";
  };

  const getActiveStatusValue = (row) => {
    const rawValue = row?.isActive ?? row?.active ?? row?.status;
    if (typeof rawValue === "boolean") {
      return rawValue ? "true" : "false";
    }
    if (typeof rawValue === "string") {
      const normalizedValue = rawValue.toLowerCase();
      if (["active", "true", "1", "yes", "y"].includes(normalizedValue)) return "true";
      if (["inactive", "false", "0", "no", "n"].includes(normalizedValue)) return "false";
    }
    return row?.status === "ACTIVE" || row?.status === "Active" ? "true" : "false";
  };

  const getSiteValue = (row) => {
    const siteId = row?.site?.id ?? row?.siteId ?? row?.site_id ?? row?.site?.siteId ?? row?.site?.site_id ?? "";
    return String(siteId ?? "");
  };

  const centeredNumericColumns = [
    "Number of Labours",
    "Number of Masons",
    "Amount",
    "Salary Amount",
    "Previous Pending Amount",
    "Quantity",
    "Rate",
    "Daily Wage",
    "Mason Rate",
    "Hours",
    "Transit Mixer Number",
    "Chainage No"
  ];

  const showPreviewInvoice = onPreviewInvoice;
  const showViewExpenseAttachment = onViewExpenseAttachment;
  const showViewEmployeeAadhar = typeof onViewEmployeeAadhar === "function";
  const showViewContractorDocuments = typeof onViewContractorDocuments === "function";
  const showDownloadAttachment = typeof onDownloadAttachment === "function";
  const isEmployeeTable = visibleCols.includes("Empcode");
  const showViewRow = typeof onViewRow === "function";
  const showEditRow = Boolean(isAdmin && typeof onEditRow === "function");
  const showDeleteAction = Boolean(onDelete && isAdmin);
  const showAttachmentColumn = Boolean(showPreviewInvoice || showViewExpenseAttachment || showViewEmployeeAadhar || showViewContractorDocuments || showDownloadAttachment);
  const hasActionColumn = showDeleteAction || showEditRow || showViewRow;
  const shouldInsertActionBeforeRemarks = hasActionColumn && actionColumnPosition === "before-remarks";
  const remarksIndex = visibleCols.findIndex(c => c === "Remarks");
  const extraColumnsCount = (showAttachmentColumn ? 1 : 0) + (hasActionColumn ? 1 : 0);

  const renderAttachmentActions = (row, rowId) => (
    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-start", flexWrap: "wrap" }}>
      {(showViewExpenseAttachment || showPreviewInvoice || showViewEmployeeAadhar || showViewContractorDocuments) && (
        <button
          className="btn-secondary"
          type="button"
          disabled={invoiceLoadingId === rowId}
          onClick={() => showViewExpenseAttachment
            ? onViewExpenseAttachment(row)
            : showPreviewInvoice
              ? onPreviewInvoice(row)
              : showViewEmployeeAadhar
                ? onViewEmployeeAadhar(row)
                : onViewContractorDocuments(row)}
          aria-label="View attachment"
          title="View attachment"
          style={{ padding: "8px", display: "inline-flex" }}
        >
          <FiFileText size={15} />
        </button>
      )}
      {showDownloadAttachment && (
        <button
          className="btn-secondary"
          type="button"
          disabled={invoiceLoadingId === rowId}
          onClick={() => onDownloadAttachment(row)}
          aria-label="Download attachment"
          title="Download attachment"
          style={{ padding: "8px", display: "inline-flex" }}
        >
          <FiDownload size={15} />
        </button>
      )}
    </div>
  );

  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            {visibleCols.flatMap((c, index) => {
              const headerCells = [];
              if (shouldInsertActionBeforeRemarks && remarksIndex >= 0 && index === remarksIndex) {
                headerCells.push(<th key={`action-before-${c}`}>Action</th>);
              }
              const headerStyle = centeredNumericColumns.includes(c)
                ? { textAlign: "center" }
                : undefined;
              headerCells.push(<th key={c} style={headerStyle}>{c}</th>);
              return headerCells;
            })}
            {/* Created By column removed per request */}
            {showAttachmentColumn && <th>Attachment</th>}
            {hasActionColumn && (!shouldInsertActionBeforeRemarks || remarksIndex < 0) && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={visibleCols.length + extraColumnsCount} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>No records yet</td></tr>
          )}
          {rows.map((row, i) => {
            const rowId = resolveRowId(row, idKey, i);
            const isSelected = selectedRowId !== undefined && selectedRowId !== null && String(rowId) === String(selectedRowId);
            return (
              <>
                <tr
                  key={`${rowId}-${i}`}
                  className={`${i % 2 === 0 ? "even" : ""}${isSelected ? " selected-row" : ""}`}
                  style={{ cursor: onRowClick ? "pointer" : undefined, backgroundColor: isSelected ? "#eff6ff" : undefined }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                {visibleCols.flatMap((c, colIndex) => {
                  const cells = [];
                  if (shouldInsertActionBeforeRemarks && remarksIndex >= 0 && colIndex === remarksIndex) {
                    cells.push(
                      <td key={`${rowId}-${i}-action`} style={{ minWidth: 140, whiteSpace: 'nowrap', padding: '10px 12px' }}>
                        {renderAttachmentActions(row, rowId)}
                      </td>
                    );
                  }

                  const value = getFieldValue(row, c);
                  const amountColumns = ["Amount", "Amount Received", "Remaining Amount", "Grand Total"];
                  const displayValue = amountColumns.includes(c) ? formatAmountIN(value) : value;
                  const cellStyle = centeredNumericColumns.includes(c)
                    ? { textAlign: "center" }
                    : undefined;
                  if (c === "Active Status") {
                    const activeStatusValue = getActiveStatusValue(row);
                    if (isAdmin && typeof onEditEmployeeStatus === "function") {
                      cells.push(
                        <td key={`${rowId}-${i}-${colIndex}`} style={cellStyle}>
                          <select
                            value={activeStatusValue}
                            onChange={e => onEditEmployeeStatus(row, e.target.value)}
                            style={{ width: "100%", padding: "6px 8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </td>
                      );
                    } else {
                      cells.push(
                        <td key={`${rowId}-${i}-${colIndex}`} style={cellStyle}>
                          {activeStatusValue === "true" ? "Active" : "Inactive"}
                        </td>
                      );
                    }
                    return cells;
                  }

                  if (c === "Site Name") {
                    const selectedSiteValue = getSiteValue(row);
                    if (isAdmin && typeof onEditEmployeeSite === "function") {
                      cells.push(
                        <td key={`${rowId}-${i}-${colIndex}`} style={cellStyle}>
                          <select
                            value={selectedSiteValue}
                            onChange={e => onEditEmployeeSite(row, e.target.value)}
                            style={{ width: "100%", padding: "6px 8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                          >
                            <option value="">Select site</option>
                            {employeeSiteOptions.map(option => (
                              <option key={option.id ?? option.siteId ?? option.site_id ?? option.name} value={option.id ?? option.siteId ?? option.site_id ?? ""}>
                                {option.name || option.siteName || option.site_name || option.label || `Site ${option.id ?? ""}`}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    } else {
                      // Non-admin users should see only the site name (read-only)
                      const displayName = (employeeSiteOptions.find(opt => String(opt.id ?? opt.siteId ?? opt.site_id ?? "") === String(selectedSiteValue))?.name) ||
                        row.siteName || row.site?.siteName || row.site?.site_name || row.site || selectedSiteValue || "-";
                      cells.push(
                        <td key={`${rowId}-${i}-${colIndex}`} style={cellStyle}>{displayName}</td>
                      );
                    }
                    return cells;
                  }

                  if (c === "Payment Status" && typeof onEditPaymentStatus === "function") {
                    const normalizedValue = normalizeStatusValue(value);
                    const selectedValue = paymentStatusOptions.find(option => option.value === normalizedValue)?.value || "";
                    cells.push(
                      <td key={`${rowId}-${i}-${colIndex}`} style={cellStyle}>
                        <select
                          value={selectedValue}
                          onChange={e => onEditPaymentStatus(row, e.target.value)}
                          style={{ width: "100%", padding: "6px 8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                        >
                          <option value="">Select</option>
                          {paymentStatusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                    );
                    return cells;
                  }

                  cells.push(<td key={`${rowId}-${i}-${colIndex}`} style={cellStyle}>{displayValue}</td>);
                  return cells;
                })}
                {/* Created By column removed */}
                {showAttachmentColumn && (
                  <td key={`${rowId}-${i}-attachment`} style={{ minWidth: 150, whiteSpace: 'nowrap', padding: '10px 12px' }}>
                    {renderAttachmentActions(row, rowId)}
                  </td>
                )}
                {hasActionColumn && (!shouldInsertActionBeforeRemarks || remarksIndex < 0) && (
                  <td key={`${rowId}-${i}-action`} style={{ minWidth: 120, whiteSpace: 'nowrap', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                      {showViewRow && (
                        <button
                          className="btn-secondary"
                          onClick={e => { e.stopPropagation(); onViewRow(row); }}
                          style={{ padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, minWidth: 34, width: 34, height: 34 }}
                          aria-label="View details"
                          title="View details"
                        >
                          <FiEye size={14} />
                        </button>
                      )}
                      {showEditRow && (
                        <button
                          className="btn-secondary"
                          onClick={() => onEditRow(row)}
                          style={{ padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, minWidth: 34, width: 34, height: 34 }}
                          aria-label="Edit"
                          title="Edit"
                        >
                          <FiEdit size={14} />
                        </button>
                      )}
                      {showDeleteAction && (
                        <button
                          className="btn-secondary"
                          onClick={e => { e.stopPropagation(); if (typeof onDelete === 'function') { onDelete(rowId, row); } }}
                          style={{ padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#ef4444', border: '1px solid #ef4444', borderRadius: 4, minWidth: 34, width: 34, height: 34 }}
                          aria-label="Delete"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
              {String(rowId) === String(expandedRowId) && typeof renderExpandedRow === 'function' && (
                <tr key={`${rowId}-${i}-expanded`}>
                  <td colSpan={cols.length + (showAttachmentColumn ? 1 : 0) + (hasActionColumn ? 1 : 0)} style={{ padding: 0, background: '#f8fafc' }}>
                    <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
                      {renderExpandedRow(row)}
                    </div>
                  </td>
                </tr>
              )}
            </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TabBar({ tab, onAdd, onAddAttendance, onDownload, onUpload, fileRef, showDownload = true, showUpload = true, showAdd = true, downloadLabel = "Download Template", uploadLabel = "Upload Excel / CSV", onUploadClick }) {
  const handleUploadClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (typeof onUploadClick === "function") {
      onUploadClick(e);
      return;
    }
    fileRef?.current?.click();
  };

  return (
    <div className="tab-toolbar">
      {showAdd && (
        <button className="btn-primary" onClick={onAdd}>
          <FiPlus size={16} /> Add {tab}
        </button>
      )}
      {onAddAttendance && (
        <button className="btn-primary" onClick={onAddAttendance}>
          <FiPlus size={16} /> Add Attendance
        </button>
      )}
      {showDownload && onDownload && (
        <button className="btn-secondary" onClick={onDownload}>
          <FiDownload size={16} /> {downloadLabel}
        </button>
      )}
      {showUpload && (
        <button className="btn-secondary" onClick={handleUploadClick}>
          <FiUpload size={16} /> {uploadLabel}
        </button>
      )}
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }} onChange={onUpload} />
    </div>
  );
}

function EmployeeForm({ onSave, onClose, selectedSite, initialData }) {
  const [f, setF] = useState({ 
    name: "", 
    position: "", 
    phone: "", 
    aadharNumber: "",
    aadharFile: null,
    panFile: null,
    bankFile: null,
    site_id: selectedSite?.id || "",
    doj: "", 
    email: "", 
    empType: "",
    salary: "",
    village: "",
    post: "",
    district: "",
    state: "",
    pin: ""
  });

  const [message, setMessage] = useState({ type: "", text: "" });

  const handleSaveClick = () => {
    // Validation
    if (!f.name || f.name.trim() === "") {
      setMessage({ type: "error", text: "Please Enter Name" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.email || f.email.trim() === "") {
      setMessage({ type: "error", text: "Please Enter Email" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.phone || f.phone.trim() === "") {
      setMessage({ type: "error", text: "Please Enter Phone" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.aadharNumber || f.aadharNumber.trim() === "") {
      setMessage({ type: "error", text: "Please Enter Aadhar Number" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.position || f.position.trim() === "") {
      setMessage({ type: "error", text: "Please Enter Position" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.empType || f.empType.trim() === "") {
      setMessage({ type: "error", text: "Please Select Employee Type" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.doj) {
      setMessage({ type: "error", text: "Please Select Date of Joining" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.aadharFile && !initialData?.aadharDocument) {
      setMessage({ type: "error", text: "Please upload Aadhaar document" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.site_id) {
      setMessage({ type: "error", text: "Please Select Site" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    if (!f.salary || f.salary === "" || Number(f.salary) <= 0) {
      setMessage({ type: "error", text: "Please Enter Salary" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    const requiredAddressFields = [f.village, f.post, f.district, f.state, f.pin];
    if (requiredAddressFields.some(field => !field || !String(field).trim())) {
      setMessage({ type: "error", text: "Please fill all address details" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3500);
      return;
    }
    // All validations passed
    setMessage({ type: "", text: "" });
    onSave({ ...f, currentSite: selectedSite });
  };

  useEffect(() => {
    setF(prev => ({ ...prev, site_id: selectedSite?.id || "" }));
  }, [selectedSite?.id]);

  // If initialData is provided (for edit), populate form
  useEffect(() => {
    if (initialData) {
      setF({
        name: initialData.name || initialData.fullName || initialData.employee || "",
        position: initialData.position || "",
        phone: initialData.phone || initialData.mobile || "",
        aadharNumber: initialData.aadharNumber || initialData.aadhar_Number || "",
        aadharFile: null,
        panFile: null,
        bankFile: null,
        site_id: selectedSite?.id || "",
        doj: initialData.doj || initialData.joiningDate || "",
        email: initialData.email || "",
        empType: initialData.empType || "",
        salary: initialData.salary || "",
        village: initialData.village || (initialData.address && initialData.address.village) || "",
        post: initialData.post || (initialData.address && initialData.address.post) || "",
        district: initialData.district || (initialData.address && initialData.address.district) || "",
        state: initialData.state || (initialData.address && initialData.address.state) || "",
        pin: initialData.pin || (initialData.address && initialData.address.pin) || ""
      });
    }
  }, [initialData, selectedSite]);

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label>👤 Full name *</label>
          <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Rajesh Kumar" />
        </div>

        <div className="field">
          <label>✉️ Email *</label>
          <input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="name@example.com" />
        </div>

        <div className="field">
          <label>📞 Phone *</label>
          <input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="10-digit number" />
        </div>

        <div className="field">
          <label>🪪 Aadhar number *</label>
          <input value={f.aadharNumber} onChange={e => setF({ ...f, aadharNumber: e.target.value })} placeholder="12-digit Aadhar number" />
        </div>

        <div className="field">
          <label>📎 Aadhar document {initialData?.aadharDocument ? "(optional)" : "*"}</label>
          <input type="file" required={!initialData?.aadharDocument} onChange={e => setF({ ...f, aadharFile: e.target.files?.[0] || null })} />
          {f.aadharFile && <div style={{ fontSize: 13, color: "#475569" }}>Selected: {f.aadharFile.name}</div>}
          {initialData?.aadharDocument && <div style={{ fontSize: 13, color: "#475569" }}>Leave empty to keep the existing document, or select a file to replace it.</div>}
        </div>

        <div className="field">
          <label>📎 PAN document</label>
          <input type="file" onChange={e => setF({ ...f, panFile: e.target.files?.[0] || null })} />
          {f.panFile && <div style={{ fontSize: 13, color: "#475569" }}>Selected: {f.panFile.name}</div>}
        </div>

        <div className="field">
          <label>📎 Bank document (optional)</label>
          <input type="file" onChange={e => setF({ ...f, bankFile: e.target.files?.[0] || null })} />
          {f.bankFile && <div style={{ fontSize: 13, color: "#475569" }}>Selected: {f.bankFile.name}</div>}
        </div>

        <div className="field">
          <label>🏢 Current site</label>
          <input
            value={selectedSite?.name || selectedSite?.siteName || selectedSite?.site_name || "No site selected"}
            readOnly
            style={{ backgroundColor: "#f1f5f9", cursor: "default" }}
          />
        </div>

        <div className="field">
          <label>💼 Position *</label>
          <input value={f.position} onChange={e => setF({ ...f, position: e.target.value })} placeholder="Site supervisor" required />
        </div>

        <div className="field">
          <label>🧑‍💼 Employee type *</label>
          <select value={f.empType} onChange={e => setF({ ...f, empType: e.target.value })} required>
            <option value="">Select Employee Type</option>
            <option value="Regular">Regular</option>
            <option value="Temporary">Temporary</option>
          </select>
        </div>

        <div className="field">
          <label>📅 Date of joining *</label>
          <input type="date" value={f.doj} onChange={e => setF({ ...f, doj: e.target.value })} required />
        </div>

        <div className="field">
          <label>💰 Salary *</label>
          <input type="number" value={f.salary} onChange={e => setF({ ...f, salary: e.target.value })} placeholder="25000" min="0" />
        </div>
      </div>

      <div className="address-card">
        <h4 className="address-title">📍 Address Details</h4>

        <div className="form-grid address-grid">
          <div className="field">
            <label>🏡 Village *</label>
            <input value={f.village} onChange={e => setF({ ...f, village: e.target.value })} placeholder="Village name" required />
          </div>

          <div className="field">
            <label>🏠 Post *</label>
            <input value={f.post} onChange={e => setF({ ...f, post: e.target.value })} placeholder="Post name" required />
          </div>

          <div className="field">
            <label>🗺️ District *</label>
            <input value={f.district} onChange={e => setF({ ...f, district: e.target.value })} placeholder="District name" required />
          </div>

          <div className="field">
            <label>🌍 State *</label>
            <select value={f.state} onChange={e => setF({ ...f, state: e.target.value })} required>
              <option value="">Select State</option>
              <optgroup label="States">
                <option value="Andhra Pradesh">Andhra Pradesh</option>
                <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                <option value="Assam">Assam</option>
                <option value="Bihar">Bihar</option>
                <option value="Chhattisgarh">Chhattisgarh</option>
                <option value="Goa">Goa</option>
                <option value="Gujarat">Gujarat</option>
                <option value="Haryana">Haryana</option>
                <option value="Himachal Pradesh">Himachal Pradesh</option>
                <option value="Jharkhand">Jharkhand</option>
                <option value="Karnataka">Karnataka</option>
                <option value="Kerala">Kerala</option>
                <option value="Madhya Pradesh">Madhya Pradesh</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Manipur">Manipur</option>
                <option value="Meghalaya">Meghalaya</option>
                <option value="Mizoram">Mizoram</option>
                <option value="Nagaland">Nagaland</option>
                <option value="Odisha">Odisha</option>
                <option value="Punjab">Punjab</option>
                <option value="Rajasthan">Rajasthan</option>
                <option value="Sikkim">Sikkim</option>
                <option value="Tamil Nadu">Tamil Nadu</option>
                <option value="Telangana">Telangana</option>
                <option value="Tripura">Tripura</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="Uttarakhand">Uttarakhand</option>
                <option value="West Bengal">West Bengal</option>
              </optgroup>
              <optgroup label="Union Territories">
                <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                <option value="Chandigarh">Chandigarh</option>
                <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                <option value="Lakshadweep">Lakshadweep</option>
                <option value="Delhi">Delhi</option>
                <option value="Puducherry">Puducherry</option>
                <option value="Ladakh">Ladakh</option>
                <option value="Jammu and Kashmir">Jammu and Kashmir</option>
              </optgroup>
            </select>
          </div>

          <div className="field">
            <label>📮 Pin code *</label>
            <input value={f.pin} onChange={e => setF({ ...f, pin: e.target.value })} placeholder="6-digit pin code" required />
          </div>
        </div>
      </div>

      <div className="actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={handleSaveClick}>
          {initialData ? "Update Employee" : "Save Employee"}
        </button>
      </div>

      {message.text && (
        <div style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          maxWidth: "90%",
          width: "500px",
          padding: "14px 16px",
          borderRadius: "8px",
          background: message.type === "error" ? "#fee2e2" : "#f0fdf4",
          border: `2px solid ${message.type === "error" ? "#dc2626" : "#16a34a"}`,
          color: message.type === "error" ? "#dc2626" : "#16a34a",
          fontSize: "14px",
          fontWeight: "600",
          zIndex: 9999,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          animation: "slideDown 0.3s ease-out"
        }}>
          {message.type === "error" ? "❌ " : "✓ "} {message.text}
        </div>
      )}
    </div>
  );
}

function AttendanceForm({ employees, selectedSite, onSave, onClose }) {
  const employeesArray = useMemo(() => Array.isArray(employees) ? employees : [], [employees]);
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const defaultDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const [monthValue, setMonthValue] = useState(defaultMonth);
  const [entries, setEntries] = useState([]);

  const getMonthStartDate = (value) => {
    if (!value) return defaultDate;
    const [year, month] = value.split("-");
    return `${year}-${month}-01`;
  };

  const getInitialAttendanceDate = () => defaultDate;

  const normalizeAttendanceStatus = (status) => {
    const normalized = String(status || "PRESENT").trim().toUpperCase();
    const map = {
      PRESENT: "PRESENT",
      ABSENT: "ABSENT",
      "HALF DAY": "HALF_DAY",
      HOLIDAY: "HOLIDAY",
      HOLD: "HOLD",
      LEAVE: "LEAVE",
    };
    return map[normalized] || normalized;
  };

  const updateEntryValue = (index, field, value) => {
    setEntries(prev => prev.map((entry, entryIndex) => entryIndex === index ? { ...entry, [field]: value } : entry));
  };

  useEffect(() => {
    if (!employeesArray.length) {
      setEntries([]);
      return;
    }

    setEntries(prevEntries => {
      const nextEntries = employeesArray.map((emp, index) => {
        const existingEntry = prevEntries.find(existing => {
          const existingEmployeeId = existing.employeeId || existing.employeeObject?.id || existing.employeeObject?.empCode;
          const currentEmployeeId = emp.id || emp.empCode || `emp-${index}`;
          return existingEmployeeId === currentEmployeeId;
        });

        return {
          id: emp.id || emp.empCode || `emp-${index}`,
          employeeId: emp.id || emp.empCode || "",
          employee: emp.name || emp.empCode || "",
          employeeObject: emp,
          site: selectedSite || null,
          attendanceDate: existingEntry?.attendanceDate || getInitialAttendanceDate(),
          status: existingEntry?.status || "Present",
          remarks: existingEntry?.remarks || "",
        };
      });

      return nextEntries;
    });
  }, [employeesArray, selectedSite, monthValue]);

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setMonthValue(value);
    setEntries(prev => prev.map(entry => ({
      ...entry,
      attendanceDate: entry.attendanceDate || (value ? getMonthStartDate(value) : defaultDate),
    })));
  };

  return (
    <div>
      <div className="form-group">
        <label>Month *</label>
        <input type="month" value={monthValue} onChange={handleMonthChange} />
      </div>

      <div className="form-group">
        <label>Site</label>
        <div style={{ padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: "#f8fafc", color: "#334155", fontSize: "13px", fontWeight: "500" }}>
          {selectedSite?.name || selectedSite?.siteName || "No site selected"}
        </div>
      </div>

      {entries.length > 0 && (
        <div style={{ overflowX: "auto", marginTop: "8px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Employee ID</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Site</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Attendance Date</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Status</th>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr key={entry.id || index}>
                  <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ fontWeight: 600 }}>{entry.employeeId || entry.employee || "—"}</div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>{entry.employee || ""}</div>
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                    {selectedSite?.name || selectedSite?.siteName || "—"}
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                    <input
                      type="date"
                      value={entry.attendanceDate || ""}
                      onChange={e => updateEntryValue(index, "attendanceDate", e.target.value)}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: "4px" }}
                    />
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                    <select
                      value={entry.status || "Present"}
                      onChange={e => updateEntryValue(index, "status", e.target.value)}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: "4px" }}
                    >
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="Half Day">Half Day</option>
                      <option value="Holiday">Holiday</option>
                      <option value="Hold">Hold</option>
                      <option value="Leave">Leave</option>
                    </select>
                  </td>
                  <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                    <textarea
                      value={entry.remarks || ""}
                      onChange={e => updateEntryValue(index, "remarks", e.target.value)}
                      rows="2"
                      placeholder="Add remarks"
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: "4px", minWidth: "120px" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          onClick={() => {
            const attendanceEntries = entries
              .filter(entry => entry.employeeId || entry.employeeObject)
              .map(entry => ({
                employeeId: entry.employeeObject || entry.employee || null,
                attendanceDate: entry.attendanceDate || getMonthStartDate(monthValue),
                status: normalizeAttendanceStatus(entry.status || "Present"),
              }));

            if (attendanceEntries.length > 0) {
              onSave({ attendanceEntries });
            }
          }}
        >
          Save Attendance
        </button>
      </div>
    </div>
  );
}

function EmployeeFNFForm({ employees, initialData, onSave, onClose }) {
  const employeeList = Array.isArray(employees) ? employees : [];
  const initialEmployee = initialData?.employee || initialData?.employeeId || null;
  const [employeeId, setEmployeeId] = useState(String(initialEmployee?.id ?? initialEmployee?.empId ?? ""));
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [exitDate, setExitDate] = useState(String(initialData?.exitDate || "").slice(0, 10));
  const [amount, setAmount] = useState(initialData?.amount ?? "");
  const [file, setFile] = useState(null);
  const selectedEmployee = employeeList.find(employee => String(employee.id ?? employee.empId ?? "") === employeeId) ||
    (String(initialEmployee?.id ?? initialEmployee?.empId ?? "") === employeeId ? initialEmployee : null);
  const matchingEmployees = employeeSearch.trim()
    ? employeeList.filter(employee => {
        const query = employeeSearch.trim().toLowerCase();
        return [employee.name, employee.empName, employee.empCode, employee.empcode, employee.phone]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(query));
      })
    : [];

  return (
    <div>
      <div className="form-group">
        <label>Search Employee</label>
        <input
          type="search"
          value={employeeSearch}
          onChange={event => {
            setEmployeeSearch(event.target.value);
            setEmployeeId("");
          }}
          placeholder={selectedEmployee ? `Selected: ${selectedEmployee.empCode || selectedEmployee.empcode || ""} ${selectedEmployee.name || selectedEmployee.empName || ""}`.trim() : "Search by name, employee code, or phone"}
          aria-label="Search employees by name, employee code, or phone"
        />
        {selectedEmployee && !employeeSearch && (
          <button type="button" className="btn-secondary" onClick={() => setEmployeeId("")} style={{ marginTop: "8px" }}>
            Change Employee
          </button>
        )}
        {employeeSearch && matchingEmployees.length > 0 && !selectedEmployee && (
          <div style={{ display: "grid", gap: "6px", marginTop: "8px", maxHeight: "220px", overflowY: "auto" }}>
            {matchingEmployees.map((employee, index) => {
              const id = employee.id ?? employee.empId;
              const label = [employee.empCode || employee.empcode, employee.name || employee.empName, employee.phone]
                .filter(Boolean)
                .join(" - ");
              return (
                <button
                  type="button"
                  key={id ?? index}
                  className="btn-secondary"
                  onClick={() => {
                    setEmployeeId(String(id ?? ""));
                    setEmployeeSearch("");
                  }}
                  style={{ justifyContent: "flex-start", textAlign: "left" }}
                >
                  {label || `Employee ${id ?? ""}`}
                </button>
              );
            })}
          </div>
        )}
        {employeeSearch && matchingEmployees.length === 0 && (
          <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "13px" }}>No employees found.</p>
        )}
      </div>
      <div className="form-group">
        <label>Exit Date</label>
        <input type="date" value={exitDate} onChange={event => setExitDate(event.target.value)} required />
      </div>
      <div className="form-group">
        <label>Amount</label>
        <input type="number" min="0" step="0.01" value={amount} onChange={event => setAmount(event.target.value)} required />
      </div>
      <div className="form-group">
        <label>Document</label>
        <input type="file" onChange={event => setFile(event.target.files?.[0] || null)} />
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="btn-primary"
          disabled={!selectedEmployee || !exitDate || amount === "" || Number(amount) < 0}
          onClick={() => onSave({ employee: selectedEmployee, exitDate, amount, file })}
        >
          {initialData ? "Update Employee FNF" : "Save Employee FNF"}
        </button>
      </div>
    </div>
  );
}

function EmployeeAdvanceForm({ employees, selectedSite, onSave, onClose }) {
  const [f, setF] = useState({ 
    employeeId: "", 
    employee: "",
    employeeObject: null,
    paidByEmployee: null,
    advanceDate: new Date().toISOString().split('T')[0], 
    amount: "", 
    paymentType: "Cash",
  });
  
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [paidByEmployees, setPaidByEmployees] = useState([]);
  const [paidByEmployeesLoading, setPaidByEmployeesLoading] = useState(true);
  const [paidByEmployeesError, setPaidByEmployeesError] = useState("");

  // Memoize employeesArray to prevent recreating on every render
  const employeesArray = useMemo(() => Array.isArray(employees) ? employees : [], [employees]);

  useEffect(() => {
    const fetchPaidByEmployees = async () => {
      try {
        const response = await fetchWithAuth(`${API_ENDPOINTS.employee.list}?isManagementGroup=true`, { method: "GET" });
        if (!response.ok) {
          throw new Error("Failed to load employees for Paid By");
        }

        const result = await response.json();
        const rows = Array.isArray(result)
          ? result
          : ["data", "rows", "records", "items", "content", "result", "results", "list"]
              .map(key => result?.[key])
              .find(Array.isArray) || [];
        setPaidByEmployees(rows);
      } catch (error) {
        console.error("Error fetching employees for advance Paid By:", error);
        setPaidByEmployeesError(error.message || "Failed to load employees for Paid By");
      } finally {
        setPaidByEmployeesLoading(false);
      }
    };

    fetchPaidByEmployees();
  }, []);
  
  // Search employees from API as user types
  useEffect(() => {
    const searchEmployees = async () => {
      if (!employeeSearch.trim()) {
        setFilteredEmployees(employeesArray);
        return;
      }
      
      try {
        setSearchLoading(true);
        const searchQuery = encodeURIComponent(employeeSearch);
        let url = `${API_ENDPOINTS.employee.searchByName}?q=${searchQuery}`;
        
        if (selectedSite && selectedSite.id) {
          url += `&siteid=${encodeURIComponent(selectedSite.id)}`;
        }
        
        const response = await fetchWithAuth(url, { method: "GET" });
        if (response.ok) {
          const results = await response.json();
          setFilteredEmployees(Array.isArray(results) ? results : []);
        } else {
          // Fallback to client-side filtering
          setFilteredEmployees(employeesArray.filter(emp => {
            const searchLower = employeeSearch.toLowerCase();
            const empCode = (emp.empCode || "").toLowerCase();
            const name = (emp.name || "").toLowerCase();
            const phone = (emp.phone || "").toLowerCase();
            return empCode.includes(searchLower) || name.includes(searchLower) || phone.includes(searchLower);
          }));
        }
      } catch (err) {
        console.error("Search error:", err);
        setFilteredEmployees(employeesArray.filter(emp => {
          const searchLower = employeeSearch.toLowerCase();
          const empCode = (emp.empCode || "").toLowerCase();
          const name = (emp.name || "").toLowerCase();
          const phone = (emp.phone || "").toLowerCase();
          return empCode.includes(searchLower) || name.includes(searchLower) || phone.includes(searchLower);
        }));
      } finally {
        setSearchLoading(false);
      }
    };
    
    const timer = setTimeout(() => {
      searchEmployees();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [employeeSearch, selectedSite?.id, employeesArray]);

  return (
    <div>
      <div className="form-group">
        <label>Select Employee *</label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            value={employeeSearch}
            onChange={e => setEmployeeSearch(e.target.value)}
            placeholder={f.employeeId ? `✓ ${f.employee}` : "Search by ID, Name, or Phone..."}
            style={{
              padding: "8px 12px",
              border: f.employeeId ? "2px solid #0284c7" : "1px solid #cbd5e1",
              borderRadius: "4px",
              fontSize: "14px",
              width: "100%",
              paddingRight: searchLoading ? "36px" : (f.employeeId ? "80px" : "12px"),
              background: f.employeeId ? "#dbeafe" : "#fff",
              fontWeight: f.employeeId ? "600" : "400"
            }}
          />
          {searchLoading && (
            <span style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "14px"
            }}>
              🔄
            </span>
          )}
          {f.employeeId && !employeeSearch && (
            <button
              onClick={() => {
                setF({ ...f, employeeId: "", employee: "", employeeObject: null });
                setEmployeeSearch("");
              }}
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "4px 10px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "600"
              }}
              onMouseEnter={e => e.target.style.background = "#dc2626"}
              onMouseLeave={e => e.target.style.background = "#ef4444"}
            >
              ✕ Change
            </button>
          )}
        </div>
        <p style={{ fontSize: "11px", color: "#64748b", margin: "4px 0 0 0" }}>
          💡 {searchLoading ? "🔄 Searching..." : f.employeeId ? `✓ Selected: ${f.employee}` : `Results: ${employeeSearch ? `${filteredEmployees.length} employee(s)` : "Start typing"}`}
        </p>

        {/* FILTERED EMPLOYEE LIST - Show as grid when searching and not selected */}
        {employeeSearch && !f.employeeId && filteredEmployees.length > 0 && (
          <div style={{
            marginTop: "8px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "8px",
            maxHeight: "280px",
            overflowY: "auto",
            padding: "8px",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            background: "#f8fafc"
          }}>
            {filteredEmployees.map(emp => (
              <div
                key={emp.id || emp.empCode}
                onClick={() => {
                  setF({
                    ...f,
                    employeeId: emp.id || emp.empCode,
                    employee: emp.empCode ? `${emp.empCode} - ${emp.name}` : emp.name,
                    employeeObject: emp
                  });
                  setEmployeeSearch("");
                }}
                style={{
                  padding: "10px 12px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontSize: "13px"
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#0284c7";
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.borderColor = "#0284c7";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.color = "#334155";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
              >
                <div style={{ fontWeight: "600" }}>{emp.empCode}</div>
                <div style={{ fontSize: "12px", marginTop: "2px", opacity: 0.8 }}>{emp.name}</div>
                {emp.phone && <div style={{ fontSize: "11px", marginTop: "2px", opacity: 0.7 }}>📱 {emp.phone}</div>}
              </div>
            ))}
          </div>
        )}

        {/* NO RESULTS MESSAGE */}
        {employeeSearch && !f.employeeId && filteredEmployees.length === 0 && (
          <div style={{
            marginTop: "8px",
            padding: "12px",
            textAlign: "center",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            color: "#991b1b",
            fontSize: "13px"
          }}>
            ❌ No employees found
          </div>
        )}
      </div>

      <div className="form-group">
        <label>📅 Expense Date *</label>
        <input
          type="date"
          value={f.advanceDate}
          onChange={e => setF({ ...f, advanceDate: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>💰 Amount (₹) *</label>
        <input
          type="number"
          value={f.amount}
          onChange={e => setF({ ...f, amount: e.target.value })}
          placeholder="e.g. 10000"
        />
      </div>

      <div className="form-group">
        <label>👤 Paid By *</label>
        <select
          value={f.paidByEmployee?.id ?? f.paidByEmployee?.empId ?? f.paidByEmployee?.employeeId ?? ""}
          onChange={event => {
            const paidByEmployee = paidByEmployees.find(employee =>
              String(employee?.id ?? employee?.empId ?? employee?.employeeId ?? "") === event.target.value
            ) || null;
            setF(current => ({ ...current, paidByEmployee }));
          }}
          disabled={paidByEmployeesLoading || Boolean(paidByEmployeesError)}
        >
          <option value="">
            {paidByEmployeesLoading ? "Loading employees..." : paidByEmployeesError ? "Unable to load employees" : "Select employee"}
          </option>
          {paidByEmployees.map(employee => {
            const employeeId = employee?.id ?? employee?.empId ?? employee?.employeeId;
            const employeeName = employee?.name || employee?.fullName || employee?.employeeName || "";
            const employeeCode = employee?.empCode || employee?.empcode || employee?.employeeCode || "";
            return (
              <option key={employeeId ?? employeeCode} value={employeeId ?? ""}>
                {employeeCode && employeeName ? `${employeeCode} - ${employeeName}` : employeeName || employeeCode}
              </option>
            );
          })}
        </select>
        {paidByEmployeesError && (
          <p role="alert" style={{ color: "#b91c1c", fontSize: "12px" }}>
            {paidByEmployeesError}
          </p>
        )}
      </div>

      <div className="form-group">
        <label>Payment Type *</label>
        <select value={f.paymentType} onChange={e => setF({ ...f, paymentType: e.target.value })}>
          <option value="Cash">Cash</option>
          <option value="Check">Check</option>
          <option value="Bank Transfer">Bank Transfer</option>
          <option value="Digital">Digital (UPI/Online)</option>
        </select>
      </div>

      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          onClick={() => {
            if (f.employee && f.paidByEmployee && f.amount && f.advanceDate && f.paymentType) {
              const advanceData = {
                employeeId: f.employeeId,
                employee: f.employee,
                employeeObject: f.employeeObject,
                paidByEmployee: f.paidByEmployee,
                advanceDate: f.advanceDate,
                amount: Number(f.amount),
                paymentType: f.paymentType,
                site: selectedSite
              };
              onSave(advanceData);
            }
          }}
        >
          Save Employee Advance
        </button>
      </div>
    </div>
  );
}

function PurchaseForm({ onSave, onClose, selectedSite, initialData }) {
  const [f, setF] = useState({
    purchaseNo: initialData?.purchaseNo || initialData?.purchaseNumber || initialData?.purchase_no || "",
    purchaseDate: initialData?.purchaseDate || initialData?.date || "",
    partyName: initialData?.partyName || "",
    receiverName: initialData?.receiverName || "",
    paymentStatus: initialData?.paymentStatus || "UNPAID",
    taxAmount: initialData?.taxAmount ?? initialData?.tax_amount ?? "",
    description: initialData?.description || ""
  });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [items, setItems] = useState(Array.isArray(initialData?.purchaseItems) && initialData.purchaseItems.length > 0
    ? initialData.purchaseItems.map(item => ({
        materialName: item?.materialName || "",
        unit: item?.unit || "",
        quantity: item?.quantity ?? "",
        rate: item?.rate ?? "",
        amount: item?.amount ?? "",
        remarks: item?.remarks || "",
      }))
    : [{ materialName: "", unit: "", quantity: "", rate: "", amount: "", remarks: "" }]);

  useEffect(() => {
    if (initialData) {
      setF({
        purchaseNo: initialData.purchaseNo || initialData.purchaseNumber || initialData.purchase_no || "",
        purchaseDate: initialData.purchaseDate || initialData.date || "",
        partyName: initialData.partyName || "",
        receiverName: initialData.receiverName || "",
        paymentStatus: initialData.paymentStatus || "UNPAID",
        taxAmount: initialData.taxAmount ?? initialData.tax_amount ?? "",
        description: initialData.description || ""
      });
      setItems(Array.isArray(initialData.purchaseItems) && initialData.purchaseItems.length > 0
        ? initialData.purchaseItems.map(item => ({
            materialName: item?.materialName || "",
            unit: item?.unit || "",
            quantity: item?.quantity ?? "",
            rate: item?.rate ?? "",
            amount: item?.amount ?? "",
            remarks: item?.remarks || "",
          }))
        : [{ materialName: "", unit: "", quantity: "", rate: "", amount: "", remarks: "" }]);
    }
  }, [initialData]);

  const updateItem = (index, field, value) => {
    setItems(currentItems => currentItems.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const addItem = () => {
    setItems(currentItems => [...currentItems, { materialName: "", unit: "", quantity: "", rate: "", amount: "", remarks: "" }]);
  };

  const removeItem = (index) => {
    setItems(currentItems => currentItems.length === 1
      ? currentItems
      : currentItems.filter((_, itemIndex) => itemIndex !== index));
  };

  const savePurchase = () => {
    const hasHeader = f.purchaseDate && f.partyName && f.receiverName && f.paymentStatus;
    const hasValidItems = items.length > 0 && items.every(item => (
      item.materialName && item.unit && item.quantity && item.rate && item.amount
    ));
    const hasDocument = Boolean(invoiceFile || initialData?.invoiceFile || initialData?.documentFile);

    if (!hasDocument) {
      window.alert("Please upload a document before saving the Purchase.");
      return;
    }

    if (hasHeader && hasValidItems) {
      onSave({ ...f, taxAmount: Number(f.taxAmount) || 0, site: selectedSite, purchaseItems: items, invoiceFile });
    }
  };

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label>Purchase No</label>
          <input value={f.purchaseNo} onChange={e => setF({ ...f, purchaseNo: e.target.value })} placeholder="e.g. PUR-001" />
        </div>
        <div className="field">
          <label>Purchase Date *</label>
          <input type="date" value={f.purchaseDate} onChange={e => setF({ ...f, purchaseDate: e.target.value })} />
        </div>
        <div className="field">
          <label>Site</label>
          <input value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
        </div>
        <div className="field">
          <label>Party Name *</label>
          <input value={f.partyName} onChange={e => setF({ ...f, partyName: e.target.value })} placeholder="Supplier/Party name" />
        </div>
        <div className="field">
          <label>Receiver Name *</label>
          <input value={f.receiverName} onChange={e => setF({ ...f, receiverName: e.target.value })} placeholder="Receiver name" />
        </div>
        <div className="field">
          <label>Payment Status *</label>
          <select value={f.paymentStatus} onChange={e => setF({ ...f, paymentStatus: e.target.value })}>
            <option value="PARTIAL_PAYMENT">Partial Payment</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
          </select>
        </div>
        <div className="field">
          <label>Tax Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={f.taxAmount}
            onChange={e => setF({ ...f, taxAmount: e.target.value })}
            placeholder="0"
          />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Description</label>
          <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Purchase description" />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Invoice</label>
          <input
            type="file"
            accept="application/pdf,image/*"
            style={{ marginTop: 6 }}
            onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
          />
          {invoiceFile && <div style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>Selected: {invoiceFile.name}</div>}
        </div>
      </div>

      <div style={{ marginTop: 18, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "#0f172a" }}>Purchase Items</h3>
          <button type="button" className="btn-secondary" onClick={addItem}>+ Add Item</button>
        </div>

        {items.map((item, index) => (
          <div key={index} style={{ border: "1px solid #cbd5e1", borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div className="form-grid">
              <div className="field">
                <label>Material Name *</label>
                <input value={item.materialName} onChange={e => updateItem(index, "materialName", e.target.value)} placeholder="e.g. Cement" />
              </div>
              <div className="field">
                <label>Unit *</label>
                <input value={item.unit} onChange={e => updateItem(index, "unit", e.target.value)} placeholder="e.g. Bag, Kg" />
              </div>
              <div className="field">
                <label>Quantity *</label>
                <input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(index, "quantity", e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Rate *</label>
                <input type="number" step="0.01" value={item.rate} onChange={e => updateItem(index, "rate", e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Amount *</label>
                <input type="number" step="0.01" value={item.amount} onChange={e => updateItem(index, "amount", e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Remarks</label>
                <input value={item.remarks} onChange={e => updateItem(index, "remarks", e.target.value)} placeholder="Optional remarks" />
              </div>
            </div>
            {items.length > 1 && (
              <button type="button" className="btn-secondary" onClick={() => removeItem(index)} style={{ marginTop: 8 }}>Remove Item</button>
            )}
          </div>
        ))}
      </div>

      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={savePurchase}>{initialData ? "Update Purchase" : "Save Purchase"}</button>
      </div>
    </div>
  );
}

function BillForm({ onSave, onClose, selectedSite = null, workOrders = [], initialData = null }) {
  const [f, setF] = useState({ billNo: "", billDate: "", billType: "", amount: "", description: "", workOrderSearch: "" });
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(initialData?.workOrder || null);
  const [searchedWorkOrders, setSearchedWorkOrders] = useState([]);
  const [workOrderSearchLoading, setWorkOrderSearchLoading] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const invoiceInputRef = useRef(null);

  useEffect(() => {
    if (!initialData) return;
    const workOrder = initialData.workOrder || initialData.workorder || null;
    setSelectedWorkOrder(workOrder);
    setF({
      billNo: initialData.billNo || initialData.billNumber || initialData.bill_no || "",
      billDate: String(initialData.billDate || initialData.bill_date || initialData.date || "").slice(0, 10),
      billType: initialData.billType || initialData.bill_type || "",
      amount: initialData.amount ?? "",
      description: initialData.description || initialData.desc || "",
      workOrderSearch: workOrder?.woNumber || workOrder?.workOrderNo || initialData.woNumber || initialData.workOrderNo || "",
    });
  }, [initialData]);

  const normalizedSearch = f.workOrderSearch.trim().toLowerCase();
  const matchingWorkOrders = normalizedSearch
    ? [...searchedWorkOrders, ...workOrders].filter((workOrder, index, allWorkOrders) => {
        const number = workOrder.woNumber || workOrder.workOrderNo || workOrder.work_order_no || "";
        return String(number).toLowerCase().includes(normalizedSearch)
          && allWorkOrders.findIndex(candidate => String(candidate.workOrderId || candidate.workorderId || candidate.id || candidate.woNumber || candidate.workOrderNo || "") === String(workOrder.workOrderId || workOrder.workorderId || workOrder.id || workOrder.woNumber || workOrder.workOrderNo || "")) === index;
      }).slice(0, 8)
    : [];

  const searchWorkOrders = async () => {
    if (!normalizedSearch) return;
    setWorkOrderSearchLoading(true);
    try {
      const params = new URLSearchParams({ workOrder: f.workOrderSearch.trim() });
      if (selectedSite?.id) params.set("siteId", selectedSite.id);
      const response = await fetchWithAuth(`${API_ENDPOINTS.workorder.search}?${params.toString()}`, { method: "GET" });
      if (!response.ok) throw new Error("Failed to search Work Orders");
      const payload = await response.json();
      const results = Array.isArray(payload)
        ? payload
        : Object.values(payload || {}).find(value => Array.isArray(value)) || [];
      setSearchedWorkOrders(results.length > 0 ? results : (Array.isArray(payload) ? payload : []));
    } catch (error) {
      console.error("Work Order search error:", error);
      setSearchedWorkOrders([]);
    } finally {
      setWorkOrderSearchLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWorkOrder || !normalizedSearch) {
      setSearchedWorkOrders([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      searchWorkOrders();
    }, 350);

    return () => clearTimeout(timer);
  }, [normalizedSearch, selectedSite?.id, selectedWorkOrder]);

  const handleInvoiceChange = (e) => {
    const file = e.target.files?.[0];
    setInvoiceFile(file || null);
  };

  const openInvoicePicker = () => {
    invoiceInputRef.current?.click();
  };

  const saveBill = () => {
    const amount = Number(f.amount);
    if (!initialData) {
      const invalidFields = [];
      if (!f.billNo.trim()) invalidFields.push("bill number");
      if (!f.billDate) invalidFields.push("date");
      if (!f.billType) invalidFields.push("type");
      if (f.amount === "" || !Number.isFinite(amount) || amount < 0) invalidFields.push("amount");
      if (invalidFields.length > 0) {
        window.alert(`Please enter a valid ${invalidFields.join(", ")} before saving.`);
        return;
      }
    }
    if (!initialData && !selectedWorkOrder) {
      window.alert("Please select a Work Order before saving the Bill.");
      return;
    }
    if (!initialData && !invoiceFile) {
      window.alert("Please upload a bill or receipt before saving the Bill.");
      return;
    }
    const existingWorkOrder = initialData?.workOrder || initialData?.workorder || initialData?.work_order
      || (initialData?.workOrderId || initialData?.workorderId || initialData?.work_order_id
        ? { workOrderId: initialData.workOrderId || initialData.workorderId || initialData.work_order_id }
        : null)
      || (initialData?.woNumber || initialData?.workOrderNo || initialData?.work_order_no
        ? { woNumber: initialData.woNumber || initialData.workOrderNo || initialData.work_order_no }
        : null);
    onSave({
      ...f,
      billNo: f.billNo || initialData?.billNo || initialData?.billNumber || initialData?.bill_no || "",
      billDate: f.billDate || initialData?.billDate || initialData?.bill_date || initialData?.date || "",
      billType: f.billType || initialData?.billType || initialData?.bill_type || "",
      amount: f.amount === ""
        ? (initialData?.amount ?? initialData?.totalAmount ?? initialData?.total_amount ?? "")
        : Number(f.amount),
      description: f.description || initialData?.description || initialData?.desc || "",
      invoiceFile,
      workOrder: selectedWorkOrder || existingWorkOrder,
      billId: initialData?.billId || initialData?.id
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "18px",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
        border: "1px solid #dbeafe"
      }}>
        <div style={{
          width: "46px",
          height: "46px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "12px",
          color: "#fff",
          background: "linear-gradient(135deg, #2563eb, #4f46e5)",
          boxShadow: "0 5px 12px rgba(37, 99, 235, 0.22)"
        }}>
          <FiFileText size={22} />
        </div>
        <div>
          <div style={{ color: "#0f172a", fontSize: "16px", fontWeight: 700 }}>
            {initialData ? "Update bill details" : "Create a new bill"}
          </div>
          <div style={{ color: "#64748b", fontSize: "13px", marginTop: "3px" }}>
            Enter the bill information, link its work order, and attach the invoice or receipt.
          </div>
        </div>
      </div>

      <div className="form-grid">
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiHash size={14} color="#2563eb" /> Bill No{!initialData && " *"}</label>
          <input required={!initialData} value={f.billNo} onChange={e => setF({ ...f, billNo: e.target.value })} placeholder="e.g. BL001" />
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiCalendar size={14} color="#2563eb" /> Bill Date</label>
          <input type="date" value={f.billDate} onChange={e => setF({ ...f, billDate: e.target.value })} />
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiTag size={14} color="#2563eb" /> Bill Type</label>
          <select value={f.billType} onChange={e => setF({ ...f, billType: e.target.value })} required>
            <option value="">Select Bill Type</option>
            {BILL_TYPE_OPTIONS.map(billType => <option key={billType} value={billType}>{billType}</option>)}
          </select>
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiDollarSign size={14} color="#2563eb" /> Amount{!initialData && " *"}</label>
          <input required={!initialData} type="number" min="0" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="Enter bill amount" />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1", position: "relative" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiBriefcase size={14} color="#2563eb" /> Work Order Number{!initialData && " *"}</label>
          <div>
            <input
              style={{ width: "100%" }}
              required={!initialData}
              value={selectedWorkOrder
                ? (selectedWorkOrder.woNumber || selectedWorkOrder.workOrderNo || selectedWorkOrder.work_order_no || "")
                : f.workOrderSearch}
              onChange={e => {
                setSelectedWorkOrder(null);
                setSearchedWorkOrders([]);
                setF({ ...f, workOrderSearch: e.target.value });
              }}
              placeholder="Search Work Order number"
            />
            {workOrderSearchLoading && <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>Searching Work Orders...</div>}
          </div>
          {!selectedWorkOrder && matchingWorkOrders.length > 0 && (
            <div style={{ position: "absolute", zIndex: 2, left: 0, right: 0, top: "100%", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)", overflow: "hidden" }}>
              {matchingWorkOrders.map((workOrder, index) => {
                const number = workOrder.woNumber || workOrder.workOrderNo || workOrder.work_order_no || "";
                return (
                  <button
                    key={workOrder.workOrderId || workOrder.workorderId || workOrder.id || number || index}
                    type="button"
                    onClick={() => {
                      setSelectedWorkOrder(workOrder);
                      setF(prev => ({ ...prev, workOrderSearch: number }));
                    }}
                    style={{ display: "block", width: "100%", padding: "10px 12px", border: 0, borderBottom: "1px solid #e2e8f0", background: "#fff", textAlign: "left", cursor: "pointer" }}
                  >
                    <strong>{number}</strong>
                    {workOrder.projectName || workOrder.project ? ` - ${workOrder.projectName || workOrder.project}` : ""}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiFileText size={14} color="#2563eb" /> Description</label>
          <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Enter description" rows="4" />
        </div>
        <div className="field" style={{
          gridColumn: "1 / -1",
          padding: "14px",
          border: "1px dashed #93c5fd",
          borderRadius: "10px",
          background: "#f8fbff"
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiUpload size={14} color="#2563eb" /> Invoice File</label>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginTop: "4px" }}>
            <button className="btn-secondary" type="button" onClick={openInvoicePicker}>
              <FiUpload size={15} /> Choose Invoice
            </button>
            <span style={{ color: invoiceFile ? "#0f172a" : "#64748b", fontSize: "13px" }}>
              {invoiceFile ? invoiceFile.name : "PDF or image file · No file selected"}
            </span>
          </div>
          <input ref={invoiceInputRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={handleInvoiceChange} />
        </div>
      </div>
      <div className="form-actions" style={{ justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "14px" }}>
        <button type="button" className="btn-secondary" onClick={onClose}><FiX size={15} /> Cancel</button>
        <button className="btn-primary" onClick={saveBill}>
          {initialData ? <FiCheck size={15} /> : <FiPlus size={15} />}
          {initialData ? "Update Bill" : "Save Bill"}
        </button>
      </div>
    </div>
  );
}

function PartnershipForm({ onSave, onClose, partners = [], selectedSite = null, selectedPartnerId = null, initialData = null }) {
  const [f, setF] = useState({
    partnerId: selectedPartnerId || "",
    billNo: "",
    billDate: "",
    amount: "",
    description: "",
    file: null,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const partnerId = initialData?.partnerId ?? initialData?.partner?.id ?? selectedPartnerId ?? "";
    setF({
      partnerId: partnerId ? String(partnerId) : "",
      billNo: initialData?.billNo || initialData?.billNumber || initialData?.bill_number || "",
      billDate: (initialData?.billDate || initialData?.bill_date || "").slice(0, 10),
      amount: initialData?.amount ?? "",
      description: initialData?.description || initialData?.document?.description || "",
      file: null,
    });
  }, [initialData, selectedPartnerId]);

  const handleSave = () => {
    if (!f.description.trim()) {
      window.alert("Please enter a description for the Partnership bill.");
      return;
    }

    if (!f.partnerId || !f.billNo?.trim() || !f.billDate || !f.amount) {
      return;
    }

    onSave({
      ...f,
      partnerId: Number(f.partnerId),
      amount: Number(f.amount),
      siteId: selectedSite?.id ?? null,
    });
  };

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label>🤝 Partner</label>
          <select value={f.partnerId} onChange={e => setF({ ...f, partnerId: e.target.value })}>
            <option value="">Select partner</option>
            {partners.map((partner) => {
              const partnerIdValue = partner?.id ?? partner?.partnerId;
              const partnerName = partner?.name || partner?.partnerName || partner?.contactPerson || `Partner ${partnerIdValue ?? ""}`;
              return (
                <option key={partnerIdValue ?? partnerName} value={partnerIdValue ?? ""}>
                  {partnerName}
                </option>
              );
            })}
          </select>
        </div>
        <div className="field">
          <label>🧾 Bill Number</label>
          <input value={f.billNo} onChange={e => setF({ ...f, billNo: e.target.value })} placeholder="Enter bill number" />
        </div>
        <div className="field">
          <label>📅 Bill Date</label>
          <input type="date" value={f.billDate} onChange={e => setF({ ...f, billDate: e.target.value })} />
        </div>
        <div className="field">
          <label>💰 Amount (₹)</label>
          <input type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>📝 Description *</label>
          <textarea required value={f.description} onChange={e => setF({ ...f, description: e.target.value })} rows="4" placeholder="Enter description" />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>📎 Document</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: 6 }}>
            <button className="btn-secondary" type="button" onClick={() => fileInputRef.current?.click()}>Choose File</button>
            <span style={{ color: '#475569', fontSize: '14px' }}>{f.file ? f.file.name : 'No file selected'}</span>
          </div>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={e => setF({ ...f, file: e.target.files?.[0] || null })} />
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={handleSave}>{initialData ? "Update Partnership" : "Save Partnership"}</button>
      </div>
    </div>
  );
}

function AddPartnerForm({ onSave, onClose, initialData = null }) {
  const [f, setF] = useState({
    name: "",
    contactPerson: "",
    phone: "",
    email: "",
    gstNo: "",
    village: "",
    post: "",
    district: "",
    state: "",
    pin: "",
  });

  useEffect(() => {
    const address = initialData?.address || {};
    setF({
      name: initialData?.name || initialData?.partnerName || "",
      contactPerson: initialData?.contactPerson || "",
      phone: initialData?.phone || initialData?.mobile || "",
      email: initialData?.email || "",
      gstNo: initialData?.gstNo || initialData?.gstNumber || "",
      village: initialData?.village || address.village || "",
      post: initialData?.post || address.post || "",
      district: initialData?.district || address.district || "",
      state: initialData?.state || address.state || "",
      pin: initialData?.pin || address.pin || "",
    });
  }, [initialData]);

  const handleSave = () => {
    if (!f.name?.trim()) {
      return;
    }
    onSave({
      name: f.name,
      contactPerson: f.contactPerson,
      phone: f.phone,
      email: f.email,
      gstNo: f.gstNo,
      address: {
        village: f.village,
        post: f.post,
        district: f.district,
        state: f.state,
        pin: f.pin,
      },
    });
  };

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label>👤 Name</label>
          <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Enter partner name" />
        </div>
        <div className="field">
          <label>👔 Contact Person</label>
          <input value={f.contactPerson} onChange={e => setF({ ...f, contactPerson: e.target.value })} placeholder="Enter contact person" />
        </div>
        <div className="field">
          <label>📞 Phone</label>
          <input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="Enter phone number" />
        </div>
        <div className="field">
          <label>✉️ Email</label>
          <input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="Enter email" />
        </div>
        <div className="field">
          <label>🧾 GST No</label>
          <input value={f.gstNo} onChange={e => setF({ ...f, gstNo: e.target.value })} placeholder="Enter GST number" />
        </div>
      </div>

      <div className="address-card">
        <h4 className="address-title">Address Details</h4>
        <div className="form-grid address-grid">
          <div className="field">
            <label>Village</label>
            <input value={f.village} onChange={e => setF({ ...f, village: e.target.value })} placeholder="Village name" />
          </div>
          <div className="field">
            <label>Post</label>
            <input value={f.post} onChange={e => setF({ ...f, post: e.target.value })} placeholder="Post name" />
          </div>
          <div className="field">
            <label>District</label>
            <input value={f.district} onChange={e => setF({ ...f, district: e.target.value })} placeholder="District name" />
          </div>
          <div className="field">
            <label>State</label>
            <select value={f.state} onChange={e => setF({ ...f, state: e.target.value })}>
              <option value="">Select State</option>
              <option value="Andhra Pradesh">Andhra Pradesh</option>
              <option value="Arunachal Pradesh">Arunachal Pradesh</option>
              <option value="Assam">Assam</option>
              <option value="Bihar">Bihar</option>
              <option value="Chhattisgarh">Chhattisgarh</option>
              <option value="Goa">Goa</option>
              <option value="Gujarat">Gujarat</option>
              <option value="Haryana">Haryana</option>
              <option value="Himachal Pradesh">Himachal Pradesh</option>
              <option value="Jharkhand">Jharkhand</option>
              <option value="Karnataka">Karnataka</option>
              <option value="Kerala">Kerala</option>
              <option value="Madhya Pradesh">Madhya Pradesh</option>
              <option value="Maharashtra">Maharashtra</option>
              <option value="Manipur">Manipur</option>
              <option value="Meghalaya">Meghalaya</option>
              <option value="Mizoram">Mizoram</option>
              <option value="Nagaland">Nagaland</option>
              <option value="Odisha">Odisha</option>
              <option value="Punjab">Punjab</option>
              <option value="Rajasthan">Rajasthan</option>
              <option value="Sikkim">Sikkim</option>
              <option value="Tamil Nadu">Tamil Nadu</option>
              <option value="Telangana">Telangana</option>
              <option value="Tripura">Tripura</option>
              <option value="Uttar Pradesh">Uttar Pradesh</option>
              <option value="Uttarakhand">Uttarakhand</option>
              <option value="West Bengal">West Bengal</option>
              <option value="Delhi">Delhi</option>
              <option value="Puducherry">Puducherry</option>
              <option value="Chandigarh">Chandigarh</option>
              <option value="Ladakh">Ladakh</option>
            </select>
          </div>
          <div className="field">
            <label>Pin Code</label>
            <input value={f.pin} onChange={e => setF({ ...f, pin: e.target.value })} placeholder="6-digit pin code" />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={handleSave}>{initialData ? "Update Partner" : "Save Partner"}</button>
      </div>
    </div>
  );
}

export function ExpenseForm({ onSave, onClose, initialData }) {
  const getEmployeeId = (employee) => employee?.id ?? employee?.empId ?? employee?.employeeId ?? null;
  const getEmployeeLabel = (employee) => {
    const name = employee?.name || employee?.fullName || employee?.employeeName || "";
    const code = employee?.empCode || employee?.empcode || employee?.employeeCode || "";
    return code && name ? `${code} - ${name}` : name || code || `Employee ${getEmployeeId(employee) || ""}`;
  };
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState("");

  const resolveEmployee = (value) => {
    if (!value) return null;
    if (typeof value === "object") {
      return value;
    }
    const valueString = String(value).trim();
    return employees.find((employee) => {
      const employeeId = String(getEmployeeId(employee) ?? "");
      const employeeName = String(employee?.name || employee?.fullName || employee?.employeeName || "").trim();
      const employeeCode = String(employee?.empCode || employee?.empcode || employee?.employeeCode || "").trim();
      return employeeId === valueString || employeeName === valueString || employeeCode === valueString;
    }) || null;
  };
  const existingSpentBy = initialData?.spentBy || initialData?.spent_by || null;
  const existingSpentById = getEmployeeId(existingSpentBy);
  const employeeOptions = existingSpentBy && !employees.some(employee => String(getEmployeeId(employee) ?? "") === String(existingSpentById ?? ""))
    ? [...employees, existingSpentBy]
    : employees;

  const [f, setF] = useState({
    expenseType: initialData?.expenseType || initialData?.category || "",
    expenseDate: initialData?.expenseDate || initialData?.date || "",
    amount: initialData?.amount ?? "",
    remark: initialData?.remark || initialData?.remarks || "",
    spentBy: initialData?.spentBy || initialData?.spent_by || null,
    attachmentFile: null,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    let active = true;
    const loadEmployees = async () => {
      setEmployeesLoading(true);
      setEmployeesError("");
      try {
        const response = await fetchWithAuth(`${API_ENDPOINTS.employee.list}?isManagementGroup=true`, { method: "GET" });
        if (!response.ok) {
          throw new Error(`Employee request failed (${response.status})`);
        }
        const result = await response.json();
        const employeeRows = Array.isArray(result)
          ? result
          : ["data", "rows", "records", "items", "content", "result", "results", "list"]
              .map(key => result?.[key])
              .find(Array.isArray);
        if (!employeeRows) {
          throw new Error("Employee response was not a list");
        }
        if (active) setEmployees(employeeRows);
      } catch (error) {
        if (active) {
          console.error("Error fetching employees for expense Spent By:", error);
          setEmployeesError(error.message || "Unable to load employees");
        }
      } finally {
        if (active) setEmployeesLoading(false);
      }
    };

    loadEmployees();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (initialData) {
      setF({
        expenseType: initialData.expenseType || initialData.category || "",
        expenseDate: initialData.expenseDate || initialData.date || "",
        amount: initialData.amount ?? "",
        remark: initialData.remark || initialData.remarks || "",
        spentBy: initialData.spentBy || initialData.spent_by || null,
        attachmentFile: null,
      });
    }
  }, [initialData]);

  const updateSpentBy = (value) => {
    const employee = resolveEmployee(value);
    setF(current => ({ ...current, spentBy: employee }));
  };

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label>🗂️ Expense Type *</label>
          <select required value={f.expenseType} onChange={e => setF({ ...f, expenseType: e.target.value })}>
            <option value="">Select expense type</option>
            <option value="Mess">Mess</option>
            <option value="Accommodation">Accommodation</option>
            <option value="Machinery">Machinery</option>
            <option value="Site">Site</option>
            <option value="Office Expenses">Office Expenses</option>
            <option value="Hardware">Hardware</option>
            <option value="Refreshments">Refreshments</option>
            <option value="Mobile Recharge">Mobile Recharge</option>
            <option value="GST Tax">GST Tax</option>
            <option value="Travel">Travel</option>
            <option value="Others">Others</option>
          </select>
        </div>
        <div className="field">
          <label>📅 Expense Date *</label>
          <input required type="date" value={f.expenseDate} onChange={e => setF({ ...f, expenseDate: e.target.value })} />
        </div>
        <div className="field">
          <label>💰 Amount (₹) *</label>
          <input required type="number" min="0" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="field">
          <label htmlFor="expense-spent-by">👤 Spent By *</label>
          <select
            id="expense-spent-by"
            required
            value={getEmployeeId(resolveEmployee(f.spentBy)) || ""}
            onChange={e => updateSpentBy(e.target.value)}
            disabled={employeesLoading || Boolean(employeesError)}
          >
            <option value="">
              {employeesLoading ? "Loading employees..." : employeesError ? "Unable to load employees" : "Select employee"}
            </option>
            {employeeOptions.map((employee, index) => (
              <option key={getEmployeeId(employee) || `employee-${index}`} value={getEmployeeId(employee)}>{getEmployeeLabel(employee)}</option>
            ))}
          </select>
          {employeesError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12, margin: "5px 0 0" }}>{employeesError}</p>}
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>📝 Remarks</label>
          <input value={f.remark} onChange={e => setF({ ...f, remark: e.target.value })} placeholder="e.g. Site fuel bill" />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>📎 Bill / Receipt File</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ marginTop: 0 }}
              onChange={(e) => setF({ ...f, attachmentFile: e.target.files?.[0] || null })}
            />
            {f.attachmentFile && (
              <div style={{ fontSize: 13, color: "#475569" }}>
                Selected: {f.attachmentFile.name}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="btn-primary"
          disabled={employeesLoading || Boolean(employeesError)}
          onClick={() => {
            const invalidFields = [];
            const amount = Number(f.amount);
            if (!f.expenseType) invalidFields.push("expense type");
            if (!f.expenseDate) invalidFields.push("expense date");
            if (f.amount === "" || !Number.isFinite(amount) || amount < 0) invalidFields.push("a valid amount");
            if (!resolveEmployee(f.spentBy)) invalidFields.push("spent by");
            if (invalidFields.length > 0) {
              const message = `Please complete the required fields: ${invalidFields.join(", ")}.`;
              window.alert(message);
              return;
            }
            if (!initialData && !f.attachmentFile) {
              window.alert("Please upload a document before saving the Expense.");
              return;
            }
            onSave({ ...f, spentBy: resolveEmployee(f.spentBy) });
          }}
        >
          {initialData ? "Update Expense" : "Save Expense"}
        </button>
      </div>
    </div>
  );
}

export function SellForm({ onSave, onClose, selectedSite, initialData }) {
  const [f, setF] = useState({
    sellNo: initialData?.sellNo || initialData?.sell_no || initialData?.saleNo || initialData?.billNo || "",
    billType: initialData?.billType || "",
    sellDate: initialData?.sellDate || initialData?.date || "",
    paymentStatus: initialData?.paymentStatus || "UNPAID"
  });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [sellItems, setSellItems] = useState(Array.isArray(initialData?.sellItems) && initialData.sellItems.length > 0
    ? initialData.sellItems.map(item => ({
        materialName: item?.materialName || "",
        unit: item?.unit || "",
        quantity: item?.quantity ?? "",
        rate: item?.rate ?? "",
        amount: item?.amount ?? "",
        remarks: item?.remarks || "",
      }))
    : [{ materialName: "", unit: "", quantity: "", rate: "", amount: "", remarks: "" }]);

  useEffect(() => {
    if (initialData) {
      setF({
        sellNo: initialData.sellNo || initialData.sell_no || initialData.saleNo || initialData.billNo || "",
        billType: initialData.billType || "",
        sellDate: initialData.sellDate || initialData.date || "",
        paymentStatus: initialData.paymentStatus || "UNPAID"
      });
      setSellItems(Array.isArray(initialData.sellItems) && initialData.sellItems.length > 0
        ? initialData.sellItems.map(item => ({
            materialName: item?.materialName || "",
            unit: item?.unit || "",
            quantity: item?.quantity ?? "",
            rate: item?.rate ?? "",
            amount: item?.amount ?? "",
            remarks: item?.remarks || "",
          }))
        : [{ materialName: "", unit: "", quantity: "", rate: "", amount: "", remarks: "" }]);
    }
  }, [initialData]);

  const updateSellItem = (index, field, value) => {
    setSellItems(currentItems => currentItems.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const addSellItem = () => {
    setSellItems(currentItems => [...currentItems, { materialName: "", unit: "", quantity: "", rate: "", amount: "", remarks: "" }]);
  };

  const removeSellItem = (index) => {
    setSellItems(currentItems => currentItems.length === 1
      ? currentItems
      : currentItems.filter((_, itemIndex) => itemIndex !== index));
  };

  const saveSell = () => {
    const hasDocument = Boolean(invoiceFile || initialData?.invoiceFile || initialData?.documentFile);
    if (!hasDocument) {
      window.alert("Please upload a document before saving the Sell.");
      return;
    }

    const mergedRecord = {
      ...(initialData || {}),
      ...f,
      site: initialData?.site || selectedSite || null,
      sellItems,
      invoiceFile,
    };

    if (!mergedRecord.sellDate) {
      mergedRecord.sellDate = initialData?.sellDate || initialData?.date || "";
    }
    if (!mergedRecord.billType) {
      mergedRecord.billType = initialData?.billType || "";
    }
    if (!mergedRecord.paymentStatus) {
      mergedRecord.paymentStatus = initialData?.paymentStatus || "UNPAID";
    }

    onSave(mergedRecord);
  };

  return (
    <div>
      <div className="form-grid">
        <div className="field">
          <label>Sell No (Bill Number)</label>
          <input value={f.sellNo} onChange={e => setF({ ...f, sellNo: e.target.value })} placeholder="e.g. BL001" />
        </div>
        <div className="field">
          <label>Sell Date *</label>
          <input
            type="date"
            value={f.sellDate}
            onChange={e => setF({ ...f, sellDate: e.target.value })}
            style={{ width: "100%", minWidth: 0, boxSizing: "border-box", appearance: "auto", WebkitAppearance: "auto" }}
          />
        </div>
        <div className="field">
          <label>Site</label>
          <input value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
        </div>
        <div className="field">
          <label>Bill Type *</label>
          <input value={f.billType} onChange={e => setF({ ...f, billType: e.target.value })} placeholder="e.g. Tax Invoice" />
        </div>
        <div className="field">
          <label>Payment Status *</label>
          <select value={f.paymentStatus} onChange={e => setF({ ...f, paymentStatus: e.target.value })}>
            <option value="PARTIAL_PAYMENT">Partial Payment</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
          </select>
        </div>
        <div className="field">
          <label>Sell Invoice</label>
          <input type="file" accept="application/pdf,image/*" style={{ marginTop: 6 }} onChange={e => setInvoiceFile(e.target.files?.[0] || null)} />
          {invoiceFile && <div style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>Selected: {invoiceFile.name}</div>}
        </div>
      </div>

      <div style={{ marginTop: 18, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "#0f172a" }}>Sell Items</h3>
          <button type="button" className="btn-secondary" onClick={addSellItem}>+ Add Item</button>
        </div>

        {sellItems.map((item, index) => (
          <div key={index} style={{ border: "1px solid #cbd5e1", borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div className="form-grid">
              <div className="field">
                <label>Material Name *</label>
                <input value={item.materialName} onChange={e => updateSellItem(index, "materialName", e.target.value)} placeholder="e.g. Cement" />
              </div>
              <div className="field">
                <label>Unit *</label>
                <input value={item.unit} onChange={e => updateSellItem(index, "unit", e.target.value)} placeholder="e.g. Bag, Kg" />
              </div>
              <div className="field">
                <label>Quantity *</label>
                <input type="number" step="0.01" value={item.quantity} onChange={e => updateSellItem(index, "quantity", e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Rate *</label>
                <input type="number" step="0.01" value={item.rate} onChange={e => updateSellItem(index, "rate", e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Amount *</label>
                <input type="number" step="0.01" value={item.amount} onChange={e => updateSellItem(index, "amount", e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Remarks</label>
                <input value={item.remarks} onChange={e => updateSellItem(index, "remarks", e.target.value)} placeholder="Optional remarks" />
              </div>
            </div>
            {sellItems.length > 1 && (
              <button type="button" className="btn-secondary" onClick={() => removeSellItem(index)} style={{ marginTop: 8 }}>Remove Item</button>
            )}
          </div>
        ))}
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={saveSell}>{initialData ? "Update Sell" : "Save Sell"}</button>
      </div>
    </div>
  );
}

export default function Dashboard({ username, userType, authUser, onLogout, selectedSite, onChangeSite, initialActiveTab = "dashboard" }) {
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [data, setData] = useState(INIT);
  const [modal, setModal] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [editingEmployeeFNF, setEditingEmployeeFNF] = useState(null);
  const [editingSalary, setEditingSalary] = useState(null);
  const [editingContractor, setEditingContractor] = useState(null);
  const [editingBill, setEditingBill] = useState(null);
  const [editingManagementExpense, setEditingManagementExpense] = useState(null);
  const [managementExpenseEmployees, setManagementExpenseEmployees] = useState([]);
  const [managementGroupEmployees, setManagementGroupEmployees] = useState([]);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editingSell, setEditingSell] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingContractorWork, setEditingContractorWork] = useState(null);
  const [editingContractorPayment, setEditingContractorPayment] = useState(null);
  const [editingLabour, setEditingLabour] = useState(null);
  const [editingLabourPayment, setEditingLabourPayment] = useState(null);
  const [editingFuelPump, setEditingFuelPump] = useState(null);
  const [editingFuelTransaction, setEditingFuelTransaction] = useState(null);
  const [editingFuelPayment, setEditingFuelPayment] = useState(null);
  const [editingConcreteProduction, setEditingConcreteProduction] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    name: "",
    username: "",
    email: "",
    password: "",
    userType: "USER",
    site: selectedSite ? { ...selectedSite } : null,
    siteId: selectedSite?.id || ""
  });
  const fileRef = useRef(null);
  const [pagination, setPagination] = useState({
    dashboard: 0,
    employees: 0,
    attendance: 0,
    employeefnf: 0,
    employeeadvance: 0,
    salary: 0,
    salaryreport: 0,
    purchases: 0,
    sells: 0,
    expenses: 0,
    diesel: 0,
    petrol: 0,
    fueltransaction: 0,
    fuelpayment: 0,
    labourcoast: 0,
    labour: 0,
    contractor: 0,
    labouradvance: 0,
    labourattendance: 0,
    labourpayment: 0,
    concreteproduction: 0,
    partnership: 0,
    settings: 0,
    managementExpense: 0,
  });

  const [partnershipBills, setPartnershipBills] = useState([]);
  const [partnershipPartners, setPartnershipPartners] = useState([]);
  const [employeeSiteOptions, setEmployeeSiteOptions] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedPartnerName, setSelectedPartnerName] = useState("");
  const [partnershipForm, setPartnershipForm] = useState({ partnerId: selectedPartnerId || "", billNo: "", billDate: "", amount: "", description: "", file: null });
  const [partnershipSearch, setPartnershipSearch] = useState("");
  const [partnershipFromDate, setPartnershipFromDate] = useState("");
  const [partnershipToDate, setPartnershipToDate] = useState("");
  const [partnershipTotalAmount, setPartnershipTotalAmount] = useState(null);
  const [partnershipTotalLoading, setPartnershipTotalLoading] = useState(false);
  const [dashboardFromDate, setDashboardFromDate] = useState("");
  const [dashboardToDate, setDashboardToDate] = useState("");
  const [dashboardPartnershipTotal, setDashboardPartnershipTotal] = useState(null);
  const [dashboardBillTotal, setDashboardBillTotal] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [workOrderBills, setWorkOrderBills] = useState([]);
  const [workOrderBillsLoading, setWorkOrderBillsLoading] = useState(false);

  const [fuelCounts, setFuelCounts] = useState({
    dieselIn: 0,
    dieselOut: 0,
    petrolIn: 0,
    petrolOut: 0
  });
  const fuelCountsSiteIdRef = useRef(null);

  const [fuelPumpCounts, setFuelPumpCounts] = useState({
    diesel: 0,
    petrol: 0
  });

  const [showFuelStatus] = useState(true);
  const [showPumpFuelStatus] = useState(true);

  const showPartnershipManagement = true;

  useEffect(() => {
    if (activeTab !== "purchases") {
      setSelectedPurchase(null);
    }
    if (activeTab !== "workorders") {
      setSelectedWorkOrder(null);
      setWorkOrderBills([]);
    }
  }, [activeTab]);

  const getPartnershipRowPartnerName = (row) => {
    const partner = row?.partner || null;
    if (partner) {
      return partner.name || partner.partnerName || partner.contactPerson || "-";
    }

    return row?.partnerName || row?.partner_name || row?.partner?.name || "-";
  };

  const visiblePartnershipBills = useMemo(() => {
    if (!selectedPartnerName && selectedPartnerId == null) {
      return [];
    }

    const normalizedSelectedName = (selectedPartnerName || "").trim().toLowerCase();
    return partnershipBills.filter((row) => {
      const rowPartnerName = getPartnershipRowPartnerName(row).trim().toLowerCase();
      const rowPartnerId = row?.partnerId ?? row?.partner?.id ?? null;
      const matchesPartnerId = selectedPartnerId != null && rowPartnerId != null && String(rowPartnerId) === String(selectedPartnerId);
      const matchesPartnerName = normalizedSelectedName && rowPartnerName === normalizedSelectedName;
      return matchesPartnerId || matchesPartnerName;
    });
  }, [partnershipBills, selectedPartnerId, selectedPartnerName]);

  // Check if user is Admin - based on userType or authUser payload
  const isAdmin = (() => {
    const resolvedUserRole = [
      userType,
      authUser?.userType,
      authUser?.type,
      authUser?.user_type,
      authUser?.usertype,
      authUser?.role,
      authUser?.userRole,
      authUser?.roleName,
      authUser?.roles,
    ].find(role => role != null && String(role).trim() !== "");

    return isAdminUser(resolvedUserRole);
  })();
  const isDashboardView = activeTab === "dashboard";
  const isSettingsView = activeTab === "settings";
  const isDataView = !isDashboardView && !isSettingsView;

  useEffect(() => {
    setDashboardPartnershipTotal(null);
    setDashboardBillTotal(null);
  }, [selectedSite?.id, selectedSite?.siteId, selectedSite?.site_id]);

  // Log for debugging
  useEffect(() => {
    console.log("=== Dashboard Debug ===");
    console.log("username:", username);
    console.log("userType (raw):", userType);
    console.log("userType (type):", typeof userType);
    console.log("userType (trimmed):", userType?.trim());
    console.log("userType (lowercased):", userType?.toLowerCase());
    console.log("isAdmin flag value:", isAdmin);
    console.log("activeTab:", activeTab);
  }, [username, userType, isAdmin, activeTab]);

  // Ensure site-specific tabs do not remain active after changing sites.
  useEffect(() => {
    if (!isKheplSite(selectedSite) && (activeTab === "labour" || activeTab === "concreteproduction")) {
      setActiveTab("dashboard");
    }
  }, [selectedSite, activeTab]);

  // Track user activity for token expiration
  useEffect(() => {
    const handleActivity = () => {
      updateActivity();
    };

    // List of events that count as user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners for all activity events
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Cleanup event listeners on unmount
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  // Attendance-specific filters - with localStorage persistence
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [attendanceStartDate, setAttendanceStartDate] = useState("");
  const [attendanceEndDate, setAttendanceEndDate] = useState("");

  // Salary report filters
  const [salaryReportMonth, setSalaryReportMonth] = useState("");
  const [salaryReportYear, setSalaryReportYear] = useState("");

  // Employee Advance-specific filters
  const [employeeAdvanceStartDate, setEmployeeAdvanceStartDate] = useState("");
  const [employeeAdvanceEndDate, setEmployeeAdvanceEndDate] = useState("");
  const [salaryStartDate, setSalaryStartDate] = useState("");
  const [salaryEndDate, setSalaryEndDate] = useState("");

  // Employee FNF filters
  const [employeeFNFSearch, setEmployeeFNFSearch] = useState("");
  const [employeeFNFStartDate, setEmployeeFNFStartDate] = useState("");
  const [employeeFNFEndDate, setEmployeeFNFEndDate] = useState("");

  // Purchase-specific filters
  const [purchaseStartDate, setPurchaseStartDate] = useState("");
  const [purchaseEndDate, setPurchaseEndDate] = useState("");
  const [purchasePaymentStatus, setPurchasePaymentStatus] = useState("ALL");

  // Work Orders-specific filters
  const [workorderStartDate, setWorkorderStartDate] = useState("");
  const [workorderEndDate, setWorkorderEndDate] = useState("");

  // Bill-specific filters
  const [billSearch, setBillSearch] = useState("");
  const [billStartDate, setBillStartDate] = useState("");
  const [billEndDate, setBillEndDate] = useState("");

  const [invoicePreview, setInvoicePreview] = useState({ open: false, url: "", filename: "", contentType: "" });
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [employeeDocumentRow, setEmployeeDocumentRow] = useState(null);
  const [employeeDocumentAction, setEmployeeDocumentAction] = useState("view");
  const [contractorDocumentRow, setContractorDocumentRow] = useState(null);
  const [contractorDocumentAction, setContractorDocumentAction] = useState("view");
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);

  // Contractor-specific filters
  const [contractorStartDate, setContractorStartDate] = useState("");
  const [contractorEndDate, setContractorEndDate] = useState("");
  const [contractorworkStartDate, setContractorworkStartDate] = useState("");
  const [contractorworkEndDate, setContractorworkEndDate] = useState("");
  const [contractorpaymentStartDate, setContractorpaymentStartDate] = useState("");
  const [contractorpaymentEndDate, setContractorpaymentEndDate] = useState("");

  // Sell-specific filters
  const [sellStartDate, setSellStartDate] = useState("");
  const [sellEndDate, setSellEndDate] = useState("");

  // Expense-specific filters
  const [expenseStartDate, setExpenseStartDate] = useState("");
  const [expenseEndDate, setExpenseEndDate] = useState("");
  const [managementExpenseStartDate, setManagementExpenseStartDate] = useState("");
  const [managementExpenseEndDate, setManagementExpenseEndDate] = useState("");
  const EXPENSE_TYPE_NAMES = ["Mess", "Machinery", "Site", "Refreshments", "Office Expenses", "Hardware", "Mobile Recharge", "Travel"];
  const [selectedExpenseType, setSelectedExpenseType] = useState("All");
  const [selectedWorkOrderType, setSelectedWorkOrderType] = useState("All");
  const [workOrderSearch, setWorkOrderSearch] = useState("");

  // Diesel-specific filters
  const [dieselStartDate, setDieselStartDate] = useState("");
  const [dieselEndDate, setDieselEndDate] = useState("");

  // Petrol-specific filters
  const [petrolStartDate, setPetrolStartDate] = useState("");
  const [petrolEndDate, setPetrolEndDate] = useState("");

  // Pump Fuel-specific filters
  const [fuelpumpStartDate, setFuelpumpStartDate] = useState("");
  const [fuelpumpEndDate, setFuelpumpEndDate] = useState("");

  // Labour Coast-specific filters
  const [labourStartDate, setLabourStartDate] = useState("");
  const [labourEndDate, setLabourEndDate] = useState("");

  // Labour-specific filters
  const [labourSearch, setLabourSearch] = useState("");
  const [labourFilterStartDate, setLabourFilterStartDate] = useState("");
  const [labourFilterEndDate, setLabourFilterEndDate] = useState("");

  // LabourAdvance-specific filters
  const [labouradvanceStartDate, setLabouradvanceStartDate] = useState("");
  const [labouradvanceEndDate, setLabouradvanceEndDate] = useState("");

  // LabourAttendance-specific filters
  const [labourattendanceStartDate, setLabourattendanceStartDate] = useState("");
  const [labourattendanceEndDate, setLabourattendanceEndDate] = useState("");

  // Labour Payment-specific filters
  const [labourpaymentStartDate, setLabourpaymentStartDate] = useState("");
  const [labourpaymentEndDate, setLabourpaymentEndDate] = useState("");

  // Concrete Production-specific filters
  const [concreteproductionStartDate, setConcreteproductionStartDate] = useState("");
  const [concreteproductionEndDate, setConcreteproductionEndDate] = useState("");

  // Fuel Transaction-specific filters
  const [fueltransactionStartDate, setFueltransactionStartDate] = useState("");
  const [fueltransactionEndDate, setFueltransactionEndDate] = useState("");

  // Fuel Payment-specific filters
  const [fuelpaymentStartDate, setFuelpaymentStartDate] = useState("");
  const [fuelpaymentEndDate, setFuelpaymentEndDate] = useState("");

  const [activeSubTab, setActiveSubTab] = useState({
    employee_group: "employees",
    labour_group: "labour",
    plantlabour_group: "labour",
    cashflow_group: "purchases",
    bill_group: "ALL",
    fuel_group: "fueltransaction",
    pump_fuel_management_group: "fuelpump",
    concreteproduction_group: "concreteproduction",
    partnership_group: "partnership"
  });

  const fetchEmployeeSiteOptions = useCallback(async () => {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.sites.list, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to load site options");
      }

      const data = await response.json();
      const payloadArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.sites)
          ? data.sites
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.content)
              ? data.content
              : Array.isArray(data?.records)
                ? data.records
                : Array.isArray(data?.result)
                  ? data.result
                  : Array.isArray(data?.results)
                    ? data.results
                    : Array.isArray(data?.list)
                      ? data.list
                      : data && typeof data === "object" && data.id !== undefined
                        ? [data]
                        : [];

      const sites = payloadArray
        .map(site => ({
          id: site?.id ?? site?.siteId ?? site?.site_id ?? site?.site?.id ?? null,
          name: site?.siteName || site?.site_name || site?.name || site?.location || site?.site?.siteName || `Site ${site?.id ?? ""}`,
          siteObject: site?.site ? { ...site.site } : { ...site },
        }))
        .filter(site => site.id != null);

      const uniqueSites = sites.reduce((acc, site) => {
        const key = String(site.id);
        if (!acc.some(existing => String(existing.id) === key)) {
          acc.push(site);
        }
        return acc;
      }, []);

      setEmployeeSiteOptions(uniqueSites);
    } catch (err) {
      console.error("Error fetching employee site options", err);
      setEmployeeSiteOptions([]);
    }
  }, []);

  const fetchActiveUsers = useCallback(async () => {
    setUserError(null);
    setUserLoading(true);
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.users.activeUsers, { method: "GET" });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Unable to load users: ${text}`);
      }
      const payload = await response.json();
      const users = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.users)
          ? payload.users
          : payload.data || [];
      setActiveUsers(users);
    } catch (err) {
      console.error("Error fetching active users", err);
      setUserError(err.message || "Failed to load users");
      setActiveUsers([]);
    } finally {
      setUserLoading(false);
    }
  }, []);

  const updateUserActiveStatus = useCallback(async (userId, active) => {
    try {
      const endpoint = API_ENDPOINTS.users.deactivateUser(userId);
      const response = await fetchWithAuth(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: active }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Unable to update user status: ${text}`);
      }
      const result = await response.json();
      await fetchActiveUsers();
      return result;
    } catch (err) {
      console.error(`Failed to update active status for user ${userId}`, err);
      throw err;
    }
  }, [fetchActiveUsers]);

  const handleCreateUser = useCallback(async () => {
    try {
      setAddUserError(null);
      setAddUserLoading(true);
      const registrationPayload = {
        ...newUser,
        userType: (newUser.userType || "USER").toString().toUpperCase(),
        firstName: newUser.firstName || "",
        lastName: newUser.lastName || "",
        name: `${(newUser.firstName || "").trim()} ${(newUser.lastName || "").trim()}`.trim(),
        site: newUser.site || (selectedSite ? { ...selectedSite } : undefined),
      };
      const response = await fetchWithAuth(API_ENDPOINTS.auth.register, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registrationPayload),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Unable to add user: ${text}`);
      }
      await response.json();
      setShowAddUserModal(false);
      setNewUser({ firstName: "", lastName: "", name: "", username: "", email: "", password: "", userType: "USER", site: selectedSite ? { ...selectedSite } : null, siteId: selectedSite?.id || "" });
      await fetchActiveUsers();
      showToast("User added successfully", "success");
    } catch (err) {
      console.error("Error creating user", err);
      setAddUserError(err.message || "Failed to add user");
    } finally {
      setAddUserLoading(false);
    }
  }, [newUser, selectedSite?.id, fetchActiveUsers]);

  const handleSaveUserEdit = useCallback(async (updated) => {
    try {
      const resolvedUserType = (updated.userType || "USER").toString().toUpperCase();
      const payload = {
        id: updated.id,
        username: updated.username,
        email: updated.email,
        userType: resolvedUserType,
        firstName: updated.firstName || "",
        lastName: updated.lastName || "",
        site: updated.site || (selectedSite ? { ...selectedSite } : undefined),
      };
      if (updated.password) {
        payload.password = updated.password;
      }
      const response = await fetchWithAuth(API_ENDPOINTS.auth.update, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Unable to update user: ${text}`);
      }

      await response.json();
      setEditingUser(null);
      await fetchActiveUsers();
      showToast("User updated successfully", "success");
    } catch (err) {
      console.error("Error updating user", err);
      showToast(err.message || "Failed to update user", "error");
    }
  }, [fetchActiveUsers]);

  // Fetch attendance data with date filters
  const fetchAttendanceWithDateFilter = async (fromDate, toDate) => {
    try {
      // Only fetch if both dates are provided
      if (!fromDate || !toDate) {
        return;
      }

      // Build query parameters matching backend contract
      const params = new URLSearchParams();
      
      
      let url = `${API_ENDPOINTS.attendance.dateFilter}?fromDate=${fromDate}&toDate=${toDate}`;
      
      // Add site parameter if site is selected
      if (selectedSite && selectedSite.id) {
        url += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      console.log("Final URL:", url);
      
      const response = await fetchWithAuth(url, { method: "GET" });
      
      if (response.ok) {
        const attendanceData = await response.json();
        
        // Check if data is empty array or null
        if (!attendanceData || (Array.isArray(attendanceData) && attendanceData.length === 0)) {
          setData(prevData => ({ ...prevData, attendance: [] }));
        } else {
          setData(prevData => ({ ...prevData, attendance: attendanceData }));
          console.log("Attendance data fetched with filters:", attendanceData);
        }
      } else {
        const responseText = await response.text();
        console.error("Failed to fetch attendance - Status:", response.status, "URL:", url, "Response:", responseText.substring(0, 200));
      }
    } catch (err) {
      console.error("Error fetching attendance with date filter:", err);
    }
  };

  // Get current month date range
  const getCurrentMonthDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    
    return {
      fromDate: formatDate(firstDay),
      toDate: formatDate(lastDay)
    };
  };

  // Fetch data on component mount and when site changes
  useEffect(() => {
    fetchAllData();
    
    // Clear attendance localStorage on initial mount so filters start empty after login
    localStorage.removeItem("attendanceStartDate");
    localStorage.removeItem("attendanceEndDate");
  }, [selectedSite]);

  // Ensure pagination is valid when active tab or data changes
  useEffect(() => {
    const totalPages = getTotalPages(activeTab);
    if (pagination[activeTab] >= totalPages && totalPages > 0) {
      setPagination(prev => ({ ...prev, [activeTab]: totalPages - 1 }));
    }
  }, [activeTab, data, search]);

  // Save attendance dates to localStorage
  useEffect(() => {
    if (attendanceStartDate) {
      localStorage.setItem("attendanceStartDate", attendanceStartDate);
    }
  }, [attendanceStartDate]);

  useEffect(() => {
    if (attendanceEndDate) {
      localStorage.setItem("attendanceEndDate", attendanceEndDate);
    }
  }, [attendanceEndDate]);

  // Fetch fuel counts when component mounts or site changes
  useEffect(() => {
    const siteId = selectedSite?.id;
    if (siteId == null || siteId === "") return;

    const normalizedSiteId = String(siteId);
    if (fuelCountsSiteIdRef.current === normalizedSiteId) return;

    fuelCountsSiteIdRef.current = normalizedSiteId;
    fetchFuelCounts();
  }, [selectedSite?.id]);

  // Fetch attendance tab data when tab is clicked
  useEffect(() => {
    if (activeTab === "attendance") {
      // Fetch all attendance records for the site (without date filters)
      fetchTabData("attendance");
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "employeefnf") {
      fetchTabData("employeefnf");
      fetchTabData("employees");
    }
  }, [activeTab, selectedSite?.id]);

  useEffect(() => {
    if (activeTab === "salary") {
      fetchTabData("salary");
      fetchTabData("employees");
    }
  }, [activeTab, selectedSite?.id]);

  // Fetch employee tab data when tab is clicked
  useEffect(() => {
    if (activeTab === "employees" && isAdmin) {
      // Fetch all employee records for the site
      fetchTabData("employees");
      fetchEmployeeSiteOptions();
    }
  }, [activeTab, isAdmin, fetchEmployeeSiteOptions]);

  useEffect(() => {
    if (activeTab === "settings" && isAdmin) {
      fetchActiveUsers();
      fetchEmployeeSiteOptions();
    }
  }, [activeTab, isAdmin, fetchActiveUsers, fetchEmployeeSiteOptions]);

  useEffect(() => {
    if (activeTab !== "dashboard") {
      setDashboardFromDate("");
      setDashboardToDate("");
    }
  }, [activeTab]);

  useEffect(() => {
    setNewUser(prev => ({ ...prev, siteId: selectedSite?.id ?? "" }));
  }, [selectedSite?.id]);

  // Fetch only count data for stat cards on dashboard load (not full list)
  // Helper function to extract count from API response
  // Count APIs return ResponseEntity with data: { count: 5 } or just number
  const extractCount = (response) => {
    if (typeof response === 'number') return response;
    if (response && typeof response === 'object' && response.count !== undefined) {
      return Number(response.count);
    }
    return getListRowCount(response);
  };

  const buildCountQueryParams = (site, fromDate, toDate) => {
    const params = new URLSearchParams();
    if (site && site.id) {
      params.set("siteid", site.id);
    }
    if (fromDate) {
      params.set("fromDate", fromDate);
    }
    if (toDate) {
      params.set("toDate", toDate);
    }
    return params.toString() ? `?${params.toString()}` : "";
  };

  const fetchCountsData = async (site, fromDate, toDate) => {
    try {
      const queryString = buildCountQueryParams(site, fromDate, toDate);
      const employeeListEndpoint = `${API_ENDPOINTS.employee.list}${site && site.id ? `?siteid=${encodeURIComponent(site.id)}` : ""}`;
      const contractorPaymentEndpoint = `${API_ENDPOINTS.contractorpayment.sum}${queryString}`;
      const buildFuelpumpCountUrl = (fuelType) => {
        const params = new URLSearchParams();
        if (site && site.id) {
          params.set("siteid", site.id);
        }
        if (fuelType) {
          params.set("fuelType", fuelType);
        }
        if (fromDate) {
          params.set("fromDate", fromDate);
        }
        if (toDate) {
          params.set("toDate", toDate);
        }
        const paramString = params.toString();
        return paramString ? `${API_ENDPOINTS.fuelpump.count}?${paramString}` : API_ENDPOINTS.fuelpump.count;
      };
      
      // Fetch only count APIs for stat cards (much faster than full list)
      // Note: Labour Coast = Labour Payment count + Labour Advance count (no separate labourcoast count needed)
      const [purchasesRes, sellsRes, expensesRes, managementExpenseRes, employeeAdvanceRes, dieselRes, petrolRes, fuelpumpPetrolRes, fuelpumpDieselRes, labourpaymentRes, labouradvanceRes, empRes, concreteRes, employeesListRes, contractorpaymentRes] = 
        await Promise.all([
          fetchWithAuth(`${API_ENDPOINTS.purchase.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.sell.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.expense.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.managementExpense.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.employeeadvance.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.diesel.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.petrol.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(buildFuelpumpCountUrl("PETROL"), { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(buildFuelpumpCountUrl("DIESEL"), { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.labourpayment.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.labouradvance.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.employee.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(`${API_ENDPOINTS.concreteproduction.count}${queryString}`, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(employeeListEndpoint, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; }),
          fetchWithAuth(contractorPaymentEndpoint, { method: "GET" }).catch(e => { console.error(e); return { ok: false }; })
        ]);

      // Process responses - count APIs return { count: number }
      const purchasesData = purchasesRes.ok ? await purchasesRes.json() : { count: 0 };
      const sellsData = sellsRes.ok ? await sellsRes.json() : { count: 0 };
      const expensesData = expensesRes.ok ? await expensesRes.json() : { count: 0 };
      const managementExpenseData = managementExpenseRes.ok ? await managementExpenseRes.json() : 0;
      const employeeAdvanceData = employeeAdvanceRes.ok ? await employeeAdvanceRes.json() : 0;
      const dieselData = dieselRes.ok ? await dieselRes.json() : { count: 0 };
      const petrolData = petrolRes.ok ? await petrolRes.json() : { count: 0 };
      const fuelpumpPetrolData = fuelpumpPetrolRes.ok ? await fuelpumpPetrolRes.json() : { count: 0 };
      const fuelpumpDieselData = fuelpumpDieselRes.ok ? await fuelpumpDieselRes.json() : { count: 0 };
      const labourpaymentData = labourpaymentRes.ok ? await labourpaymentRes.json() : { count: 0 };
      const labouradvanceData = labouradvanceRes.ok ? await labouradvanceRes.json() : { count: 0 };
      const employeesData = empRes.ok ? await empRes.json() : { count: 0 };
      const concreteData = concreteRes.ok ? await concreteRes.json() : { count: 0 };
      const employeesListData = employeesListRes.ok ? await employeesListRes.json() : [];
      const contractorpaymentData = contractorpaymentRes.ok ? await contractorpaymentRes.json() : 0;

      const expenseTypeResponses = await Promise.all(EXPENSE_TYPE_NAMES.map(type => {
        const params = new URLSearchParams();
        if (site && site.id) {
          params.set("siteid", site.id);
        }
        if (fromDate) {
          params.set("fromDate", fromDate);
        }
        if (toDate) {
          params.set("toDate", toDate);
        }
        params.set("expenseType", type);
        const paramString = params.toString();
        return fetchWithAuth(`${API_ENDPOINTS.expense.count}${paramString ? `?${paramString}` : ""}`, { method: "GET" })
          .catch(e => {
            console.error(`Error fetching ${type} expense total:`, e);
            return { ok: false };
          });
      }));

      const expenseTypeTotalsData = await Promise.all(expenseTypeResponses.map(async (res, index) => {
        if (res.ok) {
          const json = await res.json();
          return extractCount(json);
        }
        return 0;
      }));

      const expenseTypeTotals = {
        Mess: expenseTypeTotalsData[0] || 0,
        Machinery: expenseTypeTotalsData[1] || 0,
        Site: expenseTypeTotalsData[2] || 0,
        Refreshments: expenseTypeTotalsData[3] || 0,
        "Office Expenses": expenseTypeTotalsData[4] || 0,
        Hardware: expenseTypeTotalsData[5] || 0,
        "Mobile Recharge": expenseTypeTotalsData[6] || 0,
        Travel: expenseTypeTotalsData[7] || 0,
      };

      // Extract count values from responses
      const purchases = extractCount(purchasesData);
      const sells = extractCount(sellsData);
      const expenses = extractCount(expensesData);
      const diesel = extractCount(dieselData);
      const petrol = extractCount(petrolData);
      const fuelpumpPetrol = extractCount(fuelpumpPetrolData);
      const fuelpumpDiesel = extractCount(fuelpumpDieselData);
      const fuelpump = fuelpumpPetrol + fuelpumpDiesel;
      const fuelPumpCounts = {
        diesel: fuelpumpDiesel,
        petrol: fuelpumpPetrol
      };
      const labourpayment = extractCount(labourpaymentData);
      const labouradvance = extractCount(labouradvanceData);
      const employeesFromCount = extractCount(employeesData);
      const employees = employeesFromCount > 0 ? employeesFromCount : getListRowCount(employeesListData);
      const concreteproduction = extractCount(concreteData);
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        if (typeof value === "number") return value;
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed === "") return 0;
          const parsed = Number(trimmed);
          return Number.isFinite(parsed) ? parsed : 0;
        }
        if (typeof value === "object") {
          return parseNumericValue(value.amount ?? value.total ?? value.count ?? value.value ?? value.sum);
        }
        return 0;
      };
      const managementExpense = parseNumericValue(managementExpenseData);
      const employeeadvance = parseNumericValue(employeeAdvanceData);
      const contractorpayment = parseNumericValue(contractorpaymentData);

      setFuelPumpCounts(fuelPumpCounts);

      setData(prevData => ({
        ...prevData,
        purchases,
        sells,
        expenses,
        managementExpense,
        employeeadvance,
        diesel,
        petrol,
        fuelpump,
        labourpayment,
        labouradvance,
        employees,
        concreteproduction,
        contractorpayment,
        expenseTypeTotals
      }));
    } catch (err) {
      console.error("Error fetching counts data:", err);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all counts data for stat cards (count endpoints only)
      await fetchCountsData(selectedSite);
      
      // Reset pagination to first page
      setPagination(prev => ({ ...prev, employees: 0 }));
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Please try again.");
      showToast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardBillTotals = useCallback(async (fromDate, toDate) => {
    const siteId = selectedSite?.id ?? selectedSite?.siteId ?? selectedSite?.site_id;
    if (!isAdmin || !siteId) {
      setDashboardPartnershipTotal(null);
      setDashboardBillTotal(null);
      return;
    }

    const partnershipParams = new URLSearchParams({
      siteId: String(siteId),
    });
    const billParams = new URLSearchParams({
      siteid: String(siteId),
    });
    if (fromDate && toDate) {
      partnershipParams.set("fromDate", fromDate);
      partnershipParams.set("toDate", toDate);
      billParams.set("fromDate", fromDate);
      billParams.set("toDate", toDate);
    }

    try {
      const [partnershipResponse, billResponse] = await Promise.all([
        fetchWithAuth(`${API_ENDPOINTS.partnership.sum}?${partnershipParams.toString()}`, { method: "GET" }),
        fetchWithAuth(`${API_ENDPOINTS.bill.sum}?${billParams.toString()}`, { method: "GET" }),
      ]);
      if (!partnershipResponse.ok) {
        throw new Error(`Failed to fetch Partnership total (${partnershipResponse.status})`);
      }
      if (!billResponse.ok) {
        throw new Error(`Failed to fetch Bill total (${billResponse.status})`);
      }
      const [partnershipTotal, billTotal] = await Promise.all([
        partnershipResponse.json().then(Number),
        billResponse.json().then(Number),
      ]);
      if (!Number.isFinite(partnershipTotal) || !Number.isFinite(billTotal)) {
        throw new Error("Invalid bill total response");
      }
      setDashboardPartnershipTotal(partnershipTotal);
      setDashboardBillTotal(billTotal);
    } catch (err) {
      console.error("Dashboard bill totals error:", err);
      setDashboardPartnershipTotal(null);
      setDashboardBillTotal(null);
      showToast("Failed to load bill totals", "error");
    }
  }, [isAdmin, selectedSite?.id, selectedSite?.siteId, selectedSite?.site_id]);

  useEffect(() => {
    fetchDashboardBillTotals();
  }, [fetchDashboardBillTotals]);

  const handleDashboardCountSearch = async () => {
    const fromDate = dashboardFromDate?.trim();
    const toDate = dashboardToDate?.trim();

    if (!fromDate || !toDate) {
      showToast("Please select both From Date and To Date to search dashboard counts", "error");
      return;
    }

    setLoading(true);
    try {
      console.log("Dashboard count search", { fromDate, toDate, selectedSite });
      await fetchCountsData(selectedSite, fromDate, toDate);
      await fetchDashboardBillTotals(fromDate, toDate);
      showToast("Dashboard totals updated", "success");
    } catch (err) {
      console.error("Error fetching dashboard totals with date filters:", err);
      showToast("Failed to load dashboard totals", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClearDashboardFilters = async () => {
    const { fromDate, toDate } = getCurrentMonthDateRange();
    setDashboardFromDate("");
    setDashboardToDate("");
    setDashboardPartnershipTotal(null);
    setDashboardBillTotal(null);
    setLoading(true);

    try {
      await fetchCountsData(selectedSite, fromDate, toDate);
      await fetchDashboardBillTotals();
      showToast("Dashboard counts reset to current month", "success");
    } catch (err) {
      console.error("Error resetting dashboard counts to current month:", err);
      showToast("Failed to reset dashboard totals", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleContractorDateSearch = async () => {
    const startDate = activeTab === "contractorwork" ? contractorworkStartDate : contractorpaymentStartDate;
    const endDate = activeTab === "contractorwork" ? contractorworkEndDate : contractorpaymentEndDate;

    if (!startDate || !endDate) {
      showToast("Please select both From Date and To Date to search", "error");
      return;
    }

    setPagination(prev => ({ ...prev, [activeTab]: 0 }));
    await fetchTabData(activeTab, undefined, undefined, startDate, endDate);
  };

  const normalizeListRows = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    if (value && typeof value === "object") {
      const candidateKeys = ["data", "rows", "records", "items", "content", "result", "results", "list"];
      for (const key of candidateKeys) {
        if (Array.isArray(value[key])) {
          return value[key];
        }
      }
    }

    return [];
  };

  // Helper function to check if data needs to be fetched as a list
  // Returns true if data is not already an array (e.g., it's count data from dashboard load)
  const shouldFetchTabData = (section) => {
    const sectionData = data[section];
    return !Array.isArray(sectionData) || normalizeListRows(sectionData).length === 0;
  };

  // Fetch other tab data on demand
  const fetchTabData = async (tabName, overridePaymentStatus, overrideExpenseType, fromDate, toDate, overrideWorkOrderType) => {
    try {
      let endpoint;
      const buildSiteQuery = (baseUrl, useSiteIdParam = false) => {
        const params = new URLSearchParams();
        if (selectedSite && selectedSite.id) {
          params.set(useSiteIdParam ? "siteId" : "siteid", selectedSite.id);
        }
        const query = params.toString();
        return query ? `${baseUrl}?${query}` : baseUrl;
      };
      switch (tabName) {
        case "attendance":
          endpoint = API_ENDPOINTS.attendance.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;

        case "employeefnf":
          endpoint = API_ENDPOINTS.employeefnf.list;
          break;

        case "employeeadvance": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          if (fromDate) {
            params.set("fromDate", fromDate);
          }
          if (toDate) {
            params.set("toDate", toDate);
          }
          endpoint = (fromDate || toDate) ? API_ENDPOINTS.employeeadvance.dateFilter : API_ENDPOINTS.employeeadvance.list;
          const query = params.toString();
          if (query) {
            endpoint += `?${query}`;
          }
          break;
        }
        case "salary": {
          const params = new URLSearchParams();
          if (selectedSite?.id) {
            params.set("siteid", selectedSite.id);
          }
          if (fromDate && toDate) {
            params.set("fromDate", fromDate);
            params.set("toDate", toDate);
            endpoint = API_ENDPOINTS.salary.dateRange;
          } else {
            endpoint = API_ENDPOINTS.salary.list;
          }
          const query = params.toString();
          if (query) {
            endpoint += `?${query}`;
          }
          break;
        }
              case "purchases": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          const paymentStatusFilter = overridePaymentStatus ?? purchasePaymentStatus;
          if (paymentStatusFilter && paymentStatusFilter !== "ALL") {
            params.set("paymentStatus", paymentStatusFilter);
          }
          if (fromDate) {
            params.set("fromDate", fromDate);
          }
          if (toDate) {
            params.set("toDate", toDate);
          }
          endpoint = (fromDate || toDate) ? API_ENDPOINTS.purchase.dateRange : API_ENDPOINTS.purchase.list;
          const purchaseQuery = params.toString();
          if (purchaseQuery) {
            endpoint += `?${purchaseQuery}`;
          }
          break;
        }
        case "bills": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }

          if (billSearch) {
            endpoint = `${API_ENDPOINTS.bill.billNo}?billNo=${encodeURIComponent(billSearch)}`;
            if (params.toString()) {
              endpoint += `&${params.toString()}`;
            }
          } else if (billStartDate || billEndDate) {
            if (billStartDate) {
              params.set("fromDate", billStartDate);
            }
            if (billEndDate) {
              params.set("toDate", billEndDate);
            }
            endpoint = API_ENDPOINTS.bill.dateFilter;
            const query = params.toString();
            if (query) {
              endpoint += `?${query}`;
            }
          } else {
            endpoint = API_ENDPOINTS.bill.list;
            const query = params.toString();
            if (query) {
              endpoint += `?${query}`;
            }
          }
          break;
        }
        case "managementExpense": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          if (fromDate) params.set("fromDate", fromDate);
          if (toDate) params.set("toDate", toDate);
          endpoint = fromDate || toDate ? API_ENDPOINTS.managementExpense.dateRange : API_ENDPOINTS.managementExpense.site;
          const query = params.toString();
          if (query) endpoint += `?${query}`;
          break;
        }
        case "sells": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          if (fromDate) {
            params.set("fromDate", fromDate);
          }
          if (toDate) {
            params.set("toDate", toDate);
          }
          endpoint = (fromDate || toDate) ? API_ENDPOINTS.sell.dateRange : API_ENDPOINTS.sell.list;
          const sellQuery = params.toString();
          if (sellQuery) {
            endpoint += `?${sellQuery}`;
          }
          break;
        }
        case "expenses": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          const expenseTypeFilter = overrideExpenseType ?? selectedExpenseType;
          if (expenseTypeFilter && expenseTypeFilter !== "All") {
            params.set("expenseType", expenseTypeFilter);
          }
          if (fromDate) {
            params.set("fromDate", fromDate);
          }
          if (toDate) {
            params.set("toDate", toDate);
          }
          endpoint = (fromDate || toDate) ? API_ENDPOINTS.expense.dateRange : API_ENDPOINTS.expense.list;
          const expenseQuery = params.toString();
          if (expenseQuery) {
            endpoint += `?${expenseQuery}`;
          }
          break;
        }
        case "workorders": {
          endpoint = API_ENDPOINTS.workorder.list;
          const q = buildWorkOrderQuery({
            siteId: selectedSite?.id,
            fromDate,
            toDate,
            workOrderType: overrideWorkOrderType ?? selectedWorkOrderType,
          });
          if (q) endpoint += `?${q}`;
          break;
        }
        case "fuelpump":
          endpoint = API_ENDPOINTS.fuelpump.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "fuelpayment":
          endpoint = selectedSite?.id
            ? `${API_ENDPOINTS.fuelpayment.site}?siteid=${encodeURIComponent(selectedSite.id)}`
            : API_ENDPOINTS.fuelpayment.list;
          break;
        case "labourcoast":
          endpoint = API_ENDPOINTS.labourcoast.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "labour":
          endpoint = API_ENDPOINTS.labour.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "contractor":
          endpoint = buildSiteQuery(API_ENDPOINTS.contractor.list);
          break;
        case "contractorwork": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          if (fromDate) {
            params.set("fromDate", fromDate);
          }
          if (toDate) {
            params.set("toDate", toDate);
          }
          endpoint = (fromDate || toDate) ? API_ENDPOINTS.contractorwork.dateRange : API_ENDPOINTS.contractorwork.list;
          const query = params.toString();
          if (query) {
            endpoint += `?${query}`;
          }
          break;
        }
        case "contractorpayment": {
          const params = new URLSearchParams();
          if (selectedSite && selectedSite.id) {
            params.set("siteid", selectedSite.id);
          }
          if (fromDate) {
            params.set("fromDate", fromDate);
          }
          if (toDate) {
            params.set("toDate", toDate);
          }
          endpoint = (fromDate || toDate) ? API_ENDPOINTS.contractorpayment.dateRange : API_ENDPOINTS.contractorpayment.list;
          const query = params.toString();
          if (query) {
            endpoint += `?${query}`;
          }
          break;
        }
        case "labouradvance":
          endpoint = API_ENDPOINTS.labouradvance.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "labourattendance":
          endpoint = API_ENDPOINTS.labourattendance.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "labourpayment":
          endpoint = API_ENDPOINTS.labourpayment.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "concreteproduction":
          endpoint = API_ENDPOINTS.concreteproduction.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "fueltransaction":
          endpoint = API_ENDPOINTS.fueltransaction.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        case "employees":
          endpoint = API_ENDPOINTS.employee.list;
          if (selectedSite && selectedSite.id) {
            endpoint += `?siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          break;
        default:
          return;
      }

      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (response.ok) {
        const tabData = await response.json();
        const normalizedRows = normalizeListRows(tabData);
        const fallbackRows = Array.isArray(tabData) ? tabData : (tabData && typeof tabData === "object" ? Object.values(tabData).filter(value => Array.isArray(value) ? value : []) : []);
        const rowsToStore = normalizedRows.length > 0 ? normalizedRows : (Array.isArray(fallbackRows) ? fallbackRows.flat() : []);
        setData(prevData => ({ ...prevData, [tabName]: rowsToStore }));
      }
    } catch (err) {
      console.error(`Error fetching ${tabName}:`, err);
    }
  };

  const fetchManagementExpenseEmployees = async () => {
    try {
      const [employeesResponse, managementResponse] = await Promise.all([
        fetchWithAuth(API_ENDPOINTS.employee.list, { method: "GET" }),
        fetchWithAuth(`${API_ENDPOINTS.employee.list}?isManagementGroup=true`, { method: "GET" }),
      ]);
      if (!employeesResponse.ok) throw new Error("Failed to load employees");
      if (!managementResponse.ok) throw new Error("Failed to load management employees");

      const [employeesData, managementData] = await Promise.all([
        employeesResponse.json(),
        managementResponse.json(),
      ]);
      const employeeRows = normalizeListRows(employeesData);
      setManagementExpenseEmployees(employeeRows);
      setManagementGroupEmployees(normalizeListRows(managementData));
      return employeeRows;
    } catch (error) {
      console.error("Error fetching employees for management expense:", error);
      showToast(error.message || "Failed to load employees", "error");
      return [];
    }
  };

  const searchLabours = async (value) => {
    const trimmedValue = value.trim();
    setLabourSearch(value);
    setPagination(prev => ({ ...prev, labour: 0 }));

    if (!trimmedValue) {
      await fetchTabData("labour");
      return;
    }

    try {
      const params = new URLSearchParams({ query: trimmedValue });
      if (selectedSite?.id) {
        params.set("siteid", selectedSite.id);
      }

      const response = await fetchWithAuth(`${API_ENDPOINTS.labour.search}?${params.toString()}`, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to search labour");
      }

      const result = await response.json();
      const rows = Array.isArray(result)
        ? result
        : result?.data || result?.labours || result?.content || [];
      setData(prev => ({ ...prev, labour: Array.isArray(rows) ? rows : [] }));
    } catch (err) {
      console.error("Labour search error:", err);
      showToast("Failed to search labour", "error");
    }
  };

  const handleExpenseTypeFilter = async (type) => {
    setSelectedExpenseType(type);
    setPagination(prev => ({ ...prev, expenses: 0 }));
    setActiveTab("expenses");
    await fetchTabData("expenses", undefined, type);
  };

  const searchPurchasesByDate = async () => {
    setPagination(prev => ({ ...prev, purchases: 0 }));
    await fetchTabData("purchases", purchasePaymentStatus, undefined, purchaseStartDate, purchaseEndDate);
  };

  const searchWorkordersByDate = async () => {
    setPagination(prev => ({ ...prev, workorders: 0 }));
    await fetchTabData("workorders", undefined, undefined, workorderStartDate, workorderEndDate, selectedWorkOrderType);
  };

  const searchSellsByDate = async () => {
    setPagination(prev => ({ ...prev, sells: 0 }));
    await fetchTabData("sells", undefined, undefined, sellStartDate, sellEndDate);
  };

  const searchExpensesByDate = async () => {
    setPagination(prev => ({ ...prev, expenses: 0 }));
    await fetchTabData("expenses", undefined, selectedExpenseType, expenseStartDate, expenseEndDate);
  };

  const searchAttendanceByDate = async () => {
    setPagination(prev => ({ ...prev, attendance: 0 }));

    if ((attendanceStartDate && !attendanceEndDate) || (!attendanceStartDate && attendanceEndDate)) {
      showToast("Please select both From Date and To Date to search attendance", "error");
      return;
    }

    if (attendanceStartDate && attendanceEndDate) {
      await fetchAttendanceWithDateFilter(attendanceStartDate, attendanceEndDate);
    } else {
      await fetchTabData("attendance");
    }
  };

  const searchEmployeeAdvanceByDate = async () => {
    setPagination(prev => ({ ...prev, employeeadvance: 0 }));

    if ((employeeAdvanceStartDate && !employeeAdvanceEndDate) || (!employeeAdvanceStartDate && employeeAdvanceEndDate)) {
      showToast("Please select both From Date and To Date to search employee advances", "error");
      return;
    }

    await fetchTabData("employeeadvance", undefined, undefined, employeeAdvanceStartDate, employeeAdvanceEndDate);
  };

  const searchSalaryByDate = async () => {
    if (!salaryStartDate || !salaryEndDate) {
      showToast("Please select both From Date and To Date to search salaries", "error");
      return;
    }
    setPagination(prev => ({ ...prev, salary: 0 }));
    await fetchTabData("salary", undefined, undefined, salaryStartDate, salaryEndDate);
  };

  const handlePurchaseRowClick = (row) => {
    const purchaseNo = row.purchaseNo || row.purchaseNumber || row.purchase_no;
    if (!purchaseNo) return;
    setSelectedPurchase(prev => {
      const prevNo = prev?.purchaseNo || prev?.purchaseNumber || prev?.purchase_no;
      return prevNo === purchaseNo ? null : row;
    });
  };

  const handleWorkOrderRowClick = async (row) => {
    const workOrderId = row.workOrderId || row.workorderId || row.work_order_id || row.id || null;
    const woNumber = row.woNumber || row.workOrderNo || row.work_order_no || null;
    const currentId = selectedWorkOrder?.workOrderId || selectedWorkOrder?.workorderId || selectedWorkOrder?.work_order_id || selectedWorkOrder?.id || null;
    const currentNumber = selectedWorkOrder?.woNumber || selectedWorkOrder?.workOrderNo || selectedWorkOrder?.work_order_no || null;

    if ((workOrderId && String(workOrderId) === String(currentId)) || (!workOrderId && woNumber && woNumber === currentNumber)) {
      setSelectedWorkOrder(null);
      setWorkOrderBills([]);
      return;
    }

    setSelectedWorkOrder(row);
    setWorkOrderBills([]);
    setWorkOrderBillsLoading(true);

    try {
      const params = new URLSearchParams();
      if (selectedSite?.id) params.set("siteid", selectedSite.id);
      if (workOrderId) params.set("workOrderId", workOrderId);
      if (woNumber) params.set("woNumber", woNumber);
      const endpoint = `${API_ENDPOINTS.bill.list}?${params.toString()}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) throw new Error("Failed to load bills for work order");

      const payload = await response.json();
      const rows = normalizeListRows(payload);
      const fallbackRows = Array.isArray(payload) ? payload : [];
      const bills = rows.length > 0 ? rows : fallbackRows;
      const relatedBills = bills.filter((bill) => {
        const linkedWorkOrder = bill.workOrder || bill.workorder || bill.work_order || null;
        const billWorkOrderId = bill.workOrderId
          || bill.workorderId
          || bill.work_order_id
          || (linkedWorkOrder && typeof linkedWorkOrder === "object" ? linkedWorkOrder.id || linkedWorkOrder.workOrderId || linkedWorkOrder.workorderId : null);
        const billWoNumber = bill.woNumber
          || bill.workOrderNo
          || bill.work_order_no
          || (typeof linkedWorkOrder === "string" ? linkedWorkOrder : null)
          || (linkedWorkOrder && typeof linkedWorkOrder === "object" ? linkedWorkOrder.woNumber || linkedWorkOrder.workOrderNo || linkedWorkOrder.work_order_no : null);
        return (workOrderId && billWorkOrderId && String(billWorkOrderId) === String(workOrderId))
          || (woNumber && billWoNumber && String(billWoNumber) === String(woNumber));
      });
      setWorkOrderBills(relatedBills);
    } catch (error) {
      console.error("Error fetching work order bills:", error);
      showToast("Failed to load bills for this work order", "error");
    } finally {
      setWorkOrderBillsLoading(false);
    }
  };

  const renderWorkOrderBills = (row) => {
    const woNumber = row.woNumber || row.workOrderNo || row.work_order_no || "Work Order";
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Bills for {woNumber}</div>
          <button className="btn-secondary" type="button" onClick={() => { setSelectedWorkOrder(null); setWorkOrderBills([]); }} style={{ minWidth: 120, justifyContent: "center" }}>
            Close
          </button>
        </div>
        {workOrderBillsLoading ? (
          <div style={{ color: "#475569", padding: "10px 12px" }}>Loading related bills...</div>
        ) : workOrderBills.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#edf2ff", color: "#0f172a", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>Bill No</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>Bill Date</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>Description</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "right" }}>Amount</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "right" }}>Amount Received</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "right" }}>Remaining Amount</th>
                </tr>
              </thead>
              <tbody>
                {workOrderBills.map((bill, index) => (
                  <tr key={bill.billId || bill.id || bill.billNo || index} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "10px 12px" }}>{bill.billNo || bill.billNumber || bill.bill_no || "-"}</td>
                    <td style={{ padding: "10px 12px" }}>{bill.billDate || bill.date || bill.bill_date || "-"}</td>
                    <td style={{ padding: "10px 12px" }}>{bill.description || bill.remark || "-"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatAmountIN(bill.amount ?? bill.totalAmount)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatAmountIN(bill.amountReceived ?? bill.amount_received)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{formatAmountIN(bill.remainingAmount ?? bill.remaining_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: "#475569", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc" }}>
            No bills found for this work order.
          </div>
        )}
      </div>
    );
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handlePurchaseUploadSuccess = async () => {
    await fetchTabData("purchases");
    setPagination(prev => ({ ...prev, purchases: 0 }));
  };

  const handleSellUploadSuccess = async () => {
    await fetchTabData("sells");
    setPagination(prev => ({ ...prev, sells: 0 }));
  };

  const handleUpdatePurchasePaymentStatus = async (row, newStatus) => {
    try {
      const purchaseNo = row.purchaseNo || row.purchaseNumber || row.purchase_no;
      if (!purchaseNo) {
        throw new Error("Missing purchase identifier");
      }

      const endpoint = API_ENDPOINTS.purchase.updatePaymentStatus(purchaseNo, newStatus);
      const response = await fetchWithAuth(endpoint, { method: "PUT" });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        showToast(`Failed to update payment status${errorText ? `: ${errorText}` : ""}`, "error");
        return;
      }

      const updatedPurchase = await response.json();
      const updatedStatus = updatedPurchase?.paymentStatus || newStatus;

      setData(prev => ({
        ...prev,
        purchases: Array.isArray(prev.purchases) ? prev.purchases.map(item => {
          const itemPurchaseNo = item.purchaseNo || item.purchaseNumber || item.purchase_no;
          return itemPurchaseNo === purchaseNo ? { ...item, paymentStatus: updatedStatus } : item;
        }) : prev.purchases
      }));

      showToast(`Payment status updated to ${updatedStatus}`);
    } catch (err) {
      console.error("Update purchase payment status error:", err);
      showToast("Error updating payment status", "error");
    }
  };

  const handleUpdateSellPaymentStatus = async (row, newStatus) => {
    try {
      const sellNo = row.sellNo || row.sell_no || row.saleNo || row.billNo || row.bill_number;
      if (!sellNo) {
        throw new Error("Missing sell identifier");
      }

      const endpoint = API_ENDPOINTS.sell.updatePaymentStatus(sellNo, newStatus);
      const response = await fetchWithAuth(endpoint, { method: "PUT" });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        showToast(`Failed to update payment status${errorText ? `: ${errorText}` : ""}`, "error");
        return;
      }

      const updatedSell = await response.json();
      const updatedStatus = updatedSell?.paymentStatus || newStatus;

      setData(prev => ({
        ...prev,
        sells: Array.isArray(prev.sells) ? prev.sells.map(item => {
          const itemSellNo = item.sellNo || item.sell_no || item.saleNo || item.billNo || item.bill_number;
          return itemSellNo === sellNo ? { ...item, paymentStatus: updatedStatus } : item;
        }) : prev.sells
      }));

      showToast(`Payment status updated to ${updatedStatus}`);
    } catch (err) {
      console.error("Update sell payment status error:", err);
      showToast("Error updating payment status", "error");
    }
  };

  const handleUpdateEmployeeStatusAndSite = async (row, nextValue) => {
    try {
      const empCode = row?.empCode || row?.empcode || row?.employee?.empCode || row?.employeeId?.empCode || "";
      if (!empCode) {
        throw new Error("Missing employee code");
      }

      const currentSiteId = row?.site?.id ?? row?.siteId ?? row?.site_id ?? row?.site?.siteId ?? null;
      const currentIsActive = row?.isActive ?? row?.active ?? row?.status;
      const nextIsActive = typeof nextValue === "boolean"
        ? nextValue
        : nextValue === "true" || nextValue === true || nextValue === "ACTIVE" || nextValue === "Active";
      const payload = {
        empCode,
        isActive: nextIsActive,
        siteId: currentSiteId,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.employee.status, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || "Failed to update employee status");
      }

      setData(prev => ({
        ...prev,
        employees: Array.isArray(prev.employees)
          ? prev.employees.map(item => {
              const itemEmpCode = item?.empCode || item?.empcode || item?.employee?.empCode || item?.employeeId?.empCode || "";
              if (!itemEmpCode || itemEmpCode !== empCode) {
                return item;
              }
              return {
                ...item,
                isActive: nextIsActive,
                active: nextIsActive,
                status: nextIsActive ? "Active" : "Inactive",
                siteId: currentSiteId,
                site: item.site ? { ...item.site, id: currentSiteId, siteId: currentSiteId } : item.site,
              };
            })
          : prev.employees,
      }));

      await fetchTabData("employees");
      showToast(`Employee ${nextIsActive ? "activated" : "deactivated"}`);
    } catch (err) {
      console.error("Employee status update error:", err);
      showToast(err.message || "Failed to update employee status", "error");
    }
  };

  const handleUpdateContractorActiveStatus = async (row, nextValue) => {
    try {
      const contractorCode = row?.contractorCode || row?.contractor?.contractorCode || row?.contractor?.code || row?.code || row?.contractor_code || row?.code;
      if (!contractorCode) {
        throw new Error("Missing contractor code");
      }

      const currentSiteId = row?.site?.id ?? row?.siteId ?? row?.site_id ?? row?.site?.siteId ?? null;
      const nextIsActive = typeof nextValue === "boolean"
        ? nextValue
        : nextValue === "true" || nextValue === true || nextValue === "ACTIVE" || nextValue === "Active";
      const payload = {
        contractorCode,
        isActive: nextIsActive,
        siteId: currentSiteId,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.contractor.status, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || "Failed to update contractor status");
      }

      setData(prev => ({
        ...prev,
        contractor: Array.isArray(prev.contractor)
          ? prev.contractor.map(item => {
              const itemCode = item?.contractorCode || item?.code || item?.contractor?.contractorCode || item?.contractor?.code;
              if (!itemCode || itemCode !== contractorCode) {
                return item;
              }
              return {
                ...item,
                isActive: nextIsActive,
                active: nextIsActive,
                status: nextIsActive ? "Active" : "Inactive",
                siteId: currentSiteId,
                site: item.site ? { ...item.site, id: currentSiteId, siteId: currentSiteId } : item.site,
              };
            })
          : prev.contractor,
      }));

      await fetchTabData("contractor");
      showToast(`Contractor ${nextIsActive ? "activated" : "deactivated"}`);
    } catch (err) {
      console.error("Contractor status update error:", err);
      showToast(err.message || "Failed to update contractor status", "error");
    }
  };

  const handleUpdateLabourStatus = async (row, nextValue) => {
    try {
      const labourId = row?.id || row?.labour_id || row?.labourId || row?.labourID || null;
      if (!labourId) {
        throw new Error("Missing labour id");
      }

      const nextIsActive = typeof nextValue === "boolean"
        ? nextValue
        : nextValue === "true" || nextValue === true || nextValue === "ACTIVE" || nextValue === "Active";

      const url = `${API_ENDPOINTS.labour.status}?id=${encodeURIComponent(labourId)}&isActive=${encodeURIComponent(nextIsActive)}`;

      const response = await fetchWithAuth(url, {
        method: "PUT"
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || "Failed to update labour status");
      }

      setData(prev => ({
        ...prev,
        labour: Array.isArray(prev.labour)
          ? prev.labour.map(item => {
              const itemId = item?.id || item?.labour_id || item?.labourId || item?.labourID;
              if (!itemId || itemId !== labourId) {
                return item;
              }
              return {
                ...item,
                active: nextIsActive,
                isActive: nextIsActive,
                status: nextIsActive ? "Active" : "Inactive",
              };
            })
          : prev.labour,
      }));

      await fetchTabData("labour");
      showToast(`Labour ${nextIsActive ? "activated" : "deactivated"}`);
    } catch (err) {
      console.error("Labour status update error:", err);
      showToast(err.message || "Failed to update labour status", "error");
    }
  };

  const handleUpdateEmployeeSite = async (row, newSiteId) => {
    try {
      const empCode = row?.empCode || row?.empcode || row?.employee?.empCode || row?.employeeId?.empCode || "";
      if (!empCode) {
        throw new Error("Missing employee code");
      }

      const currentSiteId = newSiteId || row?.site?.id || row?.siteId || row?.site_id || row?.site?.siteId || null;
      const currentIsActive = row?.isActive ?? row?.active ?? row?.status;
      const payload = {
        empCode,
        isActive: typeof currentIsActive === "boolean"
          ? currentIsActive
          : currentIsActive === "Active" || currentIsActive === "ACTIVE" || currentIsActive === true || currentIsActive === "true",
        siteId: currentSiteId ? Number(currentSiteId) : null,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.employee.status, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || "Failed to update employee site");
      }

      const selectedSiteOption = employeeSiteOptions.find(option => String(option.id) === String(currentSiteId));
      const siteName = selectedSiteOption?.name || row?.site?.siteName || row?.siteName || row?.site?.name || "";

      setData(prev => ({
        ...prev,
        employees: Array.isArray(prev.employees)
          ? prev.employees.map(item => {
              const itemEmpCode = item?.empCode || item?.empcode || item?.employee?.empCode || item?.employeeId?.empCode || "";
              if (!itemEmpCode || itemEmpCode !== empCode) {
                return item;
              }
              return {
                ...item,
                siteId: currentSiteId ? Number(currentSiteId) : null,
                site: item.site ? { ...item.site, id: currentSiteId ? Number(currentSiteId) : null, siteId: currentSiteId ? Number(currentSiteId) : null, siteName, name: siteName } : item.site,
                siteName,
              };
            })
          : prev.employees,
      }));

      await fetchEmployeeSiteOptions();
      await fetchTabData("employees");
      showToast(`Employee site updated`);
    } catch (err) {
      console.error("Employee site update error:", err);
      showToast(err.message || "Failed to update employee site", "error");
    }
  };

  const handleViewExpenseAttachment = async (row) => {
    const expenseId = row?.id ?? row?.expenseId ?? row?.expense_id ?? row?.expense?.id ?? null;
    if (!expenseId) {
      showToast("No expense id available for this record", "error");
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.expense.document}?expenseId=${encodeURIComponent(expenseId)}`, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Attachment request failed (${response.status})`);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `expense_${expenseId}`;
      const objectUrl = URL.createObjectURL(blob);

      if (invoicePreview.url) {
        URL.revokeObjectURL(invoicePreview.url);
      }

      setInvoicePreview({
        open: true,
        url: objectUrl,
        filename,
        contentType: response.headers.get("content-type") || "application/octet-stream"
      });
    } catch (err) {
      console.error("Expense attachment view error:", err);
      showToast("Failed to load expense attachment", "error");
    }
  };

  const getSalaryId = row => row?.salaryId ?? row?.id ?? null;

  const handlePreviewSalaryDocument = async (row) => {
    const salaryId = getSalaryId(row);
    if (!salaryId) {
      showToast("No salary id available for this document", "error");
      return;
    }
    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.salary.document}?salaryId=${encodeURIComponent(salaryId)}`, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Salary document request failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `salary_${salaryId}`;
      const url = URL.createObjectURL(blob);
      if (invoicePreview.url) {
        URL.revokeObjectURL(invoicePreview.url);
      }
      setInvoicePreview({
        open: true,
        url,
        filename,
        contentType: response.headers.get("content-type") || "application/octet-stream"
      });
    } catch (error) {
      console.error("Salary document preview error:", error);
      showToast("Failed to load salary document", "error");
    }
  };

  const handleDownloadSalaryDocument = async (row) => {
    const salaryId = getSalaryId(row);
    if (!salaryId) {
      showToast("No salary id available for this document", "error");
      return;
    }
    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.salary.document}?salaryId=${encodeURIComponent(salaryId)}`, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Salary document download failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `salary_${salaryId}`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      showToast("Salary document downloaded successfully", "success");
    } catch (error) {
      console.error("Salary document download error:", error);
      showToast("Failed to download salary document", "error");
    }
  };

  const getEmployeeDocumentId = (row) => row?.empId ?? row?.employee_id ?? row?.id ?? row?.employee?.id ?? row?.employeeId?.id ?? row?.employeeId;

  const handleDownloadEmployeeAadhar = (row) => {
    setEmployeeDocumentAction("download");
    setEmployeeDocumentRow(row);
  };

  const handleViewEmployeeDocument = async (documentType) => {
    const employeeId = getEmployeeDocumentId(employeeDocumentRow);
    if (!employeeId || typeof employeeId === "object") {
      showToast("No employee id available for this document", "error");
      return;
    }

    try {
      const endpoint = `${API_ENDPOINTS.employee[documentType]}?empId=${encodeURIComponent(employeeId)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Employee document request failed (${response.status})`);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `${documentType}_${employeeId}`;
      const url = URL.createObjectURL(blob);
      setEmployeeDocumentRow(null);
      if (employeeDocumentAction === "download") {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        showToast(`Employee ${documentType} document downloaded successfully`);
        return;
      }
      setInvoicePreview({
        open: true,
        url,
        filename,
        contentType: response.headers.get("content-type") || "application/octet-stream"
      });
    } catch (err) {
      console.error(`Employee ${documentType} document view error:`, err);
      showToast(`Failed to load employee ${documentType} document`, "error");
    }
  };

  const handleDownloadExpenseAttachment = async (row) => {
    const expenseId = row?.id ?? row?.expenseId ?? row?.expense_id ?? row?.expense?.id ?? null;
    if (!expenseId) {
      showToast("No expense id available for this record", "error");
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.expense.document}?expenseId=${encodeURIComponent(expenseId)}`, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Attachment download failed (${response.status})`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get("content-type") || "application/octet-stream";
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `expense_${expenseId}`;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      showToast("Attachment downloaded successfully");
    } catch (err) {
      console.error("Expense attachment download error:", err);
      showToast("Failed to download expense attachment", "error");
    }
  };

  const handleViewEmployeeAadhar = (row) => {
    setEmployeeDocumentAction("view");
    setEmployeeDocumentRow(row);
  };

  const handleViewContractorDocuments = (row, action = "view") => {
    setContractorDocumentAction(action);
    setContractorDocumentRow(row);
  };

  const handleDownloadContractorDocuments = (row) => {
    handleViewContractorDocuments(row, "download");
  };

  const handleViewContractorDocument = async (documentType) => {
    const contractorId = contractorDocumentRow?.id
      ?? contractorDocumentRow?.contractorId
      ?? contractorDocumentRow?.contractorID
      ?? contractorDocumentRow?.contractor_id;
    if (contractorId === undefined || contractorId === null || contractorId === "") {
      showToast("No contractor id available for this document", "error");
      return;
    }

    try {
      const endpoint = `${API_ENDPOINTS.contractor[documentType]}?contractorId=${encodeURIComponent(contractorId)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Contractor document request failed (${response.status})`);
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `${documentType}_${contractorId}`;
      const url = URL.createObjectURL(blob);
      setContractorDocumentRow(null);
      if (contractorDocumentAction === "download") {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        showToast(`Contractor ${documentType} document downloaded successfully`);
        return;
      }
      if (invoicePreview.url) URL.revokeObjectURL(invoicePreview.url);
      setInvoicePreview({
        open: true,
        url,
        filename,
        contentType: response.headers.get("content-type") || "application/octet-stream"
      });
    } catch (error) {
      console.error(`Contractor ${documentType} document view error:`, error);
      showToast(`Failed to load contractor ${documentType} document`, "error");
    }
  };

  const handleDelete = async (section, id) => {
    if ((section === "employees" || section === "fuelpayment") && !isAdmin) {
      return;
    }

    const sectionLabel = section === "employees" ? "employee" : section === "fuelpayment" ? "fuel payment" : section === "purchases" ? "purchase" : section === "attendance" ? "attendance" : section === "labour" ? "labour" : section === "employeefnf" ? "Employee FNF" : section === "salary" ? "salary" : "record";
    const confirmed = window.confirm(`Are you sure you want to delete this ${sectionLabel}?`);
    if (!confirmed) {
      return;
    }

    try {
      const rawId = id ?? null;
      const numericId = Number(rawId);
      if (rawId === null || rawId === undefined || rawId === "" || (section !== "employees" && !Number.isFinite(numericId))) {
        showToast(`Invalid record id for ${sectionLabel} delete`, "error");
        return;
      }

      const endpoint = section === "concreteproduction"
        ? API_ENDPOINTS.concreteproduction.deleteById(numericId)
        : section === "employees"
          ? `${API_ENDPOINTS.employee.delete}?empCode=${encodeURIComponent(rawId)}`
          : constructEndpoint(section, numericId);
      const response = await fetchWithAuth(endpoint, { method: "DELETE" });
      
      if (response.ok) {
        setData(d => ({
          ...d,
          [section]: (d[section] || []).filter(r => {
            if (section === "employees") {
              const employeeIds = [r.id, r.empId, r.empCode, r.empcode, r.emp_code, r.employeeCode]
                .filter(recordId => recordId !== null && recordId !== undefined && recordId !== "");
              return !employeeIds.some(recordId => String(recordId) === String(id));
            }
            const recordId = r.id ?? r.salaryId ?? r.fnfId ?? r.paymentId ?? r.fuelPaymentId ?? r.fuel_payment_id ?? r.labourPaymentId ?? r.labour_payment_id ?? r.attendanceId ?? r.purchaseId ?? r.purchaseNo ?? r.purchaseNumber ?? r.sellId ?? r.sell_id ?? r.saleId ?? r.sale_id ?? r.billId ?? r.empId ?? r.employeeId ?? r.labourId ?? r.labour_id ?? null;
            const normalizedRecordId = section === "concreteproduction"
              ? getConcreteProductionId(r)
              : recordId;
            return String(normalizedRecordId) !== String(id);
          })
        }));
        setPagination(prev => ({ ...prev, [section]: 0 }));
        showToast("Record deleted successfully!");
        const refreshTabs = getRefreshTabsAfterDelete(section);
        await Promise.allSettled(refreshTabs.map(tab => fetchTabData(tab)));
      } else {
        showToast("Failed to delete record", "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Error deleting record", "error");
    }
  };

  const fetchPartnerList = useCallback(async () => {
    const siteId = selectedSite?.id;
    if (!siteId) {
      setPartnershipPartners([]);
      return;
    }

    try {
      const endpoint = `${API_ENDPOINTS.partner.site}?siteid=${encodeURIComponent(siteId)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to load partner list");
      }
      const data = await response.json();
      const partners = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : [];
      setPartnershipPartners(partners);
    } catch (err) {
      console.error("Error fetching partner list", err);
      setPartnershipPartners([]);
    }
  }, [selectedSite?.id]);

  const handlePartnerSave = async (partner) => {
    const partnerId = editingPartner?.id ?? editingPartner?.partnerId ?? partner?.id ?? null;
    const site = selectedSite
      ? {
          id: selectedSite.id ?? selectedSite.siteId ?? selectedSite.site_id ?? null,
          name: selectedSite.name || selectedSite.siteName || selectedSite.site_name || null,
          siteName: selectedSite.siteName || selectedSite.site_name || selectedSite.name || null,
          site_id: selectedSite.site_id ?? selectedSite.id ?? selectedSite.siteId ?? null,
        }
      : partner?.site || null;
    try {
      const response = await fetchWithAuth(
        partnerId ? API_ENDPOINTS.partner.updateById(partnerId) : API_ENDPOINTS.partner.create,
        {
          method: partnerId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...partner, site }),
        }
      );
      if (!response.ok) throw new Error("Failed to save partner");
      setEditingPartner(null);
      setModal(null);
      await fetchPartnerList();
      showToast(partnerId ? "Partner updated successfully" : "Partner created successfully", "success");
    } catch (err) {
      console.error("Partner save error", err);
      showToast(err.message || "Failed to save partner", "error");
    }
  };

  const handlePartnerDelete = async (partner) => {
    const partnerId = partner?.id ?? partner?.partnerId ?? null;
    if (partnerId === null || partnerId === undefined) {
      showToast("No partner id available for this record", "error");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${partner?.name || partner?.partnerName || "this partner"}?`)) return;
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.partner.deleteById(partnerId), { method: "DELETE" });
      if (!response.ok) throw new Error("Failed to delete partner");
      if (String(selectedPartnerId) === String(partnerId)) {
        setSelectedPartnerId(null);
        setSelectedPartnerName("");
      }
      await fetchPartnerList();
      showToast("Partner deleted successfully", "success");
    } catch (err) {
      console.error("Partner delete error", err);
      showToast("Failed to delete partner", "error");
    }
  };

  const fetchPartnershipData = useCallback(async () => {
    try {
      const siteId = selectedSite?.id;
      let endpoint = API_ENDPOINTS.partnership.list;
      if (siteId) {
        endpoint = `${API_ENDPOINTS.partnership.site}?siteid=${encodeURIComponent(siteId)}`;
      }
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to load partnership data");
      }
      const data = await response.json();
      setPartnershipBills(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching partnership data", err);
      showToast("Failed to load partnership records", "error");
    }
  }, [selectedSite?.id]);

  const fetchPartnershipBillsByPartner = useCallback(async (partnerId) => {
    if (!partnerId) {
      await fetchPartnershipData();
      return;
    }

    try {
      const response = await fetchWithAuth(API_ENDPOINTS.partnership.byPartner(partnerId), { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to load partner bills");
      }
      const data = await response.json();
      setPartnershipBills(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching partner bills", err);
      showToast("Failed to load partner bills", "error");
    }
  }, [fetchPartnershipData]);

  const fetchPartnershipTotalAmount = useCallback(async () => {
    const siteId = selectedSite?.id;
    if (!isAdmin || !siteId || !partnershipFromDate || !partnershipToDate) {
      setPartnershipTotalAmount(null);
      return;
    }

    setPartnershipTotalLoading(true);
    try {
      const params = new URLSearchParams({
        siteId: String(siteId),
        fromDate: partnershipFromDate,
        toDate: partnershipToDate,
      });
      const response = await fetchWithAuth(`${API_ENDPOINTS.partnership.sum}?${params.toString()}`, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to load partnership total");
      }
      const total = Number(await response.json());
      if (!Number.isFinite(total)) {
        throw new Error("Invalid partnership total response");
      }
      setPartnershipTotalAmount(total);
    } catch (err) {
      console.error("Partnership total amount error", err);
      setPartnershipTotalAmount(null);
      showToast("Failed to load partnership bill total", "error");
    } finally {
      setPartnershipTotalLoading(false);
    }
  }, [isAdmin, partnershipFromDate, partnershipToDate, selectedSite?.id]);

  useEffect(() => {
    if (activeTab === "partnership") {
      fetchPartnerList();
      fetchPartnershipData();
    }
  }, [activeTab, fetchPartnerList, fetchPartnershipData]);

  const handlePartnershipUpload = async (payload = partnershipForm) => {
    const partnerId = payload?.partnerId ?? selectedPartnerId ?? null;
    const siteId = selectedSite?.id ?? payload?.siteId ?? null;

    if (!partnerId || !payload?.billNo?.trim() || !payload?.billDate || !payload?.amount) {
      showToast("Please select a partner, enter bill number, bill date and amount", "error");
      return;
    }

    try {
      const partnershipId = editingPartnership?.id ?? editingPartnership?.partnershipId ?? editingPartnership?.partnership_id ?? null;
      const selectedPartner = partnershipPartners.find((partner) => String(partner?.id ?? partner?.partnerId) === String(partnerId)) || null;
      const selectedSiteObject = selectedSite
        ? {
            id: selectedSite.id ?? selectedSite.siteId ?? selectedSite.site_id ?? null,
            name: selectedSite.name || selectedSite.siteName || selectedSite.site_name || null,
            siteName: selectedSite.siteName || selectedSite.site_name || selectedSite.name || null,
            site_id: selectedSite.site_id ?? selectedSite.id ?? selectedSite.siteId ?? null,
          }
        : null;

      const partnershipPayload = {
        billNo: payload.billNo.trim(),
        billDate: payload.billDate,
        amount: Number(payload.amount),
        description: payload.description || "",
        partner: selectedPartner
          ? {
              id: selectedPartner.id ?? selectedPartner.partnerId ?? null,
              name: selectedPartner.name || selectedPartner.partnerName || selectedPartner.contactPerson || null,
              partnerName: selectedPartner.partnerName || selectedPartner.name || selectedPartner.contactPerson || null,
              contactPerson: selectedPartner.contactPerson || null,
              phone: selectedPartner.phone || null,
              email: selectedPartner.email || null,
              gstNo: selectedPartner.gstNo || null,
              village: selectedPartner.village || null,
              post: selectedPartner.post || null,
              district: selectedPartner.district || null,
              state: selectedPartner.state || null,
              pin: selectedPartner.pin || null,
            }
          : partnerId
            ? { id: Number(partnerId) }
            : null,
        site: selectedSiteObject
          ? {
              id: selectedSiteObject.id,
              name: selectedSiteObject.name,
              siteName: selectedSiteObject.siteName,
              site_id: selectedSiteObject.site_id,
            }
          : siteId
            ? { id: Number(siteId) }
            : null,
      };
      const requestBody = partnershipId
        ? JSON.stringify(partnershipPayload)
        : (() => {
            const formData = new FormData();
            formData.append("partnership", new Blob([JSON.stringify(partnershipPayload)], { type: "application/json" }));
            if (payload.file) formData.append("file", payload.file);
            return formData;
          })();

      const response = await fetchWithAuth(
        partnershipId ? API_ENDPOINTS.partnership.updateById(partnershipId) : API_ENDPOINTS.partnership.create,
        {
          method: partnershipId ? "PUT" : "POST",
          ...(partnershipId ? { headers: { "Content-Type": "application/json" } } : {}),
          body: requestBody,
        }
      );

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || (partnershipId ? "Failed to update partnership" : "Failed to create partnership"));
      }

      setPartnershipForm({ partnerId: selectedPartnerId || "", billNo: "", billDate: "", amount: "", description: "", file: null });
      setEditingPartnership(null);
      setModal(null);
      await fetchPartnershipData();
      await fetchPartnershipTotalAmount();
      showToast(editingPartnership?.id ? "Partnership updated successfully" : "Partnership saved successfully");
    } catch (err) {
      console.error("Partnership upload error", err);
      showToast(err.message || "Failed to save partnership", "error");
    }
  };

  const getPartnershipRowId = (row) => row?.id ?? row?.partnershipId ?? row?.partnership_id ?? null;

  const handlePartnershipDelete = async (row) => {
    const recordId = getPartnershipRowId(row);
    if (recordId === null || recordId === undefined) {
      showToast("No partnership id available for this record", "error");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this partnership bill?")) return;

    try {
      const response = await fetchWithAuth(API_ENDPOINTS.partnership.deleteById(recordId), { method: "DELETE" });
      if (!response.ok) throw new Error("Failed to delete partnership bill");
      await fetchPartnershipData();
      await fetchPartnershipTotalAmount();
      showToast("Partnership bill deleted successfully");
    } catch (err) {
      console.error("Partnership delete error", err);
      showToast("Failed to delete partnership bill", "error");
    }
  };

  const handlePartnershipSearch = useCallback(async (value = partnershipSearch) => {
    const trimmedValue = (value || "").trim();

    if (!trimmedValue) {
      setSelectedPartnerId(null);
      setSelectedPartnerName("");
      await fetchPartnerList();
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_ENDPOINTS.partner.search}?name=${encodeURIComponent(trimmedValue)}`, { method: "GET" });
      if (!response.ok) {
        throw new Error("Search failed");
      }
      const data = await response.json();
      const partners = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data?.data)
            ? data.data
            : [];
      setSelectedPartnerId(null);
      setSelectedPartnerName("");
      setPartnershipPartners(partners);
    } catch (err) {
      console.error("Partner search error", err);
      showToast("Failed to search partners", "error");
    }
  }, [fetchPartnerList, partnershipSearch]);

  const handlePartnershipDateFilter = useCallback(async () => {
    const siteId = selectedSite?.id;
    if (!siteId || !partnershipFromDate || !partnershipToDate) {
      setPartnershipTotalAmount(null);
      await fetchPartnershipData();
      return;
    }

    try {
      const params = new URLSearchParams({
        siteId: String(siteId),
        fromDate: partnershipFromDate,
        toDate: partnershipToDate,
      });
      const response = await fetchWithAuth(`${API_ENDPOINTS.partnership.dateRange}?${params.toString()}`, { method: "GET" });
      if (!response.ok) {
        throw new Error("Date filter failed");
      }
      const data = await response.json();
      setPartnershipBills(Array.isArray(data) ? data : []);
      await fetchPartnershipTotalAmount();
    } catch (err) {
      console.error("Partnership date filter error", err);
      showToast("Failed to filter partnership records", "error");
    }
  }, [fetchPartnershipData, fetchPartnershipTotalAmount, partnershipFromDate, partnershipToDate, selectedSite?.id]);

  const getPartnershipRowBillNo = (row) => {
    return (
      row?.billNo ||
      row?.billNumber ||
      row?.bill_number ||
      row?.bill_no ||
      row?.billno ||
      row?.document?.billNo ||
      row?.document?.billNumber ||
      row?.document?.bill_number ||
      row?.document?.bill_no ||
      row?.document?.billno ||
      row?.document?.invoiceNumber ||
      row?.document?.invoice_number ||
      ""
    );
  };

  const getSelectedSiteId = () => {
    const site = selectedSite || {};
    return site.id || site.siteId || site.site_id || site.locationId || site.location_id || null;
  };

  const fetchPartnershipBillBlob = async (siteId, billNo, recordId) => {
    const params = new URLSearchParams();
    if (billNo) {
      params.append("billNo", billNo);
    } else if (recordId !== undefined && recordId !== null) {
      params.append("id", recordId);
    }
    if (siteId !== undefined && siteId !== null) {
      params.append("siteId", siteId);
    }

    const downloadEndpoint = `${API_ENDPOINTS.partnership.download}?${params.toString()}`;
    const response = await fetchWithAuth(downloadEndpoint, { method: "GET" });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Partnership download failed (${response.status}): ${text}`);
    }
    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || "application/octet-stream";
    const disposition = response.headers.get("content-disposition") || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `partnership_${billNo}`;
    return { blob, filename, contentType };
  };

  const handlePartnershipView = async (row) => {
    try {
      const billNo = getPartnershipRowBillNo(row);
      const siteId = getSelectedSiteId();
      const recordId = row?.id;
      if (!siteId || (!billNo && !recordId)) {
        console.error("Partnership view missing data", { siteId, billNo, recordId, row });
        showToast("Unable to preview bill: missing site or bill identifier", "error");
        return;
      }
      setInvoiceLoadingId(row.id || billNo || `partnership-preview`);
      const { blob, filename, contentType } = await fetchPartnershipBillBlob(siteId, billNo, recordId);
      const url = URL.createObjectURL(blob);
      setInvoicePreview({ open: true, url, filename, contentType });
    } catch (err) {
      console.error("Partnership preview error:", err);
      showToast("Failed to preview partnership bill", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handlePartnershipDownload = async (row) => {
    try {
      const billNo = getPartnershipRowBillNo(row);
      const siteId = getSelectedSiteId();
      const recordId = row?.id;
      if (!siteId || (!billNo && !recordId)) {
        console.error("Partnership download missing data", { siteId, billNo, recordId, row });
        showToast("Unable to download bill: missing site or bill identifier", "error");
        return;
      }
      console.log("Partnership download starting", { siteId, billNo, recordId, row });
      const { blob, filename } = await fetchPartnershipBillBlob(siteId, billNo, recordId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast("Partnership bill downloaded successfully!");
    } catch (err) {
      console.error("Partnership download error:", err);
      showToast("Failed to download partnership bill", "error");
    }
  };

  const handleUpload = async (section, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("excel", file);
      
      const sectionKey = getSingularForm(section);
      const endpoint = API_ENDPOINTS[sectionKey].upload;
      console.log("Uploading to:", endpoint);
      
      const response = await fetchWithAuth(endpoint, {
        method: "POST",
        body: formData,
      });
      
      console.log("Upload response status:", response.status);
      
      // Get response text first
      const contentType = response.headers.get("content-type");
      let result;
      
      try {
        result = contentType && contentType.includes("application/json") 
          ? await response.json() 
          : { message: await response.text() };
      } catch (parseErr) {
        result = { message: await response.text() };
      }
      
      console.log("Upload response:", result);
      console.log("Response structure - keys:", typeof result === 'object' ? Object.keys(result) : 'not an object');
      
      if (response.ok) {
        // Handle different response formats from backend
        let newRows = [];
        let successMessage = null;
        
        // Try different property names for data
        if (Array.isArray(result)) {
          newRows = result;
        } else if (result && typeof result === 'object') {
          // Check for common property names
          const dataProps = ['data', 'rows', 'records', 'items', 'result', 'results', 'attendance', 'employees', 'purchases', 'sells', 'expenses'];
          for (let prop of dataProps) {
            if (Array.isArray(result[prop])) {
              newRows = result[prop];
              console.log(`Found data in result.${prop}`);
              break;
            }
          }
          
          // Get success message from backend
          if (result.message || result.msg) {
            successMessage = result.message || result.msg;
          }
        }
        
        console.log("Parsed rows count:", newRows.length);
        
        // Update state with new data
        if (newRows.length > 0) {
          setData(d => ({ ...d, [section]: [...d[section], ...newRows] }));
        }
        
        // Show backend message if available, otherwise show row count
        const toastMsg = successMessage || `${newRows.length} rows imported from ${file.name}`;
        showToast(toastMsg);
        
        // Refresh only the current tab data after upload
        if (section !== "employees") {
          setTimeout(() => fetchTabData(section), 500);
        }
      } else {
        console.error("Upload failed:", result);
        const errMsg = result.message || result.error || result || "Failed to upload file";
        showToast(String(errMsg), "error");
      }
    } catch (err) {
      console.error("Upload error:", err);
      showToast("Error uploading file: " + err.message, "error");
    } finally {
      e.target.value = "";
    }
  };

  const normalizeAttendanceStatus = (status) => {
    const normalized = String(status || "PRESENT").trim().toUpperCase();
    const map = {
      PRESENT: "PRESENT",
      ABSENT: "ABSENT",
      "HALF DAY": "HALF_DAY",
      HOLIDAY: "HOLIDAY",
      HOLD: "HOLD",
      LEAVE: "LEAVE",
    };
    return map[normalized] || normalized;
  };

  const normalizeAttendanceEntries = (attendanceEntries) => {
    if (!attendanceEntries) return [];
    if (Array.isArray(attendanceEntries)) return attendanceEntries;
    if (typeof attendanceEntries === "object") {
      const numericKeys = Object.keys(attendanceEntries)
        .filter(key => String(Number(key)) === key)
        .sort((a, b) => Number(a) - Number(b));
      if (numericKeys.length > 0) {
        return numericKeys.map(key => attendanceEntries[key]);
      }
    }
    return [];
  };

  const handleSave = async (section, record) => {
    const documentRequiredSections = ["purchases", "sells", "workorders", "bills", "managementExpense", "fueltransaction", "fuelpump"];
    const hasDocument = record?.invoiceFile || record?.attachmentFile || record?.documentFile;
    if (documentRequiredSections.includes(section) && !hasDocument) {
      if (section === "bills" || section === "fueltransaction" || section === "fuelpump") {
        const documentMessage = section === "bills"
          ? "Please upload a bill or receipt before saving the Bill."
          : `Please upload a document before saving ${section === "fuelpump" ? "Pump Fuel" : "Plant Fuel"}.`;
        window.alert(documentMessage);
        return;
      }
      const sectionLabels = {
        purchases: "Purchase",
        sells: "Sell",
        workorders: "Work Order",
        bills: "Bill",
        managementExpense: "Management Expense",
      };
      const documentLabel = section === "managementExpense" ? "document" : "bill or receipt";
      showToast(`Please upload a ${documentLabel} before saving the ${sectionLabels[section]}`, "error");
      return;
    }

    try {
      const sectionKey = getSingularForm(section);
      const endpoint = API_ENDPOINTS[sectionKey].create;
      
      // Transform data for employees section
      let transformedRecord = { ...record, authUser: authUser || record.authUser || null };
      if (section === "employees") {
        const mappedEmpType = record.empType === "Regular" ? "R" : record.empType === "Temporary" ? "T" : record.empType;
        const employeePayload = {
          name: record.name,
          email: record.email,
          phone: record.phone,
          aadhar_Number: record.aadharNumber || record.aadhar_Number || "",
          salary: record.salary || 0,
          site: record.currentSite || selectedSite || null,
          position: record.position,
          empType: mappedEmpType,
          doj: record.doj,
          address: {
            village: record.village,
            post: record.post,
            district: record.district,
            state: record.state,
            pin: record.pin
          }
        };
        const formData = new FormData();
        formData.append("employee", new Blob([JSON.stringify(employeePayload)], { type: "application/json" }));
        if (record.aadharFile) formData.append("aadharFile", record.aadharFile);
        if (record.panFile) formData.append("panFile", record.panFile);
        if (record.bankFile) formData.append("bankFile", record.bankFile);
        transformedRecord = formData;
      } else if (section === "employeeadvance") {
        transformedRecord = {
          employee: record.employeeObject,  // Send complete employee object
          paidByEmployee: record.paidByEmployee || null,
          advanceDate: record.advanceDate,
          amount: record.amount,
          paymentType: record.paymentType,
          site: record.site
        };
      } else if (section === "attendance") {
        const normalizedEntries = normalizeAttendanceEntries(record?.attendanceEntries);
        if (normalizedEntries.length > 0) {
          transformedRecord = normalizedEntries.map(entry => ({
            employeeId: entry.employeeObject || entry.employee || entry.employeeId || null,
            attendanceDate: entry.attendanceDate,
            status: normalizeAttendanceStatus(entry.status || "Present"),
            site: selectedSite || entry.site || null,
            siteId: selectedSite?.id || entry.site?.id || null,
            authUser: authUser || entry.authUser || null,
          }));
        } else {
          transformedRecord = {
            employeeId: record.employeeObject || record.employee || record.employeeId || null,
            attendanceDate: record.attendanceDate,
            status: normalizeAttendanceStatus(record.status || "Present"),
            site: selectedSite || record.site || null,
            siteId: selectedSite?.id || record.site?.id || null,
            authUser: authUser || record.authUser || null,
          };
        }
      } else if (section === "labourattendance") {
        const attendanceEntries = Array.isArray(record?.attendanceEntries) ? record.attendanceEntries : [record];
        transformedRecord = attendanceEntries
          .filter(entry => entry.labourCode || entry.labourObject || entry.labour)
          .map(entry => ({
            labourCode: entry.labourCode || entry.labour?.labourCode || entry.labour?.labour_code || "",
            labourName: entry.labourName || entry.labour?.labourName || entry.labour?.name || "",
            labour: entry.labourObject || entry.labour || null,
            attendanceDate: entry.attendanceDate,
            status: normalizeAttendanceStatus(entry.status || "Present"),
            remarks: entry.remarks || "",
            site: selectedSite || entry.site || null,
            siteId: selectedSite?.id || entry.site?.id || null,
            authUser: authUser || entry.authUser || null,
          }));
      } else if (section === "bills") {
        const formData = new FormData();
        const amount = Number(record.amount || 0);
        const billPayload = {
          billNo: record.billNo,
          billDate: record.billDate,
          billType: record.billType,
          amount,
          amountReceived: 0,
          remainingAmount: amount,
          description: record.description || "",
          site: selectedSite || null,
          workOrder: record.workOrder || null
        };
        formData.append("bill", new Blob([JSON.stringify(billPayload)], { type: "application/json" }));
        if (record.invoiceFile) {
          formData.append("file", record.invoiceFile);
        }
        transformedRecord = formData;
      } else if (section === "managementExpense") {
        const formData = new FormData();
        const remarks = record.remarks || record.remark || "";
        const managementExpensePayload = {
          expenseDate: record.expenseDate,
          amount: Number(record.amount),
          fromEmployee: record.fromEmployee || null,
          toEmployee: record.toEmployee || null,
          remarks,
          remark: remarks,
          site: record.site || selectedSite || null,
        };
        formData.append("managementExpense", new Blob([JSON.stringify(managementExpensePayload)], { type: "application/json" }));
        if (record.documentFile) formData.append("file", record.documentFile);
        transformedRecord = formData;
      } else if (section === "expenses") {
        const formData = new FormData();
        const expensePayload = {
          expenseType: record.expenseType === "Hardware Expenses" ? "Hardware" : record.expenseType,
          expenseDate: record.expenseDate,
          amount: Number(record.amount),
          remark: record.remark || "",
          spentBy: record.spentBy || record.spent_by || null,
          authUser: authUser || record.authUser || null,
          site: selectedSite || null
        };
        formData.append("expense", new Blob([JSON.stringify(expensePayload)], { type: "application/json" }));
        if (record.attachmentFile) {
          formData.append("file", record.attachmentFile);
        }
        transformedRecord = formData;
      } else if (section === "sells") {
        const formData = new FormData();
        const sellPayload = {
          sellNo: record.sellNo,
          sellDate: record.sellDate,
          billType: record.billType,
          paymentStatus: record.paymentStatus,
          site: record.site || selectedSite || null,
          sellItems: record.sellItems || [],
          authUser: authUser || null
        };
        formData.append("sell", new Blob([JSON.stringify(sellPayload)], { type: "application/json" }));
        if (record.invoiceFile) {
          formData.append("invoice", record.invoiceFile);
        }
        transformedRecord = formData;
      } else if (section === "fueltransaction") {
        const formData = new FormData();
        const fuelTransactionPlant = {
          fuelType: record.fuelType,
          transactionType: record.transactionType,
          quantity: Number(record.quantity || 0),
          amount: Number(record.amount || 0),
          date: record.date || null,
          vehicleName: record.vehicleName || "",
          operatorName: record.operatorName || "",
          remarks: record.remarks || "",
          site: record.site || selectedSite || null,
          authUser: authUser || null,
        };
        formData.append("fuelTransactionPlant", new Blob([JSON.stringify(fuelTransactionPlant)], { type: "application/json" }));
        if (record.documentFile) {
          formData.append("file", record.documentFile);
        }
        transformedRecord = formData;
      } else if (section === "fuelpump") {
        const formData = new FormData();
        const fuelTransactionPump = {
          fuelType: record.fuelType,
          vehicleNumber: record.vehicleNumber,
          amount: Number(record.amount || 0),
          quantity: Number(record.quantity || 0),
          receivedBy: record.receivedBy || "",
          approvedBy: record.approvedBy || "",
          fuelDate: record.fuelDate || null,
          site: record.site || selectedSite || null,
          authUser: authUser || null,
        };
        formData.append("fuelTransactionPump", new Blob([JSON.stringify(fuelTransactionPump)], { type: "application/json" }));
        if (record.documentFile) {
          formData.append("file", record.documentFile);
        }
        transformedRecord = formData;
      } else if (section === "purchases") {
        const formData = new FormData();
        const purchasePayload = {
          purchaseNo: record.purchaseNo,
          purchaseDate: record.purchaseDate,
          partyName: record.partyName,
          receiverName: record.receiverName,
          paymentStatus: record.paymentStatus,
          taxAmount: Number(record.taxAmount) || 0,
          description: record.description || "",
          site: record.site || selectedSite || null,
          siteId: record.site?.id || selectedSite?.id || null,
          purchaseItems: record.purchaseItems || [],
          authUser: authUser || null
        };
        formData.append("purchase", new Blob([JSON.stringify(purchasePayload)], { type: "application/json" }));
        if (record.invoiceFile) {
          formData.append("invoice", record.invoiceFile);
        }
        transformedRecord = formData;
      } else if (section === "workorders") {
        const formData = new FormData();
        const workorderPayload = {
          woNumber: record.woNumber,
          woType: record.woType,
          issueDate: record.issueDate,
          projectName: record.projectName || "",
          remarks: record.remarks || "",
          grandTotal: record.grandTotal ? Number(record.grandTotal) : 0,
          site: selectedSite || null,
          authUser: authUser || null
        };
        formData.append("workOrder", new Blob([JSON.stringify(workorderPayload)], { type: "application/json" }));
        if (record.attachmentFile) {
          formData.append("document", record.attachmentFile);
        }
        transformedRecord = formData;
      } else if (section === "contractor") {
        const contractorPayload = {
          ...(record.contractorCode ? { contractorCode: record.contractorCode } : {}),
          contractorName: record.contractorName,
          mobileNumber: record.mobile || record.mobileNumber || "",
          email: record.email || "",
          gstNumber: record.gstNumber || "",
          panNumber: record.panNumber || "",
          joiningDate: record.joiningDate || null,
          contractorType: record.contractorType || record.contractor_type || "DAILY_BASIS",
          active: record.active !== undefined ? record.active : true,
          description: record.description || "",
          aadhar_Number: record.aadhar_Number || record.aadharNumber || "",
          address: {
            village: record.village || "",
            post: record.post || "",
            district: record.district || "",
            state: record.state || "",
            pin: record.pin || ""
          },
          authUser: record.authUser || null,
          site: record.currentSite || selectedSite || null
        };
        const formData = new FormData();
        formData.append("contractor", new Blob([JSON.stringify(contractorPayload)], { type: "application/json" }));
        if (record.aadharFile) formData.append("aadharFile", record.aadharFile);
        if (record.panFile) formData.append("panFile", record.panFile);
        if (record.bankFile) formData.append("bankFile", record.bankFile);
        transformedRecord = formData;
      } else if (section === "contractorwork") {
        transformedRecord = {
          contractor: record.contractorObject || record.contractor || null,
          workDate: record.workDate ? record.workDate : null,
          numberOfLabours: record.numberOfLabours ? Number(record.numberOfLabours) : null,
          labourRate: record.labourRate ? Number(record.labourRate) : null,
          numberOfMasons: record.numberOfMasons ? Number(record.numberOfMasons) : null,
          masonAmount: record.masonAmount ? Number(record.masonAmount) : null,
          otherCharges: record.otherCharges ? Number(record.otherCharges) : null,
          description: record.description || "",
          authUser: record.authUser || null,
          siteId: record.siteId || selectedSite?.id || ""
        };
      } else if (section === "contractorpayment") {
        const formData = new FormData();
        const contractorPaymentPayload = {
          paymentDate: record.paymentDate,
          contractor: record.contractorObject || record.contractor || null,
          contractorName: record.contractorName || record.contractor?.contractorName || "",
          paidBy: record.paidByEmployee || record.paidBy || null,
          paymentAmount: record.amount ? Number(record.amount) : null,
          paymentMethod: normalizeContractorPaymentMethod(record.paymentMethod),
          description: record.description || "",
          siteId: record.siteId || selectedSite?.id || "",
          authUser: authUser || null,
        };
        formData.append("contractorPayment", new Blob([JSON.stringify(contractorPaymentPayload)], { type: "application/json" }));
        if (record.documentFile) formData.append("document", record.documentFile);
        transformedRecord = formData;
      } else if (section === "labour") {
        transformedRecord = {
          labourName: record.labourName || "",
          joiningDate: record.labourJoiningDate || "",
          labourType: record.labourType || "",
          category: record.category || "Plant",
          dailyWage: record.dailyWage ? Number(record.dailyWage) : 0,
          remarks: record.remark || "",
          site: selectedSite || record.site || null,
          authUser: authUser || record.authUser || null
        };
      }

      if (
        section !== "bills" &&
        section !== "managementExpense" &&
        section !== "expenses" &&
        section !== "sells" &&
        section !== "purchases" &&
        section !== "fueltransaction" &&
        section !== "fuelpump" &&
        !Array.isArray(transformedRecord) &&
        !(transformedRecord instanceof FormData)
      ) {
        transformedRecord = {
          ...transformedRecord,
          authUser: authUser || transformedRecord.authUser || null
        };
      }
      
      const requestOptions = {
        method: "POST",
        body: transformedRecord,
      };

      if (section !== "employees" && section !== "contractor" && section !== "bills" && section !== "managementExpense" && section !== "expenses" && section !== "sells" && section !== "purchases" && section !== "fueltransaction" && section !== "fuelpump" && section !== "workorders" && section !== "contractorpayment") {
        requestOptions.headers = { "Content-Type": "application/json" };
        requestOptions.body = JSON.stringify(transformedRecord);
      }
      if (section === "workorders" && transformedRecord instanceof FormData) {
        try {
          for (const entry of transformedRecord.entries()) {
            const [key, value] = entry;
            console.debug("WorkOrder FormData entry:", key, value, value && value.name, Object.prototype.toString.call(value));
          }
        } catch (e) {
          console.debug("Error iterating FormData entries", e);
        }
      }

      const response = await fetchWithAuth(endpoint, requestOptions);
      
      if (response.ok) {
        const newRecord = await response.json();

        if (section === "purchases" || section === "labourattendance") {
          await fetchTabData(section);
        } else {
          // Check if data[section] is an array or a count (number)
          setData(prevData => {
            if (Array.isArray(prevData[section])) {
              return { ...prevData, [section]: [...prevData[section], newRecord] };
            }

            setTimeout(() => fetchTabData(section), 500);
            return prevData;
          });
        }

        if (section === "attendance") {
          try {
            const siteAttendanceUrl = selectedSite?.id
              ? `${API_ENDPOINTS.attendance.list}?siteid=${encodeURIComponent(selectedSite.id)}`
              : API_ENDPOINTS.attendance.list;
            const siteAttendanceResponse = await fetchWithAuth(siteAttendanceUrl, { method: "GET" });
            if (siteAttendanceResponse.ok) {
              const siteAttendanceData = await siteAttendanceResponse.json();
              setData(prevData => ({ ...prevData, attendance: Array.isArray(siteAttendanceData) ? siteAttendanceData : [] }));
            }
          } catch (err) {
            console.error("Error refreshing attendance from site API:", err);
          }
        }
        
        setModal(null);
        showToast("Record saved successfully!");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to save record", "error");
      }
    } catch (err) {
      console.error("Save error:", err);
      showToast("Error saving record", "error");
    }
  };

  const handleSaveEmployeeFNF = async (record) => {
    const fnfId = editingEmployeeFNF?.fnfId ?? editingEmployeeFNF?.id;
    const employeeFNF = {
      employee: record.employee,
      exitDate: record.exitDate,
      amount: Number(record.amount),
    };
    const formData = new FormData();
    formData.append("employeeFNF", new Blob([JSON.stringify(employeeFNF)], { type: "application/json" }));
    if (record.file) formData.append("file", record.file);

    try {
      const response = await fetchWithAuth(
        fnfId ? API_ENDPOINTS.employeefnf.updateById(fnfId) : API_ENDPOINTS.employeefnf.create,
        { method: fnfId ? "PUT" : "POST", body: formData }
      );
      if (!response.ok) throw new Error("Failed to save Employee FNF");
      await fetchTabData("employeefnf");
      setEditingEmployeeFNF(null);
      setModal(null);
      showToast(fnfId ? "Employee FNF updated successfully" : "Employee FNF created successfully", "success");
    } catch (error) {
      console.error("Employee FNF save error:", error);
      showToast(error.message || "Failed to save Employee FNF", "error");
    }
  };

  const handleSaveSalary = async (record) => {
    const salaryId = editingSalary?.salaryId ?? editingSalary?.id;
    const site = selectedSite || record.site || null;
    const salaryPayload = {
      salaryDate: record.salaryDate,
      employee: record.employee,
      paidByEmployee: record.paidByEmployee || null,
      salaryAmount: Number(record.salaryAmount),
      previousPendingAmount: record.previousPendingAmount === "" ? null : record.previousPendingAmount,
      remarks: record.remarks || "",
      site,
      siteId: site?.id ?? site?.siteId ?? site?.site_id ?? null,
    };
    const formData = new FormData();
    formData.append("salary", new Blob([JSON.stringify(salaryPayload)], { type: "application/json" }));
    if (record.file) {
      formData.append("file", record.file);
    }

    try {
      const response = await fetchWithAuth(
        salaryId ? API_ENDPOINTS.salary.updateById(salaryId) : API_ENDPOINTS.salary.create,
        { method: salaryId ? "PUT" : "POST", body: formData }
      );
      if (!response.ok) {
        throw new Error(`Failed to ${salaryId ? "update" : "create"} salary`);
      }
      await fetchTabData("salary");
      setEditingSalary(null);
      setModal(null);
      showToast(salaryId ? "Salary updated successfully" : "Salary created successfully", "success");
    } catch (error) {
      console.error("Salary save error:", error);
      showToast(error.message || "Failed to save salary", "error");
    }
  };

  const handleEditSalary = (row) => {
    setEditingSalary(row);
  };

  // Edit employee: open edit modal with row data
  const handleEditEmployee = (row) => {
    setEditingEmployee(row);
  };

  const handleEditContractor = (row) => {
    setEditingContractor(row);
  };

  const handleEditBill = (row) => {
    setEditingBill(row);
  };

  const handleSaveBillEdit = async (record) => {
    try {
      const billId = editingBill?.billId || editingBill?.id || record?.billId || record?.id;
      if (!billId) throw new Error("Missing bill id for update");

      const amount = Number(record.amount ?? editingBill?.amount ?? editingBill?.totalAmount ?? editingBill?.total_amount ?? 0);
      const billPayload = {
        billNo: record.billNo || editingBill?.billNo || editingBill?.billNumber || editingBill?.bill_no || "",
        billDate: record.billDate || editingBill?.billDate || editingBill?.bill_date || editingBill?.date || "",
        billType: record.billType || editingBill?.billType || editingBill?.bill_type || "",
        amount,
        amountReceived: editingBill?.amountReceived ?? editingBill?.amount_received ?? 0,
        remainingAmount: editingBill?.remainingAmount ?? editingBill?.remaining_amount ?? amount,
        description: record.description || editingBill?.description || editingBill?.desc || "",
        site: selectedSite || null,
        workOrder: record.workOrder || editingBill?.workOrder || null,
        authUser: authUser || null,
      };

      const formData = new FormData();
      formData.append("bill", new Blob([JSON.stringify(billPayload)], { type: "application/json" }));
      if (record.invoiceFile) {
        formData.append("file", record.invoiceFile);
      }

      const response = await fetchWithAuth(API_ENDPOINTS.bill.updateById(billId), {
        method: "PUT",
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to update bill");
      }
      await fetchTabData("bills");
      setEditingBill(null);
      showToast("Bill updated successfully", "success");
    } catch (error) {
      console.error("Error updating bill:", error);
      showToast(error.message || "Failed to update bill", "error");
    }
  };

  const handleEditManagementExpense = async (row) => {
    await fetchManagementExpenseEmployees();
    setEditingManagementExpense({
      ...row,
      fromEmployee: row?.fromEmployee || row?.from_employee || null,
      toEmployee: row?.toEmployee || row?.to_employee || null,
    });
  };

  const handleSaveManagementExpenseEdit = async (record) => {
    try {
      const managementExpenseId = editingManagementExpense?.id ?? editingManagementExpense?.managementExpenseId;
      if (!managementExpenseId) throw new Error("Missing management expense id for update");

      const formData = new FormData();
      formData.append("managementExpense", new Blob([JSON.stringify({
        expenseDate: record.expenseDate,
        amount: Number(record.amount),
        fromEmployee: record.fromEmployee || null,
        toEmployee: record.toEmployee || null,
        site: record.site || selectedSite || editingManagementExpense?.site || null,
      })], { type: "application/json" }));
      if (record.documentFile) formData.append("file", record.documentFile);

      const response = await fetchWithAuth(API_ENDPOINTS.managementExpense.updateById(managementExpenseId), {
        method: "PUT",
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to update management expense");
      await fetchTabData("managementExpense");
      setEditingManagementExpense(null);
      showToast("Management expense updated successfully", "success");
    } catch (error) {
      console.error("Error updating management expense:", error);
      showToast(error.message || "Failed to update management expense", "error");
    }
  };

  const handleEditPurchase = (row) => {
    setEditingPurchase(buildPurchaseEditState(row, selectedSite));
  };

  const handleEditSell = (row) => {
    setEditingSell(buildSellEditState(row, selectedSite));
  };

  const handleEditExpense = (row) => {
    setEditingExpense(buildExpenseEditState(row));
  };

  const handleEditContractorWork = (row) => {
    setEditingContractorWork(buildContractorWorkEditState(row, selectedSite));
  };

  const handleEditContractorPayment = (row) => {
    setEditingContractorPayment(buildContractorPaymentEditState(row, selectedSite));
  };

  const handleEditLabour = (row) => {
    setEditingLabour(row);
  };

  const handleSaveLabourEdit = async (record) => {
    try {
      const labourId = editingLabour?.id || editingLabour?.labour_id || editingLabour?.labourId || editingLabour?.labourID || record?.id || record?.labour_id || record?.labourId || record?.labourID || null;
      if (!labourId) {
        throw new Error("Missing labour id for update");
      }

      const endpoint = API_ENDPOINTS.labour.updateById(Number(labourId));
      const transformedRecord = {
        labourName: record.labourName || "",
        joiningDate: record.labourJoiningDate || "",
        labourType: record.labourType || "",
        category: record.category || "Plant",
        dailyWage: record.dailyWage ? Number(record.dailyWage) : 0,
        remarks: record.remark || "",
        site: selectedSite || editingLabour?.site || null,
        authUser: authUser || record.authUser || null
      };

      const response = await fetchWithAuth(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(transformedRecord)
      });

      if (response.ok) {
        const updated = await response.json();
        setData(prev => {
          if (Array.isArray(prev.labour)) {
            return {
              ...prev,
              labour: prev.labour.map(item => {
                const itemId = item?.id || item?.labour_id || item?.labourId || item?.labourID;
                return itemId === labourId ? updated : item;
              })
            };
          }
          return prev;
        });
        setEditingLabour(null);
        showToast("Labour updated successfully", "success");
      } else {
        throw new Error("Failed to update labour");
      }
    } catch (err) {
      console.error("Error updating labour:", err);
      showToast(err.message || "Failed to update labour", "error");
    }
  };

  const handleEditFuelPump = (row) => {
    setEditingFuelPump(buildFuelPumpEditState(row, selectedSite));
  };

  const handleEditFuelTransaction = (row) => {
    setEditingFuelTransaction({
      id: row?.id ?? row?.fuelTransactionId ?? row?.fuel_transaction_id ?? null,
      fuelType: row?.fuelType || row?.fuel_type || "",
      transactionType: row?.transactionType || row?.transaction_type || "",
      quantity: row?.quantity ?? "",
      amount: row?.amount ?? "",
      date: row?.date || row?.fuelDate || row?.transactionDate || "",
      vehicleName: row?.vehicleName || row?.vehicle_name || "",
      operatorName: row?.operatorName || row?.operator_name || "",
      remarks: row?.remarks || row?.remark || "",
      site: row?.site || selectedSite || null,
      siteId: (row?.siteId ?? row?.site?.id ?? selectedSite?.id) || "",
    });
  };

  const handleEditFuelPayment = async (row) => {
    const fuelPaymentId = row?.id ?? row?.fuelPaymentId;
    if (fuelPaymentId == null) {
      showToast("Cannot edit fuel payment: record ID is missing", "error");
      return;
    }

    try {
      const response = await fetchWithAuth(API_ENDPOINTS.fuelpayment.byId(fuelPaymentId), { method: "GET" });
      if (!response.ok) {
        throw new Error(`Fuel payment request failed (${response.status})`);
      }
      setEditingFuelPayment(await response.json());
    } catch (error) {
      console.error("Error loading fuel payment:", error);
      showToast(error.message || "Failed to load fuel payment", "error");
    }
  };

  const handleEditConcreteProduction = (row) => {
    if (getConcreteProductionId(row) == null) {
      showToast("Cannot edit concrete production: record ID is missing", "error");
      return;
    }
    setEditingConcreteProduction(row);
  };

  const handleSaveConcreteProductionEdit = async (record) => {
    try {
      const concreteProductionId = getConcreteProductionId(editingConcreteProduction)
        ?? getConcreteProductionId(record);
      if (concreteProductionId == null) {
        throw new Error("Missing concrete production id for update");
      }

      const payload = {
        id: concreteProductionId,
        productionDate: record.productionDate,
        productionTime: record.productionTime,
        transitMixerNumber: record.transitMixerNumber,
        gradeOfConcrete: record.gradeOfConcrete,
        quantity: Number(record.quantity),
        dispatchedTo: record.dispatchedTo,
        chainageNo: record.chainageNo,
        operatorSign: record.operatorSign,
        approvedBy: record.approvedBy,
        remarks: record.remarks || "",
        site: record.site || selectedSite || editingConcreteProduction?.site || null,
        authUser: authUser || null,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.concreteproduction.updateById(concreteProductionId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to update concrete production");
      }

      await fetchTabData("concreteproduction");
      setEditingConcreteProduction(null);
      showToast("Concrete production updated successfully", "success");
    } catch (error) {
      console.error("Error updating concrete production:", error);
      showToast(error.message || "Failed to update concrete production", "error");
    }
  };

  const handleSaveFuelPumpEdit = async (record) => {
    try {
      const fuelPumpId = editingFuelPump?.id ?? record?.id ?? editingFuelPump?.fuelPumpId ?? null;
      if (!fuelPumpId) {
        throw new Error("Missing fuel pump id for update");
      }

      const payload = {
        id: fuelPumpId,
        fuelType: record.fuelType,
        vehicleNumber: record.vehicleNumber,
        amount: Number(record.amount || 0),
        quantity: Number(record.quantity || 0),
        receivedBy: record.receivedBy || "",
        approvedBy: record.approvedBy || "",
        fuelDate: record.fuelDate || null,
        site: record.site || selectedSite || null,
        authUser: authUser || null,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.fuelpump.update(fuelPumpId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchTabData("fuelpump");
        setEditingFuelPump(null);
        showToast("Pump fuel updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update pump fuel", "error");
      }
    } catch (err) {
      console.error("Error updating pump fuel:", err);
      showToast("Error updating pump fuel", "error");
    }
  };

  const handleSaveFuelTransactionEdit = async (record) => {
    try {
      const fuelTransactionId = editingFuelTransaction?.id ?? record?.id ?? null;
      if (!fuelTransactionId) {
        throw new Error("Missing plant fuel id for update");
      }

      const payload = {
        id: fuelTransactionId,
        fuelType: record.fuelType,
        transactionType: record.transactionType,
        quantity: Number(record.quantity || 0),
        amount: Number(record.amount || 0),
        date: record.date || null,
        vehicleName: record.vehicleName || "",
        operatorName: record.operatorName || "",
        remarks: record.remarks || "",
        site: record.site || selectedSite || null,
        authUser: authUser || null,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.fueltransaction.updateById(Number(fuelTransactionId)), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchTabData("fueltransaction");
        setEditingFuelTransaction(null);
        showToast("Plant fuel updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update plant fuel", "error");
      }
    } catch (err) {
      console.error("Error updating plant fuel:", err);
      showToast("Error updating plant fuel", "error");
    }
  };

  const handleSaveFuelPayment = async ({ fuelPayment, document: paymentDocument }) => {
    const fuelPaymentId = editingFuelPayment?.id ?? fuelPayment?.id ?? null;
    const isEditing = fuelPaymentId != null;

    try {
      const payload = {
        ...fuelPayment,
        ...(isEditing ? { id: fuelPaymentId } : {}),
        authUser: authUser || null,
      };
      const formData = new FormData();
      formData.append("fuelPayment", new Blob([JSON.stringify(payload)], { type: "application/json" }));
      if (paymentDocument) {
        formData.append("document", paymentDocument);
      }

      const response = await fetchWithAuth(
        isEditing ? API_ENDPOINTS.fuelpayment.updateById(fuelPaymentId) : API_ENDPOINTS.fuelpayment.create,
        {
          method: isEditing ? "PUT" : "POST",
          body: formData,
        }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || `Fuel payment save failed (${response.status})`);
      }

      await fetchTabData("fuelpayment");
      setEditingFuelPayment(null);
      setModal(null);
      showToast(isEditing ? "Fuel payment updated successfully" : "Fuel payment created successfully", "success");
    } catch (error) {
      console.error("Error saving fuel payment:", error);
      showToast(error.message || "Failed to save fuel payment", "error");
    }
  };

  const handleSavePurchaseEdit = async (record) => {
    try {
      const purchaseNo = editingPurchase?.purchaseNo || editingPurchase?.purchaseNumber || editingPurchase?.purchase_no || record.purchaseNo || null;
      if (!purchaseNo) {
        throw new Error("Missing purchase identifier for update");
      }

      const formData = new FormData();
      const purchasePayload = {
        purchaseNo,
        purchaseDate: record.purchaseDate,
        partyName: record.partyName,
        receiverName: record.receiverName,
        paymentStatus: record.paymentStatus,
        taxAmount: Number(record.taxAmount) || 0,
        description: record.description || "",
        site: record.site || selectedSite || null,
        siteId: record.site?.id || selectedSite?.id || null,
        purchaseItems: record.purchaseItems || [],
        authUser: authUser || null,
      };
      formData.append("purchase", new Blob([JSON.stringify(purchasePayload)], { type: "application/json" }));
      if (record.invoiceFile) {
        formData.append("invoice", record.invoiceFile);
      }

      const response = await fetchWithAuth(API_ENDPOINTS.purchase.updateById(purchaseNo), {
        method: "PUT",
        body: formData,
      });

      if (response.ok) {
        await fetchTabData("purchases");
        setEditingPurchase(null);
        showToast("Purchase updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update purchase", "error");
      }
    } catch (err) {
      console.error("Error updating purchase:", err);
      showToast("Error updating purchase", "error");
    }
  };

  const handleSaveSellEdit = async (record) => {
    try {
      const sellNo = editingSell?.sellNo
        || editingSell?.sell_no
        || editingSell?.saleNo
        || editingSell?.billNo
        || record?.sellNo
        || record?.sell_no
        || record?.saleNo
        || record?.billNo
        || null;
      if (!sellNo) {
        throw new Error("Missing sell identifier for update");
      }

      const formData = new FormData();
      const sellPayload = {
        id: record?.id || record?.sellId || record?.sell_id || editingSell?.id || editingSell?.sellId || editingSell?.sell_id || null,
        sellNo,
        sellDate: record.sellDate,
        billType: record.billType,
        paymentStatus: record.paymentStatus,
        site: record.site || selectedSite || null,
        sellItems: record.sellItems || [],
        authUser: authUser || null,
      };
      formData.append("sell", new Blob([JSON.stringify(sellPayload)], { type: "application/json" }));
      if (record.invoiceFile) {
        formData.append("invoice", record.invoiceFile);
      }

      const response = await fetchWithAuth(API_ENDPOINTS.sell.updateById(sellNo), {
        method: "PUT",
        body: formData,
      });

      if (response.ok) {
        await fetchTabData("sells");
        setEditingSell(null);
        showToast("Sell updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update sell", "error");
      }
    } catch (err) {
      console.error("Error updating sell:", err);
      showToast("Error updating sell", "error");
    }
  };

  const handleSaveExpenseEdit = async (record) => {
    try {
      const expenseId = editingExpense?.id
        ?? editingExpense?.expenseId
        ?? editingExpense?.expense_id
        ?? record?.id
        ?? record?.expenseId
        ?? record?.expense_id
        ?? null;
      if (!expenseId) {
        throw new Error("Missing expense id for update");
      }

      const formData = new FormData();
      const expensePayload = {
        id: expenseId,
        expenseType: record.expenseType,
        expenseDate: record.expenseDate,
        amount: Number(record.amount),
        remark: record.remark || "",
        spentBy: record.spentBy || record.spent_by || null,
        authUser: authUser || null,
        site: selectedSite || null,
      };
      formData.append("expense", new Blob([JSON.stringify(expensePayload)], { type: "application/json" }));
      if (record.attachmentFile) {
        formData.append("file", record.attachmentFile);
      }

      const response = await fetchWithAuth(API_ENDPOINTS.expense.updateById(expenseId), {
        method: "PUT",
        body: formData,
      });

      if (response.ok) {
        await fetchTabData("expenses");
        setEditingExpense(null);
        showToast("Expense updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update expense", "error");
      }
    } catch (err) {
      console.error("Error updating expense:", err);
      showToast("Error updating expense", "error");
    }
  };

  const handleSaveContractorEdit = async (record) => {
    try {
      const contractorId = editingContractor?.id || editingContractor?.contractorId || editingContractor?.contractorID || editingContractor?.contractor_id || null;
      if (!contractorId) {
        throw new Error("Missing contractor id for update");
      }

      const endpoint = API_ENDPOINTS.contractor.updateById(contractorId);
      const transformedRecord = {
        contractorName: record.contractorName,
        mobileNumber: record.mobile || record.mobileNumber || "",
        email: record.email || "",
        gstNumber: record.gstNumber || "",
        panNumber: record.panNumber || "",
        joiningDate: record.joiningDate || null,
        contractorType: record.contractorType || record.contractor_type || "DAILY_BASIS",
        description: record.description || "",
        aadhar_Number: record.aadharNumber || record.aadhar_Number || "",
        active: record.active !== undefined ? record.active : (editingContractor?.active ?? true),
        address: {
          village: record.village || "",
          post: record.post || "",
          district: record.district || "",
          state: record.state || "",
          pin: record.pin || ""
        },
        site: editingContractor?.site || selectedSite || null
      };
      const formData = new FormData();
      formData.append("contractor", new Blob([JSON.stringify({
        contractorCode: record.contractorCode || record.contractor_code || record.code || editingContractor?.contractorCode || editingContractor?.contractor_code || editingContractor?.code || "",
        ...transformedRecord
      })], { type: "application/json" }));
      if (record.aadharFile) formData.append("aadharFile", record.aadharFile);
      if (record.panFile) formData.append("panFile", record.panFile);
      if (record.bankFile) formData.append("bankFile", record.bankFile);

      const response = await fetchWithAuth(endpoint, {
        method: "PUT",
        body: formData
      });

      if (response.ok) {
        const updated = await response.json();
        setData(prev => {
          if (Array.isArray(prev.contractor)) {
            const list = prev.contractor.map(c => {
              const cId = c.id || c.contractorId || c.contractorID || c.contractor_id || null;
              const uId = updated.id || updated.contractorId || updated.contractorID || updated.contractor_id || null;
              return cId && uId && String(cId) === String(uId) ? updated : c;
            });
            return { ...prev, contractor: list };
          }
          return prev;
        });
        await fetchTabData("contractor");
        setEditingContractor(null);
        showToast("Contractor updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update contractor", "error");
      }
    } catch (err) {
      console.error("Error updating contractor:", err);
      showToast("Error updating contractor", "error");
    }
  };

  const handleSaveContractorWorkEdit = async (record) => {
    try {
      const contractorWorkId = record?.id || record?.contractorWorkId || record?.workId || null;
      if (!contractorWorkId) {
        throw new Error("Missing contractor work id for update");
      }

      const workPayload = {
        contractor: record.contractorObject || record.contractor || null,
        workDate: record.workDate,
        numberOfLabours: Number(record.numberOfLabours || 0),
        labourRate: Number(record.labourRate || 0),
        numberOfMasons: Number(record.numberOfMasons || 0),
        masonRate: Number(record.masonRate || 0),
        labourAmount: Number(record.labourAmount || 0),
        masonAmount: Number(record.masonAmount || 0),
        otherCharges: Number(record.otherCharges || 0),
        description: record.description || "",
        authUser: authUser || null,
        site: record.site || selectedSite || null,
      };

      const response = await fetchWithAuth(API_ENDPOINTS.contractorwork.update(contractorWorkId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(workPayload),
      });

      if (response.ok) {
        await fetchTabData("contractorwork");
        setEditingContractorWork(null);
        showToast("Contractor work updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update contractor work", "error");
      }
    } catch (err) {
      console.error("Error updating contractor work:", err);
      showToast("Error updating contractor work", "error");
    }
  };

  const handleSaveContractorPaymentEdit = async (record) => {
    try {
      const paymentId = record?.id || record?.contractorPaymentId || record?.paymentId || null;
      if (!paymentId) {
        throw new Error("Missing contractor payment id for update");
      }
      const hasExistingDocument = Boolean(editingContractorPayment?.document?.fileName || editingContractorPayment?.documentName);
      if (!record.documentFile && !hasExistingDocument) {
        window.alert("Please upload a document before saving the Contractor Payment.");
        return;
      }

      const paymentPayload = {
        contractor: record.contractorObject || record.contractor || null,
        paidBy: record.paidByEmployee || record.paidBy || null,
        paymentDate: record.paymentDate,
        paymentAmount: Number(record.amount || 0),
        paymentMethod: normalizeContractorPaymentMethod(record.paymentMethod),
        description: record.description || "",
        authUser: authUser || null,
        site: record.site || selectedSite || null,
      };
      const formData = new FormData();
      formData.append("contractorPayment", new Blob([JSON.stringify(paymentPayload)], { type: "application/json" }));
      if (record.documentFile) formData.append("document", record.documentFile);

      const response = await fetchWithAuth(API_ENDPOINTS.contractorpayment.update(paymentId), {
        method: "PUT",
        body: formData,
      });

      if (response.ok) {
        await fetchTabData("contractorpayment");
        setEditingContractorPayment(null);
        showToast("Contractor payment updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update contractor payment", "error");
      }
    } catch (err) {
      console.error("Error updating contractor payment:", err);
      showToast("Error updating contractor payment", "error");
    }
  };

  const handleSaveLabourPaymentEdit = async (record) => {
    try {
      const paymentId = editingLabourPayment?.id || editingLabourPayment?.paymentId || editingLabourPayment?.labourPaymentId;
      if (!paymentId) {
        throw new Error("Missing labour payment id for update");
      }

      const response = await fetchWithAuth(API_ENDPOINTS.labourpayment.updateById(paymentId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          labour: record.labour || editingLabourPayment.labour || null,
          labourCode: record.labourCode,
          paymentDate: record.paymentDate,
          amount: Number(record.amount || 0),
          paymentType: record.paymentType,
          remarks: record.remarks || "",
          authUser: authUser || null
        })
      });

      if (!response.ok) {
        const error = await response.text().catch(() => "");
        throw new Error(error || "Failed to update labour payment");
      }

      await fetchTabData("labourpayment");
      setEditingLabourPayment(null);
      showToast("Labour payment updated successfully", "success");
    } catch (err) {
      console.error("Error updating labour payment:", err);
      showToast(err.message || "Error updating labour payment", "error");
    }
  };

  const handleSaveEmployeeEdit = async (record) => {
    try {
      const empId = editingEmployee?.id || editingEmployee?.empId || editingEmployee?.empCode || editingEmployee?.empcode || record.id || record.empId || record.empCode || record.empcode || null;
      if (!empId) {
        throw new Error("Missing employee id for update");
      }

      const endpoint = API_ENDPOINTS.employee.updateById(empId);
      const mappedEmpType = record.empType === "Regular" ? "R" : record.empType === "Temporary" ? "T" : record.empType;
      const transformedRecord = {
        name: record.name,
        email: record.email,
        phone: record.phone,
        aadhar_Number: record.aadharNumber || record.aadhar_Number || "",
        salary: record.salary || 0,
        site: record.currentSite || record.site || selectedSite || null,
        position: record.position || "",
        empType: mappedEmpType,
        doj: record.doj || null,
        address: {
          village: record.village || "",
          post: record.post || "",
          district: record.district || "",
          state: record.state || "",
          pin: record.pin || ""
        },
        authUser: authUser || null
      };

      const formData = new FormData();
      formData.append("employee", new Blob([JSON.stringify(transformedRecord)], { type: "application/json" }));
      if (record.aadharFile) formData.append("aadharFile", record.aadharFile);
      if (record.panFile) formData.append("panFile", record.panFile);
      if (record.bankFile) formData.append("bankFile", record.bankFile);

      const response = await fetchWithAuth(endpoint, {
        method: "PUT",
        body: formData
      });

      if (response.ok) {
        const updated = await response.json();
        setData(prev => {
          if (Array.isArray(prev.employees)) {
            const list = prev.employees.map(e => {
              const eId = e.id || e.empcode || e.empCode || null;
              const uId = updated.id || updated.empcode || updated.empCode || null;
              if (eId && uId && String(eId) === String(uId)) return updated;
              return e;
            });
            return { ...prev, employees: list };
          }
          setTimeout(() => fetchTabData("employees"), 500);
          return prev;
        });
        setEditingEmployee(null);
        setModal(null);
        showToast("Employee updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update employee", "error");
      }
    } catch (err) {
      console.error("Error updating employee:", err);
      showToast("Error updating employee", "error");
    }
  };

  const fetchFuelCounts = async () => {
    if (!selectedSite?.id) return;

    setData(prevData => ({ ...prevData, plantFuelInAmount: 0 }));

    try {
      const [dieselInRes, dieselOutRes, petrolInRes, petrolOutRes, plantFuelAmountRes] = await Promise.all([
        fetchWithAuth(API_ENDPOINTS.diesel.dieselInCount(selectedSite.id)),
        fetchWithAuth(API_ENDPOINTS.diesel.dieselOutCount(selectedSite.id)),
        fetchWithAuth(API_ENDPOINTS.petrol.petrolInCount(selectedSite.id)),
        fetchWithAuth(API_ENDPOINTS.petrol.petrolOutCount(selectedSite.id)),
        fetchWithAuth(API_ENDPOINTS.fueltransaction.currentMonthDieselPetrolIn(selectedSite.id))
          .catch(err => {
            console.error("Error fetching current-month plant fuel total:", err);
            return null;
          })
      ]);

      let dieselIn = 0, dieselOut = 0, petrolIn = 0, petrolOut = 0;

      if (dieselInRes.ok) {
        const data = await dieselInRes.json();
        dieselIn = typeof data === 'object' && data.quantity !== undefined ? data.quantity : (typeof data === 'number' ? data : 0);
        console.log("Diesel In Count API:", dieselIn, "Raw response:", data);
      }
      if (dieselOutRes.ok) {
        const data = await dieselOutRes.json();
        dieselOut = typeof data === 'object' && data.quantity !== undefined ? data.quantity : (typeof data === 'number' ? data : 0);
        console.log("Diesel Out Count API:", dieselOut, "Raw response:", data);
      }
      if (petrolInRes.ok) {
        const data = await petrolInRes.json();
        petrolIn = typeof data === 'object' && data.quantity !== undefined ? data.quantity : (typeof data === 'number' ? data : 0);
        console.log("Petrol In Count API:", petrolIn, "Raw response:", data);
      }
      if (petrolOutRes.ok) {
        const data = await petrolOutRes.json();
        petrolOut = typeof data === 'object' && data.quantity !== undefined ? data.quantity : (typeof data === 'number' ? data : 0);
        console.log("Petrol Out Count API:", petrolOut, "Raw response:", data);
      }

      setFuelCounts({
        dieselIn: typeof dieselIn === 'number' ? dieselIn : 0,
        dieselOut: typeof dieselOut === 'number' ? dieselOut : 0,
        petrolIn: typeof petrolIn === 'number' ? petrolIn : 0,
        petrolOut: typeof petrolOut === 'number' ? petrolOut : 0
      });

      if (!plantFuelAmountRes) return;
      if (!plantFuelAmountRes.ok) {
        console.error(`Failed to fetch current-month plant fuel total: ${plantFuelAmountRes.status}`);
        return;
      }
      const plantFuelAmount = Number(await plantFuelAmountRes.json());
      if (!Number.isFinite(plantFuelAmount)) {
        console.error("Current-month plant fuel total is not a valid number");
        return;
      }
      setData(prevData => ({ ...prevData, plantFuelInAmount: plantFuelAmount }));
    } catch (err) {
      console.error("Error fetching fuel counts:", err);
    }
  };

  const getSingularForm = (plural) => {
    const map = {
      employees: "employee",
      attendance: "attendance",
      salary: "salary",
      employeeadvance: "employeeadvance",
      purchases: "purchase",
      sells: "sell",
      expenses: "expense",
      diesel: "diesel",
      petrol: "petrol",
      workorders: "workorder",
      fueltransaction: "fueltransaction",
      labourcoast: "labourcoast",
      labour: "labour",
      contractor: "contractor",
      contractorwork: "contractorwork",
      contractorpayment: "contractorpayment",
      labouradvance: "labouradvance",
      labourattendance: "labourattendance",
      labourpayment: "labourpayment",
      concreteproduction: "concreteproduction",
      bills: "bill",
      managementExpense: "managementExpense",
    };
    return map[plural] || plural;
  };

  const calculateFuelTotals = () => {
    const fuelData = data.fueltransaction || [];
    const totals = {
      dieselIn: 0,
      dieselOut: 0,
      petrolIn: 0,
      petrolOut: 0
    };

    fuelData.forEach(record => {
      const quantity = Number(record.quantity) || 0;
      if (record.fuelType === "DIESEL" && record.transactionType === "IN") {
        totals.dieselIn += quantity;
      } else if (record.fuelType === "DIESEL" && record.transactionType === "OUT") {
        totals.dieselOut += quantity;
      } else if (record.fuelType === "PETROL" && record.transactionType === "IN") {
        totals.petrolIn += quantity;
      } else if (record.fuelType === "PETROL" && record.transactionType === "OUT") {
        totals.petrolOut += quantity;
      }
    });

    return totals;
  };

  const handleDownload = async (section) => {
    try {
      const sectionKey = getSingularForm(section);
      const endpoint = API_ENDPOINTS[sectionKey]?.template;
      
      console.log("Download - section:", section, "sectionKey:", sectionKey, "endpoint:", endpoint);
      console.log("API_ENDPOINTS keys:", Object.keys(API_ENDPOINTS));
      console.log("API_ENDPOINTS[sectionKey]:", API_ENDPOINTS[sectionKey]);
      
      if (!endpoint) {
        showToast(`Download template not available for ${section}. Endpoint: ${sectionKey}`, "error");
        return;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${section}_template.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast("Template downloaded!");
      } else {
        showToast("Failed to download template", "error");
      }
    } catch (err) {
      console.error("Download error:", err);
      showToast("Error downloading template", "error");
    }
  };

  const fetchPurchaseInvoiceBlob = async (siteId, purchaseNo) => {
    const params = new URLSearchParams();
    params.append("purchaseNo", purchaseNo);
    if (siteId !== undefined && siteId !== null) {
      params.append("siteid", siteId);
    }

    const invoiceEndpoint = `${API_ENDPOINTS.purchase.invoice}?${params.toString()}`;
    const response = await fetchWithAuth(invoiceEndpoint, { method: "GET" });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Invoice fetch failed (${response.status}): ${text}`);
    }

    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || "application/octet-stream";
    const disposition = response.headers.get("content-disposition") || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `invoice_${purchaseNo}`;
    return { blob, filename, contentType };
  };

  const handlePreviewInvoice = async (row) => {
    try {
      const purchaseNo = row.purchaseNo || row.purchaseNumber || row.purchase_no;
      if (!purchaseNo) {
        showToast("Purchase number missing for invoice preview", "error");
        return;
      }
      setInvoiceLoadingId(row.id || purchaseNo);
      const siteId = selectedSite?.id;
      const { blob, filename, contentType } = await fetchPurchaseInvoiceBlob(siteId, purchaseNo);
      const url = URL.createObjectURL(blob);
      setInvoicePreview({ open: true, url, filename, contentType });
    } catch (err) {
      console.error("Invoice preview error:", err);
      showToast("Failed to load invoice preview", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const fetchSellInvoiceBlob = async (siteId, sellNo) => {
    const params = new URLSearchParams();
    params.append("sellNo", sellNo);
    if (siteId !== undefined && siteId !== null) {
      params.append("siteid", siteId);
    }

    const invoiceEndpoint = `${API_ENDPOINTS.sell.invoice}?${params.toString()}`;
    const response = await fetchWithAuth(invoiceEndpoint, { method: "GET" });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Invoice fetch failed (${response.status}): ${text}`);
    }

    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || "application/octet-stream";
    const disposition = response.headers.get("content-disposition") || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `invoice_${sellNo}`;
    return { blob, filename, contentType };
  };

  const handlePreviewSellInvoice = async (row) => {
    try {
      const sellNo = row.sellNo || row.sell_no || row.saleNo || row.billNo || row.bill_number;
      if (!sellNo) {
        showToast("Sell number missing for invoice preview", "error");
        return;
      }
      setInvoiceLoadingId(row.id || sellNo);
      const siteId = selectedSite?.id;
      const { blob, filename, contentType } = await fetchSellInvoiceBlob(siteId, sellNo);
      const url = URL.createObjectURL(blob);
      setInvoicePreview({ open: true, url, filename, contentType });
    } catch (err) {
      console.error("Sell invoice preview error:", err);
      showToast("Failed to load invoice preview", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const fetchBillInvoiceBlob = async (siteId, billNo) => {
    const params = new URLSearchParams();
    if (billNo) {
      params.append("billNo", billNo);
    }
    if (siteId !== undefined && siteId !== null) {
      params.append("siteId", siteId);
    }

    const invoiceEndpoint = `${API_ENDPOINTS.bill.invoice}?${params.toString()}`;
    const response = await fetchWithAuth(invoiceEndpoint, { method: "GET" });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Invoice fetch failed (${response.status}): ${text}`);
    }

    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || "application/octet-stream";
    const disposition = response.headers.get("content-disposition") || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `bill_${billNo}`;
    return { blob, filename, contentType };
  };

  const handlePreviewBillInvoice = async (row) => {
    try {
      const billNo = row.billNo || row.bill_number;
      if (!billNo) {
        showToast("Bill identifier missing for preview", "error");
        return;
      }
      setInvoiceLoadingId(row.billId || row.id || billNo || `bill-preview`);
      const siteId = selectedSite?.id;
      const { blob, filename, contentType } = await fetchBillInvoiceBlob(siteId, billNo);
      const url = URL.createObjectURL(blob);
      setInvoicePreview({ open: true, url, filename, contentType });
    } catch (err) {
      console.error("Bill invoice preview error:", err);
      showToast("Failed to load bill invoice", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handlePreviewContractorPaymentDocument = async (row) => {
    const paymentId = row?.id ?? row?.contractorPaymentId ?? row?.paymentId;
    if (paymentId === undefined || paymentId === null) {
      showToast("Contractor payment id missing for document preview", "error");
      return;
    }

    try {
      setInvoiceLoadingId(paymentId);
      const endpoint = `${API_ENDPOINTS.contractorpayment.document}?paymentId=${encodeURIComponent(paymentId)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) throw new Error("Document fetch failed");
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      setInvoicePreview({
        open: true,
        url: URL.createObjectURL(blob),
        filename: filenameMatch ? filenameMatch[1] : `contractor_payment_${paymentId}`,
        contentType: response.headers.get("content-type") || "application/octet-stream",
      });
    } catch (error) {
      console.error("Contractor payment document preview error:", error);
      showToast("Failed to load contractor payment document", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handlePreviewManagementExpenseDocument = async (row) => {
    const managementExpenseId = row?.id ?? row?.managementExpenseId;
    if (!managementExpenseId) {
      showToast("Management expense id missing for document preview", "error");
      return;
    }

    try {
      setInvoiceLoadingId(managementExpenseId);
      const response = await fetchWithAuth(`${API_ENDPOINTS.managementExpense.document}?managementExpenseId=${encodeURIComponent(managementExpenseId)}`, { method: "GET" });
      if (!response.ok) throw new Error("Document fetch failed");
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : `management_expense_${managementExpenseId}`;
      setInvoicePreview({
        open: true,
        url: URL.createObjectURL(blob),
        filename,
        contentType: response.headers.get("content-type") || "application/octet-stream",
      });
    } catch (error) {
      console.error("Management expense document preview error:", error);
      showToast("Failed to load management expense document", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handlePreviewEmployeeFNFDocument = async (row) => {
    const fnfId = row?.fnfId ?? row?.id;
    if (fnfId === undefined || fnfId === null) {
      showToast("Employee FNF id missing for document preview", "error");
      return;
    }
    try {
      setInvoiceLoadingId(fnfId);
      const endpoint = `${API_ENDPOINTS.employeefnf.document}?employeeFNFId=${encodeURIComponent(fnfId)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) throw new Error("Document fetch failed");
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      setInvoicePreview({
        open: true,
        url: URL.createObjectURL(blob),
        filename: filenameMatch ? filenameMatch[1] : `employee_fnf_${fnfId}`,
        contentType: response.headers.get("content-type") || "application/octet-stream",
      });
    } catch (error) {
      console.error("Employee FNF document preview error:", error);
      showToast("Failed to load Employee FNF document", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const getFuelDocumentId = (row, section) => section === "fuelpump"
    ? row?.id ?? row?.fuelTransactionPumpId ?? row?.fuelPumpId ?? row?.fuel_pump_id
    : row?.id ?? row?.fuelTransactionPlantId ?? row?.fuelTransactionId ?? row?.fuel_transaction_id;

  const handlePreviewFuelDocument = async (row, section) => {
    const fuelId = getFuelDocumentId(row, section);
    if (fuelId === undefined || fuelId === null || fuelId === "") {
      showToast("Fuel record id missing for document preview", "error");
      return;
    }
    try {
      setInvoiceLoadingId(fuelId);
      const endpoint = API_ENDPOINTS[section].document(fuelId);
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Fuel document request failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch
        ? filenameMatch[1]
        : `${section === "fuelpump" ? "fuel_pump" : "fuel_transaction"}_${fuelId}`;
      setInvoicePreview({
        open: true,
        url: URL.createObjectURL(blob),
        filename,
        contentType: response.headers.get("content-type") || "application/octet-stream",
      });
    } catch (error) {
      console.error("Fuel document preview error:", error);
      showToast(error.message || "Failed to load fuel document", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const fetchWorkOrderDocumentBlob = async (siteId, row) => {
    const params = new URLSearchParams();
    const workOrderId = row?.workOrderId || row?.workorderId || row?.id || null;
    const woNumber = row?.woNumber || row?.wo_number || row?.workOrderNo || null;
    if (workOrderId) params.append("workOrderId", workOrderId);
    else if (woNumber) params.append("woNumber", woNumber);
    if (siteId !== undefined && siteId !== null) params.append("siteId", siteId);

    const endpoint = `${API_ENDPOINTS.workorder.downloadDocument}?${params.toString()}`;
    const response = await fetchWithAuth(endpoint, { method: "GET" });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`WorkOrder document fetch failed (${response.status}): ${text}`);
    }
    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || "application/octet-stream";
    const disposition = response.headers.get("content-disposition") || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `workorder_${workOrderId || woNumber}`;
    return { blob, filename, contentType };
  };

  const handlePreviewWorkOrderDocument = async (row) => {
    try {
      const idOrNo = row.workOrderId || row.workorderId || row.id || row.woNumber || row.wo_number || null;
      if (!idOrNo) {
        showToast("Work Order identifier missing for document preview", "error");
        return;
      }
      setInvoiceLoadingId(idOrNo);
      const siteId = selectedSite?.id;
      const { blob, filename, contentType } = await fetchWorkOrderDocumentBlob(siteId, row);
      const url = URL.createObjectURL(blob);
      setInvoicePreview({ open: true, url, filename, contentType });
    } catch (err) {
      console.error("WorkOrder document preview error:", err);
      showToast("Failed to load work order document", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handleDownloadAttachment = async (row) => {
    const readDocumentResponse = async (response, name) => {
      if (!response.ok) throw new Error(`Attachment download failed (${response.status})`);
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      return {
        blob: await response.blob(),
        filename: filenameMatch ? filenameMatch[1] : name,
        contentType: response.headers.get("content-type") || "application/octet-stream",
      };
    };

    const rowId = row?.id ?? row?.fnfId ?? row?.billId ?? row?.contractorPaymentId ?? row?.paymentId ?? row?.purchaseNo ?? row?.sellNo;
    setInvoiceLoadingId(rowId);
    try {
      let fileDetails;
      if (activeTab === "purchases") {
        const purchaseNo = row.purchaseNo || row.purchaseNumber || row.purchase_no;
        if (!purchaseNo) throw new Error("Purchase number missing for attachment download");
        fileDetails = await fetchPurchaseInvoiceBlob(selectedSite?.id, purchaseNo);
      } else if (activeTab === "sells") {
        const sellNo = row.sellNo || row.sell_no || row.saleNo || row.billNo || row.bill_number;
        if (!sellNo) throw new Error("Sell number missing for attachment download");
        fileDetails = await fetchSellInvoiceBlob(selectedSite?.id, sellNo);
      } else if (activeTab === "bills") {
        const billNo = row.billNo || row.bill_number;
        if (!billNo) throw new Error("Bill number missing for attachment download");
        fileDetails = await fetchBillInvoiceBlob(selectedSite?.id, billNo);
      } else if (activeTab === "contractorpayment") {
        const paymentId = row?.id ?? row?.contractorPaymentId ?? row?.paymentId;
        if (paymentId === undefined || paymentId === null) throw new Error("Contractor payment id missing for document download");
        const response = await fetchWithAuth(`${API_ENDPOINTS.contractorpayment.document}?paymentId=${encodeURIComponent(paymentId)}`, { method: "GET" });
        fileDetails = await readDocumentResponse(response, `contractor_payment_${paymentId}`);
      } else if (activeTab === "workorders") {
        fileDetails = await fetchWorkOrderDocumentBlob(selectedSite?.id, row);
      } else if (activeTab === "expenses") {
        const expenseId = row?.id ?? row?.expenseId ?? row?.expense_id ?? row?.expense?.id;
        if (!expenseId) throw new Error("Expense id missing for attachment download");
        const response = await fetchWithAuth(`${API_ENDPOINTS.expense.document}?expenseId=${encodeURIComponent(expenseId)}`, { method: "GET" });
        fileDetails = await readDocumentResponse(response, `expense_${expenseId}`);
      } else if (activeTab === "managementExpense") {
        const managementExpenseId = row?.id ?? row?.managementExpenseId;
        if (!managementExpenseId) throw new Error("Management expense id missing for document download");
        const response = await fetchWithAuth(`${API_ENDPOINTS.managementExpense.document}?managementExpenseId=${encodeURIComponent(managementExpenseId)}`, { method: "GET" });
        fileDetails = await readDocumentResponse(response, `management_expense_${managementExpenseId}`);
      } else if (activeTab === "employeefnf") {
        const fnfId = row?.fnfId ?? row?.id;
        if (fnfId === undefined || fnfId === null) throw new Error("Employee FNF id missing for document download");
        const response = await fetchWithAuth(`${API_ENDPOINTS.employeefnf.document}?employeeFNFId=${encodeURIComponent(fnfId)}`, { method: "GET" });
        fileDetails = await readDocumentResponse(response, `employee_fnf_${fnfId}`);
      } else if (activeTab === "fuelpayment") {
        const fuelPaymentId = row?.id ?? row?.fuelPaymentId;
        if (fuelPaymentId === undefined || fuelPaymentId === null) {
          throw new Error("Fuel payment id missing for document download");
        }
        if (!row?.document) {
          showToast("No document is attached to this fuel payment", "error");
          return;
        }
        const response = await fetchWithAuth(
          `${API_ENDPOINTS.fuelpayment.document}?fuelPaymentId=${encodeURIComponent(fuelPaymentId)}`,
          { method: "GET" }
        );
        fileDetails = await readDocumentResponse(response, `fuel_payment_${fuelPaymentId}`);
      } else if (activeTab === "fuelpump" || activeTab === "fueltransaction") {
        const fuelId = getFuelDocumentId(row, activeTab);
        if (fuelId === undefined || fuelId === null || fuelId === "") {
          throw new Error("Fuel record id missing for document download");
        }
        const response = await fetchWithAuth(API_ENDPOINTS[activeTab].document(fuelId), { method: "GET" });
        fileDetails = await readDocumentResponse(
          response,
          `${activeTab === "fuelpump" ? "fuel_pump" : "fuel_transaction"}_${fuelId}`
        );
      } else {
        throw new Error("Attachment download is not available for this tab");
      }

      const url = URL.createObjectURL(fileDetails.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileDetails.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      showToast("Attachment downloaded successfully");
    } catch (error) {
      console.error("Attachment download error:", error);
      showToast(error.message || "Failed to download attachment", "error");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handleEditWorkOrder = (row) => {
    setEditingWorkOrder(row);
    setModal("workorders");
  };

  const handleSaveWorkOrderEdit = async (record) => {
    try {
      const workOrderId = record.workOrderId ?? record.workorderId ?? record.work_order_id ?? record.id;
      if (workOrderId === undefined || workOrderId === null || workOrderId === "") {
        throw new Error("Missing work order id for update");
      }

      const formData = new FormData();
      const workorderPayload = {
        workOrderId,
        woType: record.woType,
        woNumber: record.woNumber,
        issueDate: record.issueDate,
        projectName: record.projectName || "",
        remarks: record.remarks || "",
        grandTotal: record.grandTotal ? Number(record.grandTotal) : 0,
        site: selectedSite || null,
        authUser: authUser || null
      };
      formData.append("workOrder", new Blob([JSON.stringify(workorderPayload)], { type: "application/json" }));
      if (record.attachmentFile) {
        formData.append("document", record.attachmentFile);
      }

      const updateUrl = API_ENDPOINTS.workorder.updateById(workOrderId);
      const response = await fetchWithAuth(updateUrl, {
        method: "PUT",
        body: formData,
      });

      if (response.ok) {
        await fetchTabData("workorders");
        setEditingWorkOrder(null);
        setModal(null);
        showToast("Work Order updated successfully", "success");
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.message || "Failed to update work order", "error");
      }
    } catch (err) {
      console.error("Error updating work order:", err);
      showToast("Error updating work order", "error");
    }
  };

  const closeInvoicePreview = () => {
    if (invoicePreview.url) {
      URL.revokeObjectURL(invoicePreview.url);
    }
    setInvoicePreview({ open: false, url: "", filename: "", contentType: "" });
  };

  const downloadSalaryReport = async () => {
    try {
      const month = Number(salaryReportMonth);
      const year = Number(salaryReportYear);

      if (!month || month < 1 || month > 12) {
        showToast("Please select a valid salary report month", "error");
        return;
      }
      if (!year || year < 1900 || year > 2100) {
        showToast("Please enter a valid salary report year", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.salaryReport.download}?month=${month}&year=${year}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        console.error("Salary report download failed", response.status, errorText);
        showToast("Failed to download salary report", "error");
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `salary_report_${year}_${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast("Salary report downloaded successfully!");
    } catch (err) {
      console.error("Salary report download error:", err);
      showToast("Error downloading salary report", "error");
    }
  };

  const downloadAttendanceExcel = async () => {
    try {
      const hasStartDate = Boolean(attendanceStartDate);
      const hasEndDate = Boolean(attendanceEndDate);

      if (hasStartDate !== hasEndDate) {
        showToast("Please select both From Date and To Date to download attendance", "error");
        return;
      }

      if (hasStartDate && hasEndDate) {
        if (!selectedSite?.id) {
          showToast("Please select a site to download attendance", "error");
          return;
        }

        let endpoint = `${API_ENDPOINTS.attendance.excel}?fromDate=${encodeURIComponent(attendanceStartDate)}&toDate=${encodeURIComponent(attendanceEndDate)}`;
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;

        const response = await fetchWithAuth(endpoint, { method: "GET" });
        if (!response.ok) {
          showToast("Failed to download attendance records", "error");
          return;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `attendance_${attendanceStartDate}_${attendanceEndDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showToast("Attendance records downloaded successfully!");
        return;
      }

      if (!selectedEmployee) {
        showToast("Please select an employee or date range to download", "error");
        return;
      }

      // Get filtered attendance data
      const filteredData = filtered("attendance");
      if (filteredData.length === 0) {
        showToast("No attendance records to download", "error");
        return;
      }

      // Prepare data for Excel
      const exportData = filteredData.map(record => ({
        "Employee ID": record.employeeId?.empCode || record.empCode || "-",
        "Employee Name": record.employeeId?.name || record.empName || record.name || "-",
        "Date": record.attendanceDate || record.date || "-",
        "Status": record.status || "-",
        "Hours": record.hours || "-",
      }));

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      
      // Set column widths
      ws["!cols"] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 12 },
        { wch: 12 },
        { wch: 8 }
      ];

      // Generate filename
      const empName = selectedEmployee ? selectedEmployee.replace(/\s+/g, '_') : "All";
      const filename = `attendance_${empName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      showToast("Attendance records downloaded successfully!");
    } catch (err) {
      console.error("Attendance download error:", err);
      showToast("Error downloading attendance records", "error");
    }
  };

  const downloadEmployeeAdvanceExcel = async () => {
    try {
      if (!employeeAdvanceStartDate && !employeeAdvanceEndDate) {
        showToast("Please select a date range to download employee advance records", "error");
        return;
      }

      // Get filtered employee advance data
      const filteredData = filtered("employeeadvance");
      if (filteredData.length === 0) {
        showToast("No employee advance records to download", "error");
        return;
      }

      // Prepare data for Excel
      const exportData = filteredData.map(record => ({
        "Employee ID": record.employee?.empCode || record.empCode || "-",
        "Employee Name": record.employee?.name || record.empName || record.name || "-",
        "Advance Date": record.advanceDate || record.date || "-",
        "Amount": record.amount || "-",
        "Payment Type": record.paymentType || "-",
      }));

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Employee Advance");
      
      // Set column widths
      ws["!cols"] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 15 },
        { wch: 20 }
      ];

      // Generate filename
      const filename = `employee_advance_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      showToast("Employee advance records downloaded successfully!");
    } catch (err) {
      console.error("Employee advance download error:", err);
      showToast("Error downloading employee advance records", "error");
    }
  };

  const convertToCSV = (data) => {
    if (data.length === 0) return "";
    
    const headers = ["Employee ID", "Employee Name", "Date", "Status", "Hours"];
    const rows = data.map(r => [
      r.employeeId?.empCode || r.empCode || "-",
      r.employeeId?.name || r.empName || r.name || "-",
      r.attendanceDate || r.date || "-",
      r.status || "-",
      r.hours || "-"
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    return csvContent;
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const downloadPurchaseExcel = async () => {
    try {
      if (!purchaseStartDate && !purchaseEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "purchase";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${purchaseStartDate}&toDate=${purchaseEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `purchases_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Purchase records downloaded successfully!");
      } else {
        showToast("Failed to download purchase records", "error");
      }
    } catch (err) {
      console.error("Purchase download error:", err);
      showToast("Error downloading purchase records", "error");
    }
  };

  const downloadDateFilterExcel = async ({ startDate, endDate, sectionKey, filename, toastMessage }) => {
    try {
      if (!startDate || !endDate) {
        showToast("Please select both From Date and To Date to download", "error");
        return;
      }

      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${startDate}&toDate=${endDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }

      const response = await fetchWithAuth(endpoint, { method: "GET" });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast(toastMessage);
      } else {
        showToast(`Failed to download ${filename.replace(/_/g, " ")} records`, "error");
      }
    } catch (err) {
      console.error(`${filename} download error:`, err);
      showToast(`Error downloading ${filename.replace(/_/g, " ")} records`, "error");
    }
  };

  const downloadSellExcel = async () => {
    try {
      if (!sellStartDate && !sellEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "sell";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${sellStartDate}&toDate=${sellEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `sells_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Sell records downloaded successfully!");
      } else {
        showToast("Failed to download sell records", "error");
      }
    } catch (err) {
      console.error("Sell download error:", err);
      showToast("Error downloading sell records", "error");
    }
  };

  const downloadExpenseExcel = async () => {
    try {
      if (!expenseStartDate && !expenseEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "expense";
      const params = [
        `fromDate=${expenseStartDate}`,
        `toDate=${expenseEndDate}`,
      ];
      if (selectedSite && selectedSite.id) {
        params.push(`siteid=${encodeURIComponent(selectedSite.id)}`);
      }
      if (selectedExpenseType && selectedExpenseType !== "All") {
        params.push(`expenseType=${encodeURIComponent(selectedExpenseType)}`);
      }
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?${params.join("&")}`;
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `expenses_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Expense records downloaded successfully!");
      } else {
        showToast("Failed to download expense records", "error");
      }
    } catch (err) {
      console.error("Expense download error:", err);
      showToast("Error downloading expense records", "error");
    }
  };

  const downloadDieselExcel = async () => {
    try {
      if (!dieselStartDate || !dieselEndDate) {
        showToast("Please select both dates to download diesel records", "error");
        return;
      }
      if (!selectedSite?.id) {
        showToast("Please select a site to download diesel records", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.diesel.excel}?fromDate=${encodeURIComponent(dieselStartDate)}&toDate=${encodeURIComponent(dieselEndDate)}&siteid=${encodeURIComponent(selectedSite.id)}`;
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `diesel_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Diesel records downloaded successfully!");
      } else {
        showToast("Failed to download diesel records", "error");
      }
    } catch (err) {
      console.error("Diesel download error:", err);
      showToast("Error downloading diesel records", "error");
    }
  };

  const downloadPetrolExcel = async () => {
    try {
      if (!petrolStartDate || !petrolEndDate) {
        showToast("Please select both dates to download petrol records", "error");
        return;
      }
      if (!selectedSite?.id) {
        showToast("Please select a site to download petrol records", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.petrol.excel}?fromDate=${encodeURIComponent(petrolStartDate)}&toDate=${encodeURIComponent(petrolEndDate)}&siteid=${encodeURIComponent(selectedSite.id)}`;
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `petrol_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Petrol records downloaded successfully!");
      } else {
        showToast("Failed to download petrol records", "error");
      }
    } catch (err) {
      console.error("Petrol download error:", err);
      showToast("Error downloading petrol records", "error");
    }
  };

  const searchFuelPumpData = async () => {
    try {
      if (!fuelpumpStartDate || !fuelpumpEndDate) {
        showToast("Please select both dates to search", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.fuelpump.filter}?siteId=${encodeURIComponent(selectedSite?.id ?? "")}&fromDate=${encodeURIComponent(fuelpumpStartDate)}&toDate=${encodeURIComponent(fuelpumpEndDate)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });

      if (response.ok) {
        const rows = await response.json();
        setData(prevData => ({ ...prevData, fuelpump: Array.isArray(rows) ? rows : normalizeListRows(rows) }));
        setPagination(prev => ({ ...prev, fuelpump: 0 }));
        showToast("Pump fuel records loaded successfully!");
      } else {
        showToast("Failed to load pump fuel records", "error");
      }
    } catch (err) {
      console.error("Fuel pump search error:", err);
      showToast("Error loading pump fuel records", "error");
    }
  };

  const downloadFuelPumpExcel = async () => {
    try {
      if (!fuelpumpStartDate || !fuelpumpEndDate) {
        showToast("Please select both dates to download pump fuel records", "error");
        return;
      }
      if (!selectedSite?.id) {
        showToast("Please select a site to download pump fuel records", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.fuelpump.excel}?siteid=${encodeURIComponent(selectedSite.id)}&fromDate=${encodeURIComponent(fuelpumpStartDate)}&toDate=${encodeURIComponent(fuelpumpEndDate)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        showToast("Failed to download pump fuel records", "error");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pump_fuel_${fuelpumpStartDate}_${fuelpumpEndDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Pump fuel records downloaded successfully!");
    } catch (err) {
      console.error("Pump fuel download error:", err);
      showToast("Error downloading pump fuel records", "error");
    }
  };

  const downloadLabourCoastExcel = async () => {
    try {
      if (!labourStartDate && !labourEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "labourcoast";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${labourStartDate}&toDate=${labourEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `labour_coast_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Labour coast records downloaded successfully!");
      } else {
        showToast("Failed to download labour coast records", "error");
      }
    } catch (err) {
      console.error("Labour coast download error:", err);
      showToast("Error downloading labour coast records", "error");
    }
  };

  const downloadLabourExcel = async () => {
    try {
      if (!labourFilterStartDate && !labourFilterEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "labour";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${labourFilterStartDate}&toDate=${labourFilterEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `labour_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Labour records downloaded successfully!");
      } else {
        showToast("Failed to download labour records", "error");
      }
    } catch (err) {
      console.error("Labour download error:", err);
      showToast("Error downloading labour records", "error");
    }
  };

  const downloadLabourAdvanceExcel = async () => {
    try {
      if (!labouradvanceStartDate && !labouradvanceEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "labouradvance";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${labouradvanceStartDate}&toDate=${labouradvanceEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `labour_advance_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Labour advance records downloaded successfully!");
      } else {
        showToast("Failed to download labour advance records", "error");
      }
    } catch (err) {
      console.error("Labour advance download error:", err);
      showToast("Error downloading labour advance records", "error");
    }
  };

  const downloadLabourAttendanceExcel = async () => {
    try {
      if (!labourattendanceStartDate && !labourattendanceEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "labourattendance";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${labourattendanceStartDate}&toDate=${labourattendanceEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `labour_attendance_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Labour attendance records downloaded successfully!");
      } else {
        showToast("Failed to download labour attendance records", "error");
      }
    } catch (err) {
      console.error("Labour attendance download error:", err);
      showToast("Error downloading labour attendance records", "error");
    }
  };

  const downloadLabourPaymentExcel = async () => {
    try {
      if (!labourpaymentStartDate && !labourpaymentEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "labourpayment";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${labourpaymentStartDate}&toDate=${labourpaymentEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `labour_payment_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Labour payment records downloaded successfully!");
      } else {
        showToast("Failed to download labour payment records", "error");
      }
    } catch (err) {
      console.error("Labour payment download error:", err);
      showToast("Error downloading labour payment records", "error");
    }
  };

  const downloadConcreteProductionExcel = async () => {
    try {
      if (!concreteproductionStartDate && !concreteproductionEndDate) {
        showToast("Please select a date range to download", "error");
        return;
      }

      const sectionKey = "concreteproduction";
      let endpoint = `${API_ENDPOINTS[sectionKey].dateFilter}?fromDate=${concreteproductionStartDate}&toDate=${concreteproductionEndDate}`;
      if (selectedSite && selectedSite.id) {
        endpoint += `&siteid=${encodeURIComponent(selectedSite.id)}`;
      }
      
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `concrete_production_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("Concrete production records downloaded successfully!");
      } else {
        showToast("Failed to download concrete production records", "error");
      }
    } catch (err) {
      console.error("Concrete production download error:", err);
      showToast("Error downloading concrete production records", "error");
    }
  };

  const searchFuelTransactionData = async () => {
    try {
      if (!fueltransactionStartDate || !fueltransactionEndDate) {
        showToast("Please select both dates to search", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.fueltransaction.filter}?siteid=${encodeURIComponent(selectedSite?.id ?? "")}&fromDate=${encodeURIComponent(fueltransactionStartDate)}&toDate=${encodeURIComponent(fueltransactionEndDate)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });

      if (response.ok) {
        const rows = await response.json();
        setData(prevData => ({ ...prevData, fueltransaction: Array.isArray(rows) ? rows : normalizeListRows(rows) }));
        setPagination(prev => ({ ...prev, fueltransaction: 0 }));
        showToast("Plant fuel records loaded successfully!");
      } else {
        showToast("Failed to load plant fuel records", "error");
      }
    } catch (err) {
      console.error("Fuel transaction search error:", err);
      showToast("Error loading plant fuel records", "error");
    }
  };

  const searchFuelPaymentData = async () => {
    if (!selectedSite?.id) {
      showToast("Please select a site to search fuel payments", "error");
      return;
    }
    if (!fuelpaymentStartDate || !fuelpaymentEndDate) {
      showToast("Please select both dates to search fuel payments", "error");
      return;
    }
    if (fuelpaymentStartDate > fuelpaymentEndDate) {
      showToast("From Date cannot be later than To Date", "error");
      return;
    }

    try {
      const params = new URLSearchParams({
        siteid: String(selectedSite.id),
        fromDate: fuelpaymentStartDate,
        toDate: fuelpaymentEndDate,
      });
      const response = await fetchWithAuth(`${API_ENDPOINTS.fuelpayment.dateRange}?${params.toString()}`, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Fuel payment search failed (${response.status})`);
      }
      const rows = normalizeListRows(await response.json());
      setData(prevData => ({ ...prevData, fuelpayment: rows }));
      setPagination(prev => ({ ...prev, fuelpayment: 0 }));
      showToast("Fuel payment records loaded successfully");
    } catch (error) {
      console.error("Fuel payment search error:", error);
      showToast(error.message || "Failed to search fuel payments", "error");
    }
  };

  const downloadFuelTransactionExcel = async () => {
    try {
      if (!fueltransactionStartDate || !fueltransactionEndDate) {
        showToast("Please select both dates to download plant fuel records", "error");
        return;
      }
      if (!selectedSite?.id) {
        showToast("Please select a site to download plant fuel records", "error");
        return;
      }

      const endpoint = `${API_ENDPOINTS.fueltransaction.excel}?siteid=${encodeURIComponent(selectedSite.id)}&fromDate=${encodeURIComponent(fueltransactionStartDate)}&toDate=${encodeURIComponent(fueltransactionEndDate)}`;
      const response = await fetchWithAuth(endpoint, { method: "GET" });
      if (!response.ok) {
        showToast("Failed to download plant fuel records", "error");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `plant_fuel_${fueltransactionStartDate}_${fueltransactionEndDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Plant fuel records downloaded successfully!");
    } catch (err) {
      console.error("Plant fuel download error:", err);
      showToast("Error downloading plant fuel records", "error");
    }
  };

  // Memoized filtered function to prevent unnecessary recalculation
  const filtered = useCallback((section) => {
    const sectionKey = section === "purchases" ? "purchases" : section === "sells" ? "sells" : section === "expenses" ? "expenses" : section;
    let result = normalizeListRows(data[sectionKey]);
    
    // Ensure result is always an array (count APIs may return objects, but we need arrays for filtering)
    if (!Array.isArray(result)) {
      result = [];
    }

    if (section === "bills" && activeSubTab.bill_group !== "ALL") {
      result = result.filter(record =>
        String(record.billType || record.bill_type || "").toUpperCase() === activeSubTab.bill_group
      );
    }

    if (section === "workorders") {
      result = filterWorkOrdersByType(result, selectedWorkOrderType);
      if (workOrderSearch.trim()) {
        const query = workOrderSearch.trim().toLowerCase();
        result = result.filter(workOrder => [
          workOrder.woNumber,
          workOrder.workOrderNo,
          workOrder.work_order_no,
          workOrder.projectName,
          workOrder.project,
          workOrder.remarks,
        ].some(value => String(value || "").toLowerCase().includes(query)));
      }
    }

    if (section === "employeefnf" && selectedSite?.id && Array.isArray(data.employees)) {
      const siteEmployeeIds = new Set(data.employees.map(employee => String(employee.id ?? employee.empId ?? "")));
      result = result.filter(record => siteEmployeeIds.has(String(record.employee?.id ?? record.employee?.empId ?? record.employeeId?.id ?? record.employeeId ?? "")));
    }

    if (section === "employeefnf") {
      if (employeeFNFSearch.trim()) {
        const query = employeeFNFSearch.trim().toLowerCase();
        result = result.filter(record => {
          const employee = record.employee || record.employeeId || {};
          const searchable = [employee.name, employee.empName, employee.empCode, employee.empcode, employee.id]
            .filter(value => value !== null && value !== undefined)
            .join(" ")
            .toLowerCase();
          return searchable.includes(query);
        });
      }

      if (employeeFNFStartDate || employeeFNFEndDate) {
        result = result.filter(record => {
          const exitDate = String(record.exitDate || record.exit_date || "").slice(0, 10);
          return (!employeeFNFStartDate || exitDate >= employeeFNFStartDate) &&
            (!employeeFNFEndDate || exitDate <= employeeFNFEndDate);
        });
      }
    }

    // Apply search filter to employees
    if (section === "employees" && search) {
      result = result.filter(e => {
        const name = (e.name || "").toLowerCase();
        const empcode = (e.empCode || "").toLowerCase();
        const phone = (e.phone || "").toLowerCase();
        const searchLower = search.toLowerCase();
        return name.includes(searchLower) || empcode.includes(searchLower) || phone.includes(searchLower);
      });
    }

    // Apply attendance-specific filters
    if (section === "attendance") {
      if (selectedEmployee) {
        result = result.filter(r => {
          const empName = r.employeeId?.name || r.empName || r.name || "";
          return empName.toLowerCase().includes(selectedEmployee.toLowerCase());
        });
      }

      // Filter by date range - combined into single pass
      if (attendanceStartDate || attendanceEndDate) {
        result = result.filter(r => {
          const recordDate = r.attendanceDate || r.date || "";
          const afterStart = !attendanceStartDate || recordDate >= attendanceStartDate;
          const beforeEnd = !attendanceEndDate || recordDate <= attendanceEndDate;
          return afterStart && beforeEnd;
        });
      }
    }

    // Apply employee advance-specific date filters - combined into single pass
    if (section === "employeeadvance" && (employeeAdvanceStartDate || employeeAdvanceEndDate)) {
      result = result.filter(r => {
        const recordDate = r.advanceDate || r.date || "";
        const afterStart = !employeeAdvanceStartDate || recordDate >= employeeAdvanceStartDate;
        const beforeEnd = !employeeAdvanceEndDate || recordDate <= employeeAdvanceEndDate;
        return afterStart && beforeEnd;
      });
    }

    if (section === "salary" && (salaryStartDate || salaryEndDate)) {
      result = result.filter(r => {
        const recordDate = String(r.salaryDate || r.salary_date || "").slice(0, 10);
        return (!salaryStartDate || recordDate >= salaryStartDate) &&
          (!salaryEndDate || recordDate <= salaryEndDate);
      });
    }

    // Apply purchase-specific date filters - combined into single pass
    if (section === "purchases" && (purchaseStartDate || purchaseEndDate)) {
      result = result.filter(r => {
        const recordDate = r.purchaseDate || r.date || "";
        const afterStart = !purchaseStartDate || recordDate >= purchaseStartDate;
        const beforeEnd = !purchaseEndDate || recordDate <= purchaseEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply bill-specific date filters - combined into single pass
    if (section === "bills" && (billStartDate || billEndDate)) {
      result = result.filter(r => {
        const recordDate = r.billDate || r.date || "";
        const afterStart = !billStartDate || recordDate >= billStartDate;
        const beforeEnd = !billEndDate || recordDate <= billEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply bill number search locally when billSearch is active
    if (section === "bills" && billSearch) {
      const searchLower = billSearch.toLowerCase();
      result = result.filter(r => {
        const billNo = (r.billNo || r.bill_number || "").toString().toLowerCase();
        return billNo.includes(searchLower);
      });
    }

    // Apply management expense date filters - combined into single pass
    if (section === "managementExpense" && (managementExpenseStartDate || managementExpenseEndDate)) {
      result = result.filter(r => {
        const recordDate = r.expenseDate || r.date || "";
        const afterStart = !managementExpenseStartDate || recordDate >= managementExpenseStartDate;
        const beforeEnd = !managementExpenseEndDate || recordDate <= managementExpenseEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply sell-specific date filters - combined into single pass
    if (section === "sells" && (sellStartDate || sellEndDate)) {
      result = result.filter(r => {
        const recordDate = r.sellDate || r.date || "";
        const afterStart = !sellStartDate || recordDate >= sellStartDate;
        const beforeEnd = !sellEndDate || recordDate <= sellEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply expense-specific date filters - combined into single pass
    if (section === "expenses" && (expenseStartDate || expenseEndDate)) {
      result = result.filter(r => {
        const recordDate = r.expenseDate || r.date || "";
        const afterStart = !expenseStartDate || recordDate >= expenseStartDate;
        const beforeEnd = !expenseEndDate || recordDate <= expenseEndDate;
        return afterStart && beforeEnd;
      });
    }

    if (section === "expenses" && selectedExpenseType && selectedExpenseType !== "All") {
      const filterType = selectedExpenseType.toLowerCase();
      result = result.filter(r => {
        const expenseTypeValue = (r.expenseType || r.type || r.category || "").toString().toLowerCase();
        return expenseTypeValue === filterType;
      });
    }

    // Apply diesel-specific date filters - combined into single pass
    if (section === "diesel" && (dieselStartDate || dieselEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || "";
        const afterStart = !dieselStartDate || recordDate >= dieselStartDate;
        const beforeEnd = !dieselEndDate || recordDate <= dieselEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply petrol-specific date filters - combined into single pass
    if (section === "petrol" && (petrolStartDate || petrolEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || "";
        const afterStart = !petrolStartDate || recordDate >= petrolStartDate;
        const beforeEnd = !petrolEndDate || recordDate <= petrolEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply pump fuel-specific date filters - combined into single pass
    if (section === "fuelpump" && (fuelpumpStartDate || fuelpumpEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || r.fuelDate || "";
        const afterStart = !fuelpumpStartDate || recordDate >= fuelpumpStartDate;
        const beforeEnd = !fuelpumpEndDate || recordDate <= fuelpumpEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply labour coast-specific date filters - combined into single pass
    if (section === "labourcoast" && (labourStartDate || labourEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || "";
        const afterStart = !labourStartDate || recordDate >= labourStartDate;
        const beforeEnd = !labourEndDate || recordDate <= labourEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply labour search filter
    if (section === "labour" && labourSearch) {
      const searchLower = labourSearch.toLowerCase();
      result = result.filter(r => {
        const labourName = (r.labourName || "").toLowerCase();
        const labourCode = (r.labourCode || r.labour_code || "").toLowerCase();
        const labourType = (r.labourType || r.labour_type || "").toLowerCase();
        const dailyWage = (r.dailyWage || "").toString();
        return labourName.includes(searchLower) || labourCode.includes(searchLower) || labourType.includes(searchLower) || dailyWage.includes(searchLower);
      });
    }

    // Apply labour advance-specific date filters
    if (section === "labouradvance" && (labouradvanceStartDate || labouradvanceEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || "";
        const afterStart = !labouradvanceStartDate || recordDate >= labouradvanceStartDate;
        const beforeEnd = !labouradvanceEndDate || recordDate <= labouradvanceEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply labour attendance-specific date filters
    if (section === "labourattendance" && (labourattendanceStartDate || labourattendanceEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || "";
        const afterStart = !labourattendanceStartDate || recordDate >= labourattendanceStartDate;
        const beforeEnd = !labourattendanceEndDate || recordDate <= labourattendanceEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Apply labour payment-specific date filters
    if (section === "labourpayment" && (labourpaymentStartDate || labourpaymentEndDate)) {
      result = result.filter(r => {
        const recordDate = r.date || "";
        const afterStart = !labourpaymentStartDate || recordDate >= labourpaymentStartDate;
        const beforeEnd = !labourpaymentEndDate || recordDate <= labourpaymentEndDate;
        return afterStart && beforeEnd;
      });
    }

    // Sort labour by labour code
    if (section === "labour" && Array.isArray(result)) {
      result = result.sort((a, b) => {
        const codeA = (a.labourCode || a.labour?.labourCode || "").toString().toLowerCase();
        const codeB = (b.labourCode || b.labour?.labourCode || "").toString().toLowerCase();
        return codeA.localeCompare(codeB);
      });
    }

    if (section === "bills") {
      result = [...result].sort((a, b) => {
        const dateA = Date.parse(a.billDate || a.bill_date || a.date || "");
        const dateB = Date.parse(b.billDate || b.bill_date || b.date || "");
        return (Number.isFinite(dateB) ? dateB : 0) - (Number.isFinite(dateA) ? dateA : 0);
      });
    }

    if (section === "fuelpump" || section === "fueltransaction" || section === "fuelpayment") {
      result = [...result].sort((a, b) => {
        const getFuelTimestamp = record => {
          const recordDate = record.fuelDate
            || record.fuel_date
            || record.transactionDate
            || record.transaction_date
            || record.date
            || "";
          const timestamp = Date.parse(recordDate);
          return Number.isFinite(timestamp) ? timestamp : 0;
        };
        return getFuelTimestamp(b) - getFuelTimestamp(a);
      });
    }

    if (section === "concreteproduction") {
      result = [...result].sort((a, b) => {
        const getProductionTimestamp = record => {
          const date = record.productionDate || record.production_date || record.date || "";
          const time = record.productionTime || record.production_time || "";
          const timestamp = Date.parse(time ? `${date}T${time}` : date);
          return Number.isFinite(timestamp) ? timestamp : 0;
        };
        return getProductionTimestamp(b) - getProductionTimestamp(a);
      });
    }

    return result;
  }, [
    data, search, selectedSite?.id, selectedEmployee, attendanceStartDate, attendanceEndDate,
    employeeFNFSearch, employeeFNFStartDate, employeeFNFEndDate,
    purchaseStartDate, purchaseEndDate, billStartDate, billEndDate, sellStartDate, sellEndDate,
    expenseStartDate, expenseEndDate, managementExpenseStartDate, managementExpenseEndDate, selectedExpenseType, selectedWorkOrderType, workOrderSearch, dieselStartDate, dieselEndDate,
    petrolStartDate, petrolEndDate, labourStartDate, labourEndDate,
    labourSearch, labouradvanceStartDate, labouradvanceEndDate, salaryStartDate, salaryEndDate,
    labourattendanceStartDate, labourattendanceEndDate, labourpaymentStartDate, labourpaymentEndDate,
    activeSubTab.bill_group
  ]);

  // Get unique dates from dataset
  const getUniqueDates = (section) => {
    const dataMap = {
      attendance: "attendanceDate",
      purchases: "purchaseDate",
      sells: "sellDate",
      expenses: "expenseDate",
      managementExpense: "expenseDate",
    };
    const dateField = dataMap[section];
    if (!dateField) return [];
    
    let filteredData = filtered(section);
    // Ensure filteredData is an array
    if (!Array.isArray(filteredData)) {
      filteredData = [];
    }
    
    const uniqueDates = [...new Set(filteredData.map(r => r[dateField] || ""))].filter(Boolean).sort().reverse();
    return uniqueDates;
  };

  // Memoized pagination data calculation to prevent unnecessary recalculation
  const getPaginatedData = useCallback((section) => {
    const itemsPerPage = 10;
    const currentPage = pagination[section] || 0;
    let allData = filtered(section);
    
    // Ensure allData is an array before calling slice
    if (!Array.isArray(allData)) {
      allData = [];
    }
    if (section === "salary") {
      allData = sortSalaryRowsLatestFirst(allData);
    }
    
    const startIdx = currentPage * itemsPerPage;
    return allData.slice(startIdx, startIdx + itemsPerPage);
  }, [pagination, filtered]);

  // Get total pages - memoized
  const getTotalPages = useCallback((section) => {
    const itemsPerPage = 10;
    let filteredData = filtered(section);
    
    // Ensure filteredData is an array
    if (!Array.isArray(filteredData)) {
      filteredData = [];
    }
    
    const filteredDataLength = filteredData.length;
    return Math.ceil(filteredDataLength / itemsPerPage) || 1;
  }, [filtered]);

  // Handle pagination
  const handlePageChange = (section, newPage) => {
    const totalPages = getTotalPages(section);
    if (newPage >= 0 && newPage < totalPages) {
      setPagination(prev => ({ ...prev, [section]: newPage }));
    }
  };

  const TABS = [
    { 
      key: "employee_group", 
      label: "Employees", 
      icon: <FiUsers />,
      children: [
        { key: "employees", label: "Details" },
        { key: "attendance", label: "Attendance" },
        { key: "employeefnf", label: "Employee FNF" },
        { key: "employeeadvance", label: "Advance Salary" },
        { key: "salary", label: "Salary" },
        { key: "salaryreport", label: "Salary Report" }
      ]
    },
    { 
      key: "labour_group", 
      label: "Contractor Workforce", 
      icon: <FiTag />,
      children: [
        { key: "contractor", label: "Contractor" },
        { key: "contractorwork", label: "Contractor Work Details" },
        { key: "contractorpayment", label: "Contractor Payment" }
      ]
    },
    { 
      key: "plantlabour_group", 
      label: "Plant Labours", 
      icon: <FiUsers />,
      children: [
        { key: "labour", label: "Details" },
        { key: "labourattendance", label: "Attendance" },
        { key: "labourpayment", label: "Payment" }
      ]
    },
    { 
      key: "fuel_group", 
      label: "Fuel", 
      icon: <FiTag />,
      children: [
        { key: "fueltransaction", label: "Plant Fuel Management" },
        { key: "fuelpump", label: "Pump Fuel Management" },
        { key: "fuelpayment", label: "Fuel Payment" },
      ]
    },
    { 
      key: "concreteproduction_group", 
      label: "Concrete Production", 
      icon: <FiTag />,
      children: [
        { key: "concreteproduction", label: "Concrete Production" }
      ]
    },
    { 
      key: "cashflow_group", 
      label: "Cash Flow", 
      icon: <FiShoppingCart />,
      children: [
        { key: "purchases", label: "Purchase" },
        { key: "sells", label: "Sell" }
      ]
    },
  ];

  const COL_MAP = {
    employees: ["Empcode", "Name", "Role", "Phone", "Active Status", "Site Name"],
    attendance: ["ID", "Employee", "Attendance Date", "Status"],
    employeefnf: ["Employee Code", "Employee", "Exit Date", "Amount"],
    employeeadvance: ["Employee Code", "Employee", "Advance Date", "Amount", "Payment Type"],
    salary: ["Salary Date", "Employee", "Paid By", "Salary Amount", "Previous Pending Amount", "Remarks"],
    purchases: ["Purchase Number", "Party Name", "Purchase Date", "Payment Status", "Receiver Name", "Uploaded By"],
    bills: ["Bill No", "Bill Type", "Bill Date", "Amount", "Amount Received", "Remaining Amount", "Description"],
    managementExpense: ["Expense Date", "Amount", "From Employee", "To Employee"],
    sells: ["Sell No", "Bill Type", "Payment Status", "Sell Date", "Uploaded By"],
    diesel: ["Vehicle Number", "Diesel Date", "Amount", "Quantity", "Received By", "Approved By"],
    petrol: ["Vehicle Number", "Petrol Date", "Amount", "Quantity", "Received By", "Approved By"],
    fuelpump: ["Fuel Type", "Vehicle Number", "Date", "Amount", "Quantity", "Received By", "Approved By"],
    fueltransaction: ["Fuel Type", "Transaction Type", "Quantity", "Amount", "Date", "Vehicle Name", "Operator Name"],
    fuelpayment: ["Fuel Date", "Amount", "Paid By", "Paid To", "Remarks"],
    labourcoast: ["Labour Name", "Date", "Hours", "Rate", "Amount"],
    labour: ["Labour Name", "Labour Code", "Labour Type", "Category", "Daily Wage", "Active Status"],
    contractor: ["Contractor Code", "Contractor Name", "Contractor Type", "Mobile Number", "Joining Date", "Aadhar Number", "Active Status"],
    contractorwork: ["Contractor Code", "Contractor Name", "Work Date", "Number of Labours", "Labour Rate", "Number of Masons", "Mason Rate", "Other Charges"],
    contractorpayment: ["Contractor Code", "Contractor Name", "Paid By", "Amount", "Payment Date"],
    labouradvance: ["Labour Code", "Advance Date", "Amount", "Daily Wage"],
    labourattendance: ["Labour Code", "Attendance Date", "Status", "Site"],
    labourpayment: ["Labour Code", "Amount", "Date", "Payment Type"],
    expenses: ["Spent By", "Expense Type", "Amount", "Expense Date", "Uploaded By", "Remarks"],
    concreteproduction: ["Production Date", "Production Time", "Transit Mixer Number", "Grade of Concrete", "Quantity", "Dispatched To", "Chainage No", "Operator Sign", "Approved By"],
    workorders: ["WO Type", "WO Number", "Issue Date", "Project", "Site Name", "Grand Total", "Remarks"],
  };

  // DieselPetroForm is similar to SellForm but with Fuel Type
  function LabourCoastForm({ onSave, onClose }) {
    const [f, setF] = useState({ labourName: "", date: "", hours: "", rate: "", amount: "" });
    useEffect(() => {
      // Auto-calculate amount if hours and rate are filled
      if (f.hours && f.rate) {
        setF(prev => ({ ...prev, amount: Number(f.hours) * Number(f.rate) }));
      }
    }, [f.hours, f.rate]);
    return (
      <div>
        <div className="form-group">
          <label>Labour Name</label>
          <input value={f.labourName} onChange={e => setF({ ...f, labourName: e.target.value })} placeholder="e.g. Ram Lal" />
        </div>
        <div className="form-group">
          <label>Date</label>
          <input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Hours</label>
          <input type="number" value={f.hours} onChange={e => setF({ ...f, hours: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Rate (₹/hr)</label>
          <input type="number" value={f.rate} onChange={e => setF({ ...f, rate: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Amount (₹)</label>
          <input type="number" value={f.amount} readOnly placeholder="0" />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => f.labourName && f.date && f.hours && f.rate && onSave(f)}>Save Labour Coast</button>
        </div>
      </div>
    );
  }

  function LabourForm({ onSave, onClose, initialData }) {
    const [f, setF] = useState({ 
      id: initialData?.id || "",
      labourJoiningDate: initialData?.labourJoiningDate || initialData?.joiningDate || "", 
      siteId: initialData?.siteId || selectedSite?.id || "", 
      labourName: initialData?.labourName || "", 
      category: initialData?.category || "Plant", 
      labourType: initialData?.labourType || "", 
      dailyWage: initialData?.dailyWage || "", 
      remark: initialData?.remark || initialData?.remarks || "" 
    });
    
    useEffect(() => {
      setF({
        id: initialData?.id || "",
        labourJoiningDate: initialData?.labourJoiningDate || initialData?.joiningDate || "", 
        siteId: initialData?.siteId || selectedSite?.id || "", 
        labourName: initialData?.labourName || "", 
        category: initialData?.category || "Plant", 
        labourType: initialData?.labourType || "", 
        dailyWage: initialData?.dailyWage || "", 
        remark: initialData?.remark || initialData?.remarks || "" 
      });
    }, [initialData]);
    
    const labourTypeOptions = ["Skilled", "Semi-Skilled", "Unskilled"];
    
    return (
      <div>
        <div className="form-group">
          <label>📅 Labour Joining Date</label>
          <input type="date" value={f.labourJoiningDate} onChange={e => setF({ ...f, labourJoiningDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>🏢 Site</label>
          <div style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", backgroundColor: "#f8fafc", color: "#334155", fontSize: "14px", fontWeight: "500" }}>
            {selectedSite?.siteName || "No site selected"}
          </div>
        </div>
        <div className="form-group">
          <label>🧑 Labour Name</label>
          <input value={f.labourName} onChange={e => setF({ ...f, labourName: e.target.value })} placeholder="e.g. Ram Kumar" />
        </div>
        <div className="form-group">
          <label>🗂️ Category</label>
          <div style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", backgroundColor: "#f8fafc", color: "#334155", fontSize: "14px", fontWeight: "500" }}>
            Plant
          </div>
        </div>
        <div className="form-group">
          <label>📊 Labour Type</label>
          <select value={f.labourType} onChange={e => setF({ ...f, labourType: e.target.value })}>
            <option value="">Select Labour Type</option>
            {labourTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>💰 Daily Wage (₹)</label>
          <input type="number" value={f.dailyWage} onChange={e => setF({ ...f, dailyWage: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>📝 Remark</label>
          <textarea value={f.remark} onChange={e => setF({ ...f, remark: e.target.value })} placeholder="Add any remarks..." style={{ minHeight: "80px" }} />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!(f.labourName && f.labourJoiningDate && f.labourType && f.dailyWage)} onClick={() => f.labourName && f.labourJoiningDate && f.labourType && f.dailyWage && onSave(f)}>{initialData ? "Update Labour" : "Save Labour"}</button>
        </div>
      </div>
    );
  }

  function ContractorForm({ initialData, onSave, onClose, selectedSite }) {
    const [f, setF] = useState({
      contractorId: initialData?.id || initialData?.contractorId || initialData?.contractorID || initialData?.contractor_id || "",
      contractorCode: initialData?.contractorCode || initialData?.contractor_code || initialData?.code || "",
      contractorName: initialData?.contractorName || initialData?.name || initialData?.contractor_name || "",
      contractorType: initialData?.contractorType || initialData?.contractor_type || "DAILY_BASIS",
      mobile: initialData?.mobile || initialData?.mobileNumber || initialData?.mobile_number || "",
      email: initialData?.email || "",
      gstNumber: initialData?.gstNumber || initialData?.gst_number || "",
      panNumber: initialData?.panNumber || initialData?.pan_number || "",
      joiningDate: initialData?.joiningDate || initialData?.joinDate || "",
      description: initialData?.description || "",
      village: initialData?.address?.village || initialData?.village || "",
      post: initialData?.address?.post || initialData?.post || "",
      district: initialData?.address?.district || initialData?.district || "",
      state: initialData?.address?.state || initialData?.state || "",
      pin: initialData?.address?.pin || initialData?.pin || "",
      aadharNumber: initialData?.aadhar_Number || initialData?.aadharNumber || "",
      aadharFile: null,
      panFile: null,
      bankFile: null,
      siteId: initialData?.site?.id || initialData?.siteId || selectedSite?.id || ""
    });

    useEffect(() => {
      setF(prev => ({
        ...prev,
        siteId: initialData?.site?.id || initialData?.siteId || selectedSite?.id || prev.siteId || ""
      }));
    }, [initialData, selectedSite?.id]);

    return (
      <div>
        <div className="form-grid">
          <div className="field">
            <label>🏢 Current site</label>
            <input
              value={selectedSite?.name || selectedSite?.siteName || selectedSite?.site_name || "No site selected"}
              readOnly
              style={{ backgroundColor: "#f1f5f9", cursor: "default" }}
            />
            <input type="hidden" value={f.siteId} />
          </div>
          {initialData && (
            <div className="field">
              <label>🆔 Contractor Code</label>
              <input value={f.contractorCode} readOnly />
            </div>
          )}
          <div className="field">
            <label>👷 Contractor Name</label>
            <input value={f.contractorName} onChange={e => setF({ ...f, contractorName: e.target.value })} placeholder="e.g. Ravi Contractor" />
          </div>
          <div className="field">
            <label>🧩 Contractor Type</label>
            <select value={f.contractorType} onChange={e => setF({ ...f, contractorType: e.target.value })}>
              <option value="DAILY_BASIS">Daily Basis</option>
              <option value="WORK_BASIS">Work Basis</option>
            </select>
          </div>
          <div className="field">
            <label>📱 Mobile</label>
            <input value={f.mobile} onChange={e => setF({ ...f, mobile: e.target.value })} placeholder="e.g. 9876543210" />
          </div>
          <div className="field">
            <label>✉️ Email</label>
            <input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="e.g. contractor@example.com" />
          </div>
          <div className="field">
            <label>🧾 GST Number</label>
            <input value={f.gstNumber} onChange={e => setF({ ...f, gstNumber: e.target.value })} placeholder="e.g. 22AAAAA0000A1Z5" />
          </div>
          <div className="field">
            <label>🆔 PAN Number</label>
            <input value={f.panNumber} onChange={e => setF({ ...f, panNumber: e.target.value })} placeholder="e.g. ABCDE1234F" />
          </div>
          <div className="field">
            <label>📅 Joining Date</label>
            <input type="date" value={f.joiningDate} onChange={e => setF({ ...f, joiningDate: e.target.value })} />
          </div>
          <div className="field">
            <label>🪪 Aadhar Number</label>
            <input value={f.aadharNumber} onChange={e => setF({ ...f, aadharNumber: e.target.value })} placeholder="12-digit Aadhar number" />
          </div>
          <div className="field">
            <label>📎 Aadhar document {!initialData && "*"}</label>
            <input type="file" accept="application/pdf,image/*" required={!initialData} onChange={e => setF({ ...f, aadharFile: e.target.files?.[0] || null })} />
            {f.aadharFile && <div style={{ fontSize: 13, color: "#475569" }}>Selected: {f.aadharFile.name}</div>}
          </div>
          <div className="field">
            <label>📎 PAN document</label>
            <input type="file" accept="application/pdf,image/*" onChange={e => setF({ ...f, panFile: e.target.files?.[0] || null })} />
            {f.panFile && <div style={{ fontSize: 13, color: "#475569" }}>Selected: {f.panFile.name}</div>}
          </div>
          <div className="field">
            <label>📎 Bank document {!initialData && "*"}</label>
            <input type="file" accept="application/pdf,image/*" required={!initialData} onChange={e => setF({ ...f, bankFile: e.target.files?.[0] || null })} />
            {f.bankFile && <div style={{ fontSize: 13, color: "#475569" }}>Selected: {f.bankFile.name}</div>}
          </div>
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>📝 Description</label>
            <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Add description" rows="3" />
          </div>
        </div>

        <div className="address-card">
          <h4 className="address-title">📍 Address Details</h4>
          <div className="form-grid address-grid">
            <div className="field">
              <label>🏡 Village</label>
              <input value={f.village} onChange={e => setF({ ...f, village: e.target.value })} placeholder="Village" />
            </div>
            <div className="field">
              <label>🏠 Post</label>
              <input value={f.post} onChange={e => setF({ ...f, post: e.target.value })} placeholder="Post" />
            </div>
            <div className="field">
              <label>🗺️ District</label>
              <input value={f.district} onChange={e => setF({ ...f, district: e.target.value })} placeholder="District" />
            </div>
            <div className="field">
              <label>🌍 State</label>
              <select value={f.state} onChange={e => setF({ ...f, state: e.target.value })}>
            <option value="">Select State</option>
            <optgroup label="States">
              <option value="Andhra Pradesh">Andhra Pradesh</option>
              <option value="Arunachal Pradesh">Arunachal Pradesh</option>
              <option value="Assam">Assam</option>
              <option value="Bihar">Bihar</option>
              <option value="Chhattisgarh">Chhattisgarh</option>
              <option value="Goa">Goa</option>
              <option value="Gujarat">Gujarat</option>
              <option value="Haryana">Haryana</option>
              <option value="Himachal Pradesh">Himachal Pradesh</option>
              <option value="Jharkhand">Jharkhand</option>
              <option value="Karnataka">Karnataka</option>
              <option value="Kerala">Kerala</option>
              <option value="Madhya Pradesh">Madhya Pradesh</option>
              <option value="Maharashtra">Maharashtra</option>
              <option value="Manipur">Manipur</option>
              <option value="Meghalaya">Meghalaya</option>
              <option value="Mizoram">Mizoram</option>
              <option value="Nagaland">Nagaland</option>
              <option value="Odisha">Odisha</option>
              <option value="Punjab">Punjab</option>
              <option value="Rajasthan">Rajasthan</option>
              <option value="Sikkim">Sikkim</option>
              <option value="Tamil Nadu">Tamil Nadu</option>
              <option value="Telangana">Telangana</option>
              <option value="Tripura">Tripura</option>
              <option value="Uttar Pradesh">Uttar Pradesh</option>
              <option value="Uttarakhand">Uttarakhand</option>
              <option value="West Bengal">West Bengal</option>
            </optgroup>
            <optgroup label="Union Territories">
              <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
              <option value="Chandigarh">Chandigarh</option>
              <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
              <option value="Lakshadweep">Lakshadweep</option>
              <option value="Delhi">Delhi</option>
              <option value="Puducherry">Puducherry</option>
              <option value="Ladakh">Ladakh</option>
              <option value="Jammu and Kashmir">Jammu and Kashmir</option>
            </optgroup>
              </select>
            </div>
            <div className="field">
              <label>📮 Pin</label>
              <input value={f.pin} onChange={e => setF({ ...f, pin: e.target.value })} placeholder="Pin" />
            </div>
          </div>
        </div>

        <div className="actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!(f.contractorName && f.contractorType && f.mobile && f.siteId)}
            onClick={() => {
              if (!(f.contractorName && f.contractorType && f.mobile && f.siteId)) return;
              if (!initialData && !f.aadharFile) {
                window.alert("Please upload Aadhar document");
                return;
              }
              if (!initialData && !f.bankFile) {
                window.alert("Please upload Bank document");
                return;
              }
              onSave(f);
            }}
          >
            {initialData ? "Update Contractor" : "Save Contractor"}
          </button>
        </div>
      </div>
    );
  }

  function ContractorWorkForm({ onSave, onClose, selectedSite, initialData }) {
    const [f, setF] = useState({
      id: initialData?.id ?? initialData?.contractorWorkId ?? initialData?.workId ?? null,
      contractorId: (initialData?.contractorId ?? initialData?.contractorObject?.id ?? initialData?.contractor?.id ?? initialData?.contractor) || "",
      contractorName: initialData?.contractorName || initialData?.contractorObject?.contractorName || initialData?.contractorObject?.name || initialData?.contractor?.contractorName || initialData?.contractor?.name || "",
      contractorObject: initialData?.contractorObject || initialData?.contractor || null,
      mobile: initialData?.mobile || initialData?.mobileNumber || initialData?.contractorObject?.mobile || "",
      village: initialData?.village || "",
      post: initialData?.post || "",
      district: initialData?.district || "",
      state: initialData?.state || "",
      pin: initialData?.pin || "",
      aadharNumber: initialData?.aadharNumber || initialData?.aadhar_Number || "",
      workDate: initialData?.workDate || "",
      numberOfLabours: initialData?.numberOfLabours ?? initialData?.labourCount ?? "",
      labourRate: initialData?.labourRate ?? "",
      numberOfMasons: initialData?.numberOfMasons ?? "",
      masonRate: initialData?.masonRate ?? "",
      labourAmount: initialData?.labourAmount ?? "",
      masonAmount: initialData?.masonAmount ?? "",
      otherCharges: initialData?.otherCharges ?? "",
      description: initialData?.description || "",
      siteId: (initialData?.siteId ?? initialData?.site?.id ?? selectedSite?.id) || ""
    });
    const [contractorSearch, setContractorSearch] = useState("");
    const [filteredContractors, setFilteredContractors] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    useEffect(() => {
      setF(prev => ({ ...prev, siteId: selectedSite?.id || prev.siteId || "" }));
    }, [selectedSite?.id]);

    useEffect(() => {
      const searchContractors = async () => {
        if (!contractorSearch.trim()) {
          setFilteredContractors([]);
          return;
        }

        try {
          setSearchLoading(true);
          const searchQuery = encodeURIComponent(contractorSearch);
          let url = `${API_ENDPOINTS.contractor.search}?code=${searchQuery}`;
          if (selectedSite && selectedSite.id) {
            url += `&siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          const response = await fetchWithAuth(url, { method: "GET" });
          if (response.ok) {
            const results = await response.json();
            const list = Array.isArray(results) ? results : (Array.isArray(results.data) ? results.data : []);
            setFilteredContractors(list);
          } else {
            setFilteredContractors([]);
          }
        } catch (err) {
          console.error("Contractor search error:", err);
          setFilteredContractors([]);
        } finally {
          setSearchLoading(false);
        }
      };

      const timer = setTimeout(() => {
        searchContractors();
      }, 300);

      return () => clearTimeout(timer);
    }, [contractorSearch, selectedSite?.id]);

    useEffect(() => {
      if (f.numberOfLabours && f.labourRate) {
        const labourAmountValue = Number(f.numberOfLabours || 0) * Number(f.labourRate || 0);
        setF(prev => prev.labourAmount === String(labourAmountValue) ? prev : { ...prev, labourAmount: String(labourAmountValue) });
      } else if (!f.numberOfLabours && !f.labourRate && f.labourAmount) {
        setF(prev => ({ ...prev, labourAmount: "" }));
      }
    }, [f.numberOfLabours, f.labourRate]);

    useEffect(() => {
      if (f.numberOfMasons && f.masonRate) {
        const masonAmountValue = Number(f.numberOfMasons || 0) * Number(f.masonRate || 0);
        setF(prev => prev.masonAmount === String(masonAmountValue) ? prev : { ...prev, masonAmount: String(masonAmountValue) });
      } else if (!f.numberOfMasons && !f.masonRate && f.masonAmount) {
        setF(prev => ({ ...prev, masonAmount: "" }));
      }
    }, [f.numberOfMasons, f.masonRate]);

    return (
      <div>
        <div className="form-grid">
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>🏢 Site</label>
            <div style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: "6px", backgroundColor: "#f8fafc", color: "#334155", fontSize: "13px", fontWeight: "500", minHeight: "34px", display: "flex", alignItems: "center" }}>
              {selectedSite?.siteName || selectedSite?.name || "No site selected"}
            </div>
            <input type="hidden" value={f.siteId} />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Search Contractor *</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={contractorSearch}
                onChange={e => setContractorSearch(e.target.value)}
                placeholder={f.contractorId ? `✓ ${f.contractorName}` : "Search by contractor name or mobile..."}
                style={{
                  padding: "8px 12px",
                  border: f.contractorId ? "2px solid #0284c7" : "1px solid #cbd5e1",
                  borderRadius: "4px",
                  fontSize: "14px",
                  width: "100%",
                  paddingRight: searchLoading ? "36px" : (f.contractorId ? "80px" : "12px"),
                  background: f.contractorId ? "#dbeafe" : "#fff",
                  fontWeight: f.contractorId ? "600" : "400"
                }}
              />
              {searchLoading && (
                <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "14px" }}>🔄</span>
              )}
              {f.contractorId && !contractorSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setF({ ...f, contractorId: "", contractorName: "", contractorObject: null, mobile: "" });
                    setContractorSearch("");
                  }}
                  style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "#ef4444", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "11px", fontWeight: "600" }}
                >
                  ✕ Change
                </button>
              )}
            </div>
            <p style={{ fontSize: "11px", color: "#64748b", margin: "4px 0 0 0" }}>
              💡 {searchLoading ? "🔄 Searching..." : f.contractorId ? `✓ Selected: ${f.contractorName}` : `Results: ${contractorSearch ? `${filteredContractors.length} contractor(s)` : "Start typing"}`}
            </p>
          </div>

          {contractorSearch && !f.contractorId && filteredContractors.length > 0 && (
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <div style={{ marginTop: "8px", maxHeight: "220px", overflowY: "auto", padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#f8fafc" }}>
                {filteredContractors.map(contractor => (
                  <div
                    key={contractor.id || contractor.contractorId || contractor.contractorName}
                    onClick={() => {
                      setF({
                        ...f,
                        contractorId: contractor.id || contractor.contractorId || contractor.contractorName,
                        contractorName: contractor.contractorName || contractor.name || contractor.contractor || "",
                        contractorObject: contractor,
                        mobile: contractor.mobile || contractor.phone || "",
                        village: contractor.village || "",
                        post: contractor.post || "",
                        district: contractor.district || "",
                        state: contractor.state || "",
                        pin: contractor.pin || "",
                        aadharNumber: contractor.aadharNumber || contractor.aadhar_Number || ""
                      });
                      setContractorSearch("");
                    }}
                    style={{ padding: "10px 12px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer", marginBottom: "8px", fontSize: "13px" }}
                  >
                    <div style={{ fontWeight: "600" }}>{contractor.contractorName || contractor.name || contractor.contractor}</div>
                    {contractor.mobile && <div style={{ fontSize: "12px", marginTop: "2px", opacity: 0.8 }}>📱 {contractor.mobile}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {contractorSearch && !f.contractorId && filteredContractors.length === 0 && (
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <div style={{ marginTop: "8px", padding: "12px", textAlign: "center", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#991b1b", fontSize: "13px" }}>
                ❌ No contractors found
              </div>
            </div>
          )}
        </div>

        <div className="address-card">
          <h4 className="address-title">Work Details</h4>
          <div className="form-grid address-grid">
            <div className="field">
              <label>Work Date</label>
              <input type="date" value={f.workDate} onChange={e => setF({ ...f, workDate: e.target.value })} />
            </div>
            <div className="field">
              <label>Number of Labours</label>
              <input type="number" value={f.numberOfLabours} onChange={e => setF({ ...f, numberOfLabours: e.target.value })} placeholder="0" />
            </div>
            <div className="field">
              <label>Labour Rate</label>
              <input type="number" value={f.labourRate} onChange={e => setF({ ...f, labourRate: e.target.value })} placeholder="0" />
            </div>
            <div className="field">
              <label>Number of Masons</label>
              <input type="number" value={f.numberOfMasons} onChange={e => setF({ ...f, numberOfMasons: e.target.value })} placeholder="0" />
            </div>
            <div className="field">
              <label>Mason Rate</label>
              <input type="number" value={f.masonRate} onChange={e => setF({ ...f, masonRate: e.target.value })} placeholder="0" />
            </div>
            <div className="field">
              <label>Labour Amount</label>
              <input type="number" value={f.labourAmount} readOnly placeholder="0" />
            </div>
            <div className="field">
              <label>Mason Amount</label>
              <input type="number" value={f.masonAmount} readOnly placeholder="0" />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Other Charges</label>
              <input type="number" value={f.otherCharges} onChange={e => setF({ ...f, otherCharges: e.target.value })} placeholder="0" />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Description</label>
              <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Add description" rows="3" />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={!(f.contractorId && f.siteId)} onClick={() => f.contractorId && f.siteId && onSave(f)}>{initialData ? "Update Contractor Work Details" : "Save Contractor Work Details"}</button>
        </div>
      </div>
    );
  }

  function ContractorPaymentForm({ onSave, onClose, selectedSite, initialData }) {
    const existingPaidByEmployee = initialData?.paidBy || initialData?.paidByEmployee || null;
    const [f, setF] = useState({
      id: initialData?.id ?? initialData?.contractorPaymentId ?? initialData?.paymentId ?? null,
      contractorId: (initialData?.contractorId ?? initialData?.contractorObject?.id ?? initialData?.contractor?.id ?? initialData?.contractor) || "",
      contractorName: initialData?.contractorName || initialData?.contractorObject?.contractorName || initialData?.contractorObject?.name || initialData?.contractor?.contractorName || initialData?.contractor?.name || "",
      contractorObject: initialData?.contractorObject || initialData?.contractor || null,
      paidByEmployee: existingPaidByEmployee,
      paymentDate: initialData?.paymentDate || "",
      amount: initialData?.amount ?? initialData?.paymentAmount ?? "",
      paymentMethod: normalizeContractorPaymentMethod(initialData?.paymentMethod),
      description: initialData?.description || initialData?.remarks || initialData?.remark || "",
      documentFile: null,
      siteId: (initialData?.siteId ?? initialData?.site?.id ?? selectedSite?.id) || ""
    });
    const [contractorSearch, setContractorSearch] = useState("");
    const [filteredContractors, setFilteredContractors] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [paidByEmployees, setPaidByEmployees] = useState([]);
    const [paidByEmployeesLoading, setPaidByEmployeesLoading] = useState(true);
    const [paidByEmployeesError, setPaidByEmployeesError] = useState("");
    const getEmployeeId = employee => employee?.id ?? employee?.empId ?? employee?.employeeId?.id ?? employee?.employeeId ?? employee?.employee_id ?? "";
    const getEmployeeLabel = employee => {
      const name = employee?.name || employee?.fullName || employee?.employeeName || employee?.empName || "";
      const code = employee?.empCode || employee?.empcode || employee?.employeeCode || "";
      return code && name ? `${code} - ${name}` : name || code || `Employee ${getEmployeeId(employee)}`;
    };
    const existingPaidByEmployeeId = getEmployeeId(existingPaidByEmployee);
    const employeeOptions = existingPaidByEmployeeId !== "" && paidByEmployees.some(employee => String(getEmployeeId(employee)) === String(existingPaidByEmployeeId))
      ? paidByEmployees
      : existingPaidByEmployee
        ? [...paidByEmployees, existingPaidByEmployee]
        : paidByEmployees;

    useEffect(() => {
      let active = true;
      const loadPaidByEmployees = async () => {
        setPaidByEmployeesLoading(true);
        setPaidByEmployeesError("");
        try {
          const response = await fetchWithAuth(`${API_ENDPOINTS.employee.list}?isManagementGroup=true`, { method: "GET" });
          if (!response.ok) {
            throw new Error(`Employee request failed (${response.status})`);
          }
          const employees = normalizeListRows(await response.json());
          if (active) setPaidByEmployees(employees);
        } catch (error) {
          if (active) {
            console.error("Error fetching employees for contractor payment Paid By:", error);
            setPaidByEmployeesError(error.message || "Unable to load employees");
          }
        } finally {
          if (active) setPaidByEmployeesLoading(false);
        }
      };

      loadPaidByEmployees();
      return () => {
        active = false;
      };
    }, []);

    useEffect(() => {
      setF(prev => ({ ...prev, siteId: selectedSite?.id || prev.siteId || "" }));
    }, [selectedSite?.id]);

    useEffect(() => {
      const searchContractors = async () => {
        if (!contractorSearch.trim()) {
          setFilteredContractors([]);
          return;
        }

        try {
          setSearchLoading(true);
          const searchQuery = encodeURIComponent(contractorSearch);
          let url = `${API_ENDPOINTS.contractor.search}?code=${searchQuery}`;
          if (selectedSite && selectedSite.id) {
            url += `&siteid=${encodeURIComponent(selectedSite.id)}`;
          }
          const response = await fetchWithAuth(url, { method: "GET" });
          if (response.ok) {
            const results = await response.json();
            const list = Array.isArray(results) ? results : (Array.isArray(results.data) ? results.data : []);
            setFilteredContractors(list);
          } else {
            setFilteredContractors([]);
          }
        } catch (err) {
          console.error("Contractor search error:", err);
          setFilteredContractors([]);
        } finally {
          setSearchLoading(false);
        }
      };

      const timer = setTimeout(() => {
        searchContractors();
      }, 300);

      return () => clearTimeout(timer);
    }, [contractorSearch, selectedSite?.id]);

    return (
      <div>
        <div className="form-group">
          <label>🏢 Site</label>
          <div style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: "6px", backgroundColor: "#f8fafc", color: "#334155", fontSize: "13px", fontWeight: "500", minHeight: "34px", display: "flex", alignItems: "center" }}>
            {selectedSite?.siteName || selectedSite?.name || "No site selected"}
          </div>
          <input type="hidden" value={f.siteId} />
        </div>
        <div className="form-group">
          <label htmlFor="contractor-payment-paid-by">Paid By Employee *</label>
          <select
            id="contractor-payment-paid-by"
            value={getEmployeeId(f.paidByEmployee)}
            onChange={event => {
              const paidByEmployee = employeeOptions.find(employee => String(getEmployeeId(employee)) === event.target.value) || null;
              setF(current => ({ ...current, paidByEmployee }));
            }}
            disabled={paidByEmployeesLoading || Boolean(paidByEmployeesError)}
          >
            <option value="">
              {paidByEmployeesLoading ? "Loading employees..." : paidByEmployeesError ? "Unable to load employees" : "Select employee"}
            </option>
            {employeeOptions.map((employee, index) => (
              <option key={getEmployeeId(employee) || `employee-${index}`} value={getEmployeeId(employee)}>
                {getEmployeeLabel(employee)}
              </option>
            ))}
          </select>
          {paidByEmployeesError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12, margin: "5px 0 0" }}>{paidByEmployeesError}</p>}
        </div>
        <div className="form-group">
          <label>Select Contractor *</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              value={contractorSearch}
              onChange={e => setContractorSearch(e.target.value)}
              placeholder={f.contractorId ? `✓ ${f.contractorName}` : "Search by contractor name or mobile..."}
              style={{
                padding: "8px 12px",
                border: f.contractorId ? "2px solid #0284c7" : "1px solid #cbd5e1",
                borderRadius: "4px",
                fontSize: "14px",
                width: "100%",
                paddingRight: searchLoading ? "36px" : (f.contractorId ? "80px" : "12px"),
                background: f.contractorId ? "#dbeafe" : "#fff",
                fontWeight: f.contractorId ? "600" : "400"
              }}
            />
            {searchLoading && <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "14px" }}>🔄</span>}
            {f.contractorId && !contractorSearch && (
              <button
                onClick={() => {
                  setF({ ...f, contractorId: "", contractorName: "", contractorObject: null });
                  setContractorSearch("");
                }}
                style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "#ef4444", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "11px", fontWeight: "600" }}
              >
                ✕ Change
              </button>
            )}
          </div>
          <p style={{ fontSize: "11px", color: "#64748b", margin: "4px 0 0 0" }}>
            💡 {searchLoading ? "🔄 Searching..." : f.contractorId ? `✓ Selected: ${f.contractorName}` : `Results: ${contractorSearch ? `${filteredContractors.length} contractor(s)` : "Start typing"}`}
          </p>

          {contractorSearch && !f.contractorId && filteredContractors.length > 0 && (
            <div style={{ marginTop: "8px", maxHeight: "220px", overflowY: "auto", padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#f8fafc" }}>
              {filteredContractors.map(contractor => (
                <div
                  key={contractor.id || contractor.contractorId || contractor.contractorName}
                  onClick={() => {
                    setF({
                      ...f,
                      contractorId: contractor.id || contractor.contractorId || contractor.contractorName,
                      contractorName: contractor.contractorName || contractor.name || contractor.contractor || "",
                      contractorObject: contractor
                    });
                    setContractorSearch("");
                  }}
                  style={{ padding: "10px 12px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer", marginBottom: "8px", fontSize: "13px" }}
                >
                  <div style={{ fontWeight: "600" }}>{contractor.contractorName || contractor.name || contractor.contractor}</div>
                  {contractor.mobile && <div style={{ fontSize: "12px", marginTop: "2px", opacity: 0.8 }}>📱 {contractor.mobile}</div>}
                </div>
              ))}
            </div>
          )}

          {contractorSearch && !f.contractorId && filteredContractors.length === 0 && (
            <div style={{ marginTop: "8px", padding: "12px", textAlign: "center", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#991b1b", fontSize: "13px" }}>
              ❌ No contractors found
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Payment Date</label>
          <input type="date" value={f.paymentDate} onChange={e => setF({ ...f, paymentDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Amount (₹)</label>
          <input type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Payment Method</label>
          <select value={f.paymentMethod} onChange={e => setF({ ...f, paymentMethod: e.target.value })}>
            <option value="CASH">Cash</option>
            <option value="BANK_TRANSFER">Bank Transfer</option>
            <option value="CHEQUE">Cheque</option>
            <option value="UPI">UPI</option>
          </select>
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Add payment description" style={{ minHeight: "80px" }} />
        </div>
        <div className="form-group">
          <label>Payment Document *</label>
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={event => setF(current => ({ ...current, documentFile: event.target.files?.[0] || null }))}
          />
          {f.documentFile && <small>Selected: {f.documentFile.name}</small>}
          {!f.documentFile && (initialData?.document?.fileName || initialData?.documentName) && (
            <small>Existing document: {initialData.document?.fileName || initialData.documentName}</small>
          )}
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!(f.contractorId && f.paidByEmployee && f.paymentDate && f.amount && f.siteId) || paidByEmployeesLoading || Boolean(paidByEmployeesError)}
            onClick={() => {
              if (!(f.contractorId && f.paidByEmployee && f.paymentDate && f.amount && f.siteId)) return;
              const hasExistingDocument = Boolean(initialData?.document?.fileName || initialData?.documentName);
              if (!f.documentFile && !hasExistingDocument) {
                window.alert("Please upload a document before saving the Contractor Payment.");
                return;
              }
              onSave(f);
            }}
          >
            {initialData ? "Update Contractor Payment" : "Save Contractor Payment"}
          </button>
        </div>
      </div>
    );
  }

  function LabourAdvanceForm({ onSave, onClose }) {
    const [f, setF] = useState({ labourCode: "", advanceDate: "", amount: "", remarks: "" });
    
    return (
      <div>
        <div className="form-group">
          <label>Labour Code</label>
          <input value={f.labourCode} onChange={e => setF({ ...f, labourCode: e.target.value })} placeholder="e.g. L0001" />
        </div>
        <div className="form-group">
          <label>Advance Date</label>
          <input type="date" value={f.advanceDate} onChange={e => setF({ ...f, advanceDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Amount (₹)</label>
          <input type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Remarks</label>
          <textarea value={f.remarks} onChange={e => setF({ ...f, remarks: e.target.value })} placeholder="Add any remarks..." style={{ minHeight: "80px" }} />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => f.labourCode && f.advanceDate && f.amount && onSave(f)}>Save Advance Payment</button>
        </div>
      </div>
    );
  }

  function LabourAttendanceForm({ labours, selectedSite, onSave, onClose }) {
    const laboursArray = useMemo(() => Array.isArray(labours) ? labours : [], [labours]);
    const defaultDate = new Date().toISOString().split("T")[0];
    const [entries, setEntries] = useState([]);

    useEffect(() => {
      setEntries(laboursArray.map((labour, index) => ({
        id: labour.id || labour.labourId || labour.labour_id || `labour-${index}`,
        labourCode: labour.labourCode || labour.labour_code || labour.code || labour.id || "",
        labourName: labour.labourName || labour.labour_name || labour.name || "",
        labourObject: labour,
        attendanceDate: defaultDate,
        status: "Present",
        remarks: ""
      })));
    }, [laboursArray, defaultDate]);

    const updateEntryValue = (index, field, value) => {
      setEntries(prev => prev.map((entry, entryIndex) => (
        entryIndex === index ? { ...entry, [field]: value } : entry
      )));
    };

    return (
      <div>
        <div className="form-group">
          <label>Site</label>
          <div style={{ padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: "#f8fafc", color: "#334155", fontSize: "13px", fontWeight: "500" }}>
            {selectedSite?.name || selectedSite?.siteName || "No site selected"}
          </div>
        </div>

        {entries.length > 0 ? (
          <div style={{ overflowX: "auto", marginTop: "8px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Labour Code</th>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Labour Name</th>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Attendance Date</th>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", fontWeight: 600 }}>{entry.labourCode || "—"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{entry.labourName || "—"}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                      <input
                        type="date"
                        value={entry.attendanceDate}
                        onChange={e => updateEntryValue(index, "attendanceDate", e.target.value)}
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: "4px" }}
                      />
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                      <select
                        value={entry.status}
                        onChange={e => updateEntryValue(index, "status", e.target.value)}
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: "4px" }}
                      >
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="Half Day">Half Day</option>
                        <option value="Holiday">Holiday</option>
                        <option value="Leave">Leave</option>
                      </select>
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                      <textarea
                        value={entry.remarks}
                        onChange={e => updateEntryValue(index, "remarks", e.target.value)}
                        rows="2"
                        placeholder="Add remarks"
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: "4px", minWidth: "120px" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: "#64748b", margin: "16px 0" }}>No plant labour found for this site.</p>
        )}

        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={entries.length === 0}
            onClick={() => onSave({
              attendanceEntries: entries.map(entry => ({
                labourCode: entry.labourCode,
                labourName: entry.labourName,
                labourObject: entry.labourObject,
                attendanceDate: entry.attendanceDate || defaultDate,
                status: entry.status,
                remarks: entry.remarks,
                site: selectedSite || null
              }))
            })}
          >
            Save Labour Attendance
          </button>
        </div>
      </div>
    );
  }

  function LabourPaymentForm({ onSave, onClose, selectedSite, initialData }) {
    const [f, setF] = useState({
      labourCode: initialData?.labourCode || initialData?.labour?.labourCode || initialData?.labour?.labour_code || "",
      labour: initialData?.labour || null,
      paymentDate: initialData?.paymentDate || initialData?.date || "",
      amount: initialData?.amount || "",
      paymentType: initialData?.paymentType || "Cash",
      remarks: initialData?.remarks || initialData?.remark || ""
    });
    const [labourResults, setLabourResults] = useState([]);
    const paymentTypes = ["Cash", "Check", "Bank Transfer", "UPI", "Online"];

    useEffect(() => {
      if (initialData) {
        setF({
          labourCode: initialData.labourCode || initialData.labour?.labourCode || initialData.labour?.labour_code || "",
          labour: initialData.labour || null,
          paymentDate: initialData.paymentDate || initialData.date || "",
          amount: initialData.amount || "",
          paymentType: initialData.paymentType || "Cash",
          remarks: initialData.remarks || initialData.remark || ""
        });
      }
    }, [initialData]);

    const searchLabours = async (value) => {
      setF(prev => ({ ...prev, labourCode: value, labour: null }));
      if (!value.trim()) {
        setLabourResults([]);
        return;
      }

      try {
        const params = new URLSearchParams({ labourCode: value.trim() });
        if (selectedSite?.id) {
          params.set("siteid", selectedSite.id);
        }
        const response = await fetchWithAuth(`${API_ENDPOINTS.labour.search}?${params.toString()}`, { method: "GET" });
        if (!response.ok) {
          throw new Error("Failed to search labour");
        }
        const result = await response.json();
        const rows = Array.isArray(result)
          ? result
          : result?.data || result?.labours || result?.content || [];
        setLabourResults(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Labour payment search error:", err);
        setLabourResults([]);
      }
    };
    
    return (
      <div>
        <div className="form-group">
          <label>Labour Code</label>
          <input value={f.labourCode} onChange={e => searchLabours(e.target.value)} placeholder="e.g. L0001" />
          {labourResults.length > 0 && (
            <div style={{ marginTop: 4, border: "1px solid #cbd5e1", borderRadius: 4, background: "#fff", maxHeight: 160, overflowY: "auto" }}>
              {labourResults.map((labour, index) => {
                const code = labour.labourCode || labour.labour_code || labour.code || labour.id;
                const name = labour.labourName || labour.name || "";
                return (
                  <button
                    key={labour.id || code || index}
                    type="button"
                    onClick={() => {
                      setF(prev => ({ ...prev, labourCode: String(code || ""), labour }));
                      setLabourResults([]);
                    }}
                    style={{ display: "block", width: "100%", padding: "8px 10px", border: 0, borderBottom: "1px solid #e2e8f0", background: "#fff", textAlign: "left", cursor: "pointer" }}
                  >
                    {code || "-"}{name ? ` - ${name}` : ""}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Payment Date</label>
          <input type="date" value={f.paymentDate} onChange={e => setF({ ...f, paymentDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Amount (₹)</label>
          <input type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Payment Type</label>
          <select value={f.paymentType} onChange={e => setF({ ...f, paymentType: e.target.value })}>
            {paymentTypes.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Remarks</label>
          <textarea value={f.remarks} onChange={e => setF({ ...f, remarks: e.target.value })} placeholder="Add any remarks..." style={{ minHeight: "80px" }} />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => f.labourCode && f.paymentDate && f.amount && f.paymentType && onSave(f)}>Save Payment</button>
        </div>
      </div>
    );
  }


  function DieselForm({ selectedSite, onSave, onClose }) {
    const [f, setF] = useState({ vehicleNumber: "", amount: "", quantity: "", receivedBy: "", approvedBy: "", dieselDate: "" });
    return (
      <div>
        <div className="form-group">
          <label>Site</label>
          <input type="text" value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
        </div>
        <div className="form-group">
          <label>Diesel Date</label>
          <input type="date" value={f.dieselDate} onChange={e => setF({ ...f, dieselDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Vehicle Number</label>
          <input value={f.vehicleNumber} onChange={e => setF({ ...f, vehicleNumber: e.target.value })} placeholder="e.g. UP32AB1234" />
        </div>
        <div className="form-group">
          <label>Amount (₹)</label>
          <input type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Quantity</label>
          <input type="number" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Received By</label>
          <input value={f.receivedBy} onChange={e => setF({ ...f, receivedBy: e.target.value })} placeholder="e.g. Rajesh" />
        </div>
        <div className="form-group">
          <label>Approved By</label>
          <input value={f.approvedBy} onChange={e => setF({ ...f, approvedBy: e.target.value })} placeholder="e.g. Manager" />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => {
            if (f.vehicleNumber && f.amount && f.quantity && f.receivedBy && f.approvedBy && f.dieselDate) {
              onSave({
                vehicleNumber: f.vehicleNumber,
                amount: f.amount,
                quantity: f.quantity,
                receivedBy: f.receivedBy,
                approvedBy: f.approvedBy,
                dieselDate: f.dieselDate,
                site: selectedSite
              });
            }
          }}>Save Diesel</button>
        </div>
      </div>
    );
  }

  function PetrolForm({ selectedSite, onSave, onClose }) {
    const [f, setF] = useState({ vehicleNumber: "", amount: "", quantity: "", receivedBy: "", approvedBy: "", petrolDate: "" });
    return (
      <div>
        <div className="form-group">
          <label>Site</label>
          <input type="text" value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
        </div>
        <div className="form-group">
          <label>Petrol Date</label>
          <input type="date" value={f.petrolDate} onChange={e => setF({ ...f, petrolDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Vehicle Number</label>
          <input value={f.vehicleNumber} onChange={e => setF({ ...f, vehicleNumber: e.target.value })} placeholder="e.g. UP32AB1234" />
        </div>
        <div className="form-group">
          <label>Amount (₹)</label>
          <input type="number" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Quantity</label>
          <input type="number" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group">
          <label>Received By</label>
          <input value={f.receivedBy} onChange={e => setF({ ...f, receivedBy: e.target.value })} placeholder="e.g. Rajesh" />
        </div>
        <div className="form-group">
          <label>Approved By</label>
          <input value={f.approvedBy} onChange={e => setF({ ...f, approvedBy: e.target.value })} placeholder="e.g. Manager" />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => {
            if (f.vehicleNumber && f.amount && f.quantity && f.receivedBy && f.approvedBy && f.petrolDate) {
              onSave({
                vehicleNumber: f.vehicleNumber,
                amount: f.amount,
                quantity: f.quantity,
                receivedBy: f.receivedBy,
                approvedBy: f.approvedBy,
                petrolDate: f.petrolDate,
                site: selectedSite
              });
            }
          }}>Save Petrol</button>
        </div>
      </div>
    );
  }

  function FuelPumpForm({ selectedSite, onSave, onClose, initialData = null }) {
    const formInitialData = initialData ?? null;
    const [documentFile, setDocumentFile] = useState(null);
    const [f, setF] = useState({
      fuelType: formInitialData?.fuelType || "",
      vehicleNumber: formInitialData?.vehicleNumber || "",
      amount: formInitialData?.amount ?? "",
      quantity: formInitialData?.quantity ?? "",
      receivedBy: formInitialData?.receivedBy || "",
      approvedBy: formInitialData?.approvedBy || "",
      fuelDate: formInitialData?.fuelDate || "",
    });

    useEffect(() => {
      setF({
        fuelType: formInitialData?.fuelType || "",
        vehicleNumber: formInitialData?.vehicleNumber || "",
        amount: formInitialData?.amount ?? "",
        quantity: formInitialData?.quantity ?? "",
        receivedBy: formInitialData?.receivedBy || "",
        approvedBy: formInitialData?.approvedBy || "",
        fuelDate: formInitialData?.fuelDate || "",
      });
    }, [formInitialData?.id, formInitialData?.fuelType, formInitialData?.vehicleNumber, formInitialData?.amount, formInitialData?.quantity, formInitialData?.receivedBy, formInitialData?.approvedBy, formInitialData?.fuelDate]);

    return (
      <div>
        <div className="form-grid">
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Site</label>
            <input type="text" value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
          </div>
          <div className="field">
            <label>Fuel Type *</label>
            <select value={f.fuelType} onChange={e => setF({ ...f, fuelType: e.target.value })} required>
              <option value="">Select Fuel Type</option>
              <option value="DIESEL">Diesel</option>
              <option value="PETROL">Petrol</option>
            </select>
          </div>
          <div className="field">
            <label>Fuel Date *</label>
            <input type="date" value={f.fuelDate} onChange={e => setF({ ...f, fuelDate: e.target.value })} required />
          </div>
          <div className="field">
            <label>Vehicle Number *</label>
            <input value={f.vehicleNumber} onChange={e => setF({ ...f, vehicleNumber: e.target.value })} placeholder="e.g. UP32AB1234" required />
          </div>
          <div className="field">
            <label>Amount (₹) *</label>
            <input type="number" min="0" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" required />
          </div>
          <div className="field">
            <label>Quantity *</label>
            <input type="number" min="0" step="0.01" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} placeholder="0" required />
          </div>
          <div className="field">
            <label>Received By *</label>
            <input value={f.receivedBy} onChange={e => setF({ ...f, receivedBy: e.target.value })} placeholder="e.g. Rajesh" required />
          </div>
          <div className="field">
            <label>Approved By *</label>
            <input value={f.approvedBy} onChange={e => setF({ ...f, approvedBy: e.target.value })} placeholder="e.g. Manager" required />
          </div>
          {!formInitialData && (
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Document *</label>
              <input type="file" required onChange={event => setDocumentFile(event.target.files?.[0] || null)} />
            </div>
          )}
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => {
            const invalidFields = [];
            const amount = Number(f.amount);
            const quantity = Number(f.quantity);
            if (!f.fuelType) invalidFields.push("fuel type");
            if (!f.fuelDate) invalidFields.push("fuel date");
            if (!f.vehicleNumber.trim()) invalidFields.push("vehicle number");
            if (f.amount === "" || !Number.isFinite(amount) || amount < 0) invalidFields.push("a valid amount");
            if (f.quantity === "" || !Number.isFinite(quantity) || quantity < 0) invalidFields.push("a valid quantity");
            if (!f.receivedBy.trim()) invalidFields.push("received by");
            if (!f.approvedBy.trim()) invalidFields.push("approved by");
            if (invalidFields.length > 0) {
              window.alert(`Please complete the required fields: ${invalidFields.join(", ")}.`);
              return;
            }
            if (!formInitialData && !documentFile) {
              window.alert("Please upload a document before saving Pump Fuel.");
              return;
            }
            onSave({
              id: formInitialData?.id ?? formInitialData?.fuelPumpId ?? null,
              fuelType: f.fuelType,
              vehicleNumber: f.vehicleNumber,
              amount: f.amount,
              quantity: f.quantity,
              receivedBy: f.receivedBy,
              approvedBy: f.approvedBy,
              fuelDate: f.fuelDate,
              site: selectedSite,
              documentFile,
            });
          }}>{formInitialData ? "Update Pump Fuel" : "Save Pump Fuel"}</button>
        </div>
      </div>
    );
  }

  function FuelTransactionForm({ selectedSite, onSave, onClose, initialData = null }) {
    const formInitialData = initialData ?? null;
    const [documentFile, setDocumentFile] = useState(null);
    const [f, setF] = useState({ 
      fuelType: formInitialData?.fuelType || "", 
      transactionType: formInitialData?.transactionType || "", 
      quantity: formInitialData?.quantity ?? "", 
      amount: formInitialData?.amount ?? "", 
      date: formInitialData?.date || "", 
      vehicleName: formInitialData?.vehicleName || "", 
      operatorName: formInitialData?.operatorName || "", 
      remarks: formInitialData?.remarks || ""
    });

    useEffect(() => {
      setF({
        fuelType: formInitialData?.fuelType || "",
        transactionType: formInitialData?.transactionType || "",
        quantity: formInitialData?.quantity ?? "",
        amount: formInitialData?.amount ?? "",
        date: formInitialData?.date || "",
        vehicleName: formInitialData?.vehicleName || "",
        operatorName: formInitialData?.operatorName || "",
        remarks: formInitialData?.remarks || ""
      });
    }, [formInitialData?.id, formInitialData?.fuelType, formInitialData?.transactionType, formInitialData?.quantity, formInitialData?.amount, formInitialData?.date, formInitialData?.vehicleName, formInitialData?.operatorName, formInitialData?.remarks]);
    return (
      <div>
        <div className="form-grid">
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Site</label>
            <input type="text" value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
          </div>

          <div className="field">
            <label>Fuel Type *</label>
            <select value={f.fuelType} onChange={e => setF({ ...f, fuelType: e.target.value })} required>
              <option value="">Select Fuel Type</option>
              <option value="DIESEL">Diesel</option>
              <option value="PETROL">Petrol</option>
            </select>
          </div>
          <div className="field">
            <label>Transaction Type *</label>
            <select value={f.transactionType} onChange={e => setF({ ...f, transactionType: e.target.value })} required>
              <option value="">Select Type</option>
              <option value="IN">In</option>
              <option value="OUT">Out</option>
            </select>
          </div>

          <div className="field">
            <label>Quantity (Liters) *</label>
            <input type="number" min="0" step="0.01" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} placeholder="0" required />
          </div>
          <div className="field">
            <label>Amount (₹) *</label>
            <input type="number" min="0" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} placeholder="0" required />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Transaction Date *</label>
            <input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} required />
          </div>

          <div className="field">
            <label>Vehicle Name *</label>
            <input value={f.vehicleName} onChange={e => setF({ ...f, vehicleName: e.target.value })} placeholder="e.g. JCB-01, Truck-A" required />
          </div>
          <div className="field">
            <label>Operator Name *</label>
            <input value={f.operatorName} onChange={e => setF({ ...f, operatorName: e.target.value })} placeholder="e.g. Rajesh Kumar" required />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Remarks</label>
            <textarea value={f.remarks} onChange={e => setF({ ...f, remarks: e.target.value })} placeholder="Additional notes..." style={{ minHeight: "60px", fontFamily: "inherit" }} />
          </div>
          {!formInitialData && (
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Document *</label>
              <input type="file" required onChange={event => setDocumentFile(event.target.files?.[0] || null)} />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => {
            const invalidFields = [];
            const quantity = Number(f.quantity);
            const amount = Number(f.amount);
            if (!f.fuelType) invalidFields.push("fuel type");
            if (!f.transactionType) invalidFields.push("transaction type");
            if (f.quantity === "" || !Number.isFinite(quantity) || quantity < 0) invalidFields.push("a valid quantity");
            if (f.amount === "" || !Number.isFinite(amount) || amount < 0) invalidFields.push("a valid amount");
            if (!f.date) invalidFields.push("transaction date");
            if (!f.vehicleName.trim()) invalidFields.push("vehicle name");
            if (!f.operatorName.trim()) invalidFields.push("operator name");
            if (invalidFields.length > 0) {
              window.alert(`Please complete the required fields: ${invalidFields.join(", ")}.`);
              return;
            }
            if (!formInitialData && !documentFile) {
              window.alert("Please upload a document before saving Plant Fuel.");
              return;
            }
            onSave({
              id: formInitialData?.id ?? null,
              fuelType: f.fuelType,
              transactionType: f.transactionType,
              quantity: f.quantity,
              amount: f.amount,
              date: f.date,
              vehicleName: f.vehicleName,
              operatorName: f.operatorName,
              remarks: f.remarks,
              site: selectedSite,
              documentFile,
            });
          }}>{formInitialData ? "Update Plant Fuel" : "Save Fuel Transaction"}</button>
        </div>
      </div>
    );
  }

  function ConcreteProductionForm({ selectedSite, initialData = null, onSave, onClose }) {
    const [f, setF] = useState({
      productionDate: initialData?.productionDate || initialData?.production_date || "",
      productionTime: initialData?.productionTime || initialData?.production_time || "",
      transitMixerNumber: initialData?.transitMixerNumber || initialData?.transit_mixer_number || "",
      gradeOfConcrete: initialData?.gradeOfConcrete || initialData?.grade_of_concrete || "",
      quantity: initialData?.quantity ?? "",
      dispatchedTo: initialData?.dispatchedTo || initialData?.dispatched_to || "",
      chainageNo: initialData?.chainageNo || initialData?.chainage_no || "",
      operatorSign: initialData?.operatorSign || initialData?.operator_sign || "",
      remarks: initialData?.remarks || initialData?.remark || ""
    });
    const [approvedByEmployees, setApprovedByEmployees] = useState([]);
    const [approvedBySearch, setApprovedBySearch] = useState("");
    const [selectedApprovedBy, setSelectedApprovedBy] = useState(null);
    const [approvedByLoading, setApprovedByLoading] = useState(true);
    const [approvedByError, setApprovedByError] = useState("");

    useEffect(() => {
      const fetchApprovedByEmployees = async () => {
        try {
          const response = await fetchWithAuth(API_ENDPOINTS.employee.list, { method: "GET" });
          if (!response.ok) {
            throw new Error("Failed to load employees for Approved By");
          }

          const result = await response.json();
          const rows = Array.isArray(result)
            ? result
            : ["data", "rows", "records", "items", "content", "result", "results", "list"]
                .map(key => result?.[key])
                .find(Array.isArray) || [];
          const allowedCodes = new Set(["R0002", "R0003", "R0004", "R0005"]);
          const eligibleEmployees = rows.filter(employee =>
            allowedCodes.has(String(getApproverEmployeeCode(employee)).trim().toUpperCase())
          );
          setApprovedByEmployees(eligibleEmployees);

          const initialApprover = initialData?.approvedBy || initialData?.approved_by;
          if (initialApprover) {
            const initialValue = typeof initialApprover === "object"
              ? getApproverEmployeeCode(initialApprover) || getApproverEmployeeId(initialApprover)
              : initialApprover;
            const matchedEmployee = eligibleEmployees.find(employee =>
              [getApproverEmployeeCode(employee), getApproverEmployeeId(employee), employee?.name, employee?.fullName, employee?.employeeName]
                .some(value => String(value || "").toUpperCase() === String(initialValue).toUpperCase())
            );
            if (matchedEmployee) {
              setSelectedApprovedBy(matchedEmployee);
            }
          }
        } catch (error) {
          console.error("Error fetching employees for concrete production approver:", error);
          setApprovedByError(error.message || "Failed to load employees for Approved By");
        } finally {
          setApprovedByLoading(false);
        }
      };

      fetchApprovedByEmployees();
    }, [initialData]);

    const matchingApprovedByEmployees = approvedByEmployees.filter(employee => {
      const query = approvedBySearch.trim().toLowerCase();
      if (!query) return true;
      return [
        employee?.empCode,
        employee?.empcode,
        employee?.employeeCode,
        employee?.employee?.empCode,
        employee?.name,
        employee?.fullName,
        employee?.employeeName,
      ].some(value => String(value || "").toLowerCase().includes(query));
    });

    return (
      <div>
        <div className="form-grid">
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Site</label>
            <input type="text" value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
          </div>
          <div className="field">
            <label>Production Date *</label>
            <input type="date" value={f.productionDate} onChange={e => setF({ ...f, productionDate: e.target.value })} required />
          </div>
          <div className="field">
            <label>Production Time *</label>
            <input type="time" value={f.productionTime} onChange={e => setF({ ...f, productionTime: e.target.value })} required />
          </div>
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Transit Mixer Number *</label>
            <input value={f.transitMixerNumber} onChange={e => setF({ ...f, transitMixerNumber: e.target.value })} placeholder="e.g. TM-001" required />
          </div>
          <div className="field">
            <label>Grade of Concrete *</label>
            <input value={f.gradeOfConcrete} onChange={e => setF({ ...f, gradeOfConcrete: e.target.value })} placeholder="e.g. M20, M25, M30" required />
          </div>
          <div className="field">
            <label>Quantity (m³) *</label>
            <input type="number" min="0" step="0.01" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} placeholder="0" required />
          </div>
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Dispatched To *</label>
            <input value={f.dispatchedTo} onChange={e => setF({ ...f, dispatchedTo: e.target.value })} placeholder="e.g. Location/Person Name" required />
          </div>
          <div className="field">
            <label>Chainage No *</label>
            <input value={f.chainageNo} onChange={e => setF({ ...f, chainageNo: e.target.value })} placeholder="e.g. Ch-100" required />
          </div>
          <div className="field">
            <label>Operator Sign *</label>
            <input value={f.operatorSign} onChange={e => setF({ ...f, operatorSign: e.target.value })} placeholder="Operator signature/name" required />
          </div>
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Approved By *</label>
            <input
              value={approvedBySearch || (selectedApprovedBy
                ? selectedApprovedBy.name || selectedApprovedBy.fullName || selectedApprovedBy.employeeName || ""
                : "")}
              onChange={event => {
                setApprovedBySearch(event.target.value);
                setSelectedApprovedBy(null);
              }}
              placeholder={approvedByLoading ? "Loading employees..." : "Search by employee code or name"}
              disabled={approvedByLoading || Boolean(approvedByError)}
              autoComplete="off"
              required
            />
            {approvedBySearch && !selectedApprovedBy && matchingApprovedByEmployees.length > 0 && (
              <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #cbd5e1", borderRadius: 4, marginTop: 4 }}>
                {matchingApprovedByEmployees.map(employee => {
                  const employeeCode = getApproverEmployeeCode(employee);
                  const employeeName = employee.name || employee.fullName || employee.employeeName || "";
                  return (
                    <button
                      key={getApproverEmployeeId(employee) || employeeCode}
                      type="button"
                      onClick={() => {
                        setSelectedApprovedBy(employee);
                        setApprovedBySearch("");
                      }}
                      style={{ display: "block", width: "100%", padding: "8px 10px", border: 0, borderBottom: "1px solid #e2e8f0", background: "#fff", textAlign: "left", cursor: "pointer" }}
                    >
                      {employeeName || employeeCode}
                    </button>
                  );
                })}
              </div>
            )}
            {approvedBySearch && !approvedByLoading && matchingApprovedByEmployees.length === 0 && (
              <p style={{ color: "#64748b", fontSize: 12, margin: "4px 0 0" }}>No eligible employee found.</p>
            )}
            {approvedByError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{approvedByError}</p>}
            {selectedApprovedBy && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setSelectedApprovedBy(null);
                  setApprovedBySearch("");
                }}
                style={{ marginTop: 6 }}
              >
                Change employee
              </button>
            )}
          </div>
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Remarks</label>
            <textarea value={f.remarks} onChange={e => setF({ ...f, remarks: e.target.value })} placeholder="Additional notes..." style={{ minHeight: "80px", fontFamily: "inherit" }} />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => {
            const invalidFields = [];
            const quantity = Number(f.quantity);
            if (!f.productionDate) invalidFields.push("production date");
            if (!f.productionTime) invalidFields.push("production time");
            if (!f.transitMixerNumber.trim()) invalidFields.push("transit mixer number");
            if (!f.gradeOfConcrete.trim()) invalidFields.push("grade of concrete");
            if (f.quantity === "" || !Number.isFinite(quantity) || quantity <= 0) invalidFields.push("a valid quantity greater than 0");
            if (!f.dispatchedTo.trim()) invalidFields.push("dispatched to");
            if (!f.chainageNo.trim()) invalidFields.push("chainage number");
            if (!f.operatorSign.trim()) invalidFields.push("operator sign");
            if (!selectedApprovedBy) invalidFields.push("an approved-by employee");
            if (invalidFields.length > 0) {
              window.alert(`Please complete the required fields: ${invalidFields.join(", ")}.`);
              return;
            }
            onSave({
              ...(initialData ? {
                id: getConcreteProductionId(initialData),
              } : {}),
              productionDate: f.productionDate,
              productionTime: f.productionTime,
              transitMixerNumber: f.transitMixerNumber,
              gradeOfConcrete: f.gradeOfConcrete,
              quantity: f.quantity,
              dispatchedTo: f.dispatchedTo,
              chainageNo: f.chainageNo,
              operatorSign: f.operatorSign,
              approvedBy: selectedApprovedBy,
              remarks: f.remarks,
              site: selectedSite
            });
          }}>{initialData ? "Update Concrete Production" : "Save Concrete Production"}</button>
        </div>
      </div>
    );
  }

  const FORMS = {
    employees: EmployeeForm,
    attendance: AttendanceForm,
    purchases: PurchaseForm,
    bills: BillForm,
    managementExpense: ManagementExpenseForm,
    sells: SellForm,
    diesel: DieselForm,
    petrol: PetrolForm,
    fuelpump: FuelPumpForm,
    fueltransaction: FuelTransactionForm,
    fuelpayment: FuelPaymentForm,
    labourcoast: LabourCoastForm,
    labour: LabourForm,
    labouradvance: LabourAdvanceForm,
    labourattendance: LabourAttendanceForm,
    labourpayment: LabourPaymentForm,
    expenses: ExpenseForm,
    concreteproduction: ConcreteProductionForm,
  };

  const FormComp = activeTab ? FORMS[activeTab] : null;
  let tabLabel = "";
  if (activeTab === "dashboard") tabLabel = "Dashboard";
  else if (activeTab === "settings") tabLabel = "Settings";
  else if (activeTab === "fuelpump") tabLabel = "Pump Fuel Management";
  else if (activeTab === "fuelpayment") tabLabel = "Fuel Payment";
  else if (activeTab === "employeeadvance") tabLabel = "Advance Salary";
  else if (activeTab === "salary") tabLabel = "Salary";
  else if (activeTab === "employeefnf") tabLabel = "Employee FNF";
  else if (activeTab === "fueltransaction") tabLabel = "Plant Fuel Management";
  else if (activeTab === "labourcoast") tabLabel = "Labour Coast";
  else if (activeTab === "labour") tabLabel = "Labour";
  else if (activeTab === "contractor") tabLabel = "Contractor";
  else if (activeTab === "contractorwork") tabLabel = "Contractor Work Details";
  else if (activeTab === "contractorpayment") tabLabel = "Contractor Payment";
  else if (activeTab === "labouradvance") tabLabel = "Advance Payment";
  else if (activeTab === "labourattendance") tabLabel = "Labour Attendance";
  else if (activeTab === "labourpayment") tabLabel = "Payment";
  else if (activeTab === "concreteproduction") tabLabel = "Concrete Production";
  else if (activeTab === "partnership") tabLabel = "Partnership";
  else if (activeTab === "bills") tabLabel = "Bills";
  else if (activeTab === "managementExpense") tabLabel = "Management Expense";
  else if (activeTab === "attendance") tabLabel = "Attendance";
  else if (activeTab === "expenses") tabLabel = "Expense";
  else if (activeTab === "workorders") tabLabel = "Work Orders";
  else if (activeTab === "purchases") tabLabel = "Purchase";
  else if (activeTab === "sells") tabLabel = "Sell";
  else if (activeTab === "employees") tabLabel = "Employee";
  else if (activeTab) tabLabel = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);

  const currentGroup = TABS.find(t =>
    t.children?.some(child => child.key === activeTab || (child.children && child.children.some(sc => sc.key === activeTab)))
  );

  const headerLabel = isDashboardView ? "Overview" : tabLabel;
  const headerBreadcrumb = isDashboardView
    ? "Home / Overview"
    : currentGroup
      ? `Home / ${currentGroup.label}`
      : `Home / ${tabLabel}`;

  const sidebarItems = (isAdmin ? [...SIDEBAR_ITEMS, { label: "Settings", icon: FiSettings, key: "settings" }] : SIDEBAR_ITEMS)
    .filter(item => !["labour", "concreteproduction"].includes(item.key) || isKheplSite(selectedSite))
    .map(item => item.key === "fueltransaction"
      ? { ...item, key: getFuelManagementTabKey(selectedSite) }
      : item);

  return (
    <div className="dashboard-shell">
      <aside className="dashboard-sidebar">
        <div className="sidebar-brand">
          <div className="logo">
            <img src="/unnamed.png" alt="SKT INFRACON" />
          </div>
          <div>
            <div className="logo-text">SKT INFRACON</div>
            <div className="sidebar-subtitle">Construction Tracker</div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                className={`sidebar-nav-btn ${activeTab === item.key ? "active" : ""}`}
                onClick={() => {
                  const nextTab = item.key === "settings" ? "settings" : item.key;
                  handleTabSelection({
                    nextTab,
                    setActiveTab,
                    setPagination,
                    fetchTabData,
                    shouldFetchTabData,
                  });
                }}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-footer-card">
            <div className="sidebar-footer-label">Current Site</div>
            <div className="sidebar-footer-value">{selectedSite?.site_name || selectedSite?.siteName || "No site selected"}</div>
          </div>
        </div>
      </aside>
      <div className="dashboard-main">
        <header className="navbar">
          <div className="navbar-left">
              <div>
              <div className="navbar-title">{headerLabel}</div>
              <div className="navbar-subtitle">{headerBreadcrumb}</div>
            </div>
          </div>
          <div className="navbar-right">
            <button className="navbar-chip" type="button">
              <FiCalendar size={15} /> {selectedSite?.site_name || selectedSite?.siteName || "Current Site"}
            </button>
            <button className="navbar-icon-btn" type="button"><FiBell size={16} /></button>
            <div className="navbar-user">
              <div className="avatar">{username?.charAt(0)?.toUpperCase() || "U"}</div>
              <div>
                <div className="username">{username}</div>
              </div>
              <FiChevronDown size={14} />
            </div>
            {selectedSite && (
              <button className="btn-secondary navbar-site-btn" onClick={onChangeSite} type="button">
                <FiMapPin size={14} /> Change Site
              </button>
            )}
            <button className="btn-logout" onClick={onLogout} type="button">
              <FiLogOut size={14} /> Logout
            </button>
          </div>
        </header>
        <div className="dashboard">

      {/* MAIN */}
      <main className="main-content">
        {error && (
          <div style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            {error}
            <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#991b1b" }}>✕</button>
          </div>
        )}

        {loading && (
          <div style={{
            background: "#eff6ff",
            color: "#1d4ed8",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            textAlign: "center"
          }}>
            Loading data...
          </div>
        )}

        {isSettingsView && isAdmin && (
          <div style={{ marginBottom: 24, padding: 20, background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>User Management</div>
                <div style={{ color: "#475569", fontSize: 14 }}>View active users and enable or disable access.</div>
              </div>
              <button
                className="btn-primary"
                type="button"
                onClick={() => {
                  fetchEmployeeSiteOptions();
                  setShowAddUserModal(true);
                }}
                style={{ minWidth: 140 }}
              >
                + Add User
              </button>
            </div>

            {userError && (
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#fee2e2", color: "#b91c1c", marginBottom: 16 }}>
                {userError}
              </div>
            )}

            {userLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "#475569" }}>Loading users...</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                  <thead>
                    <tr style={{ background: "linear-gradient(90deg, #1e3a5f, #2563eb)", color: "#fff", textAlign: "left" }}>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>Name</th>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>Username</th>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>Email</th>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>User Type</th>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>Status</th>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>Site</th>
                      <th style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#475569" }}>No active users found.</td>
                      </tr>
                    ) : activeUsers.map(user => {
                      const isActive = user?.isActive === true || user?.isActive === "true" || user?.active === true || user?.status === "Active" || user?.status === "active";
                      const userTypeValue = user?.userType || user?.type || user?.user_type || user?.usertype || user?.userRole || user?.role || "User";
                      const displayUserType = String(userTypeValue).trim() || "User";
                      return (
                        <tr key={user.id ?? user.userId ?? user.username} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "12px 16px" }}>
                            {(function() {
                              const nameFromFields = `${user.firstName || user.first_name || ""} ${user.lastName || user.last_name || ""}`.trim();
                              const rawName = user.name || user.fullName || nameFromFields || "";
                              return rawName || user.username || "-";
                            })()}
                          </td>
                          <td style={{ padding: "12px 16px" }}>{user.username || user.userName || user.email || "-"}</td>
                          <td style={{ padding: "12px 16px" }}>{user.email || "-"}</td>
                          <td style={{ padding: "12px 16px" }}>{displayUserType}</td>
                          <td style={{ padding: "12px 16px" }}>{isActive ? "Active" : "Inactive"}</td>
                          <td style={{ padding: "12px 16px" }}>{user.siteId || user.site?.siteName || user.siteName || "-"}</td>
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                className="btn-secondary"
                                type="button"
                                onClick={() => {
                                  const raw = (user.name || user.fullName || "").trim();
                                  const parts = raw ? raw.split(/\s+/) : [];
                                  const first = parts.length ? parts[0] : (user.firstName || user.first_name || "");
                                  const last = parts.length > 1 ? parts.slice(1).join(" ") : (user.lastName || user.last_name || "");
                                  setEditingUser({
                                    id: user.id ?? user.userId,
                                    firstName: first,
                                    lastName: last,
                                    name: raw || `${first} ${last}`.trim(),
                                    username: user.username || user.userName || "",
                                    email: user.email || "",
                                    userType: user.userType || user.type || "USER",
                                    site: user.site || (selectedSite ? { ...selectedSite } : undefined)
                                  });
                                }}
                                style={{ padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 34, width: 34, height: 34 }}
                                aria-label="Edit user"
                                title="Edit"
                              >
                                <FiEdit size={14} />
                              </button>
                              <button
                                className={isActive ? "btn-secondary" : "btn-primary"}
                                type="button"
                                onClick={async () => {
                                  try {
                                    await updateUserActiveStatus(user.id ?? user.userId, !isActive);
                                    showToast(`User ${isActive ? "disabled" : "enabled"} successfully`, "success");
                                  } catch (err) {
                                    showToast(err.message || "Unable to update user status", "error");
                                  }
                                }}
                                style={{ minWidth: 100, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}
                                aria-label={isActive ? "Disable user" : "Enable user"}
                                title={isActive ? "Disable user" : "Enable user"}
                              >
                                {isActive ? '👤🚫' : '👤✅'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {isDashboardView && (
          <>
            {isAdmin && (
              <div className="dashboard-filter-row">
              <div className="dashboard-filter-field">
                <label htmlFor="dashboard-from-date">From Date</label>
                <input
                  id="dashboard-from-date"
                  type="date"
                  value={dashboardFromDate}
                  onChange={(e) => {
                    setDashboardFromDate(e.target.value);
                    setDashboardPartnershipTotal(null);
                    setDashboardBillTotal(null);
                  }}
                />
              </div>
              <div className="dashboard-filter-field">
                <label htmlFor="dashboard-to-date">To Date</label>
                <input
                  id="dashboard-to-date"
                  type="date"
                  value={dashboardToDate}
                  onChange={(e) => {
                    setDashboardToDate(e.target.value);
                    setDashboardPartnershipTotal(null);
                    setDashboardBillTotal(null);
                  }}
                />
              </div>
              <div className="dashboard-filter-actions">
                <button className="btn-primary" type="button" onClick={handleDashboardCountSearch}>
                  <FiSearch size={14} /> Search
                </button>
                <button className="btn-secondary" type="button" onClick={handleClearDashboardFilters}>
                  Reset
                </button>
              </div>
              </div>
            )}
            {/* STAT CARDS */}
            <div className="stat-grid">
              <div className="stat-card" style={{ borderLeftColor: "#dc2626" }}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(220,38,38,0.12)", color: "#dc2626" }}><FiDollarSign size={15} /></div>
                  <div className="stat-label">Total Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#dc2626" }}>
                  {`₹${formatNumberIN(calculateTotalExpense(data))}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#2563eb", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "employees", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(37,99,235,0.12)", color: "#2563eb" }}><FiUsers size={15} /></div>
                  <div className="stat-label">Employees</div>
                </div>
                <div className="stat-value" style={{ color: "#2563eb" }}>
                  {(function() {
                    if (typeof data.employees === 'number') return formatNumberIN(data.employees);
                    if (Array.isArray(data.employees)) return formatNumberIN(data.employees.length);
                    return "0";
                  })()}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#d97706", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "purchases", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(217,119,6,0.12)", color: "#d97706" }}><FiShoppingCart size={15} /></div>
                  <div className="stat-label">Purchases</div>
                </div>
                <div className="stat-value" style={{ color: "#d97706" }}>
                  {(function() {
                    if (typeof data.purchases === 'number') return `₹${formatNumberIN(data.purchases)}`;
                    if (Array.isArray(data.purchases)) return `₹${formatNumberIN(data.purchases.reduce((s, r) => s + Number(r.amount || 0), 0))}`;
                    return "0";
                  })()}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#7c3aed", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "sells", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(124,58,237,0.12)", color: "#7c3aed" }}><FiTrendingUp size={15} /></div>
                  <div className="stat-label">Sales</div>
                </div>
                <div className="stat-value" style={{ color: "#7c3aed" }}>
                  {(function() {
                    if (typeof data.sells === 'number') return `₹${formatNumberIN(data.sells)}`;
                    if (Array.isArray(data.sells)) return `₹${formatNumberIN(data.sells.reduce((s, r) => s + Number(r.amount || 0), 0))}`;
                    return "0";
                  })()}
                </div>
              </div>
              {isAdmin && (
                <>
                  <div className="stat-card" style={{ borderLeftColor: "#0891b2" }}>
                    <div className="stat-card-top">
                      <div className="stat-icon" style={{ background: "rgba(8,145,178,0.12)", color: "#0891b2" }}><FiDollarSign size={15} /></div>
                      <div className="stat-label">Partnership Bill Amount</div>
                    </div>
                    <div className="stat-value" style={{ color: "#0891b2" }}>
                      {`₹${formatAmountIN(dashboardPartnershipTotal ?? 0)}`}
                    </div>
                  </div>
                  <div className="stat-card" style={{ borderLeftColor: "#9333ea" }}>
                    <div className="stat-card-top">
                      <div className="stat-icon" style={{ background: "rgba(147,51,234,0.12)", color: "#9333ea" }}><FiDollarSign size={15} /></div>
                      <div className="stat-label">Bill Total Amount</div>
                    </div>
                    <div className="stat-value" style={{ color: "#9333ea" }}>
                      {`₹${formatAmountIN(dashboardBillTotal ?? 0)}`}
                    </div>
                  </div>
                </>
              )}
              <div className="stat-card" style={{ borderLeftColor: "#ef4444", cursor: 'pointer' }} onClick={() => handleExpenseTypeFilter("Mess")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Mess Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#ef4444" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.Mess || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#14b8a6", cursor: 'pointer' }} onClick={() => handleExpenseTypeFilter("Machinery")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(20,184,166,0.12)", color: "#14b8a6" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Machinery Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#14b8a6" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.Machinery || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#0ea5e9", cursor: 'pointer' }} onClick={() => handleExpenseTypeFilter("Refreshments")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(14,165,233,0.12)", color: "#0ea5e9" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Refreshments Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#0ea5e9" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.Refreshments || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#0f766e", cursor: 'pointer' }} onClick={() => handleExpenseTypeFilter("Office Expenses")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(15,118,110,0.12)", color: "#0f766e" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Office Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#0f766e" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.["Office Expenses"] || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#7c3aed", cursor: 'pointer' }} onClick={() => handleExpenseTypeFilter("Hardware")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(124,58,237,0.12)", color: "#7c3aed" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Hardware Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#7c3aed" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.Hardware || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#2563eb", cursor: "pointer" }} onClick={() => handleExpenseTypeFilter("Mobile Recharge")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(37,99,235,0.12)", color: "#2563eb" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Mobile Recharge</div>
                </div>
                <div className="stat-value" style={{ color: "#2563eb" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.["Mobile Recharge"] || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#16a34a", cursor: "pointer" }} onClick={() => handleExpenseTypeFilter("Travel")}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(22,163,74,0.12)", color: "#16a34a" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Travel Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#16a34a" }}>
                  {`₹${formatNumberIN(data.expenseTypeTotals?.Travel || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#f59e0b", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "expenses", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(245,158,11,0.12)", color: "#f59e0b" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#f59e0b" }}>
                  {(function() {
                    if (typeof data.expenses === 'number') return `₹${formatNumberIN(data.expenses)}`;
                    if (Array.isArray(data.expenses)) return `₹${formatNumberIN(data.expenses.reduce((s, r) => s + Number(r.amount || 0), 0))}`;
                    return "0";
                  })()}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#0891b2", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "managementExpense", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(8,145,178,0.12)", color: "#0891b2" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Management Expenses</div>
                </div>
                <div className="stat-value" style={{ color: "#0891b2" }}>
                  {`₹${formatNumberIN(data.managementExpense || 0)}`}
                </div>
              </div>
              <div className="stat-card" style={{ borderLeftColor: "#16a34a", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "employeeadvance", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(22,163,74,0.12)", color: "#16a34a" }}><FiDollarSign size={15} /></div>
                  <div className="stat-label">Advance Salary</div>
                </div>
                <div className="stat-value" style={{ color: "#16a34a" }}>
                  {`₹${formatNumberIN(data.employeeadvance || 0)}`}
                </div>
              </div>
              {isKheplSite(selectedSite) && <div className="stat-card" style={{ borderLeftColor: "#8b5cf6", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "concreteproduction", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(139,92,246,0.12)", color: "#8b5cf6" }}><FiBox size={15} /></div>
                  <div className="stat-label">Concrete</div>
                </div>
                <div className="stat-value" style={{ color: "#8b5cf6" }}>
                  {(function() {
                    if (typeof data.concreteproduction === 'number') return `${formatNumberIN(data.concreteproduction)} m³`;
                    if (Array.isArray(data.concreteproduction)) return `${formatNumberIN(data.concreteproduction.length)} m³`;
                    return "0 m³";
                  })()}
                </div>
              </div>}
              <div className="stat-card" style={{ borderLeftColor: "#ec4899", cursor: "pointer" }} onClick={() => handleTabSelection({ nextTab: "contractorpayment", setActiveTab, setPagination, fetchTabData, shouldFetchTabData })}>
                <div className="stat-card-top">
                  <div className="stat-icon" style={{ background: "rgba(236,72,153,0.12)", color: "#ec4899" }}><FiCreditCard size={15} /></div>
                  <div className="stat-label">Contractor Payment</div>
                </div>
                <div className="stat-value" style={{ color: "#ec4899" }}>
                  {`₹${formatNumberIN(data.contractorpayment || 0)}`}
                </div>
              </div>            </div>

            <div className="fuel-status-row">
              {/* PLANT FUEL STATUS ON MAIN DASHBOARD */}
              {isKheplSite(selectedSite) && (
                <div className="status-card fuel-status-shell">
                  <div className="fuel-status-header">
                    <div className="fuel-status-title-row">
                      <div className="fuel-status-icon" style={{ background: "rgba(2,132,199,0.12)", color: "#0284c7" }}>
                        <FiDroplet size={16} />
                      </div>
                      <div>
                        <div className="fuel-status-title">Plant Fuel Status</div>
                        <div className="fuel-status-subtitle">Plant stock movement</div>
                      </div>
                    </div>
                  </div>

                  {showFuelStatus && (
                    <div className="fuel-expand-panel">
                      <div className="fuel-tile-grid">
                        <div className="fuel-tile">
                          <div className="fuel-tile-label-row">
                            <div className="fuel-tile-icon diesel"><FiDroplet size={14} /></div>
                            <span className="fuel-tile-label">Diesel</span>
                          </div>
                          <div>
                            <div className="fuel-tile-value">{formatNumberIN(fuelCounts.dieselIn - fuelCounts.dieselOut)} L</div>
                            <div className="fuel-tile-available">Available</div>
                          </div>
                          <div className="fuel-tile-inout">
                            <div className="col in">
                              <span className="label">IN</span>
                              <span className="value in">{formatNumberIN(fuelCounts.dieselIn)} L</span>
                            </div>
                            <div className="col out">
                              <span className="label">OUT</span>
                              <span className="value out">{formatNumberIN(fuelCounts.dieselOut)} L</span>
                            </div>
                          </div>
                        </div>

                        <div className="fuel-tile">
                          <div className="fuel-tile-label-row">
                            <div className="fuel-tile-icon petrol"><FiDroplet size={14} /></div>
                            <span className="fuel-tile-label">Petrol</span>
                          </div>
                          <div>
                            <div className="fuel-tile-value">{formatNumberIN(fuelCounts.petrolIn - fuelCounts.petrolOut)} L</div>
                            <div className="fuel-tile-available">Available</div>
                          </div>
                          <div className="fuel-tile-inout">
                            <div className="col in">
                              <span className="label">IN</span>
                              <span className="value in">{formatNumberIN(fuelCounts.petrolIn)} L</span>
                            </div>
                            <div className="col out">
                              <span className="label">OUT</span>
                              <span className="value out">{formatNumberIN(fuelCounts.petrolOut)} L</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PUMP FUEL STATUS ON MAIN DASHBOARD */}
              <div className="status-card fuel-status-shell">
                <div className="fuel-status-header">
                  <div className="fuel-status-title-row">
                    <div className="fuel-status-icon" style={{ background: "rgba(37,99,235,0.12)", color: "#2563eb" }}>
                      <FiMapPin size={16} />
                    </div>
                    <div>
                      <div className="fuel-status-title">Pump Fuel Status</div>
                      <div className="fuel-status-subtitle">Pump fuel availability</div>
                    </div>
                  </div>
                </div>

                {showPumpFuelStatus && (
                  <div className="fuel-expand-panel">
                    <div className="fuel-tile-grid">
                      <div className="fuel-tile">
                        <div className="fuel-tile-label-row">
                          <div className="fuel-tile-icon diesel"><FiDroplet size={14} /></div>
                          <span className="fuel-tile-label">Diesel</span>
                        </div>
                        <div>
                          <div className="fuel-tile-value">₹{formatNumberIN(fuelPumpCounts.diesel)}</div>
                          <div className="fuel-tile-available">Total amount</div>
                        </div>
                      </div>

                      <div className="fuel-tile">
                        <div className="fuel-tile-label-row">
                          <div className="fuel-tile-icon petrol"><FiDroplet size={14} /></div>
                          <span className="fuel-tile-label">Petrol</span>
                        </div>
                        <div>
                          <div className="fuel-tile-value">₹{formatNumberIN(fuelPumpCounts.petrol)}</div>
                          <div className="fuel-tile-available">Total amount</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </>
        )}

        {/* TAB PANEL */}
        <div className={`tab-panel${isDashboardView ? " company-marquee" : ""}`}>
          {isDashboardView && (
            <div className="company-marquee-track" aria-label="SKT INFRACON PVT LTD">
              <span>SKT INFRACON PVT LTD</span>
              <span aria-hidden="true">SKT INFRACON PVT LTD</span>
            </div>
          )}
          {!isDashboardView && (() => {
            const currentGroup = TABS.find(t =>
              t.children?.some(child =>
                child.key === activeTab ||
                (child.children && child.children.some(sc => sc.key === activeTab))
              )
            );

            let headerLabel = currentGroup?.label;
            let headerIcon = currentGroup?.icon;

            if (activeTab === "purchases" || activeTab === "sells" || activeTab === "expenses") {
              const cashflowChild = TABS.find(t => t.key === "cashflow_group")?.children?.find(child => child.key === activeTab);
              if (cashflowChild) {
                headerLabel = cashflowChild.label;
                headerIcon = <FiShoppingCart />;
              }
            }

            if (activeTab === "fuelpump" || activeTab === "fueltransaction" || activeTab === "fuelpayment") {
              const fuelChild = TABS.find(t => t.key === "fuel_group")?.children?.find(child => child.key === activeTab);
              if (fuelChild) {
                headerLabel = fuelChild.label;
                headerIcon = <FiDroplet />;
              }
            }

            const shouldHideHeader = [
              "concreteproduction",
              "labour",
              "contractor",
              "contractorwork",
              "contractorpayment",
              "labouradvance",
              "labourattendance",
              "labourpayment",
              "purchases",
              "sells",
              "expenses",
              "employees",
              "attendance",
              "employeefnf",
              "employeeadvance",
              "salary",
              "salaryreport",
              "diesel",
              "petrol",
              "fueltransaction",
              "fuelpayment",
              "fuelpump",
              "pump_fuel_management_group"
            ].includes(activeTab);
            return currentGroup && !shouldHideHeader ? (
              <div className="tab-headers">
                <button className="tab-btn active" type="button">
                  {headerIcon} {headerLabel}
                </button>
              </div>
            ) : null;
          })()}

          {activeTab === "bills" && (
            <div style={{
              display: "flex",
              gap: "10px",
              padding: "12px",
              background: "#f3f4f6",
              borderRadius: "6px",
              borderLeft: "4px solid #0284c7",
              border: "1px solid #dfe3ea",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              {["ALL", ...BILL_TYPE_OPTIONS].map(billType => {
                const isActive = activeSubTab.bill_group === billType;
                return (
                  <button
                    key={billType}
                    type="button"
                    onClick={() => {
                      setActiveSubTab(prev => ({ ...prev, bill_group: billType }));
                      setPagination(prev => ({ ...prev, bills: 0 }));
                    }}
                    style={{
                      minWidth: "92px",
                      padding: "10px 18px",
                      borderRadius: "6px",
                      border: isActive ? "1px solid #0284c7" : "1px solid #d0d7de",
                      background: isActive ? "#0284c7" : "#fff",
                      color: isActive ? "#fff" : "#475569",
                      cursor: "pointer",
                      fontWeight: isActive ? "700" : "600",
                      fontSize: "13px",
                      lineHeight: "1.2",
                      transition: "all 0.2s ease",
                      boxShadow: isActive ? "0 2px 8px rgba(2, 132, 199, 0.16)" : "none"
                    }}
                  >
                    {billType === "ALL" ? "All Bills" : billType}
                  </button>
                );
              })}
            </div>
          )}

          {/* SUB-TABS for Employees */}
          {(activeTab === "employees" || activeTab === "attendance" || activeTab === "employeefnf" || activeTab === "employeeadvance" || activeTab === "salary" || activeTab === "salaryreport") && (
            <div style={{
              display: "flex",
              gap: "10px",
              padding: "12px",
              background: "#f3f4f6",
              borderRadius: "6px",
              borderLeft: "4px solid #0284c7",
              border: "1px solid #dfe3ea",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              {TABS.find(t => t.key === "employee_group")?.children?.map(child => (
                <button
                  key={child.key}
                  onClick={() => {
                    setActiveSubTab(prev => ({ ...prev, employee_group: child.key }));
                    handleTabSelection({
                      nextTab: child.key,
                      setActiveTab,
                      setPagination,
                      fetchTabData,
                      shouldFetchTabData,
                    });
                  }}
                  style={{
                    minWidth: "92px",
                    padding: "10px 18px",
                    borderRadius: "6px",
                    border: activeTab === child.key ? "1px solid #0284c7" : "1px solid #d0d7de",
                    background: activeTab === child.key ? "#0284c7" : "#fff",
                    color: activeTab === child.key ? "#fff" : "#475569",
                    cursor: "pointer",
                    fontWeight: activeTab === child.key ? "700" : "600",
                    fontSize: "13px",
                    lineHeight: "1.2",
                    transition: "all 0.2s ease",
                    boxShadow: activeTab === child.key ? "0 2px 8px rgba(2, 132, 199, 0.16)" : "none"
                  }}
                >
                  {child.label}
                </button>
              ))}
            </div>
          )}

          {/* SUB-TABS for Contractor Workforce */}
          {(activeTab === "contractor" || activeTab === "contractorwork" || activeTab === "contractorpayment") && (
            <div style={{
              display: "flex",
              gap: "10px",
              padding: "12px",
              background: "#f3f4f6",
              borderRadius: "6px",
              borderLeft: "4px solid #0284c7",
              border: "1px solid #dfe3ea",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              {TABS.find(t => t.key === "labour_group")?.children?.map(child => (
                <button
                  key={child.key}
                  onClick={() => {
                    setActiveSubTab(prev => ({ ...prev, labour_group: child.key }));
                    handleTabSelection({
                      nextTab: child.key,
                      setActiveTab,
                      setPagination,
                      fetchTabData,
                      shouldFetchTabData,
                    });
                  }}
                  style={{
                    minWidth: "92px",
                    padding: "10px 18px",
                    borderRadius: "6px",
                    border: activeTab === child.key ? "1px solid #0284c7" : "1px solid #d0d7de",
                    background: activeTab === child.key ? "#0284c7" : "#fff",
                    color: activeTab === child.key ? "#fff" : "#475569",
                    cursor: "pointer",
                    fontWeight: activeTab === child.key ? "700" : "600",
                    fontSize: "13px",
                    lineHeight: "1.2",
                    transition: "all 0.2s ease",
                    boxShadow: activeTab === child.key ? "0 2px 8px rgba(2, 132, 199, 0.16)" : "none"
                  }}
                >
                  {child.label}
                </button>
              ))}
            </div>
          )}

          {/* SUB-TABS for Plant Labours */}
          {(activeTab === "labour" || activeTab === "labourattendance" || activeTab === "labourpayment") && (
            <div style={{
              display: "flex",
              gap: "10px",
              padding: "12px",
              background: "#f3f4f6",
              borderRadius: "6px",
              borderLeft: "4px solid #0284c7",
              border: "1px solid #dfe3ea",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              {TABS.find(t => t.key === "plantlabour_group")?.children?.map(child => (
                <button
                  key={child.key}
                  onClick={() => {
                    setActiveSubTab(prev => ({ ...prev, plantlabour_group: child.key }));
                    handleTabSelection({
                      nextTab: child.key,
                      setActiveTab,
                      setPagination,
                      fetchTabData,
                      shouldFetchTabData,
                    });
                  }}
                  style={{
                    minWidth: "92px",
                    padding: "10px 18px",
                    borderRadius: "6px",
                    border: activeTab === child.key ? "1px solid #0284c7" : "1px solid #d0d7de",
                    background: activeTab === child.key ? "#0284c7" : "#fff",
                    color: activeTab === child.key ? "#fff" : "#475569",
                    cursor: "pointer",
                    fontWeight: activeTab === child.key ? "700" : "600",
                    fontSize: "13px",
                    lineHeight: "1.2",
                    transition: "all 0.2s ease",
                    boxShadow: activeTab === child.key ? "0 2px 8px rgba(2, 132, 199, 0.16)" : "none"
                  }}
                >
                  {child.label}
                </button>
              ))}
            </div>
          )}

          {/* SUB-TABS for Fuel */}
          {(activeTab === "fueltransaction" || activeTab === "fuelpump" || activeTab === "fuelpayment") && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "12px",
              background: "#f3f4f6",
              borderRadius: "6px",
              borderLeft: "4px solid #0284c7",
              border: "1px solid #dfe3ea",
              marginBottom: "16px"
            }}>
              <div style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center"
              }}>
                {TABS.find(t => t.key === "fuel_group")?.children?.map(child => {
                  return (
                    <button
                      key={child.key}
                      type="button"
                      onClick={() => {
                        setActiveTab(child.key);
                        setActiveSubTab(prev => ({ ...prev, fuel_group: child.key }));
                        setPagination(prev => ({ ...prev, [child.key]: 0 }));
                        if (shouldFetchTabData(child.key)) {
                          fetchTabData(child.key);
                        }
                      }}
                      style={{
                        minWidth: "120px",
                        padding: "10px 18px",
                        borderRadius: "6px",
                        border: activeTab === child.key ? "1px solid #0284c7" : "1px solid #d0d7de",
                        background: activeTab === child.key ? "#0284c7" : "#fff",
                        color: activeTab === child.key ? "#fff" : "#475569",
                        cursor: "pointer",
                        fontWeight: activeTab === child.key ? "700" : "600",
                        fontSize: "13px",
                        lineHeight: "1.2",
                        transition: "all 0.2s ease",
                        boxShadow: activeTab === child.key ? "0 2px 8px rgba(2, 132, 199, 0.16)" : "none"
                      }}
                    >
                      {child.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="tab-content">
            {/* SEARCH - Only for Employees */}
            {activeTab === "employees" && (
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
                  Search Employee
                </label>
                <div className="search-wrapper" style={{ marginBottom: 0 }}>
                  <FiSearch size={18} className="search-icon" />
                  <input
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      setPagination(prev => ({ ...prev, [activeTab]: 0 }));
                    }}
                    placeholder="Search by Employee ID or Name..."
                    className="search-input"
                    style={{
                      padding: "8px 12px 8px 36px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px",
                      width: "100%"
                    }}
                  />
                </div>
                <p style={{ fontSize: "12px", color: "#64748b", margin: "4px 0 0 0" }}>
                  💡 Tip: Search by Employee ID (empCode) or Name or Phone number
                </p>
              </div>
            )}

            {activeTab === "partnership" && showPartnershipManagement && (
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginBottom: "16px"
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap"
                }}>
                  <h3 style={{ margin: 0, color: "#0f172a", fontSize: "16px" }}>Partnership Entries</h3>
                </div>

                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "12px",
                  background: "#f0f9ff",
                  borderRadius: "6px",
                  borderLeft: "4px solid #0284c7"
                }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Search Partner Name</label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      value={partnershipSearch}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPartnershipSearch(value);
                        if (value.trim()) {
                          handlePartnershipSearch(value);
                        } else {
                          handlePartnershipSearch("");
                        }
                      }}
                      placeholder="Search by partner name"
                      style={{ flex: 1, minWidth: "220px", padding: "8px 12px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                    />
                    <button
                      type="button"
                      onClick={() => handlePartnershipSearch(partnershipSearch)}
                      style={{
                        padding: "8px 16px",
                        background: "#0f766e",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600"
                      }}
                    >
                      Search
                    </button>
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => {
                        setPartnershipSearch("");
                        setPartnershipFromDate("");
                        setPartnershipToDate("");
                        setPartnershipTotalAmount(null);
                        handlePartnershipSearch("");
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start", flexWrap: "wrap" }}>
                    <button className="btn-primary" type="button" onClick={() => setModal("partner")}>
                      <FiPlus size={16} style={{ marginRight: "6px" }} /> Add Partner
                    </button>
                  </div>
                </div>

                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  padding: "12px",
                  background: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Partners</div>
                      <div style={{ fontSize: "13px", color: "#64748b" }}>Select a partner to view their bills</div>
                    </div>
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => {
                        setSelectedPartnerId(null);
                        setSelectedPartnerName("");
                      }}
                    >
                      Show all
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 220px))", gap: "10px" }}>
                    {partnershipPartners.length === 0 ? (
                      <div style={{ padding: "12px", borderRadius: "10px", background: "#f8fafc", color: "#64748b", fontSize: "13px", border: "1px dashed #cbd5e1" }}>
                        No partners found yet.
                      </div>
                    ) : partnershipPartners.map((partner) => {
                      const partnerName = partner?.name || partner?.partnerName || partner?.contactPerson || `Partner ${partner?.id ?? ""}`;
                      const isActive = selectedPartnerId != null
                        ? String(partner?.id ?? partnerName) === String(selectedPartnerId)
                        : selectedPartnerName === partnerName;
                      const billCount = partnershipBills.filter((row) => {
                        const rowPartnerName = getPartnershipRowPartnerName(row).trim().toLowerCase();
                        return rowPartnerName === partnerName.trim().toLowerCase();
                      }).length;

                      return (
                        <div
                          key={partner?.id ?? partnerName}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const isSameSelection =
                              (selectedPartnerId != null && String(selectedPartnerId) === String(partner?.id ?? partnerName)) ||
                              (selectedPartnerName && String(selectedPartnerName) === String(partnerName));

                            if (isSameSelection) {
                              setSelectedPartnerId(null);
                              setSelectedPartnerName("");
                              setPartnershipFromDate("");
                              setPartnershipToDate("");
                              setPartnershipTotalAmount(null);
                              fetchPartnershipData();
                              return;
                            }

                            setSelectedPartnerId(partner?.id ?? null);
                            setSelectedPartnerName(partnerName);
                            setPartnershipFromDate("");
                            setPartnershipToDate("");
                            setPartnershipTotalAmount(null);
                            fetchPartnershipBillsByPartner(partner?.id ?? null);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") event.currentTarget.click();
                          }}
                          style={{
                            padding: "16px 14px",
                            borderRadius: "14px",
                            border: isActive ? "1px solid #0284c7" : "1px solid #e2e8f0",
                            background: isActive ? "linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%)" : "#fff",
                            textAlign: "left",
                            cursor: "pointer",
                            boxShadow: isActive ? "0 6px 16px rgba(2,132,199,0.12)" : "0 1px 2px rgba(15,23,42,0.04)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            transition: "transform 0.15s ease, box-shadow 0.15s ease"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                            <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", lineHeight: 1.3, overflowWrap: "anywhere" }}>{partnerName}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {isAdmin && (
                                <>
                                  <button type="button" className="btn-secondary" onClick={(event) => { event.stopPropagation(); setEditingPartner(partner); setModal("partner"); }} aria-label={`Edit ${partnerName}`} title="Edit partner" style={{ padding: "6px", display: "inline-flex" }}>
                                    <FiEdit size={14} />
                                  </button>
                                  <button type="button" className="btn-secondary" onClick={(event) => { event.stopPropagation(); handlePartnerDelete(partner); }} aria-label={`Delete ${partnerName}`} title="Delete partner" style={{ padding: "6px", display: "inline-flex", color: "#ef4444", borderColor: "#ef4444" }}>
                                    <FiTrash2 size={14} />
                                  </button>
                                </>
                              )}
                              <div style={{
                              minWidth: "30px",
                              height: "30px",
                              borderRadius: "999px",
                              background: isActive ? "#0284c7" : "#e2e8f0",
                              color: isActive ? "#fff" : "#64748b",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              fontWeight: "700"
                              }}>{billCount}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>
                            {billCount === 0 ? "No bills yet" : `${billCount} bill${billCount === 1 ? "" : "s"}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedPartnerName && (
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    padding: "14px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>{selectedPartnerName}</div>
                        <div style={{ fontSize: "13px", color: "#64748b", marginTop: "2px" }}>Partner overview, filters, and bill actions</div>
                      </div>
                      <button className="btn-primary" type="button" onClick={() => setModal("partnership")}>
                        <FiPlus size={16} style={{ marginRight: "6px" }} /> Add Bill
                      </button>
                    </div>

                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "12px",
                      padding: "12px",
                      background: "#fff",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0"
                    }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                        <input
                          type="date"
                          value={partnershipFromDate}
                          onChange={(e) => {
                            setPartnershipFromDate(e.target.value);
                            setPartnershipTotalAmount(null);
                          }}
                          style={{ padding: "8px 12px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                        />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                        <input
                          type="date"
                          value={partnershipToDate}
                          onChange={(e) => {
                            setPartnershipToDate(e.target.value);
                            setPartnershipTotalAmount(null);
                          }}
                          style={{ padding: "8px 12px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-end" }}>
                        <button className="btn-secondary" type="button" onClick={handlePartnershipDateFilter}>
                          Apply Date Filter
                        </button>
                      </div>
                    </div>
                    {isAdmin && (
                      <div style={{
                        padding: "14px 16px",
                        background: "#fff",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0"
                      }}>
                        <div style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
                          Total Partnership Bill Amount (Site)
                        </div>
                        <div style={{ marginTop: "4px", fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>
                          {partnershipTotalLoading
                            ? "Loading..."
                            : partnershipTotalAmount === null
                              ? "Apply a date range"
                              : `₹${formatAmountIN(partnershipTotalAmount)}`}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedPartnerName ? (
                  <div className="table-wrapper" style={{ background: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <table style={{ tableLayout: "fixed" }}>
                      <thead>
                        <tr>
                          <th>Partner Name</th>
                          <th>Bill Number</th>
                          <th>Amount</th>
                          <th>Description</th>
                          <th>Attachment</th>
                          {isAdmin && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {visiblePartnershipBills.length === 0 ? (
                          <tr>
                            <td colSpan={isAdmin ? 6 : 5} style={{ padding: "16px", textAlign: "center", color: "#64748b" }}>
                              No partnership records found for this partner.
                            </td>
                          </tr>
                        ) : visiblePartnershipBills.map((row, index) => (
                          <tr key={row.id} className={index % 2 === 0 ? "even" : ""}>
                            <td style={{ wordBreak: "break-word" }}>{getPartnershipRowPartnerName(row)}</td>
                            <td style={{ wordBreak: "break-word" }}>{getPartnershipRowBillNo(row) || "-"}</td>
                            <td style={{ wordBreak: "break-word" }}>{formatAmountIN(row.amount)}</td>
                            <td style={{ wordBreak: "break-word" }}>{row.description || row.document?.description || "-"}</td>
                            <td>
                              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                <button className="btn-secondary" type="button" onClick={() => handlePartnershipView(row)} aria-label="View attachment" title="View attachment" style={{ padding: "8px", display: "inline-flex" }}>
                                  <FiFileText size={15} />
                                </button>
                                <button className="btn-secondary" type="button" onClick={() => handlePartnershipDownload(row)} aria-label="Download attachment" title="Download attachment" style={{ padding: "8px", display: "inline-flex" }}>
                                  <FiDownload size={15} />
                                </button>
                              </div>
                            </td>
                            <td>
                              {isAdmin && (
                                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                  <button className="btn-secondary" type="button" onClick={() => { setEditingPartnership(row); setModal("partnership"); }} aria-label="Edit partnership bill" title="Edit" style={{ padding: "8px", display: "inline-flex" }}>
                                    <FiEdit size={15} />
                                  </button>
                                  <button className="btn-secondary" type="button" onClick={() => handlePartnershipDelete(row)} aria-label="Delete partnership bill" title="Delete" style={{ padding: "8px", display: "inline-flex", color: "#ef4444", borderColor: "#ef4444" }}>
                                    <FiTrash2 size={15} />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ padding: "16px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                    Select a partner to view their bills.
                  </div>
                )}
              </div>
            )}

            {activeTab === "salaryreport" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Report Month</label>
                  <select
                    value={salaryReportMonth}
                    onChange={e => setSalaryReportMonth(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  >
                    <option value="">Select month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Report Year</label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={salaryReportYear}
                    onChange={e => setSalaryReportYear(e.target.value)}
                    placeholder="YYYY"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadSalaryReport}
                    style={{
                      padding: "10px 18px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      width: "100%"
                    }}
                  >
                    <FiDownload size={16} /> Download Salary Report
                  </button>
                </div>
              </div>
            )}

            {activeTab === "employeefnf" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                gap: "12px",
                alignItems: "end",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Search Employee</label>
                  <input
                    type="search"
                    value={employeeFNFSearch}
                    onChange={event => {
                      setEmployeeFNFSearch(event.target.value);
                      setPagination(prev => ({ ...prev, employeefnf: 0 }));
                    }}
                    placeholder="Name or employee code"
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Exit Date</label>
                  <input
                    type="date"
                    value={employeeFNFStartDate}
                    onChange={event => {
                      setEmployeeFNFStartDate(event.target.value);
                      setPagination(prev => ({ ...prev, employeefnf: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Exit Date</label>
                  <input
                    type="date"
                    value={employeeFNFEndDate}
                    onChange={event => {
                      setEmployeeFNFEndDate(event.target.value);
                      setPagination(prev => ({ ...prev, employeefnf: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setEmployeeFNFSearch("");
                    setEmployeeFNFStartDate("");
                    setEmployeeFNFEndDate("");
                    setPagination(prev => ({ ...prev, employeefnf: 0 }));
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* ATTENDANCE FILTERS */}
            {activeTab === "attendance" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Employee</label>
                  <input
                    type="text"
                    placeholder="Enter employee name"
                    value={selectedEmployee}
                    onChange={e => {
                      setSelectedEmployee(e.target.value);
                      setPagination(prev => ({ ...prev, attendance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={attendanceStartDate}
                    onChange={e => {
                      setAttendanceStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, attendance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={attendanceEndDate}
                    onChange={e => {
                      setAttendanceEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, attendance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px", justifyContent: "flex-end" }}>
                  <button
                    onClick={downloadAttendanceExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    <FiDownload size={16} /> Export Excel
                  </button>
                  <button
                    onClick={searchAttendanceByDate}
                    style={{
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setSelectedEmployee("");
                      setAttendanceStartDate("");
                      setAttendanceEndDate("");
                      setPagination(prev => ({ ...prev, attendance: 0 }));
                      fetchTabData("attendance");
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* EMPLOYEE ADVANCE FILTERS */}
            {activeTab === "employeeadvance" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={employeeAdvanceStartDate}
                    onChange={e => {
                      setEmployeeAdvanceStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, employeeadvance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={employeeAdvanceEndDate}
                    onChange={e => {
                      setEmployeeAdvanceEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, employeeadvance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={searchEmployeeAdvanceByDate}
                    style={{
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Search
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setEmployeeAdvanceStartDate("");
                      setEmployeeAdvanceEndDate("");
                      setPagination(prev => ({ ...prev, employeeadvance: 0 }));
                      fetchTabData("employeeadvance");
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* SALARY FILTERS */}
            {activeTab === "salary" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label htmlFor="salary-filter-from" style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    id="salary-filter-from"
                    type="date"
                    value={salaryStartDate}
                    onChange={e => {
                      setSalaryStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, salary: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label htmlFor="salary-filter-to" style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    id="salary-filter-to"
                    type="date"
                    value={salaryEndDate}
                    onChange={e => {
                      setSalaryEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, salary: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    type="button"
                    onClick={searchSalaryByDate}
                    style={{
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Search
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSalaryStartDate("");
                      setSalaryEndDate("");
                      setPagination(prev => ({ ...prev, salary: 0 }));
                      fetchTabData("salary");
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* PURCHASE FILTERS */}
            {activeTab === "purchases" && (
              <>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "12px", padding: "12px", background: "#f3f4f6", borderRadius: "6px", border: "1px solid #dbeafe", borderLeft: "4px solid #0284c7" }}>
                  {[
                    { label: "All", value: "ALL" },
                    { label: "Paid", value: "PAID" },
                    { label: "Unpaid", value: "UNPAID" },
                    { label: "Partial Payment", value: "PARTIAL_PAYMENT" }
                  ].map(status => (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => {
                        const selectedStatus = status.value;
                        setPurchasePaymentStatus(selectedStatus);
                        setPagination(prev => ({ ...prev, purchases: 0 }));
                        fetchTabData("purchases", selectedStatus);
                      }}
                      style={{
                        padding: "10px 18px",
                        borderRadius: "6px",
                        border: purchasePaymentStatus === status.value ? "1px solid #0284c7" : "1px solid #d0d7de",
                        background: purchasePaymentStatus === status.value ? "#0284c7" : "#fff",
                        color: purchasePaymentStatus === status.value ? "#fff" : "#475569",
                        cursor: "pointer",
                        fontWeight: purchasePaymentStatus === status.value ? "700" : "600",
                        fontSize: "13px",
                        boxShadow: purchasePaymentStatus === status.value ? "0 2px 8px rgba(2, 132, 199, 0.16)" : "none"
                      }}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: "12px",
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#f0f9ff",
                  borderRadius: "6px",
                  borderLeft: "4px solid #0284c7"
                }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={purchaseStartDate}
                    onChange={e => {
                      setPurchaseStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, purchases: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={purchaseEndDate}
                    onChange={e => {
                      setPurchaseEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, purchases: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
                  <button
                    onClick={downloadPurchaseExcel}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <FiDownload size={16} /> Export Excel
                  </button>
                  <button
                    onClick={searchPurchasesByDate}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setPurchaseStartDate("");
                      setPurchaseEndDate("");
                      setPagination(prev => ({ ...prev, purchases: 0 }));
                      fetchTabData("purchases", purchasePaymentStatus, undefined);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

            </>
            )}

            {/* BILL FILTERS */}
            {activeTab === "bills" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Search Bill No</label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      value={billSearch}
                      onChange={e => {
                        setBillSearch(e.target.value);
                        setPagination(prev => ({ ...prev, bills: 0 }));
                      }}
                      placeholder="BL001"
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "4px",
                        fontSize: "14px"
                      }}
                    />
                    <button
                      onClick={() => {
                        setPagination(prev => ({ ...prev, bills: 0 }));
                        fetchTabData("bills");
                      }}
                      style={{
                        padding: "8px 14px",
                        background: "#0284c7",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600"
                      }}
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={billStartDate}
                    onChange={e => {
                      setBillStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, bills: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={billEndDate}
                    onChange={e => {
                      setBillEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, bills: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", flexDirection: "column", gap: "8px" }}>
                  <button
                    onClick={() => {
                      setPagination(prev => ({ ...prev, bills: 0 }));
                      fetchTabData("bills");
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setBillSearch("");
                      setBillStartDate("");
                      setBillEndDate("");
                      setPagination(prev => ({ ...prev, bills: 0 }));
                      fetchTabData("bills");
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* MANAGEMENT EXPENSE DATE FILTERS */}
            {activeTab === "managementExpense" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={managementExpenseStartDate}
                    onChange={e => {
                      setManagementExpenseStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, managementExpense: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={managementExpenseEndDate}
                    onChange={e => {
                      setManagementExpenseEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, managementExpense: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setPagination(prev => ({ ...prev, managementExpense: 0 }));
                      fetchTabData("managementExpense", undefined, undefined, managementExpenseStartDate, managementExpenseEndDate);
                    }}
                    style={{ padding: "8px 16px", background: "#0f766e", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", fontWeight: "600", width: "100%" }}
                  >
                    Search
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setManagementExpenseStartDate("");
                      setManagementExpenseEndDate("");
                      setPagination(prev => ({ ...prev, managementExpense: 0 }));
                      fetchTabData("managementExpense");
                    }}
                    style={{ padding: "8px 16px", background: "#0284c7", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", fontWeight: "600", width: "100%" }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {activeTab === "workorders" && (
              <div role="group" aria-label="Work Order type" style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
                {["All", "Company", "Client"].map(type => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={selectedWorkOrderType === type}
                    onClick={() => {
                      setSelectedWorkOrderType(type);
                      setPagination(prev => ({ ...prev, workorders: 0 }));
                      fetchTabData("workorders", undefined, undefined, workorderStartDate, workorderEndDate, type);
                    }}
                    style={{
                      padding: "10px 18px",
                      borderRadius: "6px",
                      border: selectedWorkOrderType === type ? "1px solid #0284c7" : "1px solid #d0d7de",
                      background: selectedWorkOrderType === type ? "#0284c7" : "#fff",
                      color: selectedWorkOrderType === type ? "#fff" : "#475569",
                      cursor: "pointer",
                      fontWeight: selectedWorkOrderType === type ? "700" : "600",
                      fontSize: "13px",
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}

            {/* WORKORDERS FILTERS */}
            {activeTab === "workorders" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>Search Work Orders</label>
                  <div style={{ position: "relative" }}>
                    <FiSearch size={15} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
                    <input
                      type="search"
                      value={workOrderSearch}
                      onChange={e => {
                        setWorkOrderSearch(e.target.value);
                        setPagination(prev => ({ ...prev, workorders: 0 }));
                      }}
                      placeholder="WO number or project"
                      aria-label="Search work orders by number or project"
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "8px 12px 8px 32px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "4px",
                        fontSize: "14px"
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={workorderStartDate}
                    onChange={e => {
                      setWorkorderStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, workorders: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={workorderEndDate}
                    onChange={e => {
                      setWorkorderEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, workorders: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
                  <button
                    onClick={searchWorkordersByDate}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setWorkOrderSearch("");
                      setWorkorderStartDate("");
                      setWorkorderEndDate("");
                      setPagination(prev => ({ ...prev, workorders: 0 }));
                      fetchTabData("workorders");
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* CONTRACTOR FILTERS */}
            {(activeTab === "contractorwork" || activeTab === "contractorpayment") && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={activeTab === "contractorwork" ? contractorworkStartDate : contractorpaymentStartDate}
                    onChange={e => {
                      if (activeTab === "contractorwork") {
                        setContractorworkStartDate(e.target.value);
                      } else {
                        setContractorpaymentStartDate(e.target.value);
                      }
                      setPagination(prev => ({ ...prev, [activeTab]: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={activeTab === "contractorwork" ? contractorworkEndDate : contractorpaymentEndDate}
                    onChange={e => {
                      if (activeTab === "contractorwork") {
                        setContractorworkEndDate(e.target.value);
                      } else {
                        setContractorpaymentEndDate(e.target.value);
                      }
                      setPagination(prev => ({ ...prev, [activeTab]: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={handleContractorDateSearch}
                    style={{
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Search
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      if (activeTab === "contractorwork") {
                        setContractorworkStartDate("");
                        setContractorworkEndDate("");
                      } else {
                        setContractorpaymentStartDate("");
                        setContractorpaymentEndDate("");
                      }
                      setPagination(prev => ({ ...prev, [activeTab]: 0 }));
                      fetchTabData(activeTab);
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>

                {(activeTab === "contractorwork" || activeTab === "contractorpayment") && (
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button
                      onClick={() => downloadDateFilterExcel({
                        startDate: activeTab === "contractorwork" ? contractorworkStartDate : contractorpaymentStartDate,
                        endDate: activeTab === "contractorwork" ? contractorworkEndDate : contractorpaymentEndDate,
                        sectionKey: activeTab,
                        filename: activeTab === "contractorwork" ? "contractor_work" : "contractor_payment",
                        toastMessage: activeTab === "contractorwork"
                          ? "Contractor work records downloaded successfully!"
                          : "Contractor payment records downloaded successfully!",
                      })}
                      style={{
                        padding: "8px 16px",
                        background: "#0284c7",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        width: "100%"
                      }}
                    >
                      <FiDownload size={16} /> Export Excel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* SELL FILTERS */}
            {activeTab === "sells" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={sellStartDate}
                    onChange={e => {
                      setSellStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, sells: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={sellEndDate}
                    onChange={e => {
                      setSellEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, sells: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
                  <button
                    onClick={downloadSellExcel}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <FiDownload size={16} /> Export Excel
                  </button>
                  <button
                    onClick={searchSellsByDate}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setSellStartDate("");
                      setSellEndDate("");
                      setPagination(prev => ({ ...prev, sells: 0 }));
                      fetchTabData("sells");
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* EXPENSE FILTERS */}
            {activeTab === "expenses" && (
              <div style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "16px",
                padding: "12px",
                background: "#f3f4f6",
                borderRadius: "6px",
                border: "1px solid #dbeafe",
                borderLeft: "4px solid #0284c7"
              }}>
                {['All', 'Mess', 'Machinery', 'Site', 'Refreshments', 'Office Expenses', 'Hardware', 'Mobile Recharge', 'GST Tax', 'Travel', 'Others'].map(type => (
                  <button
                    key={type}
                    onClick={() => handleExpenseTypeFilter(type)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '6px',
                      border: selectedExpenseType === type ? '1px solid #0284c7' : '1px solid #d0d7de',
                      background: selectedExpenseType === type ? '#0284c7' : '#fff',
                      color: selectedExpenseType === type ? '#fff' : '#475569',
                      cursor: 'pointer',
                      fontWeight: selectedExpenseType === type ? '700' : '600',
                      fontSize: '13px',
                      transition: 'all 0.2s',
                      boxShadow: selectedExpenseType === type ? '0 2px 8px rgba(2, 132, 199, 0.16)' : 'none'
                    }}
                  >
                    {type === "Office Expenses" ? "Office" : type}
                  </button>
                ))}
              </div>
            )}
            {activeTab === "expenses" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={expenseStartDate}
                    onChange={e => {
                      setExpenseStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, expenses: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={expenseEndDate}
                    onChange={e => {
                      setExpenseEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, expenses: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
                  <button
                    onClick={downloadExpenseExcel}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <FiDownload size={16} /> Export Excel
                  </button>
                  <button
                    onClick={searchExpensesByDate}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setExpenseStartDate("");
                      setExpenseEndDate("");
                      setPagination(prev => ({ ...prev, expenses: 0 }));
                      fetchTabData("expenses", undefined, selectedExpenseType);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      height: "38px",
                      width: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* DIESEL FILTERS */}
            {activeTab === "diesel" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={dieselStartDate}
                    onChange={e => {
                      setDieselStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, diesel: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={dieselEndDate}
                    onChange={e => {
                      setDieselEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, diesel: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadDieselExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setDieselStartDate("");
                      setDieselEndDate("");
                      setPagination(prev => ({ ...prev, diesel: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* PETROL FILTERS */}
            {activeTab === "petrol" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={petrolStartDate}
                    onChange={e => {
                      setPetrolStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, petrol: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={petrolEndDate}
                    onChange={e => {
                      setPetrolEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, petrol: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadPetrolExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setPetrolStartDate("");
                      setPetrolEndDate("");
                      setPagination(prev => ({ ...prev, petrol: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* LABOUR COAST FILTERS */}
            {activeTab === "labourcoast" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={labourStartDate}
                    onChange={e => {
                      setLabourStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, labourcoast: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={labourEndDate}
                    onChange={e => {
                      setLabourEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, labourcoast: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadLabourCoastExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setLabourStartDate("");
                      setLabourEndDate("");
                      setPagination(prev => ({ ...prev, labourcoast: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* LABOUR FILTERS */}
            {activeTab === "labour" && (
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                {/* Search Input */}
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
                    Search Labour
                  </label>
                  <div className="search-wrapper" style={{ marginTop: "4px", marginBottom: 0 }}>
                    <FiSearch size={18} className="search-icon" />
                    <input
                      value={labourSearch}
                      onChange={e => searchLabours(e.target.value)}
                      placeholder="Search by Labour Name, Code, Type or Wage..."
                      className="search-input"
                      style={{
                        padding: "8px 12px 8px 36px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "4px",
                        fontSize: "14px",
                        width: "100%"
                      }}
                    />
                  </div>
                  <p style={{ fontSize: "12px", color: "#64748b", margin: "4px 0 0 0" }}>
                    💡 Tip: Search by Labour Name, Code, Type or Daily Wage
                  </p>
                </div>
              </div>
            )}

            {/* LABOUR ADVANCE FILTERS */}
            {activeTab === "labouradvance" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={labouradvanceStartDate}
                    onChange={e => {
                      setLabouradvanceStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, labouradvance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={labouradvanceEndDate}
                    onChange={e => {
                      setLabouradvanceEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, labouradvance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadLabourAdvanceExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setLabouradvanceStartDate("");
                      setLabouradvanceEndDate("");
                      setPagination(prev => ({ ...prev, labouradvance: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* LABOUR ATTENDANCE FILTERS */}
            {activeTab === "labourattendance" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={labourattendanceStartDate}
                    onChange={e => {
                      setLabourattendanceStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, labourattendance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={labourattendanceEndDate}
                    onChange={e => {
                      setLabourattendanceEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, labourattendance: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadLabourAttendanceExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setLabourattendanceStartDate("");
                      setLabourattendanceEndDate("");
                      setPagination(prev => ({ ...prev, labourattendance: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* LABOUR PAYMENT FILTERS */}
            {activeTab === "labourpayment" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={labourpaymentStartDate}
                    onChange={e => {
                      setLabourpaymentStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, labourpayment: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={labourpaymentEndDate}
                    onChange={e => {
                      setLabourpaymentEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, labourpayment: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadLabourPaymentExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setLabourpaymentStartDate("");
                      setLabourpaymentEndDate("");
                      setPagination(prev => ({ ...prev, labourpayment: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* CONCRETE PRODUCTION FILTERS */}
            {activeTab === "concreteproduction" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={concreteproductionStartDate}
                    onChange={e => {
                      setConcreteproductionStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, concreteproduction: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={concreteproductionEndDate}
                    onChange={e => {
                      setConcreteproductionEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, concreteproduction: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadConcreteProductionExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    <FiDownload size={16} /> Download Excel
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={() => {
                      setConcreteproductionStartDate("");
                      setConcreteproductionEndDate("");
                      setPagination(prev => ({ ...prev, concreteproduction: 0 }));
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      width: "100%"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* FUEL TRANSACTION FILTERS */}
            {activeTab === "fueltransaction" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={fueltransactionStartDate}
                    onChange={e => {
                      setFueltransactionStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, fueltransaction: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={fueltransactionEndDate}
                    onChange={e => {
                      setFueltransactionEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, fueltransaction: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
                  <button
                    onClick={searchFuelTransactionData}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setFueltransactionStartDate("");
                      setFueltransactionEndDate("");
                      setPagination(prev => ({ ...prev, fueltransaction: 0 }));
                      fetchTabData("fueltransaction");
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadFuelTransactionExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      width: "100%"
                    }}
                  >
                    <FiDownload size={16} /> Export Excel
                  </button>
                </div>
              </div>
            )}

            {activeTab === "fuelpump" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    type="date"
                    value={fuelpumpStartDate}
                    onChange={e => {
                      setFuelpumpStartDate(e.target.value);
                      setPagination(prev => ({ ...prev, fuelpump: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    type="date"
                    value={fuelpumpEndDate}
                    onChange={e => {
                      setFuelpumpEndDate(e.target.value);
                      setPagination(prev => ({ ...prev, fuelpump: 0 }));
                    }}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      fontSize: "14px"
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "8px" }}>
                  <button
                    onClick={searchFuelPumpData}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0f766e",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setFuelpumpStartDate("");
                      setFuelpumpEndDate("");
                      setPagination(prev => ({ ...prev, fuelpump: 0 }));
                      fetchTabData("fuelpump");
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600"
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={downloadFuelPumpExcel}
                    style={{
                      padding: "8px 16px",
                      background: "#0284c7",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      width: "100%"
                    }}
                  >
                    <FiDownload size={16} /> Export Excel
                  </button>
                </div>
              </div>
            )}

            {activeTab === "fuelpayment" && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "12px",
                marginBottom: "16px",
                padding: "12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                borderLeft: "4px solid #0284c7"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label htmlFor="fuel-payment-filter-from" style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>From Date</label>
                  <input
                    id="fuel-payment-filter-from"
                    type="date"
                    value={fuelpaymentStartDate}
                    onChange={event => {
                      setFuelpaymentStartDate(event.target.value);
                      setPagination(prev => ({ ...prev, fuelpayment: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <label htmlFor="fuel-payment-filter-to" style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>To Date</label>
                  <input
                    id="fuel-payment-filter-to"
                    type="date"
                    value={fuelpaymentEndDate}
                    onChange={event => {
                      setFuelpaymentEndDate(event.target.value);
                      setPagination(prev => ({ ...prev, fuelpayment: 0 }));
                    }}
                    style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                  <button
                    type="button"
                    onClick={searchFuelPaymentData}
                    style={{ flex: 1, padding: "8px 16px", background: "#0f766e", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFuelpaymentStartDate("");
                      setFuelpaymentEndDate("");
                      setPagination(prev => ({ ...prev, fuelpayment: 0 }));
                      fetchTabData("fuelpayment");
                    }}
                    style={{ flex: 1, padding: "8px 16px", background: "#0284c7", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* TOOLBAR */}
            {activeTab === "purchases" ? (
              <>
                <TabBar
                  tab={tabLabel}
                  onAdd={async () => {
                    if (activeTab === "contractorwork") {
                      try {
                        await fetchTabData("contractor");
                      } catch (e) {
                        console.error("Failed to refresh contractors before opening contractor work modal", e);
                      }
                    }
                    setModal(activeTab);
                  }}
                  showDownload={false}
                  showUpload={false}
                  fileRef={fileRef}
                />
                <PurchaseUpload selectedSite={selectedSite} onUploadSuccess={handlePurchaseUploadSuccess} />
              </>
            ) : activeTab === "sells" ? (
              <>
                <TabBar
                  tab={tabLabel}
                  onAdd={async () => {
                    if (activeTab === "contractorwork") {
                      try {
                        await fetchTabData("contractor");
                      } catch (e) {
                        console.error("Failed to refresh contractors before opening contractor work modal", e);
                      }
                    }
                    setModal(activeTab);
                  }}
                  showDownload={false}
                  showUpload={false}
                  fileRef={fileRef}
                />
                <SellUpload selectedSite={selectedSite} onUploadSuccess={handleSellUploadSuccess} />
              </>
            ) : activeTab === "expenses" ? (
              <>
                <TabBar
                  tab={tabLabel}
                  onAdd={async () => {
                    await fetchManagementExpenseEmployees();
                    setModal(activeTab);
                  }}
                  showDownload={false}
                  showUpload={false}
                  fileRef={fileRef}
                />
                <ExpenseUpload selectedSite={selectedSite} onUploadSuccess={() => fetchTabData("expenses")} />
              </>
            ) : activeTab === "workorders" ? (
              <>
                <TabBar
                  tab={tabLabel}
                  onAdd={async () => {
                    setModal(activeTab);
                  }}
                  showDownload={false}
                  showUpload={false}
                  fileRef={fileRef}
                />
                <WorkOrderUpload selectedSite={selectedSite} onUploadSuccess={() => fetchTabData("workorders")} />
              </>
            ) : activeTab === "managementExpense" ? (
              <TabBar
                tab={tabLabel}
                onAdd={async () => {
                  await fetchManagementExpenseEmployees();
                  setModal(activeTab);
                }}
                showDownload={false}
                showUpload={false}
                fileRef={fileRef}
              />
            ) : activeTab === "partnership" ? null : (
              <TabBar
                tab={tabLabel}
                onAdd={async () => {
                  if (activeTab === "contractorwork") {
                    try {
                      await fetchTabData("contractor");
                    } catch (e) {
                      console.error("Failed to refresh contractors before opening contractor work modal", e);
                    }
                  }
                  if (activeTab === "employeeadvance") {
                    await fetchTabData("employees");
                  }
                  if (activeTab === "salary") {
                    await fetchTabData("employees");
                  }
                  setModal(activeTab);
                }}
                onAddAttendance={activeTab === "labourattendance" ? () => setModal("labourattendance") : undefined}
                onDownload={() => handleDownload(activeTab)}
                onUpload={e => handleUpload(activeTab, e)}
                fileRef={fileRef}
                showAdd={activeTab !== "salaryreport" && activeTab !== "dashboard" && activeTab !== "settings" && activeTab !== "labourattendance"}
                showDownload={shouldShowBulkActionTab(activeTab)}
                showUpload={shouldShowBulkActionTab(activeTab)}
              />
            )}

            {/* TABLE */}
            {activeTab !== "salaryreport" && isDataView && (
              <DataTable
                cols={COL_MAP[activeTab] || []}
                rows={getPaginatedData(activeTab)}
                onDelete={isAdmin
                  ? (id, row) => {
                      if (activeTab === "employees") {
                        const employeeCode = row?.empCode ?? row?.empcode ?? row?.emp_code ?? row?.employeeCode;
                        if (!employeeCode) {
                          showToast("Employee code is missing; cannot delete employee", "error");
                          return;
                        }
                        handleDelete(activeTab, employeeCode);
                        return;
                      }
                      if (activeTab === "fueltransaction") {
                        const confirmed = window.confirm("Are you sure you want to delete this plant fuel record?");
                        if (!confirmed) return;
                        fetchWithAuth(API_ENDPOINTS.fueltransaction.deleteById(id), { method: "DELETE" })
                          .then(async (response) => {
                            if (!response.ok) {
                              throw new Error("Failed to delete plant fuel record");
                            }
                            await fetchTabData("fueltransaction");
                            showToast("Plant fuel record deleted successfully", "success");
                          })
                          .catch(err => {
                            console.error("Delete plant fuel error:", err);
                            showToast(err.message || "Failed to delete plant fuel record", "error");
                          });
                        return;
                      }
                      handleDelete(activeTab, id);
                    }
                  : undefined}
                onPreviewInvoice={activeTab === "salary"
                  ? handlePreviewSalaryDocument
                  : activeTab === "employeefnf"
                  ? handlePreviewEmployeeFNFDocument
                  : activeTab === "fuelpump" || activeTab === "fueltransaction"
                    ? row => handlePreviewFuelDocument(row, activeTab)
                  : activeTab === "workorders"
                  ? handlePreviewWorkOrderDocument
                  : activeTab === "purchases"
                    ? handlePreviewInvoice
                    : activeTab === "sells"
                      ? handlePreviewSellInvoice
                      : activeTab === "bills"
                        ? handlePreviewBillInvoice
                        : activeTab === "contractorpayment"
                          ? handlePreviewContractorPaymentDocument
                        : activeTab === "managementExpense"
                          ? handlePreviewManagementExpenseDocument
                        : undefined}
                onDownloadAttachment={activeTab === "employees"
                  ? handleDownloadEmployeeAadhar
                  : activeTab === "contractor"
                    ? handleDownloadContractorDocuments
                  : activeTab === "salary"
                    ? handleDownloadSalaryDocument
                  : ["purchases", "sells", "bills", "workorders", "expenses", "managementExpense", "employeefnf", "contractorpayment", "fuelpump", "fueltransaction", "fuelpayment"].includes(activeTab)
                    ? handleDownloadAttachment
                    : undefined}
                onEditPaymentStatus={activeTab === "purchases" ? handleUpdatePurchasePaymentStatus : activeTab === "sells" ? handleUpdateSellPaymentStatus : undefined}
                onEditEmployeeStatus={activeTab === "employees" ? handleUpdateEmployeeStatusAndSite : activeTab === "contractor" ? handleUpdateContractorActiveStatus : activeTab === "labour" ? handleUpdateLabourStatus : undefined}
                onEditEmployeeSite={activeTab === "employees" ? handleUpdateEmployeeSite : undefined}
                onEditRow={activeTab === "employees"
                  ? handleEditEmployee
                  : isAdmin && activeTab === "salary"
                    ? handleEditSalary
                  : isAdmin && activeTab === "employeefnf"
                    ? row => setEditingEmployeeFNF(row)
                  : isAdmin && activeTab === "contractor"
                    ? handleEditContractor
                    : isAdmin && activeTab === "bills"
                      ? handleEditBill
                    : isAdmin && activeTab === "managementExpense"
                      ? handleEditManagementExpense
                    : isAdmin && activeTab === "purchases"
                      ? handleEditPurchase
                      : isAdmin && activeTab === "sells"
                        ? handleEditSell
                        : isAdmin && activeTab === "labourpayment"
                          ? row => setEditingLabourPayment(row)
                        : isAdmin && activeTab === "expenses"
                          ? handleEditExpense
                          : isAdmin && activeTab === "workorders"
                            ? handleEditWorkOrder
                          : isAdmin && activeTab === "contractorwork"
                            ? handleEditContractorWork
                            : isAdmin && activeTab === "contractorpayment"
                              ? handleEditContractorPayment
                              : isAdmin && activeTab === "labour"
                                ? handleEditLabour
                                : isAdmin && activeTab === "fuelpump"
                                  ? handleEditFuelPump
                                  : isAdmin && activeTab === "fueltransaction"
                                    ? handleEditFuelTransaction
                                      : isAdmin && activeTab === "fuelpayment"
                                        ? handleEditFuelPayment
                                                    : isAdmin && activeTab === "concreteproduction"
                                                    ? handleEditConcreteProduction
                                                    : undefined}
                onViewRow={activeTab === "employees" ? setEmployeeDetails : undefined}
                employeeSiteOptions={employeeSiteOptions}
                invoiceLoadingId={invoiceLoadingId}
                idKey={activeTab === "employees" ? "empcode" : activeTab === "employeefnf" ? "fnfId" : activeTab === "salary" ? "salaryId" : activeTab === "bills" ? "billId" : activeTab === "managementExpense" ? "id" : activeTab === "labour" ? "id" : "id"}
                isAdmin={isAdmin}
                onViewExpenseAttachment={activeTab === "expenses" ? handleViewExpenseAttachment : undefined}
                onViewEmployeeAadhar={activeTab === "employees" ? handleViewEmployeeAadhar : undefined}
                onViewContractorDocuments={activeTab === "contractor" ? handleViewContractorDocuments : undefined}
                actionColumnPosition="end"
                onRowClick={activeTab === "purchases" ? handlePurchaseRowClick : activeTab === "workorders" ? handleWorkOrderRowClick : undefined}
                selectedRowId={activeTab === "workorders"
                  ? (selectedWorkOrder?.id ?? selectedWorkOrder?.workOrderId ?? selectedWorkOrder?.workorderId ?? selectedWorkOrder?.work_order_id ?? selectedWorkOrder?.woNumber ?? selectedWorkOrder?.workOrderNo)
                  : (selectedPurchase?.purchaseId ?? selectedPurchase?.purchaseNo ?? selectedPurchase?.purchaseNumber ?? selectedPurchase?.purchase_no ?? selectedPurchase?.id)}
                expandedRowId={activeTab === "workorders"
                  ? (selectedWorkOrder?.id ?? selectedWorkOrder?.workOrderId ?? selectedWorkOrder?.workorderId ?? selectedWorkOrder?.work_order_id ?? selectedWorkOrder?.woNumber ?? selectedWorkOrder?.workOrderNo)
                  : (selectedPurchase?.purchaseId ?? selectedPurchase?.purchaseNo ?? selectedPurchase?.purchaseNumber ?? selectedPurchase?.purchase_no ?? selectedPurchase?.id)}
                renderExpandedRow={activeTab === "workorders" ? renderWorkOrderBills : row => {
                  const purchaseItems = Array.isArray(row.purchaseItems) ? row.purchaseItems : [];
                  return (
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Purchase Items for {row.purchaseNo || row.purchaseNumber || row.purchase_no}</div>
                          <div style={{ color: "#475569", fontSize: 14 }}>
                            {row.partyName ? `Party: ${row.partyName}` : ""}
                            {row.purchaseDate ? ` · Date: ${row.purchaseDate}` : ""}
                            {row.paymentStatus ? ` · Status: ${row.paymentStatus}` : ""}
                          </div>
                        </div>
                        <button className="btn-secondary" type="button" onClick={() => setSelectedPurchase(null)} style={{ minWidth: 120, justifyContent: "center" }}>
                          Close
                        </button>
                      </div>
                      {purchaseItems.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ background: "#edf2ff", color: "#0f172a", textAlign: "left" }}>
                                <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>Material Name</th>
                                <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>Quantity</th>
                                <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>Rate</th>
                                <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "center" }}>Amount</th>
                                <th style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0" }}>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseItems.map((item, index) => (
                                <tr key={item.purchaseItemId ?? item.id ?? index} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                  <td style={{ padding: "10px 12px" }}>{item.materialName || item.material || item.itemName || "-"}</td>
                                  <td style={{ padding: "10px 12px", textAlign: "center" }}>{item.quantity ?? "-"}</td>
                                  <td style={{ padding: "10px 12px", textAlign: "center" }}>{item.rate ?? "-"}</td>
                                  <td style={{ padding: "10px 12px", textAlign: "center" }}>{item.amount ?? "-"}</td>
                                  <td style={{ padding: "10px 12px" }}>{item.remarks || item.remark || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ color: "#475569", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc" }}>
                          No items found for this purchase.
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            )}
            {/* PAGINATION */}
            {activeTab !== "salaryreport" && isDataView && (
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
                padding: "12px 16px",
                background: "#f8fafc",
                borderRadius: "6px"
              }}>
                <button
                  className="btn-secondary"
                  onClick={() => handlePageChange(activeTab, pagination[activeTab] - 1)}
                  disabled={pagination[activeTab] === 0}
                  style={{
                    opacity: pagination[activeTab] === 0 ? 0.5 : 1,
                    cursor: pagination[activeTab] === 0 ? "not-allowed" : "pointer"
                  }}
                >
                  ← Previous
                </button>
                <span style={{ color: "#64748b", fontWeight: "500" }}>
                  Page {pagination[activeTab] + 1} of {getTotalPages(activeTab) === 0 ? 1 : getTotalPages(activeTab)}
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => handlePageChange(activeTab, pagination[activeTab] + 1)}
                  disabled={pagination[activeTab] >= getTotalPages(activeTab) - 1}
                  style={{
                    opacity: pagination[activeTab] >= getTotalPages(activeTab) - 1 ? 0.5 : 1,
                    cursor: pagination[activeTab] >= getTotalPages(activeTab) - 1 ? "not-allowed" : "pointer"
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* MODALS */}
      {showAddUserModal && (
        <Modal title="Add User" onClose={() => setShowAddUserModal(false)}>
          <div>
            {addUserError && (
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#fee2e2", color: "#b91c1c" }}>
                {addUserError}
              </div>
            )}

            <div className="form-grid">
              <div className="field">
                <label>First Name</label>
                <input type="text" value={newUser.firstName} onChange={e => setNewUser(prev => ({ ...prev, firstName: e.target.value }))} />
              </div>

              <div className="field">
                <label>Last Name</label>
                <input type="text" value={newUser.lastName} onChange={e => setNewUser(prev => ({ ...prev, lastName: e.target.value }))} />
              </div>

              <div className="field">
                <label>Username</label>
                <input type="text" value={newUser.username} onChange={e => setNewUser(prev => ({ ...prev, username: e.target.value }))} />
              </div>

              <div className="field">
                <label>Email</label>
                <input type="email" value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} />
              </div>

              <div className="field">
                <label>Password</label>
                <input type="password" value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} />
              </div>

              <div className="field">
                <label>User Type</label>
                <select value={newUser.userType} onChange={e => setNewUser(prev => ({ ...prev, userType: e.target.value }))}>
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>

              <div className="field">
                <label>Site</label>
                <select
                  value={newUser.site?.id || ""}
                  onChange={e => {
                    const selected = employeeSiteOptions.find(opt => String(opt.id) === String(e.target.value));
                    setNewUser(prev => ({
                      ...prev,
                      site: selected?.siteObject || selected || null,
                      siteId: selected?.id || ""
                    }));
                  }}
                >
                  <option value="">Select site</option>
                  {employeeSiteOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
              <button className="btn-secondary" type="button" onClick={() => setShowAddUserModal(false)}>Cancel</button>
              <button className="btn-primary" type="button" disabled={addUserLoading || !newUser.firstName || !newUser.lastName || !newUser.username || !newUser.email || !newUser.password || !newUser.site?.id} onClick={handleCreateUser}>
                {addUserLoading ? "Saving..." : "Save User"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {editingUser && (
        <Modal title="Edit User" onClose={() => setEditingUser(null)}>
          <div>
            <div className="form-grid">
              <div className="field">
                <label>First Name</label>
                <input type="text" value={editingUser.firstName || ""} onChange={e => setEditingUser(prev => ({ ...prev, firstName: e.target.value }))} />
              </div>

              <div className="field">
                <label>Last Name</label>
                <input type="text" value={editingUser.lastName || ""} onChange={e => setEditingUser(prev => ({ ...prev, lastName: e.target.value }))} />
              </div>

              <div className="field">
                <label>Username</label>
                <input type="text" value={editingUser.username || ""} onChange={e => setEditingUser(prev => ({ ...prev, username: e.target.value }))} />
              </div>

              <div className="field">
                <label>Email</label>
                <input type="email" value={editingUser.email} onChange={e => setEditingUser(prev => ({ ...prev, email: e.target.value }))} />
              </div>

              <div className="field">
                <label>Password</label>
                <input type="password" value={editingUser.password || ""} onChange={e => setEditingUser(prev => ({ ...prev, password: e.target.value }))} placeholder="Leave blank to keep existing password" />
              </div>

              <div className="field">
                <label>User Type</label>
                <select value={editingUser.userType || "USER"} onChange={e => setEditingUser(prev => ({ ...prev, userType: e.target.value }))}>
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>

              <div className="field">
                <label>Site</label>
                <select
                  value={editingUser.site?.id || ""}
                  onChange={e => {
                    const selected = employeeSiteOptions.find(opt => String(opt.id) === String(e.target.value));
                    setEditingUser(prev => ({
                      ...prev,
                      site: selected?.siteObject || selected || null,
                    }));
                  }}
                >
                  <option value="">Select site</option>
                  {employeeSiteOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
              <button className="btn-secondary" type="button" onClick={() => setEditingUser(null)}>Cancel</button>
              <button className="btn-primary" type="button" onClick={() => handleSaveUserEdit(editingUser)}>
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
      {modal === "employees" && (
        <Modal title="Add Employee" onClose={() => setModal(null)}>
          <EmployeeForm selectedSite={selectedSite} onSave={r => handleSave("employees", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingEmployee && (
        <Modal title="Edit Employee" onClose={() => setEditingEmployee(null)}>
          <EmployeeForm selectedSite={selectedSite} initialData={editingEmployee} onSave={r => handleSaveEmployeeEdit(r)} onClose={() => setEditingEmployee(null)} />
        </Modal>
      )}
      {employeeDetails && (
        <Modal
          title={`Employee Details${employeeDetails.name ? ` - ${employeeDetails.name}` : ""}`}
          onClose={() => setEmployeeDetails(null)}
          contentStyle={{ maxWidth: "760px", width: "min(96vw, 760px)" }}
        >
          <EmployeeDetails employee={employeeDetails} siteOptions={employeeSiteOptions} />
        </Modal>
      )}
      {modal === "attendance" && (
        <Modal
          title="Add Attendance"
          contentStyle={{ maxWidth: "1200px", width: "min(96vw, 1200px)" }}
          onClose={() => setModal(null)}
        >
          <AttendanceForm employees={data.employees} selectedSite={selectedSite} onSave={r => handleSave("attendance", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "employeefnf" && (
        <Modal title="Add Employee FNF" onClose={() => setModal(null)}>
          <EmployeeFNFForm employees={data.employees} onSave={handleSaveEmployeeFNF} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingEmployeeFNF && (
        <Modal title="Edit Employee FNF" onClose={() => setEditingEmployeeFNF(null)}>
          <EmployeeFNFForm employees={data.employees} initialData={editingEmployeeFNF} onSave={handleSaveEmployeeFNF} onClose={() => setEditingEmployeeFNF(null)} />
        </Modal>
      )}
      {modal === "employeeadvance" && (
        <Modal title="Add Advance Salary" onClose={() => setModal(null)}>
          <EmployeeAdvanceForm employees={data.employees} selectedSite={selectedSite} onSave={r => handleSave("employeeadvance", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "salary" && (
        <Modal title="Add Salary" onClose={() => setModal(null)}>
          <SalaryForm employees={Array.isArray(data.employees) ? data.employees : []} selectedSite={selectedSite} onSave={handleSaveSalary} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingSalary && (
        <Modal title="Edit Salary" onClose={() => setEditingSalary(null)}>
          <SalaryForm employees={Array.isArray(data.employees) ? data.employees : []} selectedSite={selectedSite} initialData={editingSalary} onSave={handleSaveSalary} onClose={() => setEditingSalary(null)} />
        </Modal>
      )}
      {modal === "purchases" && (
        <Modal title="Add Purchase" onClose={() => setModal(null)}>
          <PurchaseForm selectedSite={selectedSite} onSave={r => handleSave("purchases", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingPurchase && (
        <Modal title="Edit Purchase" onClose={() => setEditingPurchase(null)}>
          <PurchaseForm selectedSite={selectedSite} initialData={editingPurchase} onSave={r => handleSavePurchaseEdit(r)} onClose={() => setEditingPurchase(null)} />
        </Modal>
      )}
      {modal === "bills" && (
        <Modal title="Add Bill" onClose={() => setModal(null)}>
          <BillForm
            selectedSite={selectedSite}
            workOrders={Array.isArray(data.workorders) ? data.workorders : []}
            onSave={r => handleSave("bills", r)}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
      {editingBill && (
        <Modal title="Edit Bill" onClose={() => setEditingBill(null)}>
          <BillForm
            selectedSite={selectedSite}
            workOrders={Array.isArray(data.workorders) ? data.workorders : []}
            initialData={editingBill}
            onSave={handleSaveBillEdit}
            onClose={() => setEditingBill(null)}
          />
        </Modal>
      )}
      {modal === "managementExpense" && (
        <Modal title="Add Management Expense" onClose={() => setModal(null)}>
          <ManagementExpenseForm
            employees={managementExpenseEmployees}
            managementEmployees={managementGroupEmployees}
            onSave={r => handleSave("managementExpense", r)}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
      {editingManagementExpense && (
        <Modal title="Edit Management Expense" onClose={() => setEditingManagementExpense(null)}>
          <ManagementExpenseForm
            employees={managementExpenseEmployees}
            managementEmployees={managementGroupEmployees}
            initialData={editingManagementExpense}
            onSave={handleSaveManagementExpenseEdit}
            onClose={() => setEditingManagementExpense(null)}
          />
        </Modal>
      )}
      {modal === "partner" && (
        <Modal title={editingPartner ? "Edit Partner" : "Add Partner"} onClose={() => { setModal(null); setEditingPartner(null); }}>
          <AddPartnerForm onSave={handlePartnerSave} initialData={editingPartner} onClose={() => { setModal(null); setEditingPartner(null); }} />
        </Modal>
      )}
      {modal === "partnership" && (
        <Modal title={editingPartnership ? "Edit Partnership Bill" : "Add Partnership Bill"} onClose={() => { setModal(null); setEditingPartnership(null); }}>
          <PartnershipForm
            onSave={r => handlePartnershipUpload(r)}
            onClose={() => { setModal(null); setEditingPartnership(null); }}
            partners={partnershipPartners}
            selectedSite={selectedSite}
            selectedPartnerId={selectedPartnerId}
            initialData={editingPartnership}
          />
        </Modal>
      )}
      {modal === "sells" && (
        <Modal title="Add Sell" onClose={() => setModal(null)}>
          <SellForm selectedSite={selectedSite} onSave={r => handleSave("sells", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingSell && (
        <Modal title="Edit Sell" onClose={() => setEditingSell(null)}>
          <SellForm selectedSite={selectedSite} initialData={editingSell} onSave={r => handleSaveSellEdit(r)} onClose={() => setEditingSell(null)} />
        </Modal>
      )}
      {employeeDocumentRow && (
        <Modal title={employeeDocumentAction === "download" ? "Download Employee Document" : "View Employee Document"} onClose={() => setEmployeeDocumentRow(null)}>
          <div className="employee-document-grid">
            <button className="employee-document-option" type="button" onClick={() => handleViewEmployeeDocument("aadhar")}>
              <span className="employee-document-icon"><FiUser size={22} /></span>
              <span className="employee-document-name">Aadhaar</span>
              <span className="employee-document-action">{employeeDocumentAction === "download" ? "Download document" : "View document"}</span>
            </button>
            <button className="employee-document-option" type="button" onClick={() => handleViewEmployeeDocument("pan")}>
              <span className="employee-document-icon"><FiCreditCard size={22} /></span>
              <span className="employee-document-name">PAN</span>
              <span className="employee-document-action">{employeeDocumentAction === "download" ? "Download document" : "View document"}</span>
            </button>
            <button className="employee-document-option" type="button" onClick={() => handleViewEmployeeDocument("bank")}>
              <span className="employee-document-icon"><FiBriefcase size={22} /></span>
              <span className="employee-document-name">Bank Details</span>
              <span className="employee-document-action">{employeeDocumentAction === "download" ? "Download document" : "View document"}</span>
            </button>
          </div>
        </Modal>
      )}
      {contractorDocumentRow && (
        <Modal title={contractorDocumentAction === "download" ? "Download Contractor Document" : "View Contractor Document"} onClose={() => setContractorDocumentRow(null)}>
          <div className="employee-document-grid">
            <button className="employee-document-option" type="button" onClick={() => handleViewContractorDocument("aadhar")}>
              <span className="employee-document-icon"><FiUser size={22} /></span>
              <span className="employee-document-name">Aadhar</span>
              <span className="employee-document-action">{contractorDocumentAction === "download" ? "Download document" : "View document"}</span>
            </button>
            <button className="employee-document-option" type="button" onClick={() => handleViewContractorDocument("pan")}>
              <span className="employee-document-icon"><FiCreditCard size={22} /></span>
              <span className="employee-document-name">PAN</span>
              <span className="employee-document-action">{contractorDocumentAction === "download" ? "Download document" : "View document"}</span>
            </button>
            <button className="employee-document-option" type="button" onClick={() => handleViewContractorDocument("bank")}>
              <span className="employee-document-icon"><FiBriefcase size={22} /></span>
              <span className="employee-document-name">Bank Details</span>
              <span className="employee-document-action">{contractorDocumentAction === "download" ? "Download document" : "View document"}</span>
            </button>
          </div>
        </Modal>
      )}
      {modal === "diesel" && (
        <Modal title="Add Diesel" onClose={() => setModal(null)}>
          <DieselForm selectedSite={selectedSite} onSave={r => handleSave("diesel", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "petrol" && (
        <Modal title="Add Petrol" onClose={() => setModal(null)}>
          <PetrolForm selectedSite={selectedSite} onSave={r => handleSave("petrol", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {invoicePreview.open && (
        <Modal title={`Document Preview - ${invoicePreview.filename}`} onClose={closeInvoicePreview} contentStyle={{ width: 'min(90vw, 1100px)', height: '80vh' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 12 }}>
            <button
              className="btn-secondary"
              onClick={() => {
                const link = document.createElement('a');
                link.href = invoicePreview.url;
                link.download = invoicePreview.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              style={{ padding: '8px 12px', fontSize: 13 }}
            >
              Download Document
            </button>
          </div>
          {invoicePreview.contentType.includes('pdf') ? (
            <iframe
              title="Document Preview"
              src={invoicePreview.url}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%' }}>
              <iframe
                title="Document Preview"
                src={invoicePreview.url}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          )}
        </Modal>
      )}
      {modal === "fueltransaction" && (
        <Modal title="Add Plant Fuel Management" onClose={() => setModal(null)}>
          <FuelTransactionForm selectedSite={selectedSite} onSave={r => handleSave("fueltransaction", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "fuelpump" && (
        <Modal title="Add Pump Fuel Management" onClose={() => setModal(null)}>
          <FuelPumpForm selectedSite={selectedSite} onSave={r => handleSave("fuelpump", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "fuelpayment" && (
        <Modal title="Add Fuel Payment" onClose={() => setModal(null)}>
          <FuelPaymentForm selectedSite={selectedSite} onSave={handleSaveFuelPayment} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingFuelPump && (
        <Modal title="Edit Pump Fuel Management" onClose={() => setEditingFuelPump(null)}>
          <FuelPumpForm selectedSite={selectedSite} initialData={editingFuelPump} onSave={r => handleSaveFuelPumpEdit(r)} onClose={() => setEditingFuelPump(null)} />
        </Modal>
      )}
      {editingFuelTransaction && (
        <Modal title="Edit Plant Fuel Management" onClose={() => setEditingFuelTransaction(null)}>
          <FuelTransactionForm selectedSite={selectedSite} initialData={editingFuelTransaction} onSave={r => handleSaveFuelTransactionEdit(r)} onClose={() => setEditingFuelTransaction(null)} />
        </Modal>
      )}
      {editingFuelPayment && (
        <Modal title="Edit Fuel Payment" onClose={() => setEditingFuelPayment(null)}>
          <FuelPaymentForm selectedSite={selectedSite} initialData={editingFuelPayment} onSave={handleSaveFuelPayment} onClose={() => setEditingFuelPayment(null)} />
        </Modal>
      )}
      {modal === "expenses" && (
        <Modal title="Add Expense" onClose={() => setModal(null)}>
          <ExpenseForm
            onSave={r => handleSave("expenses", r)}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
      {modal === "workorders" && (
        <Modal title={editingWorkOrder ? "Edit Work Order" : "Add Work Order"} onClose={() => { setModal(null); setEditingWorkOrder(null); }}>
          <WorkOrderForm
            initialData={editingWorkOrder}
            onSave={r => editingWorkOrder ? handleSaveWorkOrderEdit(r) : handleSave("workorders", r)}
            onClose={() => { setModal(null); setEditingWorkOrder(null); }}
            selectedSite={selectedSite}
          />
        </Modal>
      )}
      {editingExpense && (
        <Modal title="Edit Expense" onClose={() => setEditingExpense(null)}>
          <ExpenseForm initialData={editingExpense} onSave={r => handleSaveExpenseEdit(r)} onClose={() => setEditingExpense(null)} />
        </Modal>
      )}
      {modal === "labourcoast" && (
        <Modal title="Add Labour Coast" onClose={() => setModal(null)}>
          <LabourCoastForm onSave={r => handleSave("labourcoast", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "labour" && (
        <Modal title="Add Labour" onClose={() => setModal(null)}>
          <LabourForm onSave={r => handleSave("labour", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal === "labourattendance" && (
        <Modal
          title="Add Labour Attendance"
          contentStyle={{ maxWidth: "1200px", width: "min(96vw, 1200px)" }}
          onClose={() => setModal(null)}
        >
          <LabourAttendanceForm
            labours={data.labour}
            selectedSite={selectedSite}
            onSave={r => handleSave("labourattendance", r)}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
      {modal === "labourpayment" && (
        <Modal title="Add Labour Payment" onClose={() => setModal(null)}>
          <LabourPaymentForm selectedSite={selectedSite} onSave={r => handleSave("labourpayment", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingLabourPayment && (
        <Modal title="Edit Labour Payment" onClose={() => setEditingLabourPayment(null)}>
          <LabourPaymentForm
            selectedSite={selectedSite}
            initialData={editingLabourPayment}
            onSave={handleSaveLabourPaymentEdit}
            onClose={() => setEditingLabourPayment(null)}
          />
        </Modal>
      )}
      {editingLabour && (
        <Modal title="Edit Labour" onClose={() => setEditingLabour(null)}>
          <LabourForm initialData={editingLabour} onSave={handleSaveLabourEdit} onClose={() => setEditingLabour(null)} />
        </Modal>
      )}
      {modal === "contractor" && (
        <Modal title="Add Contractor" onClose={() => setModal(null)}>
          <ContractorForm selectedSite={selectedSite} onSave={r => handleSave("contractor", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingContractor && (
        <Modal title="Edit Contractor" onClose={() => setEditingContractor(null)}>
          <ContractorForm initialData={editingContractor} selectedSite={selectedSite} onSave={r => handleSaveContractorEdit(r)} onClose={() => setEditingContractor(null)} />
        </Modal>
      )}
      {modal === "contractorwork" && (
        <Modal title="Add Contractor Work Details" onClose={() => setModal(null)}>
          <ContractorWorkForm selectedSite={selectedSite} onSave={r => handleSave("contractorwork", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingContractorWork && (
        <Modal title="Edit Contractor Work Details" onClose={() => setEditingContractorWork(null)}>
          <ContractorWorkForm initialData={editingContractorWork} selectedSite={selectedSite} onSave={r => handleSaveContractorWorkEdit(r)} onClose={() => setEditingContractorWork(null)} />
        </Modal>
      )}
      {modal === "contractorpayment" && (
        <Modal title="Add Contractor Payment" onClose={() => setModal(null)}>
          <ContractorPaymentForm selectedSite={selectedSite} onSave={r => handleSave("contractorpayment", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingContractorPayment && (
        <Modal title="Edit Contractor Payment" onClose={() => setEditingContractorPayment(null)}>
          <ContractorPaymentForm initialData={editingContractorPayment} selectedSite={selectedSite} onSave={r => handleSaveContractorPaymentEdit(r)} onClose={() => setEditingContractorPayment(null)} />
        </Modal>
      )}
      {modal === "concreteproduction" && (
        <Modal title="Add Concrete Production" onClose={() => setModal(null)}>
          <ConcreteProductionForm selectedSite={selectedSite} onSave={r => handleSave("concreteproduction", r)} onClose={() => setModal(null)} />
        </Modal>
      )}
      {editingConcreteProduction && (
        <Modal title="Edit Concrete Production" onClose={() => setEditingConcreteProduction(null)}>
          <ConcreteProductionForm
            selectedSite={selectedSite}
            initialData={editingConcreteProduction}
            onSave={handleSaveConcreteProductionEdit}
            onClose={() => setEditingConcreteProduction(null)}
          />
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
        </div>
      </div>
    </div>
  );
}